// App.js
import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signOut, signInAnonymously, signInWithPopup, getRedirectResult, updateProfile, signInWithCustomToken, deleteUser, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, EmailAuthProvider, linkWithCredential, linkWithPopup, updatePassword, reauthenticateWithCredential } from "firebase/auth";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, setDoc, getDocs, query, where, getDoc, getDocFromServer, collectionGroup, documentId, deleteField, arrayUnion, arrayRemove, increment, enableNetwork, disableNetwork, serverTimestamp, runTransaction } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "./firebase";
import { Pencil, Trash2, History, ChevronRight, ChevronLeft, ChevronDown, ArrowLeft, ArrowRight, Users, LogOut, MessageSquare, Plus, Check, Eye, EyeOff, Shield, ShieldCheck, FileText, Camera, SlidersHorizontal, UserCog, CircleDollarSign, Coins, ListChecks, Info, Sparkles, LogIn, X, Flag } from "lucide-react";
import logo from "./logo.svg";
import "./theme.css";
import { MenuBar } from "./components/ui/bottom-menu";
import { HomeIcon, RankingIcon, NewGameIcon, SessionsIcon, ProfileIcon, LiveGameIcon } from "./components/ui/nav-icons";
import { InstallPrompt } from "./components/ui/InstallPrompt";
import { showConfirm, showAlert } from "./components/ui/app-dialog";
import { TimelineEditor } from "./components/ui/timeline-editor";
import { TimelineDetail } from "./components/ui/timeline-detail";
import { SessionLog } from "./components/ui/session-log";
import { GroupSetupCard } from "./components/ui/group-setup";
import { cleanGameEvents, applyTimelineEdit, outFromEvents } from "./timeline";
import { useBootSplash } from "./components/ui/boot-splash";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

// The poker trainer is its own little world; load it only when someone opens it.
const loadPokerTrainer = () => import("./game/PokerTrainer");
const PokerTrainer = React.lazy(loadPokerTrainer);

const DEFAULT_RANKING_CONFIG = {
  netMultiplier: 0.18,
  firstPlaceBonus: 30,
  secondPlaceBonus: 12,
  thirdPlaceBonus: 5,
  jackpotBonus: 0,
  rebuyPenalty: 2,
  streakStep: 2,
  streakMax: 10,
  streakMinNetEuro: 10,
  gameCapMin: -45,
  gameCapMax: 80
};

// App version shown in Profile › Legal. Keep in sync with package.json.
const APP_VERSION = "0.32.0";

// Release notes powering the Profile › "What's new" screen. Newest first.
// When you ship a release, bump APP_VERSION + package.json and prepend an entry.
const CHANGELOG = [
  {
    version: "0.32.0",
    date: "October 2026",
    changes: [
      "Picking a default period is simpler: choose 1–4 weeks, 1–6 months or a year from one list.",
    ],
  },
  {
    version: "0.31.0",
    date: "October 2026",
    changes: [
      "Set any tournament length: the group's default period can repeat every 1–52 weeks or 1–24 months, from a start date you choose.",
    ],
  },
  {
    version: "0.30.0",
    date: "October 2026",
    changes: [
      "Run weekly or monthly tournaments: moderators can make charts and rankings open on the current week, month or quarter, in Group settings → Table Defaults.",
      "TallyPot is now always dark, and dates follow your phone's language and region.",
    ],
  },
  {
    version: "0.29.0",
    date: "October 2026",
    changes: [
      "New here? Setup now explains how groups, players and invites work, and Home shows the steps to get your group ready.",
    ],
  },
  {
    version: "0.28.0",
    date: "October 2026",
    changes: [
      "Edit a game's timeline: fix start and end times, move buy-ins, rebuys and busts, or add one that was missed. Works on the live table and on saved games.",
      "Every game now has a change log that shows who added each buy-in, who edited what and why. Nobody can delete it, so everyone can check their own buy-ins.",
      "Moderators can stop editors from changing finished games in Permissions.",
    ],
  },
  {
    version: "0.27.0",
    date: "October 2026",
    changes: [
      "New to poker? The trainer now has a How to play guide that explains hands, seats like BB and SB, and everything on screen.",
    ],
  },
  {
    version: "0.26.0",
    date: "October 2026",
    changes: [
      "Crazy Pineapple is now playable in the poker trainer.",
    ],
  },
  {
    version: "0.25.0",
    date: "October 2026",
    changes: [
      "Omaha is now playable in the poker trainer.",
    ],
  },
  {
    version: "0.24.0",
    date: "October 2026",
    changes: [
      "Flick the jackpot orbit hard to open a hidden poker trainer. Play Hold'em against bots and get coached on every decision.",
    ],
  },
  {
    version: "0.23.0",
    date: "October 2026",
    changes: [
      "Spin the jackpot orbit with your finger.",
      "The live game timeline is marked in time played, so you can see when everyone bought in.",
    ],
  },
  {
    version: "0.22.2",
    date: "October 2026",
    changes: [
      "The jackpot on Home is now an orbit: the pot in the middle, past winners around it, and a seat for the next one.",
    ],
  },
  {
    version: "0.21.0",
    date: "October 2026",
    changes: [
      "Game details show a timeline of the night: when everyone bought in, rebought and went out.",
      "Record the jackpot winner and cards when you save a game's results.",
    ],
  },
  {
    version: "0.20.0",
    date: "September 2026",
    changes: [
      "The live game card on Home shows each player's buy-ins, rebuys and busts on a timeline.",
      "A new opening animation, and confirmations now appear as in-app sheets.",
    ],
  },
  {
    version: "0.19.0",
    date: "September 2026",
    changes: [
      "Pick a time period from the new Filter button, and see 1st, 2nd and jackpot wins at a glance in the ranking.",
      "Removing a member now changes the invite code, so they can only come back when someone invites them again.",
    ],
  },
  {
    version: "0.18.0",
    date: "September 2026",
    changes: [
      "A new look: light and dark mode (Profile → Appearance), cleaner lists, and wins and losses in green and red.",
      "Home shows top movers, your win rate, a profit/loss list with trends, and how close the jackpot is to its record.",
    ],
  },
  {
    version: "0.17.4",
    date: "September 2026",
    changes: [
      "Home now shows where you stand, and you can invite friends from the top of the screen.",
      "Rankings now count rebuys correctly for any buy-in size.",
    ],
  },
  {
    version: "0.17.3",
    date: "September 2026",
    changes: [
      "1st place now pays one buy-in into the jackpot, and the live screen shows exactly how much.",
    ],
  },
  {
    version: "0.17.0",
    date: "July 2026",
    changes: [
      "Notifications when the table opens, when results are in, and when someone hits the jackpot — each can be turned off separately.",
      "Group chat has been removed — TallyPot focuses on the ledger.",
    ],
  },
  {
    version: "0.16.0",
    date: "July 2026",
    changes: [
      "Change your password in the app, under Profile → Account → Sign-in methods.",
      "Join a group and you're added to the roster automatically — no waiting on a moderator to create your player.",
      "One-device sign-in: signing in somewhere new signs you out everywhere else.",
    ],
  },
  {
    version: "0.15.0",
    date: "July 2026",
    changes: [
      "Live player cards redesigned — swipe a card to add or remove a buy-in, or tap it to manage a player.",
      "Players sort by buy-ins, and the pot now lives in the game-clock card.",
    ],
  },
  {
    version: "0.14.0",
    date: "July 2026",
    changes: [
      "Editors can now add players, not just moderators.",
    ],
  },
  {
    version: "0.13.0",
    date: "July 2026",
    changes: [
      "Add a new player straight from the live table — no trip to settings mid-game.",
      "The jackpot amount prefills with the current balance.",
    ],
  },
  {
    version: "0.12.0",
    date: "July 2026",
    changes: [
      "Profile tab reorganized into a native-style settings screen, with Sign-in methods and Notifications as full pages.",
    ],
  },
  {
    version: "0.11.0",
    date: "July 2026",
    changes: [
      "Rebuys redesigned — swipe the buy-in counter right to add one, left to remove.",
    ],
  },
  {
    version: "0.10.0",
    date: "July 2026",
    changes: [
      "Group chat — send messages, react, reply, and edit or delete.",
      "Push notifications for new chat messages.",
      "Invite links drop you straight into the group — no code to type.",
    ],
  },
  {
    version: "0.9.0",
    date: "June 2026",
    changes: [
      "Swipeable, paginated date filter that's easier to use on phones.",
      "People settings rebuilt into a single, directly-editable hub.",
      "Jackpot history as a full-page view with per-player totals and a filter.",
      "Live-session shortcut added to the bottom navigation.",
    ],
  },
  {
    version: "0.8.0",
    date: "May 2026",
    changes: [
      "Head-to-head records with a full win–loss–tie breakdown.",
      "Hexagonal achievement badges shown next to names across profiles and stats.",
      "Settle-Up to square balances quickly after a game.",
      "Refreshed navigation with minimal icons and monogram avatars.",
    ],
  },
  {
    version: "0.7.0",
    date: "April 2026",
    changes: [
      "Jackpot history with per-player totals.",
      "Export your session history to CSV.",
      "Win-celebration confetti when you save a game.",
    ],
  },
  {
    version: "0.6.0",
    date: "March 2026",
    changes: [
      "Restyled every screen for a cleaner, mobile-first feel.",
      "Profit/loss trends chart with a theme-aware legend.",
      "Polished dark theme throughout the app.",
    ],
  },
];
const PULL_REFRESH_THRESHOLD = 72;
const PULL_REFRESH_MAX_DISTANCE = 112;
const PULL_REFRESH_SETTLE_MS = 650;
const IMAGE_URL_CACHE_PREFIX = "tp-image-cache:";
const IMAGE_URL_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14;

const APP_NAME = "TallyPot";


// Privacy: emails and unapproved photos must never be written into group-readable
// documents (members/*, players/*).
// - email always stripped (lives in users/{uid}/profile/main only)
// - uid is intentionally kept — it is required for the collectionGroup membership
//   query (where("uid", "==", user.uid)) that lets users find their own groups.
//   The uid is already exposed via the doc path, so keeping it as a field adds
//   no new information but is essential for the query to work.
// - profilePhotoURL is shared whenever the user's resolved avatar has one (upload,
//   Google or provider photo); it is empty for the monogram ("generated") case.
//   hasCustomPhoto: true on these docs just means "a shared photo is attached".
function stripMemberPrivateFields(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const { email, profilePhotoURL, hasCustomPhoto, ...rest } = payload;
  rest.email = deleteField();
  Object.assign(rest, getSharedProfilePhotoPatch({ profilePhotoURL, hasCustomPhoto }));
  return rest;
}

// Stable empty list for derived rows, so memo/effect deps don't churn.
const NO_ROWS = Object.freeze([]);

function getSharedProfilePhotoPatch(payload = {}) {
  const profilePhotoURL = String(payload.profilePhotoURL || "").trim();
  return profilePhotoURL
    ? { profilePhotoURL, hasCustomPhoto: true }
    : { profilePhotoURL: deleteField(), hasCustomPhoto: deleteField() };
}

function normalizeRankingConfig(raw = {}) {
  const next = { ...DEFAULT_RANKING_CONFIG, ...(raw || {}) };
  const normalized = {
    netMultiplier: Number(next.netMultiplier) || 0,
    firstPlaceBonus: Number(next.firstPlaceBonus) || 0,
    secondPlaceBonus: Number(next.secondPlaceBonus) || 0,
    thirdPlaceBonus: Number(next.thirdPlaceBonus) || 0,
    jackpotBonus: Number(next.jackpotBonus) || 0,
    rebuyPenalty: Math.max(0, Number(next.rebuyPenalty) || 0),
    streakStep: Math.max(0, Number(next.streakStep) || 0),
    streakMax: Math.max(0, Number(next.streakMax) || 0),
    streakMinNetEuro: Number(next.streakMinNetEuro) || 0,
    gameCapMin: Number(next.gameCapMin) || 0,
    gameCapMax: Number(next.gameCapMax) || 0
  };
  if (normalized.gameCapMin > normalized.gameCapMax) {
    const swap = normalized.gameCapMin;
    normalized.gameCapMin = normalized.gameCapMax;
    normalized.gameCapMax = swap;
  }
  return normalized;
}

// ─── Monogram avatar — "Set B / Minimal" treatment ───────────────────────
// Outline-first monogram: a thin colored ring over a subtle tint, with a light
// monogram. Color is derived deterministically from the name (stable per
// player). Replaces the previous boring-avatars fallback.
const AVATAR_PALETTE = ["#10B981", "#14B8A6", "#F59E0B", "#F87171", "#94A3B8", "#34D399"];
// brighter foreground used for the monogram on the tinted outline variant
const AVATAR_FG_LIGHT = {
  "#10B981": "#34D399",
  "#14B8A6": "#2DD4BF",
  "#F59E0B": "#F59E0B",
  "#F87171": "#F87171",
  "#94A3B8": "#CBD5E1",
  "#34D399": "#34D399",
};

function avatarHashIndex(str, mod) {
  let h = 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 100000;
  return h % mod;
}

function avatarInitials(name) {
  return (
    String(name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
}

const AvatarCircle = memo(function AvatarCircle({ src, alt, fallbackLabel, size = 40 }) {
  const label = fallbackLabel || alt || "User";
  const base = AVATAR_PALETTE[avatarHashIndex(label, AVATAR_PALETTE.length)];
  const fg = AVATAR_FG_LIGHT[base] || base;
  const monoStyle = {
    background: `color-mix(in srgb, ${base} 12%, transparent)`,
    color: fg,
    border: `1.5px solid ${base}`,
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: Math.round(size * 0.36),
    letterSpacing: "0.01em",
    lineHeight: 1,
  };
  return (
    <span className={`avatar-circle${src ? "" : " avatar-circle--mono"}`} style={{ width: size, height: size }} aria-hidden={!alt}>
      {src ? (
        <img
          key={src}
          src={src}
          alt={alt || ""}
          loading="eager"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const sibling = e.currentTarget.nextElementSibling;
            if (sibling) sibling.style.display = "inline-flex";
          }}
        />
      ) : null}
      <span className="avatar-circle-fallback" style={monoStyle}>
        {avatarInitials(label)}
      </span>
    </span>
  );
});

const TelegramLoginButton = memo(function TelegramLoginButton({ botName, botId, onAuth, loading, label = "Telegram", loadingLabel = "Signing in…" }) {
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!botName) return;
    if (document.getElementById("telegram-widget-script")) {
      setScriptReady(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "telegram-widget-script";
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", botName);
    script.setAttribute("data-request-access", "write");
    script.async = true;
    script.onload = () => setScriptReady(true);
    document.head.appendChild(script);
  }, [botName]);

  const handleClick = () => {
    if (!scriptReady || loading || !window.Telegram?.Login) return;
    window.Telegram.Login.auth(
      { bot_id: parseInt(botId, 10), request_access: true },
      (user) => { if (user) onAuth(user); }
    );
  };

  return (
    <button className="btn btn-google btn-telegram" onClick={handleClick} disabled={loading || !scriptReady}>
      <svg viewBox="0 0 24 24" width="20" height="20" style={{ flexShrink: 0 }}>
        <path fill="#2AABEE" d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
      {loading ? loadingLabel : label}
    </button>
  );
});

// Load cardmeister Web Component once
let cardmeisterLoaded = false;
function loadCardmeister() {
  if (cardmeisterLoaded || document.querySelector('script[data-cardmeister]')) return;
  cardmeisterLoaded = true;
  const s = document.createElement('script');
  s.src = 'https://cdn.jsdelivr.net/gh/cardmeister/cardmeister.github.io@master/elements.cardmeister.min.js';
  s.setAttribute('data-cardmeister', '1');
  document.head.appendChild(s);
}

// Map app card notation (e.g. "A♠", "10♥") to cardmeister cid (e.g. "As", "Th")
function toCardmeisterCid(card) {
  const suitMap = { '♠': 's', '♥': 'h', '♦': 'd', '♣': 'c' };
  const suit = suitMap[card.slice(-1)] || 's';
  const rank = card.slice(0, -1);
  const r = rank === '10' ? 'T' : rank;
  return r + suit;
}

function PlayingCard({ card }) {
  const containerRef = useRef(null);
  const cid = toCardmeisterCid(card);

  useEffect(() => {
    loadCardmeister();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const el = document.createElement('playing-card');
    el.setAttribute('cid', cid);
    container.appendChild(el);
  }, [cid]);

  return <div className="playing-card-wrap" ref={containerRef} />;
}

function getLastGroupStorageKey(uid) {
  return `lastActiveGroupId:${uid}`;
}

// Single-device sign-in: each device claims a session id on login and writes it to
// Firestore, overwriting whatever the previously-signed-in device had. Every signed-in
// device watches that doc; if it ever sees a different id than the one it claimed, it
// knows another device just signed in and signs itself out.
function getSessionStorageKey(uid) {
  return `tallypot-session-id:${uid}`;
}
function generateSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getStoredSessionId(uid) {
  try { return localStorage.getItem(getSessionStorageKey(uid)) || ""; } catch { return ""; }
}
function setStoredSessionId(uid, sessionId) {
  try { localStorage.setItem(getSessionStorageKey(uid), sessionId); } catch {}
}
function clearStoredSessionId(uid) {
  try { localStorage.removeItem(getSessionStorageKey(uid)); } catch {}
}

function haptic(pattern = [10], impact = "light") {
  try {
    const feedback = typeof window !== "undefined"
      ? window.Telegram?.WebApp?.HapticFeedback
      : null;
    if (feedback?.impactOccurred) feedback.impactOccurred(impact);
  } catch (_) {}
  if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
}

// Pure helper: resolve payout amounts per player for any enriched game.
// Handles split-first-place (g.splitWinnerId) automatically.
function computePayouts(g, awards) {
  const out = {};
  if (g.splitWinnerId && g.firstWinnerId) {
    const share = g.splitGross || 0;
    out[g.firstWinnerId] = (out[g.firstWinnerId] || 0) + share;
    out[g.splitWinnerId] = (out[g.splitWinnerId] || 0) + share;
  } else {
    if (g.firstWinnerId) out[g.firstWinnerId] = (out[g.firstWinnerId] || 0) + (g.firstGross - g.jpTaken);
    if (g.secondWinnerId) out[g.secondWinnerId] = (out[g.secondWinnerId] || 0) + g.secondGross;
    if (g.thirdWinnerId) out[g.thirdWinnerId] = (out[g.thirdWinnerId] || 0) + (g.thirdGross || 0);
  }
  if (g.jackpotWinnerId && awards) out[g.jackpotWinnerId] = (out[g.jackpotWinnerId] || 0) + (awards[g.id] || 0);
  return out;
}

// Minimal cash-flow settlement: who pays whom in the fewest transfers.
// nets: array of { pid, name, net } where net = payout - buy-in (should sum ~0).
// Greedy match of largest debtor to largest creditor; works in integer cents to avoid float drift.
function computeSettlements(nets) {
  const cents = (x) => Math.round((Number(x) || 0) * 100);
  const creditors = nets.filter((n) => cents(n.net) > 0).map((n) => ({ ...n, rem: cents(n.net) })).sort((a, b) => b.rem - a.rem);
  const debtors = nets.filter((n) => cents(n.net) < 0).map((n) => ({ ...n, rem: -cents(n.net) })).sort((a, b) => b.rem - a.rem);
  const tx = [];
  let i = 0, j = 0;
  while (i < debtors.length && j < creditors.length) {
    const pay = Math.min(debtors[i].rem, creditors[j].rem);
    if (pay > 0) {
      tx.push({ fromPid: debtors[i].pid, fromName: debtors[i].name, toPid: creditors[j].pid, toName: creditors[j].name, amount: pay / 100 });
    }
    debtors[i].rem -= pay;
    creditors[j].rem -= pay;
    if (debtors[i].rem === 0) i++;
    if (creditors[j].rem === 0) j++;
  }
  return tx;
}

// Achievement badge definitions — single source of truth for the profile
// badges section and the stats-table name icons.
const HEX_PATH = "M32 4 L54.5 17 L54.5 47 L32 60 L9.5 47 L9.5 17 Z";
const BADGE_GLYPHS = {
  flame:    ["M12 2c0 3-2 4.5-3.6 6.6C6.9 10.6 6 12.4 6 14.6a6 6 0 0 0 12 0c0-2.6-1.2-4.8-2.9-6.5C13.9 6.7 12.5 4.5 12 2z"],
  spade:    ["M12 2.2C9 6.2 4 8.6 4 12.8a3.7 3.7 0 0 0 6.3 2.6c.1 1.8-.6 3.3-1.8 4.6h7c-1.2-1.3-1.9-2.8-1.8-4.6A3.7 3.7 0 0 0 20 12.8C20 8.6 15 6.2 12 2.2z"],
  bars:     ["M4.8 13h3.4v7H4.8z", "M10.3 8.5h3.4v11.5h-3.4z", "M15.8 4.5h3.4v15.5h-3.4z"],
  chevrons: ["M5 9 L12 4 L19 9 L16.3 9 L12 5.85 L7.7 9 Z", "M5 14 L12 9 L19 14 L16.3 14 L12 10.85 L7.7 14 Z", "M5 19 L12 14 L19 19 L16.3 19 L12 15.85 L7.7 19 Z"],
};

function makeBadgeSVG(glyphName, size, locked, accent) {
  const acc = accent || { base: "#10B981", light: "#34D399", glow: "16,185,129" };
  const s = locked
    ? { hexFill: "rgba(255,255,255,.025)", stroke: "rgba(255,255,255,.11)", sw: 2.4, glyph: "#46556a", filter: "none" }
    : { hexFill: `rgba(${acc.glow},.13)`, stroke: acc.base, sw: 2.4, glyph: acc.light, filter: `drop-shadow(0 3px 8px rgba(${acc.glow},.2))` };
  const paths = (BADGE_GLYPHS[glyphName] || []).map((d, i) =>
    React.createElement("path", { key: i, d })
  );
  const children = [
    React.createElement("path", { key: "hex", d: HEX_PATH, fill: s.hexFill, stroke: s.stroke, strokeWidth: s.sw, strokeLinejoin: "round" }),
    React.createElement("g", { key: "gl", transform: "translate(20,20)", fill: s.glyph }, paths),
  ];
  if (locked) {
    children.push(React.createElement("g", { key: "lock" },
      React.createElement("circle", { cx: 50, cy: 49, r: 9, fill: "#0b0e11", stroke: "rgba(255,255,255,.16)", strokeWidth: 1 }),
      React.createElement("rect", { x: 46.7, y: 48.6, width: 6.6, height: 5, rx: 1.2, fill: "#9aa6b2" }),
      React.createElement("path", { d: "M48.1 48.6 v-1.5 a1.9 1.9 0 0 1 3.8 0 v1.5", fill: "none", stroke: "#9aa6b2", strokeWidth: 1.2 })
    ));
  }
  return React.createElement("svg", {
    width: size, height: size, viewBox: "0 0 64 64",
    style: { display: "block", overflow: "visible", filter: s.filter }
  }, children);
}

function getBadgeProgress(badgeId, stats, formatCurrency) {
  const fmt = formatCurrency || ((n) => `€${Math.round(n)}`);
  if (badgeId === "hot-streak") {
    const cur = Math.min(stats.bestStreak || 0, 3);
    return { pct: Math.round(cur / 3 * 100), label: cur >= 3 ? `Unlocked · ${stats.bestStreak} in a row` : `${cur} / 3 wins` };
  }
  if (badgeId === "jackpot-hunter") {
    const cur = Math.min(stats.jackpots || 0, 3);
    return { pct: Math.round(cur / 3 * 100), label: cur >= 3 ? "Unlocked · 3 jackpots" : `${cur} / 3 jackpots` };
  }
  if (badgeId === "big-night") {
    const raw = stats.maxSingleNet || 0;
    const cur = Math.min(raw, 100);
    const pct = Math.round(cur / 100 * 100);
    return cur >= 100
      ? { pct, label: `Unlocked · +${fmt(raw)}` }
      : { pct, label: `${fmt(cur)} / ${fmt(100)}` };
  }
  if (badgeId === "veteran") {
    const cur = Math.min(stats.games || 0, 50);
    return { pct: Math.round(cur / 50 * 100), label: cur >= 50 ? "Unlocked · 50 nights" : `${cur} / 50 nights` };
  }
  return { pct: 0, label: "" };
}

const BADGE_DEFS = [
  { id: "hot-streak",     icon: "🔥",  label: "Hot Streak",     glyph: "flame",    desc: "Win 3 nights in a row",    earned: (s) => (s.bestStreak || 0) >= 3,    accent: { base: "#F97316", light: "#FB923C", glow: "249,115,22"  } },
  { id: "jackpot-hunter", icon: "🃏",  label: "Jackpot Hunter", glyph: "spade",    desc: "Hit the jackpot 3 times",  earned: (s) => (s.jackpots || 0) >= 3,      accent: { base: "#F59E0B", light: "#FBBF24", glow: "245,158,11"  } },
  { id: "big-night",      icon: "📈", label: "Big Night",      glyph: "bars",     desc: "Net +€100 in one night",   earned: (s) => (s.maxSingleNet || 0) >= 100, accent: { base: "#10B981", light: "#34D399", glow: "16,185,129"  } },
  { id: "veteran",        icon: "🎖️", label: "Veteran",        glyph: "chevrons", desc: "Play 50 game nights",      earned: (s) => (s.games || 0) >= 50,        accent: { base: "#6366F1", light: "#818CF8", glow: "99,102,241"  } },
];
function computeBadges(stats) {
  return BADGE_DEFS.map((b) => ({ id: b.id, icon: b.icon, label: b.label, desc: b.desc, glyph: b.glyph, accent: b.accent, earned: !!b.earned(stats) }));
}

function round5(n) {
  return Math.round(n / 5) * 5;
}

// Same "2h 32m" style as saved games, for a duration still running.
function formatDurationLabel(ms) {
  return formatGameDuration(ms) || "0m";
}

function formatGameDuration(ms) {
  if (!ms || ms <= 0) return null;
  const totalMin = Math.round(ms / 60000);
  // Under a minute usually means the game was logged, not timed: show nothing.
  if (totalMin < 1) return null;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// Committing a rebuy change requires a long, deliberate horizontal drag across
// the player row: at least MIN_DX, strongly horizontal, and either FULL_DX of
// travel or the finger reaching the screen edge ("swipe to the very end").
const LIVE_BUYIN_SWIPE_FULL_DX = 160;
const LIVE_BUYIN_SWIPE_MIN_DX = 100;
const LIVE_BUYIN_SWIPE_EDGE = 24;

function calcPayoutsRounded(pot, hasThird = false) {
  const target = round5(pot);
  if (hasThird) {
    // 3-way split: 50% / 30% / 20%
    let g1 = round5(pot * 0.50);
    let g2 = round5(pot * 0.30);
    let g3 = round5(pot * 0.20);
    g1 = Math.max(0, g1 + (target - (g1 + g2 + g3)));
    return { firstGross: g1, secondGross: g2, thirdGross: g3 };
  }
  const t1 = (2 / 3) * pot;
  const t2 = pot / 3;
  let g1 = round5(t1);
  let g2 = round5(t2);
  g1 = Math.max(0, g1 + (target - (g1 + g2)));
  return { firstGross: g1, secondGross: g2, thirdGross: 0 };
}

// The jackpot contribution taken out of 1st place's winnings. New games store
// their own `jackpotCut` — the game's buy-in, or 0 when the group's jackpot is
// off. Games saved before that field existed keep the flat 5 they were played
// (and settled) with, so past results never shift.
const LEGACY_JACKPOT_CUT = 5;
function getJackpotCut(g) {
  if (g?.jackpotCut == null) return LEGACY_JACKPOT_CUT;
  return Math.max(0, Number(g.jackpotCut) || 0);
}

// Cut for a game being saved now: one buy-in, or nothing if the jackpot is off.
function jackpotCutForNewGame(buyInAmount, jackpotEnabled) {
  return jackpotEnabled ? Math.max(0, Number(buyInAmount) || 0) : 0;
}

// A manually entered game only records each player's total, so its buy-in is
// the smallest total: everyone buys in once, rebuys only add to it.
function inferBuyInFromTotals(buyInsRecord) {
  const amounts = Object.values(buyInsRecord || {}).map(Number).filter((n) => n > 0);
  return amounts.length ? Math.min(...amounts) : 0;
}

// One buy-in for a saved game, used to count buy-ins and rebuys: live games
// store it; manual games only have totals, so it's the smallest one.
function getGameBuyInUnit(g) {
  const stored = Number(g?.buyInAmount) || 0;
  return stored > 0 ? stored : (inferBuyInFromTotals(g?.buyIns) || 5);
}

function enrichGame(g) {
  const pot = Object.values(g.buyIns || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const hasThird = !!(g.thirdWinnerId && !g.splitWinnerId);
  const { firstGross, secondGross, thirdGross } = calcPayoutsRounded(pot, hasThird);
  // jpCut is what 1st place will pay in; jpTaken is what was actually taken
  // (nothing until a winner is set, and never more than 1st place's share).
  const jpCut = Math.min(getJackpotCut(g), firstGross);
  const jpTaken = g.firstWinnerId ? jpCut : 0;
  // Split pot: both players share the entire pot equally (minus jackpot contribution)
  const splitGross = (g.splitWinnerId && g.firstWinnerId) ? (pot - jpTaken) / 2 : 0;
  return { ...g, pot, firstGross, secondGross, thirdGross, jpCut, jpTaken, splitGross };
}

function splitNameParts(name = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function getProfilePreferredName(data = {}) {
  return String(
    data.nickname ||
    data.firstName ||
    data.profileName ||
    data.displayName ||
    ""
  ).trim();
}

function getPlayerPreferredName(player = {}) {
  return String(
    player.groupNickname ||
    player.nickname ||
    player.firstName ||
    player.name ||
    ""
  ).trim();
}

// Hook: fires onBack() when user swipes right from the left edge of the screen.
// edge: how far from the left the touch can start (default 30px)
// minDx: minimum horizontal distance (default 80px)
function useSwipeBack(onBack, { edge = 30, minDx = 80 } = {}) {
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const onBackRef = useRef(onBack);
  useEffect(() => { onBackRef.current = onBack; }, [onBack]);

  useEffect(() => {
    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (t.clientX > edge) { startXRef.current = null; return; }
      startXRef.current = t.clientX;
      startYRef.current = t.clientY;
    };
    const onTouchEnd = (e) => {
      if (startXRef.current == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startXRef.current;
      const dy = Math.abs(t.clientY - (startYRef.current || 0));
      startXRef.current = null;
      if (dx >= minDx && dx > dy * 1.5) {
        haptic([30]);
        onBackRef.current();
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [edge, minDx]);
}

const SECTION_VIS_DEFAULTS = {
  home_jackpot: true,
  home_leader: true,
  home_chart: true,
  home_stats: true,
  ranking_podium: true,
  sessions_highlights: true,
};

// Moderator-chosen period that charts and rankings open on (groups/{id}.defaultPeriod),
// for groups that run weekly/monthly tournaments. "all" (or missing/unknown) =
// All time; otherwise one of the presets below. Groups saved by v0.31.0 may hold
// a custom repeating period `${count}${unit}@${YYYY-MM-DD}` (every `count` weeks
// or months from the start date), which still works.
const GROUP_DEFAULT_PERIOD_OPTIONS = [
  { key: "all", label: "All time" },
  { key: "1w", label: "1 week" },
  { key: "2w", label: "2 weeks" },
  { key: "3w", label: "3 weeks" },
  { key: "4w", label: "4 weeks" },
  { key: "1m", label: "1 month" },
  { key: "2m", label: "2 months" },
  { key: "3m", label: "3 months" },
  { key: "4m", label: "4 months" },
  { key: "6m", label: "6 months" },
  { key: "1y", label: "1 year" },
];
const REPEAT_PERIOD_MAX = { w: 52, m: 24 };
const REPEAT_PERIOD_RE = /^(\d{1,2})([wm])@(\d{4}-\d{2}-\d{2})$/;
// Presets as repeating periods counted from 2024-01-01, a Monday and a 1 January:
// weeks start on Monday, and months line up with the year (Jan–Feb, Q1–Q4, H1/H2).
const PRESET_PERIOD_KEYS = { "1w": "1w", "2w": "2w", "3w": "3w", "4w": "4w", "1m": "1m", "2m": "2m", "3m": "3m", "4m": "4m", "6m": "6m", "1y": "12m" };

function parseRepeatPeriod(key) {
  const raw = PRESET_PERIOD_KEYS[key] ? `${PRESET_PERIOD_KEYS[key]}@2024-01-01` : String(key || "");
  const m = REPEAT_PERIOD_RE.exec(raw);
  if (!m) return null;
  const count = Number(m[1]);
  const unit = m[2];
  if (count < 1 || count > REPEAT_PERIOD_MAX[unit]) return null;
  if (isNaN(new Date(`${m[3]}T00:00:00`).getTime())) return null;
  return { count, unit, start: m[3] };
}

// Date shifted by whole months, keeping the day but clamping it to the month's
// length (a period starting on the 31st runs to the 30th in shorter months).
function addMonthsClamped(y, m, d, months) {
  const lastDay = new Date(y, m + months + 1, 0).getDate();
  return new Date(y, m + months, Math.min(d, lastDay));
}

// The repeat of `period` that contains `now` (before the start date, the one before it).
function repeatPeriodRange({ count, unit, start }, now = new Date()) {
  const [sy, sm, sd] = start.split("-").map(Number);
  if (unit === "w") {
    const days = Math.floor((Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) - Date.UTC(sy, sm - 1, sd)) / 86400000);
    const len = count * 7;
    const startDate = new Date(sy, sm - 1, sd + Math.floor(days / len) * len);
    const end = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + len, 0, 0, 0, -1);
    return { start: startDate, end };
  }
  const monthsSince = (now.getFullYear() - sy) * 12 + (now.getMonth() - (sm - 1))
    - (now.getDate() < Math.min(sd, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()) ? 1 : 0);
  const idx = Math.floor(monthsSince / count);
  const startDate = addMonthsClamped(sy, sm - 1, sd, idx * count);
  const next = addMonthsClamped(sy, sm - 1, sd, (idx + 1) * count);
  const end = new Date(next.getFullYear(), next.getMonth(), next.getDate(), 0, 0, 0, -1);
  return { start: startDate, end };
}

const formatPeriodDay = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
const formatPeriodRange = ({ start, end }) => `${formatPeriodDay(start)} – ${formatPeriodDay(end)}`;

// The period containing `now` for a preset or repeating key (null for others).
function calendarPeriodRange(key, now = new Date()) {
  const repeat = parseRepeatPeriod(key);
  return repeat ? repeatPeriodRange(repeat, now) : null;
}

function calendarPeriodLabel(key, now = new Date()) {
  if (key === "1w") return "This Week";
  if (key === "1m") return "This Month";
  if (key === "1y") return "This Year";
  const range = calendarPeriodRange(key, now);
  if (!range) return "";
  const month = (d) => d.toLocaleDateString(undefined, { month: "short" });
  return `${month(range.start)} – ${month(range.end)}`;
}
const normalizeGroupDefaultPeriod = (v) => (v === "all" || parseRepeatPeriod(v) ? v : "all");
function groupDefaultPeriodLabel(v) {
  const key = normalizeGroupDefaultPeriod(v);
  const preset = GROUP_DEFAULT_PERIOD_OPTIONS.find((o) => o.key === key);
  if (preset) return preset.label;
  const repeat = parseRepeatPeriod(key);
  const unit = repeat.unit === "w" ? "week" : "month";
  const every = repeat.count === 1 ? `Every ${unit}` : `Every ${repeat.count} ${unit}s`;
  return `${every} from ${new Date(`${repeat.start}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

// Dates, times and numbers follow the phone's own language/region setting.
// undefined lets Intl fall back to the runtime default.
function resolveDeviceLocale() {
  if (typeof navigator === "undefined") return undefined;
  return navigator.languages?.[0] || navigator.language || undefined;
}

// Read and clear invite URL params at module load time — before React initialises.
// This guarantees the values are captured no matter when the component mounts.
const _urlInviteCode = (() => {
  try {
    const c = new URLSearchParams(window.location.search).get("join");
    return c ? c.trim().toUpperCase() : "";
  } catch (_) { return ""; }
})();
const _urlInviteGroupName = (() => {
  try {
    const g = new URLSearchParams(window.location.search).get("gname");
    return g ? g.trim() : "";
  } catch (_) { return ""; }
})();
if (_urlInviteCode) {
  window.history.replaceState({}, "", window.location.pathname);
}

// Lightweight self-contained confetti — fires a burst whenever `trigger` changes.
// `intense` (e.g. a jackpot) throws more pieces and leans on the brass palette.
const ConfettiBurst = memo(function ConfettiBurst({ trigger, intense }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!trigger) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const palette = intense
      ? ["#E3C26B", "#C8A24B", "#10B981", "#34D399", "#F1F5F9"]
      : ["#10B981", "#34D399", "#E3C26B", "#F1F5F9", "#F59E0B"];
    const count = intense ? 160 : 100;
    const now = () => performance.now();
    const start = now();
    const DURATION = 2400;

    const parts = Array.from({ length: count }, () => {
      const fromLeft = Math.random() < 0.5;
      const originX = fromLeft ? W * 0.12 : W * 0.88;
      const angle = (fromLeft ? -1 : 1) * (Math.PI / 4) + (Math.random() - 0.5) * 0.9;
      const speed = 9 + Math.random() * 9;
      return {
        x: originX,
        y: H * 0.78,
        vx: Math.sin(angle) * speed * (fromLeft ? 1 : -1) * -1 + (fromLeft ? 1 : -1) * (3 + Math.random() * 3),
        vy: -Math.abs(Math.cos(angle) * speed) - (6 + Math.random() * 6),
        size: 5 + Math.random() * 6,
        color: palette[(Math.random() * palette.length) | 0],
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.4,
        round: Math.random() < 0.35,
      };
    });

    const gravity = 0.32;
    const drag = 0.992;

    const frame = () => {
      const t = now() - start;
      const fade = t > DURATION - 600 ? Math.max(0, (DURATION - t) / 600) : 1;
      ctx.clearRect(0, 0, W, H);
      parts.forEach((p) => {
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        }
        ctx.restore();
      });
      if (t < DURATION) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, W, H);
      }
    };
    rafRef.current = requestAnimationFrame(frame);

    return () => cancelAnimationFrame(rafRef.current);
  }, [trigger, intense]);

  return <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />;
});

// Animated count-up for hero numbers. `format` maps the running number to a
// display string. Respects prefers-reduced-motion (jumps straight to the value).
const CountUp = memo(function CountUp({ value, format = (n) => String(Math.round(n)), duration = 850 }) {
  const reduce = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
  const [display, setDisplay] = useState(reduce ? (Number(value) || 0) : 0);
  const fromRef = useRef(reduce ? (Number(value) || 0) : 0);
  const rafRef = useRef(0);
  useEffect(() => {
    const to = Number(value) || 0;
    if (reduce) { fromRef.current = to; setDisplay(to); return undefined; }
    const from = fromRef.current;
    if (from === to) { setDisplay(to); return undefined; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) { rafRef.current = requestAnimationFrame(tick); }
      else { fromRef.current = to; setDisplay(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration, reduce]);
  return <>{format(display)}</>;
});

// Self-contained live-session timer. Owns its own 1s interval so that ticking
// the elapsed clock re-renders only this text node — not the whole App (which
// would re-run charts and the entire home page every second).
function liveElapsedMs(liveGame) {
  if (!liveGame) return 0;
  const paused = liveGame.totalPausedMs || 0;
  if (liveGame.status === "active") return Date.now() - liveGame.startedAt - paused;
  if (liveGame.status === "paused") return (liveGame.pausedAt || Date.now()) - liveGame.startedAt - paused;
  return (liveGame.endedAt || Date.now()) - liveGame.startedAt - paused;
}

// "A♠ K♥ Q♦ J♣ 10♠" -> five { rank, suit } slots for the jackpot card picker.
const DEFAULT_JACKPOT_SLOTS = [
  { rank: "A", suit: "♠" }, { rank: "K", suit: "♥" }, { rank: "Q", suit: "♦" },
  { rank: "J", suit: "♣" }, { rank: "10", suit: "♠" },
];
function parseJackpotCards(str) {
  if (!str) return DEFAULT_JACKPOT_SLOTS;
  const allSuits = ["♠", "♥", "♦", "♣"];
  const allRanks = ["A", "K", "Q", "J", "10", "9", "8", "7", "6", "5", "4", "3", "2"];
  const parsed = String(str).split(" ").slice(0, 5).map((s) => {
    const suit = allSuits.find((su) => s.endsWith(su)) || "♠";
    const rank = s.slice(0, s.length - suit.length);
    return { rank: allRanks.includes(rank) ? rank : "A", suit };
  });
  while (parsed.length < 5) parsed.push({ rank: "A", suit: "♠" });
  return parsed;
}

// One entry in liveGame.events: what happened at the table and when, so the
// Home card can draw the night as a timeline. Types: "buyin" (seated),
// "rebuy", "undo" (a rebuy taken back), "out", "back". The random id stops
// arrayUnion from merging two identical taps into one.
function liveEvent(type, pid, t = Date.now()) {
  return { id: Math.random().toString(36).slice(2, 10), type, pid, t };
}

const LiveElapsed = React.memo(function LiveElapsed({ liveGame, format }) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!liveGame || liveGame.status !== "active") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [liveGame]);
  return <>{format(liveElapsedMs(liveGame))}</>;
});

// Turns a game into per-player swimlanes (Home live card, Game Info). Buy-in
// counts and isOut stay the source of truth; events only place them in time.
// A finished game passes endedAt; a live one ends at "now" (or its pause).
function buildLiveLanes(liveGame, now) {
  const start = liveGame.startedAt || now;
  const end = liveGame.endedAt || (liveGame.status === "paused" && liveGame.pausedAt ? liveGame.pausedAt : now);
  const span = Math.max(end - start, 60000);
  const pos = (t) => Math.min(1, Math.max(0, (t - start) / span));
  const events = [...(liveGame.events || [])].sort((a, b) => a.t - b.t);
  const lanes = (liveGame.playerIds || []).map((pid) => {
    const rebuys = [];
    let seatedAt = null;
    let outAt = null;
    for (const ev of events) {
      if (ev.pid !== pid) continue;
      if (ev.type === "buyin" && seatedAt === null) seatedAt = ev.t;
      else if (ev.type === "rebuy") rebuys.push(ev.t);
      else if (ev.type === "undo") rebuys.pop();
      else if (ev.type === "out") outAt = ev.t;
      else if (ev.type === "back") outAt = null;
    }
    const count = liveGame.buyIns?.[pid] || 1;
    while (rebuys.length > count - 1) rebuys.shift();
    const isOut = !!liveGame.isOut?.[pid];
    return {
      pid,
      count,
      total: count * (liveGame.buyInAmount || 0),
      seat: seatedAt !== null ? pos(seatedAt) : 0,
      rebuys: rebuys.map((t) => ({ p: pos(t), t })),
      isOut,
      out: isOut ? (outAt !== null ? pos(outAt) : 1) : null,
      outT: isOut ? outAt : null,
    };
  });
  lanes.sort((a, b) => (a.isOut - b.isOut) || (b.total - a.total));
  return { lanes, timed: events.length > 0, start, end };
}

const clock = (t) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// Live subscription to one session's change log (groups/{gid}/sessionLog,
// written by a Cloud Function), newest first. A null gameId listens to nothing.
function useSessionLog(groupId, gameId) {
  const key = groupId && gameId ? `${groupId}/${gameId}` : "";
  const [state, setState] = useState({ key: "", list: [] });
  useEffect(() => {
    if (!groupId || !gameId) return undefined;
    const k = `${groupId}/${gameId}`;
    return onSnapshot(
      query(collection(db, "groups", groupId, "sessionLog"), where("gameId", "==", gameId)),
      (snap) => setState({ key: k, list: snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.atMs || 0) - (a.atMs || 0)) }),
      (err) => { console.error("Session log failed:", err?.code, err?.message); setState({ key: k, list: [] }); }
    );
  }, [groupId, gameId]);
  return key && state.key === key ? state.list : [];
}

// A saved game in the shape buildLiveLanes reads, or null for games saved
// before timelines were kept. Buy-ins are stored as amounts; lanes want counts.
function savedGameTimeline(game) {
  if (!game?.events?.length || !game.startedAt || !game.endedAt) return null;
  const amount = game.buyInAmount || 0;
  const buyIns = {};
  Object.entries(game.buyIns || {}).forEach(([pid, v]) => {
    buyIns[pid] = amount > 0 ? Math.max(1, Math.round(Number(v) / amount)) : 1;
  });
  const isOut = {};
  [...game.events].sort((a, b) => a.t - b.t).forEach((ev) => {
    if (ev.type === "out") isOut[ev.pid] = true;
    else if (ev.type === "back") isOut[ev.pid] = false;
  });
  return { startedAt: game.startedAt, endedAt: game.endedAt, playerIds: Object.keys(buyIns), buyIns, isOut, events: game.events, buyInAmount: amount };
}

// Rebuys closer together than a marker's width merge into one marker with a
// count, and markers keep clear of the seat ring, the bust × and the "now" dot.
const LANE_GAP = 0.08;
function layoutRebuys(lane) {
  const groups = [];
  for (const r of lane.rebuys) {
    const g = groups[groups.length - 1];
    if (g && r.p - g.last < LANE_GAP) {
      g.n += 1;
      g.last = r.p;
      g.t = Math.max(g.t, r.t);
    } else {
      groups.push({ first: r.p, last: r.p, n: 1, t: r.t });
    }
  }
  const lo = lane.seat + LANE_GAP;
  const hi = (lane.isOut ? lane.out : 1) - LANE_GAP;
  return groups.map((g) => ({ ...g, p: Math.min(Math.max((g.first + g.last) / 2, lo), Math.max(lo, hi)) }));
}
// The Home live card shows this many lanes until the viewer taps "Show all";
// a table only one over the limit shows everyone, so "+1" never needs a tap.
const LIVE_LANES_MAX = 5;
const LIVE_LANES_EXPANDED_KEY = "tp.liveLanesExpanded";

// Axis marks for a lane timeline: time into the game ("30m", "1h") at round
// steps, placed where they fall on the track, kept clear of the start time on
// the left and Now/end on the right. Uses the same span as buildLiveLanes.
// The start and end labels are centred on the track's ends, so ticks keep
// clear of half a label's width on each side.
const LANE_TICK_STEPS_MIN = [5, 10, 15, 30, 60, 120, 180, 240, 360];
const LANE_TICK_LO = 0.3;
const LANE_TICK_HI = 0.74;
function formatGameOffset(ms) {
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
}
function laneTicks(start, end) {
  const span = Math.max(end - start, 60000);
  const stepMin = LANE_TICK_STEPS_MIN.find((mins) => span / (mins * 60000) <= 3) ?? LANE_TICK_STEPS_MIN[LANE_TICK_STEPS_MIN.length - 1];
  const step = stepMin * 60000;
  const ticks = [];
  for (let t = step; t < span; t += step) {
    const p = t / span;
    if (p >= LANE_TICK_LO && p <= LANE_TICK_HI) ticks.push({ p, label: formatGameOffset(t) });
  }
  return ticks;
}

// The swimlanes themselves: one lane per player (seated ring, rebuy squares,
// bust ×) over a time axis. "live" adds the rippling now-tips and ends the
// axis at Now/Paused; otherwise a finish line marks the end of the game, and
// players still in at the end get a solid end dot on it.
const LaneTimeline = memo(function LaneTimeline({ lanes, timed, start, end, live = false, paused = false, max = Infinity, revealFrom = Infinity, shownAt = Infinity, getName, formatMoney }) {
  const shown = lanes.slice(0, max);
  const pct = (p) => `${(p * 100).toFixed(2)}%`;
  const ticks = timed ? laneTicks(start, end) : [];
  const startLabel = clock(start);
  const endLabel = live ? (paused ? "Paused" : "Now") : clock(end);
  return (
    <>
      <span className="live-lanes" aria-hidden="true">
        {shown.map((l, laneIndex) => {
          const name = getName(l.pid);
          const base = AVATAR_PALETTE[avatarHashIndex(name, AVATAR_PALETTE.length)];
          return (
            <span key={l.pid} className={`live-lane${l.isOut ? " live-lane--out" : ""}${laneIndex >= revealFrom ? " is-revealed" : ""}`} style={{ "--lane": base }}>
              <span className="live-lane-name">{name}</span>
              <span className="live-lane-track">
                {timed ? (
                  <>
                    {/* Faint guides under the marks, continuing the axis ticks up through every lane. */}
                    {ticks.map((tk) => <span key={tk.label} className="live-lane-guide" style={{ left: pct(tk.p) }} />)}
                    {!live && <span className="live-lane-finish" />}
                    <span className="live-lane-line" style={{ left: pct(l.seat), right: pct(1 - (l.out ?? 1)) }} />
                    <span className="live-lane-seat" style={{ left: pct(l.seat) }} />
                    {layoutRebuys(l).map((g, i) => (
                      <span key={`${g.t}-${i}`} className={`live-lane-rebuy${g.t > shownAt ? " is-new" : ""}`} style={{ left: pct(g.p) }}>
                        {g.n > 1 ? <b>{g.n}</b> : <Plus size={9} strokeWidth={4} />}
                      </span>
                    ))}
                    {l.isOut && <span className={`live-lane-bust${l.outT > shownAt ? " is-new" : ""}`} style={{ left: pct(l.out) }}><X size={11} strokeWidth={3} /></span>}
                    {/* Still playing: the lane's tip at "now" ripples, staggered per lane. */}
                    {live && !l.isOut && <span className="live-lane-now" style={{ "--ping-delay": `${laneIndex * 0.45}s` }} />}
                    {!live && !l.isOut && <span className="live-lane-end" />}
                  </>
                ) : (
                  // Games started before timestamps existed: show the count only.
                  <span className="live-lane-pips">
                    <span className="live-lane-seat" />
                    {Array.from({ length: l.count - 1 }, (_, i) => <span key={i} className="live-lane-rebuy"><Plus size={9} strokeWidth={4} /></span>)}
                    {l.isOut && <span className="live-lane-bust"><X size={11} strokeWidth={3} /></span>}
                  </span>
                )}
              </span>
              <span className="live-lane-total">{formatMoney(l.total)}</span>
            </span>
          );
        })}
        {/* Axis sits in the lanes' grid, under the track column, so every label
            lines up with the marks above it: when the game started, time into
            the game, then Now (or when it ended). */}
        {timed && (
          <span className="live-lanes-axis">
            <span className="live-lanes-axis-start">{startLabel}</span>
            {ticks.map((tk) => (
              <span key={tk.label} className="live-lanes-axis-tick" style={{ left: pct(tk.p) }}>{tk.label}</span>
            ))}
            {endLabel !== startLabel && (
              <span className={`live-lanes-axis-end${live ? "" : " live-lanes-axis-end--finish"}`}>
                {!live && <Flag size={10} strokeWidth={2.5} aria-hidden="true" />}
                {endLabel}
              </span>
            )}
          </span>
        )}
      </span>
    </>
  );
});

// Home card for a running game: the pot, the clock, and one lane per player
// showing when they sat down, each rebuy, and when they went out.
const LiveLedgerCard = memo(function LiveLedgerCard({ liveGame, getName, formatMoney, formatClock, onOpen }) {
  const paused = liveGame.status === "paused";
  const [now, setNow] = useState(() => Date.now());
  // Only marks that happen while the card is on screen pop in; the rest are
  // drawn still, so reopening Home doesn't replay the whole night.
  const [shownAt] = useState(() => Date.now());
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [paused]);
  const built = useMemo(() => buildLiveLanes(liveGame, now), [liveGame, now]);
  const pot = built.lanes.reduce((sum, l) => sum + l.total, 0);
  const rebuyCount = built.lanes.reduce((sum, l) => sum + l.count - 1, 0);
  const seated = built.lanes.length;
  // Big tables fold to the top lanes (still playing, biggest buy-ins first).
  // The viewer's choice to see everyone sticks across visits.
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(LIVE_LANES_EXPANDED_KEY) === "1"; } catch { return false; }
  });
  const foldable = seated > LIVE_LANES_MAX + 1;
  const showAll = expanded || !foldable;
  // Lanes that appear on a tap slide in; lanes shown on load don't.
  const [revealFrom, setRevealFrom] = useState(Infinity);
  const toggleLanes = () => {
    const next = !expanded;
    setExpanded(next);
    setRevealFrom(next ? LIVE_LANES_MAX : Infinity);
    // Drop the mark once the slide-in is done, so a lane that later moves down
    // past the fold (as players reorder) doesn't replay it.
    if (next) setTimeout(() => setRevealFrom(Infinity), 600);
    try { localStorage.setItem(LIVE_LANES_EXPANDED_KEY, next ? "1" : "0"); } catch { /* storage blocked */ }
  };

  return (
    <div className={`home-live-ledger${paused ? " home-live-ledger--paused" : ""}`}>
      <button
        type="button"
        className="live-card-open"
        onClick={onOpen}
        aria-label={`${paused ? "Paused" : "Live"} game, ${formatMoney(pot)} pot, ${seated} seated, ${rebuyCount} ${rebuyCount === 1 ? "rebuy" : "rebuys"}. Open the live table.`}
      >
        <span className="live-card-head">
          <span className="live-card-pot">
            <strong><CountUp value={pot} format={(n) => formatMoney(Math.round(n))} /></strong>
            <small>{seated} seated · {rebuyCount} {rebuyCount === 1 ? "rebuy" : "rebuys"}</small>
          </span>
          <span className="live-card-status">
            <span className="home-live-ledger-dot" />
            <LiveElapsed liveGame={liveGame} format={formatClock} />
            <ChevronRight size={16} aria-hidden="true" />
          </span>
        </span>
        <LaneTimeline {...built} live paused={paused} max={showAll ? Infinity : LIVE_LANES_MAX} revealFrom={revealFrom} shownAt={shownAt} getName={getName} formatMoney={formatMoney} />
      </button>
      {foldable && (
        <button type="button" className="live-lanes-toggle" onClick={toggleLanes} aria-expanded={showAll}>
          {showAll ? "Show fewer" : `Show all ${seated} players`}
          <ChevronDown size={14} aria-hidden="true" className="live-lanes-toggle-chevron" />
        </button>
      )}
    </div>
  );
});

// Home jackpot as an orbit: the current pot at the centre, past winners
// circling it in the order they won (record holder starred), and an empty
// "?" seat for whoever hits it next. `history` is newest first.
const ORBIT_RADIUS = 100;
const ORBIT_MAX_WINNERS = 5;
// Hand-spin: drag the ring around the coin and flick it. Velocity is in deg/ms.
const ORBIT_SPIN_MAX_VELOCITY = 3;
const ORBIT_SPIN_FRICTION = 0.998; // per ms: a hard flick coasts ~3 turns
const ORBIT_SPIN_DRAG_SLOP = 6; // px of movement before a touch counts as a spin, not a tap
// A flick this fast opens the poker trainer easter egg: ~2,800 px/s at the
// ring, a deliberate hard flick (casual spins run ~0.5–1°/ms). While
// spinning, a "charge" arc around the ring fills toward it as the hint.
const ORBIT_FLICK_VELOCITY = 1.6;
// Fingers slow down just before lifting, so a flick is judged by its peak
// speed over the last ORBIT_FLICK_WINDOW ms (measured across steps of at
// least ORBIT_SPEED_STEP ms, to smooth out touch jitter).
const ORBIT_FLICK_WINDOW = 150;
const ORBIT_SPEED_STEP = 24;
// Flicks stack: a quick same-direction flick on a still-coasting ring adds to
// the speed it already had, so two or three good flicks charge it up.
const ORBIT_STACK_KEEP = 1;
const ORBIT_STACK_MS = 450; // grab-to-release longer than this is a catch, not a re-flick
const ORBIT_STACK_MIN = 0.3; // the new flick must be at least this fast to stack
const ORBIT_CENTER_DEADZONE = 16; // px: angles are meaningless right at the centre
const ORBIT_CHARGE_RADIUS = 124;
const ORBIT_SUITS = ["♠", "♥", "♦", "♣"];
// The warp into the trainer, one timeline in ms: a quick squash, a springy
// flight to the centre while the spin builds to a blur (glow + light streaks
// instead of a costly blur filter), a shockwave at top speed, then the orbit
// flies past the camera as the game fades in underneath.
const WARP_LIFT_MS = 520;
const WARP_SPIN_FROM = 60;
const WARP_SPIN_TO = 1120;
const WARP_BURST_AT = 1040;
const WARP_OUT_FROM = 1080;
const WARP_OUT_MS = 380;
const WARP_ENTER_AT = 1140;
const WARP_DONE_AT = 1900;
const WARP_MAX_VELOCITY = 9; // deg/ms ≈ 25 turns a second
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const easeOutCubic = (x) => 1 - (1 - x) ** 3;
const easeInCubic = (x) => x ** 3;
const easeInOutCubic = (x) => (x < 0.5 ? 4 * x ** 3 : 1 - (-2 * x + 2) ** 3 / 2);
const easeOutBack = (x) => 1 + 2.2 * (x - 1) ** 3 + 1.2 * (x - 1) ** 2;

// Current angle of a CSS-animated rotation (the slow drift), in degrees.
function currentRotation(el) {
  const m = el ? getComputedStyle(el).transform : "none";
  if (!m || m === "none") return 0;
  const [a, b] = m.slice(m.indexOf("(") + 1, -1).split(",").map(Number);
  return (Math.atan2(b, a) * 180) / Math.PI;
}

// Full-screen layer that plays the warp with a copy of the orbit. The copy
// starts exactly over the real one, so the hand-off is invisible.
function OrbitWarp({ rect, turn, velocity, children, onEnter, onDone }) {
  const backRef = useRef(null);
  const moverRef = useRef(null);
  const streakRef = useRef(null);
  const burstRef = useRef(null);
  useEffect(() => {
    const mover = moverRef.current;
    const stage = mover.querySelector(".jp-orbit-stage");
    const back = backRef.current;
    const streak = streakRef.current;
    const burst = burstRef.current;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dx = w / 2 - (rect.left + rect.width / 2);
    const dy = h / 2 - (rect.top + rect.height / 2);
    const zoom = Math.min(1.6, (Math.min(w, h) * 0.92) / 256);
    const dir = Math.sign(velocity) || 1;
    const v0 = Math.abs(velocity);
    let deg = turn;
    const t0 = performance.now();
    let prev = t0;
    let raf = 0;
    let lastBuzz = 0;
    let burstBuzzed = false;
    const frame = (now) => {
      const t = now - t0;
      const dt = Math.min(32, now - prev);
      prev = now;
      // Flight: squash on release, then spring to the centre (slight overshoot).
      const lift = clamp01(t / WARP_LIFT_MS);
      const travel = easeOutBack(lift);
      const squash = t < 110 ? 1 - 0.06 * Math.sin((Math.PI * t) / 110) : 1;
      const hover = 1 + 0.08 * easeInCubic(clamp01((t - WARP_LIFT_MS) / (WARP_OUT_FROM - WARP_LIFT_MS)));
      // Fly-through: rush past the camera and fade.
      const out = clamp01((t - WARP_OUT_FROM) / WARP_OUT_MS);
      const scale = (1 + (zoom - 1) * travel) * squash * hover * (1 + 5 * easeInCubic(out));
      mover.style.transform = `translate3d(${dx * travel}px, ${dy * travel}px, 0) scale(${scale})`;
      mover.style.opacity = String(1 - clamp01((out - 0.2) / 0.8) ** 1.5);
      // Spin builds smoothly from your flick to a blur.
      const speedK = easeInOutCubic(clamp01((t - WARP_SPIN_FROM) / (WARP_SPIN_TO - WARP_SPIN_FROM)));
      deg += dir * (v0 + (WARP_MAX_VELOCITY - v0) * speedK) * dt;
      stage.style.setProperty("--jp-turn", `${deg}deg`);
      mover.style.setProperty("--jp-speed", speedK.toFixed(3));
      streak.style.transform = `translate(-50%, -50%) rotate(${deg * 1.15}deg)`;
      back.style.opacity = String(easeOutCubic(clamp01(t / 650)));
      // Shockwave at top speed.
      const b = clamp01((t - WARP_BURST_AT) / 520);
      burst.style.opacity = t < WARP_BURST_AT ? "0" : String(0.85 * (1 - b));
      burst.style.transform = `translate(-50%, -50%) scale(${0.35 + 5 * easeOutCubic(b)})`;
      // A rising buzz, and a thump at the burst (Android).
      if (t < WARP_BURST_AT && now - lastBuzz > 150 - speedK * 110) { haptic([3 + Math.round(speedK * 7)]); lastBuzz = now; }
      if (t >= WARP_BURST_AT && !burstBuzzed) { haptic([28]); burstBuzzed = true; }
      if (t < WARP_DONE_AT) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    // Timers (not the frame loop) hand over, so it still completes if frames stall.
    const timers = [setTimeout(onEnter, WARP_ENTER_AT), setTimeout(onDone, WARP_DONE_AT)];
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
    // Plays once per mount; the inputs are fixed for the warp's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="jp-warp" aria-hidden="true" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <div className="jp-warp-back" ref={backRef} />
      <span className="jp-warp-burst" ref={burstRef} />
      <div className="jp-warp-mover" ref={moverRef} style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}>
        <span className="jp-warp-halo" />
        {children}
        <span className="jp-warp-streak" ref={streakRef} />
      </div>
    </div>
  );
}

const JackpotOrbit = memo(function JackpotOrbit({ balance, record, history, gamesSinceHit, perNight, lastWinnerName, getAvatar, formatMoney, onOpen, onFlick }) {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const turnRef = useRef(0); // manual rotation (deg), on top of the CSS drift
  const dragRef = useRef(null);
  const coastRef = useRef(0);
  const coastVRef = useRef(0); // the coasting speed (deg/ms) while coastRef runs
  const suppressClickRef = useRef(false);
  const tickRef = useRef({ seat: 0, at: 0 });
  const chargeRef = useRef({ full: false, preloaded: false });
  const [warp, setWarp] = useState(null);
  const chronological = [...history].reverse();
  const recordRow = record > 0 ? chronological.find((r) => r.amount === record) : null;
  let winners = chronological.slice(-ORBIT_MAX_WINNERS);
  if (recordRow && !winners.includes(recordRow)) winners = [recordRow, ...winners.slice(1)];
  const seats = [...winners.map((w) => ({ ...w, isRecord: w === recordRow })), { next: true }];
  const step = 360 / seats.length;
  const newRecord = record > 0 && balance >= record;
  const toRecord = record > 0 ? Math.max(0, Math.round(record - balance)) : 0;
  const caption = [
    perNight > 0 ? `+${formatMoney(perNight)} a night` : null,
    lastWinnerName
      ? `last hit by ${lastWinnerName}${gamesSinceHit === 0 ? ", last game" : gamesSinceHit ? `, ${gamesSinceHit} ${gamesSinceHit === 1 ? "game" : "games"} ago` : ""}`
      : "not hit yet",
  ].filter(Boolean).join(" · ");
  // Written straight to a CSS variable so a drag never re-renders React.
  const applyTurn = (deg) => {
    turnRef.current = deg;
    stageRef.current?.style.setProperty("--jp-turn", `${deg}deg`);
    // A light tick each time a seat passes (Android; iOS has no vibrate API).
    const seat = Math.floor(deg / step);
    const now = performance.now();
    if (seat !== tickRef.current.seat) {
      if (now - tickRef.current.at > 45) haptic([4]);
      tickRef.current = { seat, at: now };
    }
  };
  // The hint: how close the current spin speed is to opening the trainer.
  const setCharge = (velocity) => {
    const root = rootRef.current;
    if (!onFlick || !root) return;
    const c = Math.min(1, Math.abs(velocity) / ORBIT_FLICK_VELOCITY);
    root.style.setProperty("--jp-charge", c.toFixed(3));
    root.classList.toggle("is-charging", c > 0.12);
    root.classList.toggle("is-charged", c >= 1);
    if (c >= 1 && !chargeRef.current.full) haptic([14]);
    chargeRef.current.full = c >= 1;
    // Fetch the game while they're winding up, so the warp never waits on it.
    if (c > 0.6 && !chargeRef.current.preloaded) {
      chargeRef.current.preloaded = true;
      loadPokerTrainer().catch(() => { chargeRef.current.preloaded = false; });
    }
  };
  const stopCoast = () => {
    if (coastRef.current) cancelAnimationFrame(coastRef.current);
    coastRef.current = 0;
    coastVRef.current = 0;
  };
  useEffect(() => stopCoast, []);
  // Angle (deg) and distance (px) of the pointer from the orbit's centre.
  const pointerPolar = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    return { a: (Math.atan2(dy, dx) * 180) / Math.PI, r: Math.hypot(dx, dy) };
  };
  // Speed between the newest sample and the newest one at least a step older.
  const speedAt = (samples, j) => {
    let i = j - 1;
    while (i > 0 && samples[j].t - samples[i].t < ORBIT_SPEED_STEP) i--;
    const dt = samples[j].t - samples[i].t;
    return dt > 0 ? (samples[j].deg - samples[i].deg) / dt : 0;
  };
  const peakSpeed = (samples, now) => {
    let best = 0;
    for (let j = samples.length - 1; j > 0 && now - samples[j].t <= ORBIT_FLICK_WINDOW; j--) {
      const v = speedAt(samples, j);
      if (Math.abs(v) > Math.abs(best)) best = v;
    }
    return best;
  };
  // A quick re-flick in the ring's direction adds to the speed it still had.
  const withCarry = (drag, v, now) => (
    drag.carry && Math.abs(v) >= ORBIT_STACK_MIN && Math.sign(v) === Math.sign(drag.carry) && now - drag.t0 <= ORBIT_STACK_MS
      ? v + drag.carry * ORBIT_STACK_KEEP
      : v
  );
  const startWarp = (velocity) => {
    stopCoast();
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setCharge(0);
      onFlick();
      return;
    }
    loadPokerTrainer().catch(() => {});
    haptic([20]);
    const stage = stageRef.current;
    setWarp({
      rect: stage.getBoundingClientRect(),
      // Fold the drift into the copy's turn so it starts at the same angle.
      turn: turnRef.current + currentRotation(stage.querySelector(".jp-orbit-spin")),
      velocity,
    });
  };
  const endWarp = useCallback(() => {
    setWarp(null);
    setCharge(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onGrabDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (warp) return;
    const carry = coastRef.current ? coastVRef.current : 0;
    stopCoast();
    suppressClickRef.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const now = performance.now();
    dragRef.current = {
      id: e.pointerId, x: e.clientX, y: e.clientY, moved: false, carry, t0: now,
      last: pointerPolar(e).a, samples: [{ t: now, deg: turnRef.current }],
    };
    tickRef.current.seat = Math.floor(turnRef.current / step);
  };
  const onGrabMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== e.pointerId) return;
    if (!drag.moved) {
      if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < ORBIT_SPIN_DRAG_SLOP) return;
      drag.moved = true;
      drag.last = pointerPolar(e).a;
      rootRef.current?.classList.add("is-spinning");
      return;
    }
    const { a, r } = pointerPolar(e);
    if (r < ORBIT_CENTER_DEADZONE) { drag.last = a; return; }
    let delta = a - drag.last;
    if (delta > 180) delta -= 360;
    else if (delta < -180) delta += 360;
    drag.last = a;
    applyTurn(turnRef.current + delta);
    const now = performance.now();
    drag.samples.push({ t: now, deg: turnRef.current });
    while (drag.samples.length > 2 && now - drag.samples[0].t > ORBIT_FLICK_WINDOW + ORBIT_SPEED_STEP * 2) drag.samples.shift();
    setCharge(withCarry(drag, speedAt(drag.samples, drag.samples.length - 1), now));
  };
  const onGrabEnd = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== e.pointerId) return;
    dragRef.current = null;
    rootRef.current?.classList.remove("is-spinning");
    if (!drag.moved) return; // a tap: let the click through to open the history
    suppressClickRef.current = true;
    if (e.type === "pointercancel") { setCharge(0); return; }
    const now = performance.now();
    let v = withCarry(drag, peakSpeed(drag.samples, now), now);
    v = Math.max(-ORBIT_SPIN_MAX_VELOCITY, Math.min(ORBIT_SPIN_MAX_VELOCITY, v));
    if (onFlick && Math.abs(v) >= ORBIT_FLICK_VELOCITY) {
      startWarp(v);
      return;
    }
    if (Math.abs(v) < 0.02 || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setCharge(0);
      return;
    }
    let prev = now;
    const coast = (t) => {
      const ms = Math.min(32, t - prev);
      prev = t;
      v *= Math.pow(ORBIT_SPIN_FRICTION, ms);
      applyTurn(turnRef.current + v * ms);
      setCharge(v);
      coastVRef.current = v;
      coastRef.current = Math.abs(v) > 0.01 ? requestAnimationFrame(coast) : 0;
      if (!coastRef.current) { coastVRef.current = 0; setCharge(0); }
    };
    coastVRef.current = v;
    coastRef.current = requestAnimationFrame(coast);
  };
  const handleClick = () => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    onOpen();
  };

  const label = `Jackpot ${formatMoney(balance)}${newRecord ? ", a new record" : record > 0 ? `, ${formatMoney(toRecord)} short of the ${formatMoney(record)} record` : ""}.${winners.length ? ` Past winners: ${winners.map((w) => `${w.name} ${formatMoney(w.amount)}`).join(", ")}.` : ""}${onOpen ? " View jackpot history." : ""}`;

  // Everything inside the stage except the grab zone, shared by the live orbit
  // and the warp's copy. `live` animates the amount; the copy shows it settled.
  const renderBackdrop = () => (
    /* Backdrop: faint outer rings to the screen edges, with a few dots
       drifting the other way for depth. Purely decorative. */
    <div className="jp-orbit-bg">
      <span className="jp-orbit-bg-dots">
        <i style={{ "--a": "12deg", "--r": "164px" }} />
        <i className="is-muted" style={{ "--a": "-18deg", "--r": "234px" }} />
        <i style={{ "--a": "168deg", "--r": "198px" }} />
        <i className="is-muted" style={{ "--a": "196deg", "--r": "164px" }} />
        <i className="is-muted" style={{ "--a": "34deg", "--r": "272px" }} />
        <i style={{ "--a": "150deg", "--r": "272px" }} />
        <i className="is-muted" style={{ "--a": "-160deg", "--r": "234px" }} />
        <i style={{ "--a": "-8deg", "--r": "132px" }} />
      </span>
    </div>
  );
  const renderRing = () => (
    <div className="jp-orbit-spin">
      {onFlick && <span className="jp-orbit-charge" style={{ width: ORBIT_CHARGE_RADIUS * 2, height: ORBIT_CHARGE_RADIUS * 2 }} />}
      <div className="jp-orbit-turn">
        <span className="jp-orbit-ring" style={{ width: ORBIT_RADIUS * 2, height: ORBIT_RADIUS * 2 }} />
        {onFlick && ORBIT_SUITS.map((suit, i) => {
          const a = ((-90 + 45 + i * 90) * Math.PI) / 180;
          return (
            <span key={suit} className={`jp-suit${i % 3 ? " jp-suit--red" : ""}`} style={{ left: Math.cos(a) * ORBIT_CHARGE_RADIUS, top: Math.sin(a) * ORBIT_CHARGE_RADIUS }}>
              {suit}
            </span>
          );
        })}
        {seats.map((seat, i) => {
          const a = ((-135 + i * step) * Math.PI) / 180;
          // Avatar on the ring with its amount as a small pill on its lower
          // edge, so the label travels with the avatar and never covers it at
          // any drift angle. The point counter-rotates to stay upright.
          const at = (r) => ({ left: Math.cos(a) * r, top: Math.sin(a) * r });
          return (
            <React.Fragment key={seat.next ? "next" : seat.gameId}>
              <span className="jp-point" style={at(ORBIT_RADIUS)}>
                <span className="jp-point-upright">
                 <span className="jp-point-unturn">
                  <span className="jp-point-body jp-seat">
                    {seat.next ? (
                      <span className="jp-seat-q">?</span>
                    ) : (
                      <AvatarCircle src={getAvatar(seat.winnerId)} alt={seat.name} fallbackLabel={seat.name} size={30} />
                    )}
                    <span className={`jp-seat-label${seat.isRecord || seat.next ? " jp-seat-label--gold" : ""}`}>
                      {seat.next ? "Next" : <>{seat.isRecord && "★ "}{formatMoney(seat.amount)}</>}
                    </span>
                  </span>
                 </span>
                </span>
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
  // The live coin is part of the spin area: drag it to spin, tap it to open.
  const grabHandlers = {
    onPointerDown: onGrabDown,
    onPointerMove: onGrabMove,
    onPointerUp: onGrabEnd,
    onPointerCancel: onGrabEnd,
  };
  const renderCore = (live) => (
    <div className="jp-orbit-core" {...(live ? grabHandlers : null)}>
      <span className="jp-orbit-title">Jackpot</span>
      <strong className={formatMoney(Math.round(balance)).length > 5 ? "jp-orbit-amount--long" : undefined}>
        {live ? <CountUp value={balance} format={(n) => formatMoney(Math.round(n))} /> : formatMoney(Math.round(balance))}
      </strong>
      <small>{newRecord ? "★ A new record" : record > 0 ? `${formatMoney(toRecord)} to the record` : "Grows every night"}</small>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`jp-orbit${onOpen ? " jp-orbit--clickable" : ""}`}
      role={onOpen ? "button" : "group"}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={label}
      onClick={onOpen ? handleClick : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } } : undefined}
    >
      <div className="jp-orbit-stage" ref={stageRef} aria-hidden="true" style={warp ? { visibility: "hidden" } : undefined}>
        {renderBackdrop()}
        {renderRing()}
        {/* Grab zone for hand-spinning: a disc around the ring. The coin on top
            spins too (a tap on it still opens the history); page scroll still
            works outside the disc. */}
        <span className="jp-orbit-grab" {...grabHandlers} />
        {renderCore(true)}
      </div>
      <div className="jp-orbit-caption">
        <span className="jp-orbit-caption-text">{caption}</span>
        {onFlick && (
          <span className="jp-orbit-hint" aria-hidden="true">
            <span className="jp-orbit-hint-low">Spin harder · something's hiding in here</span>
            <span className="jp-orbit-hint-high">Let it fly!</span>
          </span>
        )}
      </div>
      {warp && createPortal(
        <OrbitWarp {...warp} onEnter={onFlick} onDone={endWarp}>
          <div className="jp-orbit jp-orbit--warp is-charging is-charged" style={{ "--jp-charge": 1 }}>
            <div className="jp-orbit-stage">
              {renderBackdrop()}
              {renderRing()}
              {renderCore(false)}
            </div>
          </div>
        </OrbitWarp>,
        document.body
      )}
    </div>
  );
});

// ── Hoisted row/card components ──────────────────────────────────────
// Defined at module scope (not inside App) so their identity is stable
// across renders — otherwise React would treat each render's copy as a new
// component type and remount the whole subtree, defeating memo and causing
// the home feed/stats table to thrash on every state change.

// Change-detail entries for member/settings events are admin-oriented and
// only shown to moderators. Game-event details (buy-ins, results) are already
// visible to everyone via the games collection so they stay unrestricted.
const MODERATOR_ONLY_CHANGE_TYPES = new Set(["member_updated", "settings_changed"]);

const ActivityItem = memo(function ActivityItem({ entry, games, groupRole, getActivityContent, formatActivityTimestamp, setInfoGameId }) {
  const content = getActivityContent(entry);
  const canOpenGame = !!entry.gameId && games.some((game) => game.id === entry.gameId);
  const showChanges = content.changes?.length &&
    (!MODERATOR_ONLY_CHANGE_TYPES.has(entry.type) || groupRole === "moderator");
  return (
    <div
      className={`activity-item${canOpenGame ? " activity-item--interactive" : ""}`}
      role={canOpenGame ? "button" : undefined}
      tabIndex={canOpenGame ? 0 : undefined}
      onClick={() => {
        if (canOpenGame) setInfoGameId(entry.gameId);
      }}
      onKeyDown={(e) => {
        if (canOpenGame && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setInfoGameId(entry.gameId);
        }
      }}
    >
      <div className="activity-item-icon" aria-hidden="true">{content.icon}</div>
      <div className="activity-item-content">
        <div className="activity-item-text">{content.title}</div>
        {showChanges ? (
          <div className="activity-item-changes">
            {content.changes.map((change, index) => (
              <div key={`${entry.id}-${index}`} className="activity-item-change">{change}</div>
            ))}
          </div>
        ) : null}
        <div className="activity-item-meta">
          <span className="activity-item-actor">{entry.actorName || "Unknown"}</span>
          <span className="activity-item-time">{formatActivityTimestamp(entry.timestamp)}</span>
          {canOpenGame ? <span className="activity-item-link">Open session</span> : null}
        </div>
      </div>
    </div>
  );
});

const GameRow = memo(function GameRow({ g, formatGameDate, getPlayerName, getPlayerAvatar, euro, setInfoGameId, jackpotAward }) {
  return (
    <div
      className="game-feed-card"
      onClick={() => setInfoGameId(g.id)}
      role="button"
      tabIndex={0}
      aria-label={`Open game info for ${g.location || formatGameDate(g.date) || "game"}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setInfoGameId(g.id); }}
    >
      {/* Top row: date + location + pot */}
      <div className="game-feed-top">
        <div className="game-feed-meta">
          <span className="game-feed-date">{formatGameDate(g.date)}</span>
          {(g.location || formatGameDuration(g.durationMs)) && (
            <span className="game-feed-location">{[g.location, formatGameDuration(g.durationMs)].filter(Boolean).join(" · ")}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {g.jackpotWinnerId && <span className="game-feed-jackpot-badge">Jackpot</span>}
          <div className="game-feed-pot">{euro(g.pot)}</div>
        </div>
      </div>
      {/* Bottom row: winners */}
      <div className="game-feed-winners">
        {g.firstWinnerId ? (
          g.splitWinnerId ? (
            <div className="game-feed-winner game-feed-1st">
              <AvatarCircle src={getPlayerAvatar(g.firstWinnerId)} alt={getPlayerName(g.firstWinnerId)} fallbackLabel={getPlayerName(g.firstWinnerId)} size={26} />
              <AvatarCircle src={getPlayerAvatar(g.splitWinnerId)} alt={getPlayerName(g.splitWinnerId)} fallbackLabel={getPlayerName(g.splitWinnerId)} size={26} />
              <div className="game-feed-winner-info">
                <span className="game-feed-place game-feed-split-badge">Tie</span>
                <span className="game-feed-winner-name">{getPlayerName(g.firstWinnerId)} & {getPlayerName(g.splitWinnerId)}</span>
              </div>
            </div>
          ) : (
            <div className="game-feed-winner game-feed-1st">
              <AvatarCircle src={getPlayerAvatar(g.firstWinnerId)} alt={getPlayerName(g.firstWinnerId)} fallbackLabel={getPlayerName(g.firstWinnerId)} size={30} />
              <div className="game-feed-winner-info">
                <span className="game-feed-place">Winner</span>
                <span className="game-feed-winner-name">{getPlayerName(g.firstWinnerId)}</span>
              </div>
            </div>
          )
        ) : <div className="game-feed-winner-placeholder" />}
        {!g.splitWinnerId && g.secondWinnerId && (
          <div className="game-feed-winner game-feed-2nd">
            <AvatarCircle src={getPlayerAvatar(g.secondWinnerId)} alt={getPlayerName(g.secondWinnerId)} fallbackLabel={getPlayerName(g.secondWinnerId)} size={26} />
            <div className="game-feed-winner-info">
              <span className="game-feed-place">Runner-up</span>
              <span className="game-feed-winner-name">{getPlayerName(g.secondWinnerId)}</span>
            </div>
          </div>
        )}
        {!g.splitWinnerId && g.thirdWinnerId && (
          <div className="game-feed-winner game-feed-3rd">
            <AvatarCircle src={getPlayerAvatar(g.thirdWinnerId)} alt={getPlayerName(g.thirdWinnerId)} fallbackLabel={getPlayerName(g.thirdWinnerId)} size={22} />
            <div className="game-feed-winner-info">
              <span className="game-feed-place">Third</span>
              <span className="game-feed-winner-name">{getPlayerName(g.thirdWinnerId)}</span>
            </div>
          </div>
        )}
        <ChevronRight size={16} className="game-feed-chevron" />
      </div>
    </div>
  );
});

// Stocks-style money pill: solid green for a win, red for a loss, grey for even.
const MoneyPill = memo(function MoneyPill({ value, euro }) {
  const v = Number(value) || 0;
  const tone = v > 0 ? "up" : v < 0 ? "down" : "even";
  return <span className={`money-pill money-pill--${tone}`}>{v > 0 ? "+" : ""}{euro(v)}</span>;
});

// Stocks-style sparkline: a player's running profit across the period, drawn
// against a faint zero line and coloured by where it ends.
const Sparkline = memo(function Sparkline({ values, width = 64, height = 24 }) {
  if (!values || values.length < 2) return <span className="sparkline" style={{ width, height }} aria-hidden="true" />;
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const y = (v) => (height - 2 - ((v - min) / span) * (height - 4)).toFixed(1);
  const points = values.map((v, i) => `${((i / (values.length - 1)) * width).toFixed(1)},${y(v)}`).join(" ");
  const last = values[values.length - 1];
  const tone = last > 0 ? "up" : last < 0 ? "down" : "even";
  return (
    <svg className={`sparkline sparkline--${tone}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <line className="sparkline-zero" x1="0" x2={width} y1={y(0)} y2={y(0)} />
      <polyline points={points} fill="none" />
    </svg>
  );
});

export default function App() {
  const deviceLocale = useMemo(() => resolveDeviceLocale(), []);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(() => localStorage.getItem("activeGroupId") || "");
  // Mirror of activeGroupId for reading the latest value inside the group-load
  // effect (which is keyed on [user] and must not re-run on every group switch).
  const activeGroupIdRef = useRef(activeGroupId);
  activeGroupIdRef.current = activeGroupId;
  const [groupNameInput, setGroupNameInput] = useState("");
  const [editingGroupIdentity, setEditingGroupIdentity] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState("");
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [guestJoinCodeInput, setGuestJoinCodeInput] = useState("");
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [telegramSignInLoading, setTelegramSignInLoading] = useState(false);
  const [emailAuthMode, setEmailAuthMode] = useState("signin");
  const [emailAuthName, setEmailAuthName] = useState("");
  const [emailAuthEmail, setEmailAuthEmail] = useState("");
  const [emailAuthPassword, setEmailAuthPassword] = useState("");
  const [emailAuthLoading, setEmailAuthLoading] = useState(false);
  const [emailAuthError, setEmailAuthError] = useState("");
  const [emailResetSent, setEmailResetSent] = useState(false);
  // Guest-viewer join box on the sign-in screen is hidden by default and revealed
  // via a "Have an invite code?" link (or automatically when arriving via invite).
  const [showGuestJoin, setShowGuestJoin] = useState(false);
  const [accountEmailDraft, setAccountEmailDraft] = useState("");
  const [accountPasswordDraft, setAccountPasswordDraft] = useState("");
  const [accountLoadingAction, setAccountLoadingAction] = useState("");
  const [accountMessage, setAccountMessage] = useState("");
  const [accountError, setAccountError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPasswordDraft, setCurrentPasswordDraft] = useState("");
  const [newPasswordDraft, setNewPasswordDraft] = useState("");
  const [confirmPasswordDraft, setConfirmPasswordDraft] = useState("");
  const [groupRole, setGroupRole] = useState("viewer");
  const [groupMeta, setGroupMeta] = useState(null);
  const [statsVisibilityDraft, setStatsVisibilityDraft] = useState({});
  // Tagged with the group they were loaded for; `members` (derived below) is empty
  // until the active group's own snapshot arrives, so no effect can ever read the
  // previous group's rows against the new activeGroupId (that caused ghost players).
  const [membersState, setMembersState] = useState({ groupId: "", list: [] });
  const members = membersState.groupId && membersState.groupId === activeGroupId ? membersState.list : NO_ROWS;
  const [memberRoleDraft, setMemberRoleDraft] = useState({});
  const [memberPlayerDraft, setMemberPlayerDraft] = useState({});
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [confirmGroupAction, setConfirmGroupAction] = useState(null); // { id, name, action: 'leave' }
  const [deleteGroupFlow, setDeleteGroupFlow] = useState(null); // { groupId, step: 'summary'|'confirm', info, typed, busy, error }
  const [deletingGroupName, setDeletingGroupName] = useState(""); // shown while deleteGroup runs
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [deleteAccountInput, setDeleteAccountInput] = useState("");
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null); // { id, name } or null
  const [pendingInviteCode, setPendingInviteCode] = useState(_urlInviteCode);
  const [pendingInviteGroupName, setPendingInviteGroupName] = useState(_urlInviteGroupName);
  const [editingProfile, setEditingProfile] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  // True once the profile snapshot reflects the real doc (cached copy or server-
  // confirmed missing). Until then `userProfile` is null or a placeholder, and
  // effects that "fill in" profile fields must not write: a merge-write to a doc
  // the SDK hasn't cached yet is overlaid locally as a full Set until acked, so
  // the group loader's profile read would briefly see no `groupIds` and send a
  // returning member to onboarding — and the write would clobber saved fields.
  const [userProfileLoaded, setUserProfileLoaded] = useState(false);
  // ⓘ toggles that reveal the exact formula behind the plain-language copy.
  const [rankingInfoOpen, setRankingInfoOpen] = useState(false);
  const [likelihoodInfoOpen, setLikelihoodInfoOpen] = useState(false);
  const [profileFirstNameDraft, setProfileFirstNameDraft] = useState("");
  const [profileLastNameDraft, setProfileLastNameDraft] = useState("");
  const [profileNicknameDraft, setProfileNicknameDraft] = useState("");
  const [profileEmailDraft, setProfileEmailDraft] = useState("");
  const [avatarUploadLoading] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState("profile"); // "profile" | "wizard" | "group"
  const [cropNaturalSize, setCropNaturalSize] = useState({ w: 0, h: 0 });
  const [cropScale, setCropScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropDragStart, setCropDragStart] = useState(null);
  const [cropUploading, setCropUploading] = useState(false);
  // Photo edit drafts — only committed when user taps Save in the profile form
  const [photoSourceDraft, setPhotoSourceDraft] = useState(null); // null = no change pending
  const [cropBlobDraft, setCropBlobDraft] = useState(null);       // compressed blob, upload on Save
  const [photoPreviewURL, setPhotoPreviewURL] = useState(null);   // object URL for in-form preview
  const [avatarSeedDraft, setAvatarSeedDraft] = useState(null);   // null = no change pending
  // Onboarding avatar draft — committed when the user completes profile setup
  const [wizardCropBlobDraft, setWizardCropBlobDraft] = useState(null);
  const [wizardPhotoPreviewURL, setWizardPhotoPreviewURL] = useState(null);
  // Group photo edit drafts — committed when user taps Save in group settings
  const [groupCropBlobDraft, setGroupCropBlobDraft] = useState(null);
  const [groupPhotoPreviewURL, setGroupPhotoPreviewURL] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const myGroupsRef = useRef(myGroups);
  myGroupsRef.current = myGroups;
  // Whether the async group-membership lookup has finished for the current user.
  // Critical for onboarding: an empty myGroups means "no groups" ONLY once this is
  // true — before that it just means "still loading", so we must not show the
  // new-user flow (which would wrongly ask a returning user for an invite code).
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  // Retry bookkeeping for the group-membership load: lets it re-run without
  // concluding "new user" while the reads are still failing on a fresh sign-in.
  const [groupLoadNonce, setGroupLoadNonce] = useState(0);
  const groupLoadRef = useRef({ uid: null, retries: 0 });
  // Groups this device is deliberately leaving/deleting, so the lost-membership
  // handler doesn't try to self-heal back into them mid-leave.
  const leavingGroupIdsRef = useRef(new Set());
  // Groups whose lost membership is being resolved (see handleLostMembership).
  const lostGroupIdsRef = useRef(new Set());
  // Surfaced on the loading screen if the membership load is slow/failing, so a
  // stuck device (e.g. blocked Firestore transport) shows the real error + a way out.
  const [groupLoadError, setGroupLoadError] = useState("");
  const [groupLoadSlow, setGroupLoadSlow] = useState(false);
  const [playersState, setPlayersState] = useState({ groupId: "", list: [] });
  const players = playersState.groupId && playersState.groupId === activeGroupId ? playersState.list : NO_ROWS;
  const ensuredPlayerLinkRef = useRef(""); // "uid:groupId" once we've ensured this member has a linked player, so the effect below doesn't re-run every snapshot
  const repairedPlayerLinkRef = useRef(""); // "uid:groupId:playerId" once we've checked a possibly-dangling member.playerId
  const [cachedGroupPhotoURL, setCachedGroupPhotoURL] = useState("");
  const [cachedPlayerAvatarMap, setCachedPlayerAvatarMap] = useState({});
  const [games, setGames] = useState([]);
  const [gamesReady, setGamesReady] = useState(false); // first games snapshot arrived (boot splash waits for it)
  // Live game session
  const [liveGame, setLiveGame] = useState(null);
  const [showManualGameEntry, setShowManualGameEntry] = useState(false);
  const [liveSetupBuyIn, setLiveSetupBuyIn] = useState("");
  const [liveEndFlow, setLiveEndFlow] = useState(false);
  const [liveEndFirstId, setLiveEndFirstId] = useState("");
  const [liveEndSecondId, setLiveEndSecondId] = useState("");
  const [liveEndThirdId, setLiveEndThirdId] = useState("");
  const [liveEndNotes, setLiveEndNotes] = useState("");
  // Save Results: whether this game's jackpot was hit (winner + cards use the
  // liveJackpot* state below, shared with the live table's jackpot form).
  const [liveEndJackpotOn, setLiveEndJackpotOn] = useState(false);
  const [liveJackpotOpen, setLiveJackpotOpen] = useState(false);
  const [liveJackpotWinnerId, setLiveJackpotWinnerId] = useState("");
  const [liveJackpotCardSlots, setLiveJackpotCardSlots] = useState(DEFAULT_JACKPOT_SLOTS);
  const [liveJackpotAmount, setLiveJackpotAmount] = useState("");
  // Amount is prefilled from the running jackpot balance and locked; the pen
  // button unlocks it for manual edits.
  const [liveJackpotAmountLocked, setLiveJackpotAmountLocked] = useState(true);
  const [chipInputDraft, setChipInputDraft] = useState("");
  const [showAddPlayerToLive, setShowAddPlayerToLive] = useState(false);
  // Inline "create a brand-new group player" form inside the live add-player card
  const [liveNewPlayerOpen, setLiveNewPlayerOpen] = useState(false);
  const [liveNewPlayerName, setLiveNewPlayerName] = useState("");
  const [pendingPlayerLinks, setPendingPlayerLinks] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [showPlayerManager, setShowPlayerManager] = useState(false);
  const [crossGroupStats, setCrossGroupStats] = useState(null);
  const [activityEntries, setActivityEntries] = useState([]);
  const [groupProfitById, setGroupProfitById] = useState({});
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerEmail, setNewPlayerEmail] = useState("");
  const [showAddPlayerForm, setShowAddPlayerForm] = useState(false);
  const [newPlayerFirstName, setNewPlayerFirstName] = useState("");
  const [newPlayerLastName, setNewPlayerLastName] = useState("");
  const [newPlayerNickname, setNewPlayerNickname] = useState("");
  const [quickPlayerName, setQuickPlayerName] = useState("");
  const [quickPlayerEmail, setQuickPlayerEmail] = useState("");
  const [playerSettingsId, setPlayerSettingsId] = useState(null);
  const [playerProfileId, setPlayerProfileId] = useState(null);
  const [h2hInfoOpen, setH2hInfoOpen] = useState(false);
  const [playerFirstNameDraft, setPlayerFirstNameDraft] = useState("");
  const [playerLastNameDraft, setPlayerLastNameDraft] = useState("");
  const [playerEmailDraft, setPlayerEmailDraft] = useState("");
  const [playerNicknameDraft, setPlayerNicknameDraft] = useState("");
  const [jackpotBase, setJackpotBase] = useState(0);
  const [savedJackpotBase, setSavedJackpotBase] = useState(0);
  const [jackpotHistoryOpen, setJackpotHistoryOpen] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const [jackpotFilterId, setJackpotFilterId] = useState(null);
  const [sidePrizeConfig, setSidePrizeConfig] = useState({ enabled: true, collectionMethod: "per_player", collectionAmount: 0 });
  const [sidePrizeConfigDraft, setSidePrizeConfigDraft] = useState({ enabled: true, collectionMethod: "per_player", collectionAmount: 0 });
  const [rankingConfig, setRankingConfig] = useState(DEFAULT_RANKING_CONFIG);
  const [rankingConfigDraft, setRankingConfigDraft] = useState(DEFAULT_RANKING_CONFIG);
  const [thirdPlaceEnabled, setThirdPlaceEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const wasSignedInRef = useRef(false); // tracks signed-out→in transitions to reset the tab
  const pendingSessionClaimRef = useRef(""); // sessionId currently being written for this device's login, so the watcher below doesn't mistake our own in-flight claim for a kick from another device
  const [editReturnTab, setEditReturnTab] = useState("home");
  const [toastMessage, setToastMessage] = useState("");
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [confettiIntense, setConfettiIntense] = useState(false);
  const [toastType, setToastType] = useState("default"); // "default" | "success"
  // Boot splash: hold until the first real screen can render — sign-in,
  // the group picker, or Home with its games loaded. It also starts the card
  // entrance animations, which then stop for later tab switches.
  useBootSplash(
    authReady && (!user || (activeGroupId ? gamesReady : user.isAnonymous || groupsLoaded))
  );
  const appLocale = deviceLocale;
  const shortDateFormatter = useMemo(() => (
    new Intl.DateTimeFormat(appLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
  ), [appLocale]);
  const [playerTrendTooltipState, setPlayerTrendTooltipState] = useState(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const pullStartYRef = useRef(null);
  const containerRef = useRef(null);
  const pullActiveRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const pullReadyHapticRef = useRef(false);
  const pullRefreshTimerRef = useRef(null);
  const pullTriggeredRef = useRef(false);
  const playerTrendChartRef = useRef(null);
  const suppressedPlayerTrendTooltipKeyRef = useRef("");
  const [lgSwipeState, setLgSwipeState] = useState({ pid: null, dx: 0, active: false, armed: false });
  const [lgRebuyFeedback, setLgRebuyFeedback] = useState({ pid: null, type: null });
  const lgRebuyFeedbackTimerRef = useRef(null);
  // Player control sheet: opened by tapping a live player card
  const [lgPlayerSheetPid, setLgPlayerSheetPid] = useState(null);
  // Swallow the ghost click iOS fires right after the opening tap, which would
  // land on the fresh overlay and instantly dismiss the sheet
  const lgPlayerSheetOpenedAtRef = useRef(0);

  // Date filter state
  const [dateFilterPreset, setDateFilterPreset] = useState("all");
  const [dateFilterStart, setDateFilterStart] = useState("");
  const [dateFilterEnd, setDateFilterEnd] = useState("");
  const [dateFilterSheetOpen, setDateFilterSheetOpen] = useState(false);
  // "groupId:period" the date filter was last reset to, so group-doc snapshots
  // don't undo a period the user picked for their own view.
  const appliedDefaultPeriodRef = useRef("");

  const [playerProfileChartMode, setPlayerProfileChartMode] = useState("profit");
  const [sessionsSubtab, setSessionsSubtab] = useState("history");

  // Game notes (for new/edit game form)
  const [gameNotes, setGameNotes] = useState("");
  const [gameEditReason, setGameEditReason] = useState(""); // optional, logged with an edit

  // Resolve group name for pending invite code (public read — no auth required)
  useEffect(() => {
    if (!pendingInviteCode) { setPendingInviteGroupName(""); return; }
    (async () => {
      try {
        const lookupSnap = await getDoc(doc(db, "inviteCodes", pendingInviteCode));
        if (lookupSnap.exists()) {
          const data = lookupSnap.data();
          if (data?.groupName) { setPendingInviteGroupName(data.groupName); return; }
        }
      } catch (_) {}
    })();
  }, [pendingInviteCode]);

  useEffect(() => {
    const auth = getAuth();
    getRedirectResult(auth).then((result) => {
      if (result?.user) claimDeviceSession(result.user.uid);
    }).catch((err) => {
      if (err?.code && err.code !== "auth/no-auth-event") {
        showAlert(`Sign-in failed: ${err.code}`);
      }
    });
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single-device sign-in: watch this account's claimed session id and sign out here
  // the moment another device claims it (see claimDeviceSession / getSessionStorageKey).
  useEffect(() => {
    if (!user || user.isAnonymous) return undefined;
    const uid = user.uid;
    const unsubscribe = onSnapshot(
      doc(db, "users", uid, "profile", "session"),
      (snap) => {
        if (pendingSessionClaimRef.current) return;
        if (!snap.exists()) return;
        const remoteSessionId = snap.data()?.sessionId;
        const localSessionId = getStoredSessionId(uid);
        if (!remoteSessionId || !localSessionId) return;
        if (remoteSessionId !== localSessionId) {
          clearStoredSessionId(uid);
          setToastMessage("Signed out — your account was signed in on another device.");
          setToastType("default");
          signOut(getAuth()).catch(() => {});
        }
      },
      (err) => {
        console.error("Session watch failed:", err?.code, err?.message);
      }
    );
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    setUserProfileLoaded(false);
    if (!user || user.isAnonymous) {
      setUserProfile(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid, "profile", "main"),
      (snap) => {
        if (snap.exists() || !snap.metadata.fromCache) setUserProfileLoaded(true);
        if (snap.exists()) {
          setUserProfile(snap.data());
        } else {
          // No profile doc. Compute the inferred state so the UI shows the right
          // photo immediately (local state only).
          const isGoogle = !!user.providerData?.find(p => p.providerId === 'google.com')?.photoURL;
          const initDoc = isGoogle
            ? { photoSource: "google", hasCustomPhoto: false }
            : user.photoURL
            ? { photoSource: "provider", hasCustomPhoto: false }
            : { photoSource: "generated", hasCustomPhoto: false };
          setUserProfile(initDoc);
          // Only persist to Firestore when the server confirms the doc truly doesn't
          // exist. A cache-miss snapshot (fromCache: true) must NOT trigger a write
          // because the real doc may already exist — writing would overwrite fields
          // like photoSource:"upload" with photoSource:"google".
          if (!snap.metadata.fromCache) {
            setDoc(doc(db, "users", user.uid, "profile", "main"), initDoc, { merge: true })
              .catch(err => console.error("Profile init failed:", err?.code, err?.message));
          }
        }
      },
      (err) => {
        console.error("User profile load failed:", err?.code, err?.message);
        setUserProfile(null);
      }
    );
    return () => unsubscribe();
  }, [user]);

  // Auto-join the group when a signed-in user arrives via an invite link, so the
  // code never has to be typed. On a fresh sign-in/sign-up the auth token may not
  // have propagated to Firestore yet (the first member write / code read fails
  // with a transient permission error), so we await the token and retry a few
  // times, silently — the onboarding screen (with the code pre-filled) is the
  // fallback if it still doesn't land.
  useEffect(() => {
    if (!authReady || !user || !pendingInviteCode) return undefined;
    let cancelled = false;
    (async () => {
      try { await user.getIdToken(); } catch (_) {}
      for (let attempt = 0; attempt < 4 && !cancelled; attempt++) {
        let ok = false;
        try {
          ok = await joinGroupWithCode(user, pendingInviteCode, null, true);
        } catch (_) { ok = false; }
        if (cancelled) return;
        if (ok) { setPendingInviteCode(""); return; }
        // Back off before retrying a transient failure (0.6s, 1.2s, 1.8s).
        await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user, pendingInviteCode]);

  useEffect(() => {
    // Theme CSS is now imported via theme.css — this effect is kept for cleanup compatibility
    const head = document.head;
    const style = document.createElement("style");
    style.innerHTML = `
      /* Design tokens moved to theme.css */

    `;
    head.appendChild(style);
    return () => {
      head.removeChild(style);
    };
  }, []);

  // Dark mode sync (state declared below with other state vars, effect moved to line ~860)

  useEffect(() => {
    if (!activeGroupId) return;
    localStorage.setItem("activeGroupId", activeGroupId);
    if (user?.uid) {
      localStorage.setItem(getLastGroupStorageKey(user.uid), activeGroupId);
    }
  }, [activeGroupId, user]);

  // Load jackpot base from active group
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const unsubscribe = onSnapshot(
      doc(db, "groups", activeGroupId, "settings", "jackpot"),
      (docSnap) => {
        const data = docSnap.exists() ? docSnap.data() : {};
        const nextBase = Number(data.base || 0);
        const config = {
          enabled: data.enabled !== false,
          collectionMethod: data.collectionMethod || "per_player",
          collectionAmount: Number(data.collectionAmount) || 0,
        };
        setSavedJackpotBase(nextBase);
        setJackpotBase(nextBase);
        setSidePrizeConfig(config);
        setSidePrizeConfigDraft(config);
      },
      (err) => {
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);

  // Load ranking configuration from active group
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const unsubscribe = onSnapshot(
      doc(db, "groups", activeGroupId, "settings", "ranking"),
      (docSnap) => {
        const normalized = normalizeRankingConfig(docSnap.exists() ? docSnap.data() : {});
        setRankingConfig(normalized);
        setRankingConfigDraft(normalized);
      },
      (err) => {
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);

  // Load game settings (thirdPlaceEnabled) from active group
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const unsubscribe = onSnapshot(
      doc(db, "groups", activeGroupId, "settings", "game"),
      (docSnap) => {
        setThirdPlaceEnabled(docSnap.exists() ? !!docSnap.data().thirdPlaceEnabled : false);
      },
      (err) => {
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);

  // Load players from active group
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const gid = activeGroupId;
    const unsubscribe = onSnapshot(
      collection(db, "groups", activeGroupId, "players"),
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        setPlayersState({ groupId: gid, list });
      },
      (err) => {
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);

  useEffect(() => {
    if (!activeGroupId) {
      setCachedGroupPhotoURL("");
      setCachedPlayerAvatarMap({});
      return;
    }
    try {
      const raw = localStorage.getItem(`${IMAGE_URL_CACHE_PREFIX}${activeGroupId}`);
      if (!raw) {
        setCachedGroupPhotoURL("");
        setCachedPlayerAvatarMap({});
        return;
      }
      const parsed = JSON.parse(raw);
      const age = Date.now() - Number(parsed?.updatedAt || 0);
      if (age > IMAGE_URL_CACHE_MAX_AGE_MS) {
        localStorage.removeItem(`${IMAGE_URL_CACHE_PREFIX}${activeGroupId}`);
        setCachedGroupPhotoURL("");
        setCachedPlayerAvatarMap({});
        return;
      }
      setCachedGroupPhotoURL(String(parsed?.groupPhotoURL || ""));
      setCachedPlayerAvatarMap(parsed?.playerAvatars && typeof parsed.playerAvatars === "object" ? parsed.playerAvatars : {});
    } catch (_) {
      setCachedGroupPhotoURL("");
      setCachedPlayerAvatarMap({});
    }
  }, [activeGroupId]);

  // Load games from active group
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const unsubscribe = onSnapshot(
      collection(db, "groups", activeGroupId, "games"),
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setGames(list);
        setGamesReady(true);
      },
      (err) => {
        setGamesReady(true);
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);

  // Load activity entries from active group
  useEffect(() => {
    if (!user || !activeGroupId) {
      setActivityEntries([]);
      return;
    }
    const unsubscribe = onSnapshot(
      collection(db, "groups", activeGroupId, "activity"),
      (snapshot) => {
        const list = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
        list.sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0));
        setActivityEntries(list);
      },
      (err) => {
        if (err?.code === "permission-denied") {
          setActiveGroupId("");
          localStorage.removeItem("activeGroupId");
        }
      }
    );
    return () => unsubscribe();
  }, [user, activeGroupId]);


  // Load active group meta + current user's role
  useEffect(() => {
    if (!user || !activeGroupId) return;
    const gid = activeGroupId;
    const handleGroupPermissionError = (err) => {
      if (err?.code === "permission-denied") {
        setGroupMeta(null);
        setGroupRole("viewer");
        setMembersState({ groupId: "", list: [] });
        setActiveGroupId("");
        localStorage.removeItem("activeGroupId");
        handleLostMembership(gid);
      }
    };

    const unsubGroup = onSnapshot(
      doc(db, "groups", activeGroupId),
      (snap) => {
        const meta = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setGroupMeta(meta);
        if (meta) {
          setGroupCurrencyDraft(meta.currency || "EUR");
          setGroupStakesDraft(meta.defaultStakes || "");
          setGroupDefaultBuyInDraft(meta.defaultBuyIn != null ? String(meta.defaultBuyIn) : "");
          const defaultPeriod = normalizeGroupDefaultPeriod(meta.defaultPeriod);
          setGroupDefaultPeriodDraft(defaultPeriod);
          const appliedKey = `${snap.id}:${defaultPeriod}`;
          if (appliedDefaultPeriodRef.current !== appliedKey) {
            appliedDefaultPeriodRef.current = appliedKey;
            setDateFilterPreset(defaultPeriod);
          }
          setGroupPermissions({
            editorsCanLog: meta.editorsCanLog !== false,
            editorsCanEditFinished: meta.editorsCanEditFinished !== false,
            viewersSeeBalances: meta.viewersSeeBalances !== false,
            membersSelfLog: !!meta.membersSelfLog,
          });
          setGroupPayoutFirstDraft(String(meta.payoutFirst ?? 65));
          setGroupPayoutSecondDraft(String(meta.payoutSecond ?? 35));
          setGroupPayoutThirdDraft(String(meta.payoutThird ?? 0));
        }
      },
      handleGroupPermissionError
    );
    const unsubMember = onSnapshot(
      doc(db, "groups", activeGroupId, "members", user.uid),
      (snap) => {
        setGroupRole(snap.exists() ? String(snap.data().role || "viewer") : "viewer");
        // Membership deleted while the group is open (e.g. a moderator removed us).
        if (!snap.exists() && !snap.metadata.fromCache) handleLostMembership(gid);
      },
      handleGroupPermissionError
    );
    const unsubMembers = onSnapshot(
      collection(db, "groups", activeGroupId, "members"),
      (snapshot) => {
        setMembersState({ groupId: gid, list: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) });
      },
      handleGroupPermissionError
    );
    return () => {
      unsubGroup();
      unsubMember();
      unsubMembers();
    };
  // handleLostMembership only reads refs and stable setters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeGroupId]);

  // Live game subscription
  useEffect(() => {
    if (!user || !activeGroupId) { setLiveGame(null); return; }
    const unsub = onSnapshot(
      doc(db, "groups", activeGroupId, "liveGame", "current"),
      (snap) => setLiveGame(snap.exists() ? snap.data() : null),
      () => setLiveGame(null)
    );
    return () => unsub();
  }, [user, activeGroupId]);

  // Pending player-link requests for moderators
  useEffect(() => {
    if (!user || !activeGroupId || groupRole !== "moderator") { setPendingPlayerLinks([]); return; }
    const unsub = onSnapshot(
      collection(db, "groups", activeGroupId, "pendingPlayerLinks"),
      (snap) => setPendingPlayerLinks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setPendingPlayerLinks([])
    );
    return () => unsub();
  }, [user, activeGroupId, groupRole]);

  // Live elapsed time is ticked locally inside <LiveElapsed> so the running
  // clock no longer forces a whole-App re-render (and chart/home reflow) every
  // second.

  useEffect(() => {
    return () => {
      if (lgRebuyFeedbackTimerRef.current) clearTimeout(lgRebuyFeedbackTimerRef.current);
    };
  }, []);


  // Load groups where current user is already a member.
  // Four complementary methods are tried so that any one of them is sufficient:
  //   0) Profile doc — groupIds array written by join/create/leave. Most reliable;
  //      works even when the member doc has no uid field or no index exists.
  //   1) documentId() collectionGroup — unreliable for equality in collection-group
  //      queries (Firestore uses full path for documentId()), kept as opportunistic.
  //   2) uid field collectionGroup — requires the collection-group index below and
  //      the uid field to be present in the member doc.
  //   3) createdBy — finds groups the user created; recreates missing member doc.
  // After loading, all found group IDs are written back to the profile doc so that
  // future logins always succeed via Method 0, regardless of index/field state.
  useEffect(() => {
    // A different account (or none) must never inherit the last one's groups:
    // effects that write to "my groups" (profile sync) would otherwise write
    // the new user into the previous user's groups.
    if (!user) { setGroupsLoaded(false); setMyGroups([]); groupLoadRef.current = { uid: null, retries: 0 }; return; }
    if (groupLoadRef.current.uid !== user.uid) {
      groupLoadRef.current = { uid: user.uid, retries: 0 };
      setMyGroups([]);
    }
    let cancelled = false;
    setGroupsLoaded(false);
    setGroupLoadError("");
    (async () => {
      // On a fresh sign-in the auth token may not have propagated to Firestore
      // yet, so the first membership reads can transiently fail with permission
      // errors. Awaiting the token here (and retrying method 0 below) prevents an
      // empty result from wrongly dropping a returning member into new-user
      // onboarding — the root cause of the "asks for invite code again" bug.
      try { await user.getIdToken(); } catch (_) {}

      const loadedById = {};

      // 0) Primary: group IDs stored in the user's own profile doc.
      //    This is the most reliable method and self-heals historical data.
      //    Retried because a transient read failure here would hide every group.
      let profileReadOk = false;
      if (!user.isAnonymous) {
        for (let attempt = 0; attempt < 5 && !cancelled; attempt++) {
          try {
            // Read the profile from the SERVER, not the local cache. A stale
            // cached copy (e.g. cached empty before the group was joined, or
            // while the connection was blocked) is the likely reason groups load
            // on one device but not another for the same account. Fall back to
            // the cached read only if the server is unreachable (offline).
            const profileRef = doc(db, "users", user.uid, "profile", "main");
            let profileSnap;
            try {
              profileSnap = await getDocFromServer(profileRef);
            } catch (_) {
              profileSnap = await getDoc(profileRef);
            }
            profileReadOk = true;
            const storedIds = profileSnap.data()?.groupIds || [];
            const joinCodes = profileSnap.data()?.joinCodes || {};
            const lostIds = [];
            for (const gid of storedIds) {
              try {
                const memberSnap = await getDocFromServer(doc(db, "groups", gid, "members", user.uid));
                if (memberSnap.exists()) {
                  loadedById[gid] = true;
                } else {
                  // Orphaned: the profile lists this group but the membership doc
                  // is gone. A normal "leave" removes the groupId too, so this is a
                  // lost/never-written membership (the cause of "loads on iOS from
                  // cache but not on a fresh device"). Recreate it as a viewer so
                  // the user gets back into their group instead of new-user
                  // onboarding. Rules allow self-creating your own viewer doc with a
                  // valid invite code; we can't read the group doc as a non-member,
                  // so write directly with the code saved at join time.
                  try {
                    await setDoc(doc(db, "groups", gid, "members", user.uid), {
                      uid: user.uid,
                      role: "viewer",
                      inviteCode: String(joinCodes[gid] || ""),
                      displayName: user.displayName || "",
                      createdAt: Date.now(),
                    }, { merge: true });
                    loadedById[gid] = true;
                  } catch (err) {
                    // Denied: the saved code no longer works — the member was
                    // removed (removal rotates the invite code) or the group is
                    // gone. Forget the group so it isn't retried on every load.
                    if (err?.code === "permission-denied") lostIds.push(gid);
                  }
                }
              } catch (_) {
                // Couldn't verify (offline/transient): trust the profile's groupIds
                // rather than drop a group the user is actually in.
                loadedById[gid] = true;
              }
            }
            if (lostIds.length && !cancelled) {
              setDoc(profileRef, {
                groupIds: arrayRemove(...lostIds),
                joinCodes: Object.fromEntries(lostIds.map((gid) => [gid, deleteField()])),
              }, { merge: true }).catch(() => {});
              setToastMessage(lostIds.length === 1 ? "You're no longer a member of one of your groups" : "You're no longer a member of some of your groups");
            }
            break; // success — stop retrying
          } catch (err) {
            if (attempt === 4) {
              console.error("Profile group list lookup failed:", err?.code, err?.message);
              if (!cancelled) setGroupLoadError(`${err?.code || "error"}: ${err?.message || "read failed"}`);
            } else {
              await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
            }
          }
        }
      }

      // 1) Opportunistic: collectionGroup by documentId (may work in some SDK versions).
      try {
        const membershipsById = await getDocs(query(collectionGroup(db, "members"), where(documentId(), "==", user.uid)));
        for (const d of membershipsById.docs) {
          if (d.metadata.hasPendingWrites) continue;
          const gid = d.ref.parent.parent?.id;
          if (gid) loadedById[gid] = true;
        }
      } catch (_) {}

      // 2) uid field collectionGroup — requires firestore.indexes.json index for members.uid.
      try {
        const membershipsByUid = await getDocs(query(collectionGroup(db, "members"), where("uid", "==", user.uid)));
        for (const d of membershipsByUid.docs) {
          // Only memberships the server has confirmed: a local write still
          // waiting for the rules' answer isn't a membership yet.
          if (d.metadata.hasPendingWrites) continue;
          const gid = d.ref.parent.parent?.id;
          if (gid) loadedById[gid] = true;
        }
      } catch (_) {}

      // 3) Groups created by this user (covers creator even without uid field).
      //    Recreates a missing member doc so permissions and listing work again.
      try {
        const createdGroups = await getDocs(query(collection(db, "groups"), where("createdBy", "==", user.uid)));
        for (const g of createdGroups.docs) {
          loadedById[g.id] = true;
          const memberRef = doc(db, "groups", g.id, "members", user.uid);
          const memberSnap = await getDoc(memberRef);
          if (!memberSnap.exists()) {
            await setDoc(memberRef, {
              email: deleteField(),
              uid: user.uid,
              displayName: user.displayName || "",
              role: "moderator",
              createdAt: Date.now()
            }, { merge: true });
          }
        }
      } catch (err) {
        console.error("Created-groups lookup failed:", err?.code, err?.message);
      }

      const foundIds = Object.keys(loadedById);

      // Found nothing, but the profile read never actually succeeded — the result
      // is inconclusive, almost always the auth token still propagating on a fresh
      // sign-in (more common on Android). Treating this as "new user" wrongly shows
      // the create/join-a-group screen to someone who's already in a group, so keep
      // the loading state and retry with backoff instead of committing an empty list.
      if (!cancelled && !user.isAnonymous && foundIds.length === 0 && !profileReadOk
          && groupLoadRef.current.retries < 6) {
        groupLoadRef.current.retries += 1;
        const delay = 500 * groupLoadRef.current.retries;
        setTimeout(() => { if (!cancelled) setGroupLoadNonce((n) => n + 1); }, delay);
        return; // leave groupsLoaded=false so the loading screen stays up
      }

      // Persist the found group IDs back to the profile doc so Method 0 works
      // on the next login regardless of whether uid field / index exist.
      if (!user.isAnonymous && foundIds.length > 0) {
        setDoc(doc(db, "users", user.uid, "profile", "main"),
          { groupIds: foundIds },
          { merge: true }
        ).catch(() => {});
      }

      // Load group metadata. A membership we already confirmed above must NEVER
      // be dropped just because its group-doc read transiently fails — otherwise
      // a returning member gets wrongly shown new-user onboarding. So each read is
      // retried, and if it still fails we keep the membership with a placeholder
      // name (which backfills from the live group listeners once inside).
      const loaded = [];
      const deletedIds = [];
      for (const gid of foundIds) {
        if (cancelled) break;
        let groupData = null;
        let groupDeleted = false;
        for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
          try {
            const groupRef = doc(db, "groups", gid);
            let groupSnap;
            let fromServer = true;
            try {
              groupSnap = await getDocFromServer(groupRef);
            } catch (_) {
              groupSnap = await getDoc(groupRef); // offline: cached copy
              fromServer = false;
            }
            if (groupSnap.exists()) groupData = { id: groupSnap.id, ...groupSnap.data() };
            // The server confirms the group doc is gone, but our member doc
            // survived it (subcollections outlive their parent). Not a group.
            else if (fromServer) groupDeleted = true;
            break; // read succeeded (whether or not the doc exists)
          } catch (err) {
            if (attempt === 2) console.error("Failed to load group doc:", gid, err?.code, err?.message);
            else await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
          }
        }
        if (groupDeleted) { deletedIds.push(gid); continue; }
        loaded.push(groupData || { id: gid, name: "" });
      }
      if (deletedIds.length && !cancelled && !user.isAnonymous) {
        for (const gid of deletedIds) {
          deleteDoc(doc(db, "groups", gid, "members", user.uid)).catch(() => {});
        }
        setDoc(doc(db, "users", user.uid, "profile", "main"), {
          groupIds: arrayRemove(...deletedIds),
          joinCodes: Object.fromEntries(deletedIds.map((gid) => [gid, deleteField()])),
        }, { merge: true }).catch(() => {});
      }
      loaded.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      if (!cancelled) {
        // Resolve which group to open in the SAME commit as the group list, so a
        // single-group user goes straight into their group with no intermediate
        // flash of the group picker. Batched with the state updates below.
        if (!activeGroupIdRef.current && loaded.length > 0) {
          const lastGroupId = localStorage.getItem(getLastGroupStorageKey(user.uid)) || "";
          const target = loaded.some((g) => g.id === lastGroupId)
            ? lastGroupId
            : (loaded.length === 1 ? loaded[0].id : "");
          if (target) setActiveGroupId(target);
        }
        setMyGroups(loaded);
        setGroupsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  // groupLoadNonce is bumped to retry an inconclusive load (see above).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, groupLoadNonce]);

  // After a fresh sign-in, always land on the Home tab rather than wherever a
  // previous session or flow left activeTab (e.g. Profile).
  useEffect(() => {
    const signedIn = !!user;
    if (signedIn && !wasSignedInRef.current) setActiveTab("home");
    wasSignedInRef.current = signedIn;
  }, [user]);

  // If the membership load hasn't finished after a few seconds, flag it so the
  // loading screen can surface an error + escape hatch instead of hanging forever.
  useEffect(() => {
    if (!user || user.isAnonymous || groupsLoaded) { setGroupLoadSlow(false); return undefined; }
    const t = setTimeout(() => setGroupLoadSlow(true), 8000);
    return () => clearTimeout(t);
  }, [user, groupsLoaded]);

  useEffect(() => {
    if (!user || !myGroups.length || activeGroupId) return;

    const lastGroupId = localStorage.getItem(getLastGroupStorageKey(user.uid)) || "";
    const matchedGroup = myGroups.find((group) => group.id === lastGroupId);

    if (matchedGroup) {
      setActiveGroupId(matchedGroup.id);
      return;
    }

    if (myGroups.length === 1) {
      setActiveGroupId(myGroups[0].id);
    }
  }, [activeGroupId, myGroups, user]);

  // Pre-fill onboarding profile fields from the signed-in account so new users
  // (Google / Telegram / email signup) never have to re-type what we already know.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    setWizardName((prev) => prev || user.displayName || "");
    setWizardEmail((prev) => prev || user.email || "");
  }, [user]);

  const syncLinkedPlayerProfile = useCallback(async (memberUid, overrides = {}) => {
    if (!activeGroupId || !memberUid) return;
    const baseMember =
      memberUid === user?.uid
        ? members.find((item) => item.id === user.uid)
        : members.find((item) => item.id === memberUid);
    const member = { ...(baseMember || {}), ...overrides };

    const linkedPlayerId = String(member?.playerId || "");
    if (!linkedPlayerId) return;

    const playerPatch = {
      linkedUserUid: memberUid,
      ...getSharedProfilePhotoPatch(member),
    };

    const nameParts = splitNameParts(member?.displayName || "");
    const firstName = String(member?.firstName || nameParts.firstName || "").trim();
    const lastName = String(member?.lastName || nameParts.lastName || "").trim();
    const nickname = String(member?.nickname || "").trim();
    const groupNickname = String(member?.groupNickname || "").trim();
    const nextName = getProfilePreferredName({
      nickname: groupNickname || nickname,
      firstName,
      profileName: member?.profileName || "",
      displayName: member?.displayName || "",
    });
    const email = String(member?.email || "").trim().toLowerCase();

    const playerRef = doc(db, "groups", activeGroupId, "players", linkedPlayerId);
    const playerSnap = await getDoc(playerRef);
    // Never create a player from a sync: a merge-setDoc on a missing doc would
    // conjure a ghost duplicate (e.g. a stale playerId from another group).
    if (!playerSnap.exists()) return;
    const playerNameUpdatedAt = Number(playerSnap.data()?.nameUpdatedAt || 0);
    const memberNameUpdatedAt = Number(
      member?.nameUpdatedAt ||
      member?.profileNameUpdatedAt ||
      member?.groupNicknameUpdatedAt ||
      0
    );
    const shouldSyncNameFields =
      memberNameUpdatedAt > 0
        ? memberNameUpdatedAt >= playerNameUpdatedAt
        : playerNameUpdatedAt === 0;

    if (shouldSyncNameFields) {
      if (firstName) playerPatch.firstName = firstName;
      else playerPatch.firstName = "";
      playerPatch.lastName = lastName;
      playerPatch.nickname = groupNickname || nickname;
      if (nextName) playerPatch.name = nextName;
      if (memberNameUpdatedAt > 0) {
        playerPatch.nameUpdatedAt = memberNameUpdatedAt;
        playerPatch.nameUpdatedBy = memberUid;
        playerPatch.nameUpdatedSource = "member";
      }
    }

    await setDoc(playerRef, playerPatch, { merge: true });
    if (email) {
      await setDoc(
        doc(db, "groups", activeGroupId, "players", linkedPlayerId, "private", "contact"),
        { email },
        { merge: true }
      );
    }
  }, [activeGroupId, members, user]);

  // Auto-link by email was removed for privacy — player emails no longer live on the
  // publicly-readable player doc. Moderators can link a member to a player manually
  // via Group Settings → Members & Roles.

  const membersForRoles = useMemo(
    () => members.filter((m) => !m.isGuest),
    [members]
  );

  useEffect(() => {
    const roleDraft = {};
    const playerDraft = {};
    membersForRoles.forEach((m) => {
      roleDraft[m.id] = m.role || "viewer";
      playerDraft[m.id] = m.playerId || "";
    });
    setMemberRoleDraft(roleDraft);
    setMemberPlayerDraft(playerDraft);
  }, [membersForRoles]);

  const myMember = useMemo(() => {
    if (!user) return null;
    return members.find((m) => m.id === user.uid) || null;
  }, [members, user]);
  const syncedStatsVisibilityByGroup = useMemo(() => {
    const raw = userProfile?.statsVisibilityByGroup;
    return raw && typeof raw === "object" ? raw : {};
  }, [userProfile?.statsVisibilityByGroup]);
  const syncedStatsVisibilityForActiveGroup = useMemo(() => {
    if (!activeGroupId) return null;
    const raw = syncedStatsVisibilityByGroup[activeGroupId];
    return raw && typeof raw === "object" ? raw : null;
  }, [activeGroupId, syncedStatsVisibilityByGroup]);

  const currentUserProfileData = useMemo(() => {
    if (!user) return null;
    const authNameParts = splitNameParts(user.displayName || "");
    const firstName = String(userProfile?.firstName || myMember?.firstName || authNameParts.firstName || "").trim();
    const lastName = String(userProfile?.lastName || myMember?.lastName || authNameParts.lastName || "").trim();
    const nickname = String(userProfile?.nickname || myMember?.nickname || "").trim();
    const preferredName = getProfilePreferredName({
      nickname,
      firstName,
      profileName: myMember?.profileName || "",
      displayName: user.displayName || "",
    });
    // Resolve photo source with backward-compat for old hasCustomPhoto flag.
    // "upload"    → user uploaded a custom image
    // "google"    → use Google account photo
    // "provider"  → use auth provider photo URL (e.g. Telegram) — transient, not yet persisted
    // "generated" → no photo; monogram avatar shown
    const isGoogleUser = !!user?.providerData?.find(p => p.providerId === 'google.com')?.photoURL;
    const photoSource = userProfile?.photoSource ?? (
      userProfile !== null && userProfile?.hasCustomPhoto ? "upload" :
      userProfile !== null ? "generated" :
      // No profile doc yet — infer from auth provider so new users see their photo
      isGoogleUser ? "google" :
      user?.photoURL ? "provider" : "generated"
    );
    const hasCustomPhoto = photoSource === "upload";
    // For Google photo, read from providerData — user.photoURL can be null after
    // updateProfile({ photoURL: null }) is called during upload/delete flows.
    const googleProviderPhotoURL = String(
      user?.providerData?.find(p => p.providerId === 'google.com')?.photoURL || user.photoURL || ""
    ).trim();
    const profilePhotoURL = String(
      photoSource === "upload"
        ? (userProfile?.profilePhotoURL || myMember?.profilePhotoURL || user?.photoURL || "")
        : photoSource === "google"
        ? googleProviderPhotoURL
        : photoSource === "provider"
        ? String(user?.photoURL || "").trim()
        : ""
    ).trim();

    return {
      firstName,
      lastName,
      nickname,
      email: String(userProfile?.email || user.email || "").trim().toLowerCase(),
      displayName: String(user.displayName || "").trim(),
      profileName: preferredName,
      profilePhotoURL,
      hasCustomPhoto,
      photoSource,
      googleProviderPhotoURL,
      avatarSeed: String(userProfile?.avatarSeed || ""),
      nameUpdatedAt: Number(userProfile?.nameUpdatedAt || myMember?.nameUpdatedAt || 0),
      profileNameUpdatedAt: Number(userProfile?.profileNameUpdatedAt || myMember?.profileNameUpdatedAt || 0),
      groupNicknameUpdatedAt: Number(myMember?.groupNicknameUpdatedAt || 0),
    };
  }, [myMember, user, userProfile]);

  useEffect(() => {
    if (!user) return;
    const authNameParts = splitNameParts(user.displayName || "");
    setProfileFirstNameDraft(String(userProfile?.firstName || myMember?.firstName || authNameParts.firstName || ""));
    setProfileLastNameDraft(String(userProfile?.lastName || myMember?.lastName || authNameParts.lastName || ""));
    setProfileNicknameDraft(String(userProfile?.nickname || myMember?.nickname || ""));
    setProfileEmailDraft(String(userProfile?.email || user.email || ""));
  }, [user, userProfile, myMember]);

  // Cross-group stats: fetch P&L, sessions, wins across all groups
  useEffect(() => {
    if (!user || !myGroups.length) return;
    let cancelled = false;
    (async () => {
      let pl = 0, sessions = 0, wins = 0;
      const nextGroupProfitById = {};
      for (const group of myGroups) {
        try {
          const memberSnap = await getDoc(doc(db, "groups", group.id, "members", user.uid));
          let playerId = String(memberSnap.data()?.playerId || "").trim();
          if (!playerId) {
            const playersSnap = await getDocs(collection(db, "groups", group.id, "players"));
            const matchedPlayer = playersSnap.docs.find((playerDoc) => {
              const data = playerDoc.data() || {};
              return String(data.linkedUserUid || "").trim() === user.uid;
            });
            playerId = String(matchedPlayer?.id || "").trim();
          }
          if (!playerId) continue;

          const gamesSnap = await getDocs(collection(db, "groups", group.id, "games"));
          const games = gamesSnap.docs.map((gDoc) => ({ id: gDoc.id, ...enrichGame(gDoc.data()) }));
          const jackpotSnap = await getDoc(doc(db, "groups", group.id, "settings", "jackpot"));
          const jackpotBase = Math.max(0, Number(jackpotSnap.data()?.base) || 0);
          const jackpotAwards = simulateJackpot(games, jackpotBase).awards;
          let groupProfit = 0;

          // eslint-disable-next-line no-loop-func
          games.forEach((g) => {
            const buyIn = Number(g.buyIns?.[playerId] || 0);
            if (!buyIn) return;
            sessions++;
            const payouts = computePayouts(g, jackpotAwards);
            const net = Number(payouts[playerId] || 0) - buyIn;
            pl += net;
            groupProfit += net;
            if (g.firstWinnerId === playerId || g.splitWinnerId === playerId) wins++;
          });
          nextGroupProfitById[group.id] = Number(groupProfit.toFixed(2));
        } catch (e) {}
      }
      if (!cancelled) {
        setCrossGroupStats({ pl, sessions, wins });
        setGroupProfitById(nextGroupProfitById);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, myGroups]);

  useEffect(() => {
    if (!user || user.isAnonymous || !activeGroupId) return;
    if (!myMember) return;
    const payload = stripMemberPrivateFields({ uid: user.uid, ...currentUserProfileData });

    const sameValues =
      String(myMember.displayName || "").trim() === String(payload.displayName || "").trim() &&
      String(myMember.profilePhotoURL || "").trim() === String(payload.profilePhotoURL || "").trim() &&
      String(myMember.profileName || "").trim() === String(payload.profileName || "").trim() &&
      String(myMember.firstName || "").trim() === String(payload.firstName || "").trim() &&
      String(myMember.lastName || "").trim() === String(payload.lastName || "").trim() &&
      String(myMember.nickname || "").trim() === String(payload.nickname || "").trim();

    if (sameValues) return;

    setDoc(doc(db, "groups", activeGroupId, "members", user.uid), payload, { merge: true })
      .then(() => syncLinkedPlayerProfile(user.uid, payload))
      .catch((err) => {
        console.error("Member profile refresh failed:", err?.code, err?.message);
      });
  }, [activeGroupId, currentUserProfileData, myMember, syncLinkedPlayerProfile, user]);

  // Ensure every signed-in member has a player on the roster linked to them.
  // Runs for new joins (once the member doc + profile load) and self-heals members
  // who joined before this existed. If the member's name matches an existing player
  // we don't create a duplicate — we raise a pendingPlayerLinks request for a
  // moderator to resolve (a member can't claim someone else's player row by rule).
  useEffect(() => {
    if (!user || user.isAnonymous || !activeGroupId || !myMember) return;
    const memberPlayerId = String(myMember.playerId || "").trim();
    if (memberPlayerId) {
      // Linked — unless that player was deleted (e.g. a ghost duplicate removed by a
      // moderator), which leaves the member pointing at nothing. Once this group's
      // roster has loaded without it, confirm on the server and drop the dead link so
      // the normal flow below relinks the real player (found by linkedUserUid).
      if (playersState.groupId !== activeGroupId || players.some((p) => p.id === memberPlayerId)) return;
      const repairKey = `${user.uid}:${activeGroupId}:${memberPlayerId}`;
      if (repairedPlayerLinkRef.current === repairKey) return;
      repairedPlayerLinkRef.current = repairKey;
      const gid = activeGroupId;
      (async () => {
        try {
          const snap = await getDocFromServer(doc(db, "groups", gid, "players", memberPlayerId));
          if (snap.exists()) return;
          ensuredPlayerLinkRef.current = "";
          await setDoc(doc(db, "groups", gid, "members", user.uid), { playerId: deleteField() }, { merge: true });
        } catch (err) {
          repairedPlayerLinkRef.current = "";
          console.error("Dangling player link repair failed:", err?.code, err?.message);
        }
      })();
      return;
    }
    if (players.some((p) => String(p.linkedUserUid || "") === user.uid)) {
      // A player already points at us but the member back-reference is missing —
      // complete the link so moderator UI and profile sync work.
      const linked = players.find((p) => String(p.linkedUserUid || "") === user.uid);
      const key = `${user.uid}:${activeGroupId}`;
      if (linked && ensuredPlayerLinkRef.current !== key) {
        ensuredPlayerLinkRef.current = key;
        setDoc(doc(db, "groups", activeGroupId, "members", user.uid), { playerId: linked.id }, { merge: true })
          .catch((err) => { ensuredPlayerLinkRef.current = ""; console.error("Player back-link failed:", err?.code, err?.message); });
      }
      return;
    }
    const joiningName = getProfilePreferredName({
      nickname: currentUserProfileData?.nickname || "",
      firstName: currentUserProfileData?.firstName || "",
      profileName: currentUserProfileData?.profileName || "",
      displayName: currentUserProfileData?.displayName || user.displayName || "",
    });
    if (!joiningName) return; // wait for the profile/name to load before deciding

    const key = `${user.uid}:${activeGroupId}`;
    if (ensuredPlayerLinkRef.current === key) return;
    ensuredPlayerLinkRef.current = key; // claim before the async work so we don't double-fire

    (async () => {
      try {
        // Authoritative read so we don't race the players snapshot into a duplicate.
        const snap = await getDocs(collection(db, "groups", activeGroupId, "players"));
        const existing = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const alreadyLinked = existing.find((p) => String(p.linkedUserUid || "") === user.uid);
        if (alreadyLinked) {
          await setDoc(doc(db, "groups", activeGroupId, "members", user.uid), { playerId: alreadyLinked.id }, { merge: true });
          return;
        }
        // Rejoining after being removed: their old player is still here (off the
        // roster). Ask a moderator to link it back rather than creating a new one.
        const formerPlayer = existing.find((p) => String(p.formerMemberUid || "") === user.uid);
        if (formerPlayer) {
          await setDoc(doc(db, "groups", activeGroupId, "pendingPlayerLinks", user.uid), {
            memberUid: user.uid,
            memberName: joiningName,
            suggestedPlayerIds: [formerPlayer.id],
            isDuplicate: false,
            returning: true,
            createdAt: Date.now(),
          }, { merge: true });
          return;
        }
        const nl = joiningName.toLowerCase();
        const matches = existing.filter((p) => {
          // A player already claimed by a different user can't be linked to us, so a
          // name clash with them isn't real ambiguity — fall through and auto-create
          // a distinct player instead of pestering a moderator with a dead-end prompt.
          const linkedUid = String(p.linkedUserUid || "").trim();
          if (linkedUid && linkedUid !== user.uid) return false;
          const pn = getPlayerPreferredName(p).toLowerCase();
          return pn === nl || String(p.firstName || "").toLowerCase() === nl;
        });
        if (matches.length === 0) {
          // No name clash — create a linked player and complete the back-reference.
          // Only attach a photo when there's a real custom one; getSharedProfilePhotoPatch
          // returns deleteField() sentinels otherwise, which addDoc (a set) rejects.
          const customPhoto = String(currentUserProfileData?.profilePhotoURL || "").trim();
          const photoFields = customPhoto
            ? { profilePhotoURL: customPhoto, hasCustomPhoto: true }
            : {};
          const ref = await addDoc(collection(db, "groups", activeGroupId, "players"), {
            firstName: currentUserProfileData?.firstName || joiningName,
            lastName: currentUserProfileData?.lastName || "",
            name: joiningName,
            linkedUserUid: user.uid,
            type: "linked",
            ...photoFields,
            createdAt: Date.now(),
            createdBySystem: true,
          });
          await setDoc(doc(db, "groups", activeGroupId, "members", user.uid), { playerId: ref.id }, { merge: true });
        } else {
          // Possible match(es) — let a moderator link manually rather than duplicate.
          await setDoc(doc(db, "groups", activeGroupId, "pendingPlayerLinks", user.uid), {
            memberUid: user.uid,
            memberName: joiningName,
            suggestedPlayerIds: matches.map((p) => p.id),
            isDuplicate: matches.length > 1,
            createdAt: Date.now(),
          }, { merge: true });
        }
      } catch (err) {
        if (err?.code === "permission-denied") {
          // Likely the auth token hasn't propagated yet on a fresh sign-in — release
          // the claim so a later snapshot/member update retries.
          ensuredPlayerLinkRef.current = "";
        } else {
          // Any other error is not something a retry will fix; keep the claim so we
          // don't storm, and surface it.
          console.error("Ensure linked player failed:", err?.code, err?.message);
        }
      }
    })();
  }, [user, activeGroupId, myMember, players, playersState.groupId, currentUserProfileData]);


  // Legacy per-user visibility fallback key scoped to group + user
  const localVisKey = user && activeGroupId ? `rr-vis:${activeGroupId}:${user.uid}` : null;

  useEffect(() => {
    const localRaw = localVisKey ? localStorage.getItem(localVisKey) : null;
    const localParsed = localRaw ? (() => { try { return JSON.parse(localRaw); } catch { return null; } })() : null;
    setStatsVisibilityDraft(() => {
      const next = {};
      players.forEach((player) => {
        if (syncedStatsVisibilityForActiveGroup && player.id in syncedStatsVisibilityForActiveGroup) {
          next[player.id] = syncedStatsVisibilityForActiveGroup[player.id];
        } else if (localParsed && player.id in localParsed) {
          next[player.id] = localParsed[player.id];
        } else {
          next[player.id] = true;
        }
      });
      return next;
    });
  }, [localVisKey, players, syncedStatsVisibilityForActiveGroup]);

  useEffect(() => {
    if (!user || user.isAnonymous || !activeGroupId || !localVisKey) return;
    if (syncedStatsVisibilityForActiveGroup) return;
    const localRaw = localStorage.getItem(localVisKey);
    if (!localRaw) return;
    let localParsed = null;
    try {
      localParsed = JSON.parse(localRaw);
    } catch {
      localParsed = null;
    }
    if (!localParsed || typeof localParsed !== "object") return;
    setDoc(doc(db, "users", user.uid, "profile", "main"), {
      statsVisibilityByGroup: {
        [activeGroupId]: localParsed,
      },
    }, { merge: true }).catch((err) => {
      console.error("Stats visibility migration failed:", err?.code, err?.message);
    });
  }, [activeGroupId, localVisKey, syncedStatsVisibilityForActiveGroup, user]);

  useEffect(() => {
    if (!toastMessage) return;
    const timeoutId = setTimeout(() => { setToastMessage(""); setToastType("default"); }, 1800);
    return () => clearTimeout(timeoutId);
  }, [toastMessage]);

  // Push notifications were removed (they never worked reliably as a PWA).
  // Unregister the old FCM push worker on devices that had enabled them, which
  // also drops their push subscription.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker
      .getRegistration("/firebase-cloud-messaging-push-scope")
      .then((reg) => reg && reg.unregister())
      .catch(() => {});
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("rr-notif-"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* storage blocked */ }
  }, []);

  useEffect(() => () => {
    if (pullRefreshTimerRef.current) {
      clearTimeout(pullRefreshTimerRef.current);
    }
  }, []);

  const finishPullRefresh = useCallback((startedAt) => {
    // Keep the spinner visible for a minimum dwell so it doesn't flicker away
    // on fast connections, then snap the indicator back smoothly.
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, PULL_REFRESH_SETTLE_MS - elapsed);
    if (pullRefreshTimerRef.current) {
      clearTimeout(pullRefreshTimerRef.current);
    }
    pullRefreshTimerRef.current = setTimeout(() => {
      pullRefreshTimerRef.current = null;
      pullDistanceRef.current = 0;
      pullReadyHapticRef.current = false;
      pullTriggeredRef.current = false;
      setPullRefreshing(false);
      setPullDistance(0);
    }, remaining);
  }, []);

  const triggerPullRefresh = useCallback(async () => {
    if (pullRefreshing) return;
    haptic([8, 30, 8], "heavy");
    pullDistanceRef.current = PULL_REFRESH_THRESHOLD;
    setPullRefreshing(true);
    setPullDistance(PULL_REFRESH_THRESHOLD);
    const startedAt = Date.now();
    try {
      // Force the live onSnapshot listeners to reconnect and resync from the
      // server. Cycling the network also recovers a stalled connection — the
      // usual reason a refresh is needed (e.g. a PWA resumed from background).
      await disableNetwork(db);
      await enableNetwork(db);
      // Await a real server round-trip so the spinner only clears once fresh
      // data has actually landed (and the listeners have re-fired with it).
      if (activeGroupId) {
        await getDocs(collection(db, "groups", activeGroupId, "games"));
      }
    } catch (err) {
      // Never leave the app stuck offline if a step failed mid-cycle.
      try { await enableNetwork(db); } catch (_) {}
      console.error("Pull-to-refresh resync failed:", err?.code, err?.message);
    } finally {
      finishPullRefresh(startedAt);
    }
  }, [pullRefreshing, activeGroupId, finishPullRefresh]);

  const handlePullTouchStart = useCallback((event) => {
    if (pullRefreshing || (containerRef.current?.scrollTop || 0) > 0) return;
    if (event.touches.length !== 1) return;
    // Don't engage pull-to-refresh when the gesture starts inside a fixed
    // overlay (group settings, player profile, manual log, etc.). That gesture
    // belongs to the overlay's own scroller — and the resulting transform on
    // .app-shell would make it the containing block for the overlay, collapsing
    // its layout (squeezed content, footers shifted off-screen).
    if (event.target?.closest?.(".gs-overlay, .profile-page-overlay, .jp-orbit-grab, .jp-orbit-core, .pt-root")) return;
    pullStartYRef.current = event.touches[0].clientY;
    pullActiveRef.current = true;
    pullDistanceRef.current = 0;
    pullReadyHapticRef.current = false;
    pullTriggeredRef.current = false;
  }, [pullRefreshing]);

  const handlePullTouchMove = useCallback((event) => {
    if (!pullActiveRef.current || pullStartYRef.current == null || pullRefreshing) return;
    if ((containerRef.current?.scrollTop || 0) > 0) {
      pullActiveRef.current = false;
      pullDistanceRef.current = 0;
      pullReadyHapticRef.current = false;
      setPullDistance(0);
      return;
    }
    const delta = event.touches[0].clientY - pullStartYRef.current;
    if (delta <= 0) {
      pullDistanceRef.current = 0;
      pullReadyHapticRef.current = false;
      pullTriggeredRef.current = false;
      setPullDistance(0);
      return;
    }
    const clamped = Math.min(PULL_REFRESH_MAX_DISTANCE, delta * 0.5);
    pullDistanceRef.current = clamped;
    setPullDistance(clamped);
    if (clamped >= PULL_REFRESH_THRESHOLD) {
      if (!pullReadyHapticRef.current) {
        haptic([12], "medium");
      }
      pullReadyHapticRef.current = true;
      pullTriggeredRef.current = true;
    } else {
      pullReadyHapticRef.current = false;
      pullTriggeredRef.current = false;
    }
  }, [pullRefreshing]);

  const handlePullTouchEnd = useCallback(() => {
    const shouldRefresh = pullActiveRef.current && (pullTriggeredRef.current || pullDistanceRef.current >= PULL_REFRESH_THRESHOLD);
    pullActiveRef.current = false;
    pullStartYRef.current = null;
    pullReadyHapticRef.current = false;
    if (shouldRefresh) {
      triggerPullRefresh();
      return;
    }
    pullDistanceRef.current = 0;
    pullTriggeredRef.current = false;
    setPullDistance(0);
  }, [triggerPullRefresh]);

  const [location, setLocation] = useState("");
  const [gameDate, setGameDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [buyIns, setBuyIns] = useState({});
  const [firstWinnerId, setFirstWinnerId] = useState("");
  const [secondWinnerId, setSecondWinnerId] = useState("");
  const [thirdWinnerId, setThirdWinnerId] = useState("");
  const [jackpotWinnerId, setJackpotWinnerId] = useState("");
  const [isSplitPot, setIsSplitPot] = useState(false);
  const [splitWinnerId, setSplitWinnerId] = useState("");
  // Groups whose "Get your group ready" card was closed on this device.
  const [setupHidden, setSetupHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tp-setup-hidden") || "[]"); } catch (_) { return []; }
  });
  // Per-user section visibility (saved to device)
  const [sectionVis] = useState(() => {
    try {
      const raw = localStorage.getItem("section_vis_v1");
      if (raw) return { ...SECTION_VIS_DEFAULTS, ...JSON.parse(raw) };
    } catch {}
    return { ...SECTION_VIS_DEFAULTS };
  });
  useEffect(() => {
    localStorage.setItem("section_vis_v1", JSON.stringify(sectionVis));
  }, [sectionVis]);
  // Group settings overlay
  const preSettingsActiveGroupIdRef = useRef(null); // saves activeGroupId when settings opened for a non-active group
  const [pendingGroupSettingsOpen, setPendingGroupSettingsOpen] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [groupSettingsSection, setGroupSettingsSection] = useState(null); // null | "general" | "people" | "members" | "ranking" | "jackpot" | "statsVisibility" | "payouts" | "permissions"
  const [peopleTab, setPeopleTab] = useState("members"); // "members" | "players"
  const [advancedRulesOpen, setAdvancedRulesOpen] = useState(false);
  const [rankingFormulaOpen, setRankingFormulaOpen] = useState(false);
  // Group meta extras (currency, stakes, permissions) — stored in groupMeta
  const [groupCurrencyDraft, setGroupCurrencyDraft] = useState("EUR");
  const [groupStakesDraft, setGroupStakesDraft] = useState("");
  const [groupDefaultBuyInDraft, setGroupDefaultBuyInDraft] = useState("");
  const [groupDefaultPeriodDraft, setGroupDefaultPeriodDraft] = useState("all");
  const [groupPayoutFirstDraft, setGroupPayoutFirstDraft] = useState("65");
  const [groupPayoutSecondDraft, setGroupPayoutSecondDraft] = useState("35");
  const [groupPayoutThirdDraft, setGroupPayoutThirdDraft] = useState("0");
  const [groupPermissions, setGroupPermissions] = useState({ editorsCanLog: true, editorsCanEditFinished: true, viewersSeeBalances: true, membersSelfLog: false });
  const [thirdPlaceDraft, setThirdPlaceDraft] = useState(false);
  const activeCurrencyCode = useMemo(
    () => String(groupMeta?.currency || groupCurrencyDraft || "EUR").toUpperCase(),
    [groupMeta?.currency, groupCurrencyDraft]
  );
  const currencyFormatter = useMemo(() => (
    new Intl.NumberFormat(appLocale, {
      style: "currency",
      currency: activeCurrencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  ), [activeCurrencyCode, appLocale]);
  const compactCurrencyFormatter = useMemo(() => (
    new Intl.NumberFormat(appLocale, {
      style: "currency",
      currency: activeCurrencyCode,
      notation: "compact",
      maximumFractionDigits: 1,
    })
  ), [activeCurrencyCode, appLocale]);
  const groupSettingsDirty = useMemo(() => {
    if (groupRole !== "moderator" || !groupMeta) return false;
    return (
      !!groupCropBlobDraft ||
      String(groupNameDraft || "").trim() !== String(groupMeta?.name || "").trim() ||
      String(groupCurrencyDraft || "EUR").toUpperCase() !== String(groupMeta?.currency || "EUR").toUpperCase() ||
      String(groupStakesDraft || "") !== String(groupMeta?.defaultStakes || "") ||
      String(groupDefaultBuyInDraft || "") !== String(groupMeta?.defaultBuyIn ?? "") ||
      groupDefaultPeriodDraft !== normalizeGroupDefaultPeriod(groupMeta?.defaultPeriod) ||
      Boolean(groupPermissions.editorsCanLog) !== (groupMeta?.editorsCanLog !== false) ||
      Boolean(groupPermissions.editorsCanEditFinished) !== (groupMeta?.editorsCanEditFinished !== false) ||
      Boolean(groupPermissions.viewersSeeBalances) !== (groupMeta?.viewersSeeBalances !== false) ||
      Boolean(groupPermissions.membersSelfLog) !== Boolean(groupMeta?.membersSelfLog) ||
      Boolean(thirdPlaceDraft) !== Boolean(thirdPlaceEnabled) ||
      Number(groupPayoutFirstDraft || 65) !== Number(groupMeta?.payoutFirst ?? 65) ||
      Number(groupPayoutSecondDraft || 35) !== Number(groupMeta?.payoutSecond ?? 35) ||
      Number(groupPayoutThirdDraft || 0) !== Number(groupMeta?.payoutThird ?? 0)
    );
  }, [
    groupRole,
    groupMeta,
    groupCropBlobDraft,
    groupNameDraft,
    groupCurrencyDraft,
    groupStakesDraft,
    groupDefaultBuyInDraft,
    groupDefaultPeriodDraft,
    groupPermissions,
    thirdPlaceDraft,
    thirdPlaceEnabled,
    groupPayoutFirstDraft,
    groupPayoutSecondDraft,
    groupPayoutThirdDraft,
  ]);
  // New session: add-player inline
  const [showInlineAddPlayer, setShowInlineAddPlayer] = useState(false);
  // Onboarding profile drafts, committed by completeWizard when the new user
  // creates or joins their first group.
  const [wizardName, setWizardName] = useState("");
  const [wizardEmail, setWizardEmail] = useState("");
  // Default currency for a newly created group. No longer chosen during onboarding
  // (keeps sign-up minimal); it's editable later in group settings.
  const [wizardCurrency] = useState("EUR");
  const [wizardGroupName, setWizardGroupName] = useState("");
  const [wizardJoinCode, setWizardJoinCode] = useState("");
  // First-run / group picker: start a group, or join one with a code.
  const [groupChooserMode, setGroupChooserMode] = useState(null); // null = pick a default
  // Pre-fill the onboarding "Join with invite code" field when the user arrived
  // via an invite link, so they never have to type the code. Seeded once (won't
  // clobber a manual edit or re-seed after the user clears it to create instead).
  useEffect(() => {
    if (pendingInviteCode) setWizardJoinCode((prev) => prev || pendingInviteCode);
  }, [pendingInviteCode]);
  const [jackpotCards, setJackpotCards] = useState([
    { rank: "A", suit: "♠" },
    { rank: "K", suit: "♥" },
    { rank: "Q", suit: "♦" },
    { rank: "J", suit: "♣" },
    { rank: "10", suit: "♠" },
  ]);
  const [editingId, setEditingId] = useState(null);
  const [infoGameId, setInfoGameId] = useState(null);
  const [infoGameSource, setInfoGameSource] = useState(null); // "jackpot-history" | null
  // Open timeline editor: { mode: "game" | "live", gameId?, startedAt, endedAt,
  // events, playerIds }, a snapshot taken when it opened.
  const [timelineEdit, setTimelineEdit] = useState(null);
  // Full-page timeline for the game open in Game Info.
  const [timelineDetailOpen, setTimelineDetailOpen] = useState(false);
  useEffect(() => { setTimelineDetailOpen(false); }, [infoGameId]);
  const [relatedGamesView, setRelatedGamesView] = useState(null);
  const [settingsPanel, setSettingsPanel] = useState(null);
  const [gameSavedMessage, setGameSavedMessage] = useState("");
  const buyRefs = useRef({});
  const avatarInputRef = useRef(null);
  const wizardAvatarInputRef = useRef(null);
  const cropImgRef = useRef(null);
  const cropLastTouchDist = useRef(null);
  const groupPhotoInputRef = useRef(null);
  const groupSettingsBodyRef = useRef(null);
  const groupSettingsScrollPositionsRef = useRef({});
  const statsVisibilitySnapshotRef = useRef(null);
  const lgSwipeRef = useRef(null); // { pid, startX } for buy-in swipe gesture

  const currentGroupSettingsScreenKey = useMemo(() => {
    if (groupSettingsSection === "people") {
      if (peopleTab === "members" && editingMemberId) return `people:members:${editingMemberId}`;
      return `people:${peopleTab}`;
    }
    return groupSettingsSection || "main";
  }, [editingMemberId, groupSettingsSection, peopleTab]);

  const handleGroupSettingsBodyScroll = useCallback(() => {
    if (!showGroupSettings || !groupSettingsBodyRef.current) return;
    groupSettingsScrollPositionsRef.current[currentGroupSettingsScreenKey] = groupSettingsBodyRef.current.scrollTop;
  }, [currentGroupSettingsScreenKey, showGroupSettings]);

  const jackpotSettingsDirty = useMemo(
    () =>
      Math.max(0, Number(jackpotBase) || 0) !== Math.max(0, Number(savedJackpotBase) || 0) ||
      sidePrizeConfigDraft.enabled !== sidePrizeConfig.enabled ||
      sidePrizeConfigDraft.collectionMethod !== sidePrizeConfig.collectionMethod ||
      Number(sidePrizeConfigDraft.collectionAmount || 0) !== Number(sidePrizeConfig.collectionAmount || 0),
    [jackpotBase, savedJackpotBase, sidePrizeConfigDraft, sidePrizeConfig]
  );
  const normalizedRankingDraft = useMemo(
    () => normalizeRankingConfig(rankingConfigDraft),
    [rankingConfigDraft]
  );
  const rankingSettingsDirty = useMemo(() => (
    Object.keys(DEFAULT_RANKING_CONFIG).some((key) => Number(normalizedRankingDraft[key]) !== Number(rankingConfig[key]))
  ), [normalizedRankingDraft, rankingConfig]);
  const memberSettingsDirty = useMemo(() => (
    membersForRoles.some((member) => (
      String(memberRoleDraft[member.id] || member.role || "viewer") !== String(member.role || "viewer") ||
      String(memberPlayerDraft[member.id] || "") !== String(member.playerId || "")
    ))
  ), [memberPlayerDraft, memberRoleDraft, membersForRoles]);
  const statsVisibilityDirty = useMemo(() => {
    const hiddenFromGroup = players
      .filter((player) => syncedStatsVisibilityForActiveGroup?.[player.id] === false)
      .map((player) => String(player.id))
      .sort();
    const hiddenFromDraft = players
      .filter((player) => statsVisibilityDraft[player.id] === false)
      .map((player) => String(player.id))
      .sort();
    return JSON.stringify(hiddenFromDraft) !== JSON.stringify(hiddenFromGroup);
  }, [players, statsVisibilityDraft, syncedStatsVisibilityForActiveGroup]);

  // Sync buy-in amount from group default whenever entering the new-session tab
  // or when the group default changes. Uses a direct assignment (no prev guard)
  // so an updated defaultBuyIn is always reflected. The user can still override
  // it while on the tab — the effect only re-fires on a dependency change.
  useEffect(() => {
    if (activeTab === "new") {
      setLiveSetupBuyIn(groupMeta?.defaultBuyIn ? String(groupMeta.defaultBuyIn) : "");
    }
  }, [activeTab, groupMeta?.defaultBuyIn]);

  useEffect(() => {
    if (!showGroupSettings) return;
    const frameId = requestAnimationFrame(() => {
      // The group-settings overlay is the scroll container (the body flows
      // naturally inside it); reset it to the top on every screen change.
      const overlay = document.querySelector(".gs-overlay--group-settings");
      if (overlay) overlay.scrollTop = 0;
      if (groupSettingsBodyRef.current) groupSettingsBodyRef.current.scrollTop = 0;
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentGroupSettingsScreenKey, showGroupSettings]);

  useEffect(() => {
    if (!showGroupSettings || groupSettingsSection !== null) return;
    setEditingGroupIdentity(false);
    setGroupNameDraft(groupMeta?.name || "");
    setGroupCurrencyDraft(groupMeta?.currency || "EUR");
    setGroupStakesDraft(groupMeta?.defaultStakes || "");
    setGroupDefaultBuyInDraft(groupMeta?.defaultBuyIn != null ? String(groupMeta.defaultBuyIn) : "");
    setGroupDefaultPeriodDraft(normalizeGroupDefaultPeriod(groupMeta?.defaultPeriod));
    setGroupPermissions({
      editorsCanLog: groupMeta?.editorsCanLog !== false,
      editorsCanEditFinished: groupMeta?.editorsCanEditFinished !== false,
      viewersSeeBalances: groupMeta?.viewersSeeBalances !== false,
      membersSelfLog: !!groupMeta?.membersSelfLog,
    });
    setThirdPlaceDraft(!!thirdPlaceEnabled);
    setGroupPayoutFirstDraft(String(groupMeta?.payoutFirst ?? 65));
    setGroupPayoutSecondDraft(String(groupMeta?.payoutSecond ?? 35));
    setGroupPayoutThirdDraft(String(groupMeta?.payoutThird ?? 0));
  }, [showGroupSettings, groupSettingsSection, groupMeta, thirdPlaceEnabled]);

  // Restore the previously active group when settings panel closes
  useEffect(() => {
    if (!showGroupSettings && preSettingsActiveGroupIdRef.current !== null) {
      setActiveGroupId(preSettingsActiveGroupIdRef.current);
      preSettingsActiveGroupIdRef.current = null;
    }
    if (!showGroupSettings) {
      setPendingGroupSettingsOpen(false);
    }
  }, [showGroupSettings]);

  useEffect(() => {
    if (!pendingGroupSettingsOpen || !activeGroupId || !groupMeta) return;
    setShowGroupSettings(true);
    setPendingGroupSettingsOpen(false);
  }, [activeGroupId, groupMeta, pendingGroupSettingsOpen]);

  useEffect(() => {
    suppressedPlayerTrendTooltipKeyRef.current = "";
    setPlayerTrendTooltipState(null);
  }, [dateFilterEnd, dateFilterPreset, dateFilterStart, playerProfileChartMode, playerProfileId]);

  useEffect(() => {
    setPlayerProfileChartMode("profit");
  }, [playerProfileId]);

  useEffect(() => {
    const head = document.head;
    const meta = document.createElement('meta');
    meta.name = "viewport";
    meta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";
    head.appendChild(meta);
    const style = document.createElement("style");
    style.innerHTML = `/* Component styles moved to theme.css */`;
    head.appendChild(style);
    return () => {
      head.removeChild(style);
      head.removeChild(meta);
    };
  }, []);

  // Save jackpot base to Firebase
  async function saveJackpotBase() {
    if (!user || !activeGroupId || groupRole !== "moderator") return;
    await setDoc(doc(db, "groups", activeGroupId, "settings", "jackpot"), {
      base: Math.max(0, Number(jackpotBase) || 0),
      enabled: sidePrizeConfigDraft.enabled,
      collectionMethod: sidePrizeConfigDraft.collectionMethod,
      collectionAmount: Number(sidePrizeConfigDraft.collectionAmount) || 0,
    }, { merge: true });
    setSidePrizeConfig(sidePrizeConfigDraft);
    logActivity("settings_changed", { settingType: "jackpot" });
    setSettingsPanel(null);
  }


  async function saveRankingConfig() {
    if (!user || !activeGroupId || groupRole !== "moderator") return;
    const normalized = normalizeRankingConfig(rankingConfigDraft);
    await setDoc(doc(db, "groups", activeGroupId, "settings", "ranking"), normalized);
    logActivity("settings_changed", { settingType: "ranking" });
    setSettingsPanel(null);
  }

  function resetGroupSettingsDrafts() {
    setEditingGroupIdentity(false);
    setGroupNameDraft(groupMeta?.name || "");
    setGroupCurrencyDraft(groupMeta?.currency || "EUR");
    setGroupStakesDraft(groupMeta?.defaultStakes || "");
    setGroupDefaultBuyInDraft(groupMeta?.defaultBuyIn != null ? String(groupMeta.defaultBuyIn) : "");
    setGroupDefaultPeriodDraft(normalizeGroupDefaultPeriod(groupMeta?.defaultPeriod));
    setGroupPermissions({
      editorsCanLog: groupMeta?.editorsCanLog !== false,
      editorsCanEditFinished: groupMeta?.editorsCanEditFinished !== false,
      viewersSeeBalances: groupMeta?.viewersSeeBalances !== false,
      membersSelfLog: !!groupMeta?.membersSelfLog,
    });
    setThirdPlaceDraft(!!thirdPlaceEnabled);
    if (groupPhotoPreviewURL) URL.revokeObjectURL(groupPhotoPreviewURL);
    setGroupPhotoPreviewURL(null);
    setGroupCropBlobDraft(null);
    setGroupPayoutFirstDraft(String(groupMeta?.payoutFirst ?? 65));
    setGroupPayoutSecondDraft(String(groupMeta?.payoutSecond ?? 35));
    setGroupPayoutThirdDraft(String(groupMeta?.payoutThird ?? 0));
  }

  function openGroupPhotoPicker() {
    groupPhotoInputRef.current?.click();
  }

  function handleGroupPhotoFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user || !activeGroupId || groupRole !== "moderator") return;
    if (!file.type.startsWith("image/")) {
      setToastMessage("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropTarget("group");
      setCropImageSrc(e.target.result);
      setCropNaturalSize({ w: 0, h: 0 });
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  }

  async function deleteGroupPhoto() {
    if (!user || !activeGroupId || groupRole !== "moderator") return;
    try {
      const previousPhotoURL = String(groupMeta?.photoURL || "").trim();
      if (previousPhotoURL) {
        const m = previousPhotoURL.match(/\/o\/(.+?)\?/);
        if (m) try { await deleteObject(storageRef(storage, decodeURIComponent(m[1]))); } catch (_) {}
      }
      await setDoc(doc(db, "groups", activeGroupId), { photoURL: deleteField() }, { merge: true });
      logActivity("group_photo_updated", { changes: ["group picture: removed"] });
      setToastMessage("Group picture removed");
      setToastType("success");
    } catch (err) {
      console.error("Group photo delete failed:", err?.code, err?.message);
      setToastMessage("Could not remove group picture");
    }
  }

  // Wizard: complete profile setup and optionally create/join a group
  async function completeWizard(mode, extraGroupName, extraJoinCode) {
    if (!user || user.isAnonymous) return;
    try {
    // Save profile name & email
    const name = wizardName.trim() || user.displayName || "Player";
    const nameParts = splitNameParts(name);
    let profilePhotoURL = "";
    let hasCustomPhoto = false;

    if (wizardCropBlobDraft) {
      const path = `avatars/${user.uid}/profile/${Date.now()}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, wizardCropBlobDraft, { contentType: "image/jpeg" });
      profilePhotoURL = await getDownloadURL(ref);
      hasCustomPhoto = true;
    }

    if (name) {
      try {
        await updateProfile(getAuth().currentUser, {
          displayName: name,
          ...(profilePhotoURL ? { photoURL: profilePhotoURL } : {}),
        });
      } catch (_) {}
    }
    const profilePayload = {
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      nickname: "",
      profileName: name,
      email: wizardEmail.trim() || user.email || "",
      displayName: name,
      profilePhotoURL,
      photoSource: profilePhotoURL ? "upload" : "generated",
      ...(hasCustomPhoto ? { hasCustomPhoto: true, photoPromptSeen: true } : {}),
      currency: wizardCurrency,
    };
    // Save to user profile doc
    await setDoc(doc(db, "users", user.uid, "profile", "main"), profilePayload, { merge: true });

    // Create or join group
    if (mode === "create" && extraGroupName?.trim()) {
      setGroupNameInput(extraGroupName.trim());
      await createGroupWithName(extraGroupName.trim(), profilePayload);
    } else if (mode === "join" && extraJoinCode?.trim()) {
      setJoinCodeInput(extraJoinCode.trim().toUpperCase());
      await joinGroupWithCode(user, extraJoinCode.trim().toUpperCase());
      if (hasCustomPhoto) {
        await syncUserProfileAcrossGroups({ ...profilePayload, hasCustomPhoto: true });
      }
    }
    if (wizardPhotoPreviewURL) URL.revokeObjectURL(wizardPhotoPreviewURL);
    setWizardPhotoPreviewURL(null);
    setWizardCropBlobDraft(null);
    } catch (err) {
      console.error("completeWizard failed:", err?.code, err?.message, err);
      showAlert(`Setup failed: ${err?.message || "Unknown error"}`);
    }
  }

  async function createGroupWithName(name, profileOverride = null) {
    if (!user || user.isAnonymous || !name.trim()) return;
    const profileForGroup = profileOverride || currentUserProfileData || {};
    const inviteCode = makeInviteCode();
    const groupRef = await addDoc(collection(db, "groups"), {
      name: name.trim(),
      createdBy: user.uid,
      createdAt: Date.now(),
      inviteCode,
      currency: wizardCurrency || "EUR",
    });
    await setDoc(doc(db, "inviteCodes", inviteCode), { groupId: groupRef.id, groupName: name.trim() });
    const authNameParts = splitNameParts(user.displayName || "");
    await setDoc(doc(db, "groups", groupRef.id, "members", user.uid), {
      uid: user.uid,
      firstName: profileForGroup?.firstName || authNameParts.firstName || "",
      lastName: profileForGroup?.lastName || authNameParts.lastName || "",
      nickname: profileForGroup?.nickname || "",
      displayName: profileForGroup?.displayName || user.displayName || "",
      profileName: profileForGroup?.profileName || wizardName.trim() || user.displayName || "",
      ...(profileForGroup?.profilePhotoURL
        ? { profilePhotoURL: profileForGroup.profilePhotoURL, hasCustomPhoto: true }
        : {}),
      role: "moderator",
      createdAt: Date.now(),
    });
    try {
      await addDoc(collection(db, "groups", groupRef.id, "activity"), {
        type: "group_created",
        actorId: user.uid,
        actorName: profileForGroup?.profileName || user?.displayName || "User",
        timestamp: Date.now(),
        changes: [`group created: ${name.trim()}`],
      });
    } catch (err) {
      console.error("Activity log failed:", err?.code, err?.message);
    }
    rememberGroupInProfile(user.uid, groupRef.id);
    setMyGroups((prev) => {
      const next = [{ id: groupRef.id, name: name.trim(), createdAt: Date.now(), createdBy: user.uid, inviteCode }, ...prev.filter((g) => g.id !== groupRef.id)];
      next.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      return next;
    });
    setActiveGroupId(groupRef.id);
    setGroupNameInput("");
    setActiveTab("home");
  }

  async function saveGameSettings(enabled) {
    if (!user || !activeGroupId || groupRole !== "moderator") return;
    await setDoc(doc(db, "groups", activeGroupId, "settings", "game"), { thirdPlaceEnabled: enabled }, { merge: true });
    setThirdPlaceEnabled(enabled);
    logActivity("settings_changed", {
      settingType: "game",
      changes: [`third place: ${thirdPlaceEnabled ? "on" : "off"} -> ${enabled ? "on" : "off"}`],
    });
  }

  async function saveMainGroupSettings() {
    if (!user || !activeGroupId || groupRole !== "moderator") return;

    const parsedDefaultBuyIn = parseFloat(groupDefaultBuyInDraft);
    const updates = {
      name: String(groupNameDraft || "").trim() || String(groupMeta?.name || "Group"),
      currency: String(groupCurrencyDraft || "EUR").toUpperCase(),
      defaultStakes: groupStakesDraft,
      defaultBuyIn: isNaN(parsedDefaultBuyIn) || parsedDefaultBuyIn <= 0 ? null : parsedDefaultBuyIn,
      defaultPeriod: normalizeGroupDefaultPeriod(groupDefaultPeriodDraft),
      editorsCanLog: !!groupPermissions.editorsCanLog,
      editorsCanEditFinished: !!groupPermissions.editorsCanEditFinished,
      viewersSeeBalances: !!groupPermissions.viewersSeeBalances,
      membersSelfLog: !!groupPermissions.membersSelfLog,
      payoutFirst: Number(groupPayoutFirstDraft) || 65,
      payoutSecond: Number(groupPayoutSecondDraft) || 35,
      payoutThird: Number(groupPayoutThirdDraft) || 0,
    };
    const changedFields = [];
    if (String(groupMeta?.name || "Group") !== updates.name) changedFields.push(`name: ${String(groupMeta?.name || "Group")} -> ${updates.name}`);
    if (String(groupMeta?.currency || "EUR").toUpperCase() !== updates.currency) changedFields.push(`currency: ${String(groupMeta?.currency || "EUR").toUpperCase()} -> ${updates.currency}`);
    if (String(groupMeta?.defaultStakes || "") !== String(updates.defaultStakes || "")) changedFields.push(`default stakes: ${String(groupMeta?.defaultStakes || "none")} -> ${String(updates.defaultStakes || "none")}`);
    if (String(groupMeta?.defaultBuyIn ?? "") !== String(updates.defaultBuyIn ?? "")) changedFields.push(`default buy-in: ${groupMeta?.defaultBuyIn ?? "none"} -> ${updates.defaultBuyIn ?? "none"}`);
    if (normalizeGroupDefaultPeriod(groupMeta?.defaultPeriod) !== updates.defaultPeriod) changedFields.push(`default period: ${groupDefaultPeriodLabel(groupMeta?.defaultPeriod)} -> ${groupDefaultPeriodLabel(updates.defaultPeriod)}`);
    if (Boolean(groupMeta?.editorsCanLog !== false) !== Boolean(updates.editorsCanLog)) changedFields.push(`editors can log: ${groupMeta?.editorsCanLog !== false ? "on" : "off"} -> ${updates.editorsCanLog ? "on" : "off"}`);
    if (Boolean(groupMeta?.editorsCanEditFinished !== false) !== Boolean(updates.editorsCanEditFinished)) changedFields.push(`editors can change finished games: ${groupMeta?.editorsCanEditFinished !== false ? "on" : "off"} -> ${updates.editorsCanEditFinished ? "on" : "off"}`);
    if (Boolean(groupMeta?.viewersSeeBalances !== false) !== Boolean(updates.viewersSeeBalances)) changedFields.push(`view balances: ${groupMeta?.viewersSeeBalances !== false ? "on" : "off"} -> ${updates.viewersSeeBalances ? "on" : "off"}`);
    if (Boolean(groupMeta?.membersSelfLog) !== Boolean(updates.membersSelfLog)) changedFields.push(`members can self-log: ${groupMeta?.membersSelfLog ? "on" : "off"} -> ${updates.membersSelfLog ? "on" : "off"}`);
    if (Boolean(thirdPlaceDraft) !== Boolean(thirdPlaceEnabled)) changedFields.push(`third place: ${thirdPlaceEnabled ? "on" : "off"} -> ${thirdPlaceDraft ? "on" : "off"}`);
    if (Number(groupMeta?.payoutFirst ?? 65) !== updates.payoutFirst) changedFields.push(`payout 1st: ${groupMeta?.payoutFirst ?? 65}% -> ${updates.payoutFirst}%`);
    if (Number(groupMeta?.payoutSecond ?? 35) !== updates.payoutSecond) changedFields.push(`payout 2nd: ${groupMeta?.payoutSecond ?? 35}% -> ${updates.payoutSecond}%`);
    if (Number(groupMeta?.payoutThird ?? 0) !== updates.payoutThird) changedFields.push(`payout 3rd: ${groupMeta?.payoutThird ?? 0}% -> ${updates.payoutThird}%`);

    if (groupCropBlobDraft) {
      const previousPhotoURL = String(groupMeta?.photoURL || "").trim();
      if (previousPhotoURL) {
        const m = previousPhotoURL.match(/\/o\/(.+?)\?/);
        if (m) try { await deleteObject(storageRef(storage, decodeURIComponent(m[1]))); } catch (_) {}
      }
      try {
        const path = `groups/${activeGroupId}/photo/${Date.now()}.jpg`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, groupCropBlobDraft, { contentType: "image/jpeg" });
        updates.photoURL = await getDownloadURL(ref);
        changedFields.push(`group picture: ${previousPhotoURL ? "updated" : "added"}`);
      } catch (err) {
        console.error("Group photo upload failed:", err?.code, err?.message);
        setToastMessage("Could not update group picture");
        return;
      }
    }

    await setDoc(doc(db, "groups", activeGroupId), updates, { merge: true });
    if (groupMeta?.inviteCode) {
      await setDoc(doc(db, "inviteCodes", groupMeta.inviteCode), { groupName: updates.name }, { merge: true });
    }

    if (Boolean(thirdPlaceDraft) !== Boolean(thirdPlaceEnabled)) {
      await saveGameSettings(!!thirdPlaceDraft);
    }
    if (changedFields.length) {
      logActivity("group_updated", { changes: changedFields });
    }

    clearGroupPhotoDraft();
    setEditingGroupIdentity(false);
    setToastMessage("Group settings saved");
    setToastType("success");
  }

  async function saveStatsVisibility() {
    if (!user || !activeGroupId) return;
    if (!user.isAnonymous) {
      await setDoc(doc(db, "users", user.uid, "profile", "main"), {
        statsVisibilityByGroup: {
          [activeGroupId]: statsVisibilityDraft,
        },
      }, { merge: true });
    }
    // Keep a local fallback for existing installs and offline usage.
    if (localVisKey) {
      localStorage.setItem(localVisKey, JSON.stringify(statsVisibilityDraft));
    }
    setSettingsPanel(null);
  }

  async function repairLinkedPlayerProfiles() {
    if (!user || !activeGroupId || groupRole !== "moderator") return;
    for (const member of membersForRoles) {
      if (!member?.playerId) continue;
      await syncLinkedPlayerProfile(member.id);
    }
    setToastMessage("Linked player profiles repaired");
  }

  // Change logs: the open Game Info's game, and the live game while its table is open.
  const infoGameLog = useSessionLog(activeGroupId, infoGameId);
  const liveGameLog = useSessionLog(activeGroupId, activeTab === "new" ? liveGame?.gameId : null);

  const canManageSettings = groupRole === "moderator";
  const canManagePlayers = groupRole === "moderator"; // edit/delete existing players
  const canAddPlayers = groupRole === "moderator" || groupRole === "editor"; // create new players
  const canEditGames = groupRole === "moderator" || groupRole === "editor";
  const canCreateGames = canEditGames; // viewers and guests cannot add or edit games

  // Changing a saved game: moderators always; editors unless the group
  // switched off "Editors can change finished games" (also enforced in rules).
  function canManageSingleGame(game) {
    if (!game || !user) return false;
    return groupRole === "moderator" || (groupRole === "editor" && groupMeta?.editorsCanEditFinished !== false);
  }

  // Every game and live-game write names its writer (the rules require it),
  // so the server-side session log can say who changed what. The reason is
  // reset on each write so an old one never sticks to a new change.
  function editStamp(reason = "") {
    return { lastEditedBy: user?.uid || "", editReason: reason };
  }

  // Close all floating panels/overlays (called on tab switch)
  function closeAllPanels() {
    setSettingsPanel(null);
    setTimelineEdit(null);
    setTimelineDetailOpen(false);
    setInfoGameId(null);
    setInfoGameSource(null);
    setPlayerProfileId(null);
    setShowPlayerManager(false);
    setJackpotHistoryOpen(false);
    setEditingMemberId(null);
    setConfirmGroupAction(null);
    setShowGroupSettings(false);
    setGroupSettingsSection(null);
    setPeopleTab("members");
    setShowInlineAddPlayer(false);
    setShowAddPlayerForm(false);
    setPlayerSettingsId(null);
  }

  // Anything typed into the manual "log a game" form yet?
  function manualGameHasInput() {
    const defaultGameDate = new Date().toISOString().slice(0, 10);
    return !!(
      String(location || "").trim() !== "" ||
      String(gameNotes || "").trim() !== "" ||
      String(gameDate || "") !== defaultGameDate ||
      Object.keys(buyIns || {}).length > 0 ||
      firstWinnerId ||
      secondWinnerId ||
      thirdWinnerId ||
      jackpotWinnerId ||
      splitWinnerId ||
      isSplitPot
    );
  }

  async function handleTabSelect(tab) {
    if (tab === activeTab) {
      // Tapping the active tab dismisses open overlays (acts as "return")
      if (timelineEdit) { setTimelineEdit(null); return; }
      if (timelineDetailOpen) { setTimelineDetailOpen(false); return; }
      if (playerProfileId) { setPlayerProfileId(null); return; }
      if (relatedGamesView) { setRelatedGamesView(null); return; }
      if (infoGameId) { setInfoGameId(null); setInfoGameSource(null); return; }
      return;
    }
    const hasUnsavedGame = activeTab === "new" && !liveGame && showManualGameEntry && manualGameHasInput();
    if (hasUnsavedGame) {
      const discard = await showConfirm({ title: "Discard this game?", message: "The details you've entered will be lost.", confirmLabel: "Discard game", destructive: true });
      if (!discard) return;
      resetForm();
      setLiveSetupBuyIn("");
      setShowManualGameEntry(false);
    }
    closeAllPanels();
    setActiveTab(tab);
    containerRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }

  // Swipe right from the left edge = that page's Back button. Checked top-most
  // first: the full-screen pages share one z-index, so a page rendered later in
  // the tree (jackpot history, What's New, game info, game lists) sits above one
  // rendered earlier (player profile). Each branch mirrors its Back button so a
  // swipe cleans up exactly what a tap would.
  useSwipeBack(async () => {
    if (timelineEdit) { setTimelineEdit(null); return; }
    if (timelineDetailOpen) { setTimelineDetailOpen(false); return; }
    if (jackpotHistoryOpen) { setJackpotHistoryOpen(false); setJackpotFilterId(null); return; }
    if (whatsNewOpen) { setWhatsNewOpen(false); return; }
    if (infoGameId) { setInfoGameId(null); setInfoGameSource(null); return; }
    if (relatedGamesView) { setRelatedGamesView(null); return; }
    if (playerProfileId) { setPlayerProfileId(null); setH2hInfoOpen(false); return; }
    if (showPlayerManager) {
      setShowPlayerManager(false);
      setShowGroupSettings(true);
      setShowAddPlayerForm(false);
      setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
      setPlayerSettingsId(null);
      return;
    }
    if (showGroupSettings) {
      if (editingMemberId) { setConfirmRemoveMember(null); setEditingMemberId(null); return; }
      if (groupSettingsSection === "people" && showAddPlayerForm) { setShowAddPlayerForm(false); return; }
      if (groupSettingsSection === "people" && playerSettingsId) { setPlayerSettingsId(null); return; }
      if (groupSettingsSection === "statsVisibility") {
        // Same as its Back button: leaving without Save discards the toggles.
        setStatsVisibilityDraft(statsVisibilitySnapshotRef.current);
        statsVisibilitySnapshotRef.current = null;
        setGroupSettingsSection(null);
        return;
      }
      if (groupSettingsSection) { setGroupSettingsSection(null); return; }
      resetGroupSettingsDrafts();
      setEditingGroupIdentity(false);
      setShowGroupSettings(false);
      return;
    }
    if (settingsPanel) { setSettingsPanel(null); return; }
    if (activeTab === "new" && liveGame && liveEndFlow) { setLiveEndFlow(false); return; }
    if (activeTab === "new" && !liveGame && showManualGameEntry) {
      // A swipe is easier to do by accident than a tap, so don't silently drop
      // a half-entered game.
      if (manualGameHasInput() && !(await showConfirm({ title: "Discard the game you're entering?", confirmLabel: "Discard game", destructive: true }))) return;
      resetForm();
      setShowManualGameEntry(false);
    }
  });

  function openPeopleSettings(tab = "members") {
    setPeopleTab(tab);
    setEditingMemberId(null);
    setPlayerSettingsId(null);
    setShowAddPlayerForm(false);
    setGroupSettingsSection("people");
  }

  async function leaveGroup(groupId) {
    if (!user || !groupId) return;
    const membersSnap = await getDocs(collection(db, "groups", groupId, "members"));
    const moderators = membersSnap.docs.filter((d) => String(d.data()?.role || "viewer") === "moderator");
    const myMemberDoc = membersSnap.docs.find((d) => d.id === user.uid);
    const myRole = String(myMemberDoc?.data()?.role || "viewer");
    if (myRole === "moderator" && moderators.length <= 1) {
      showAlert("Each group must have at least one moderator. Assign another moderator before leaving.");
      return;
    }

    // PII cleanup: strip account-linked data from the player row before leaving.
    // Game history stays attributed to the player name; only the account linkage
    // and contact info are removed.
    const linkedPlayerId = String(myMemberDoc?.data()?.playerId || "").trim();
    if (linkedPlayerId) {
      // Delete private contact first while linkedUserUid still matches — the rule
      // check relies on it. Then strip account-linked fields from the player doc.
      try {
        await deleteDoc(doc(db, "groups", groupId, "players", linkedPlayerId, "private", "contact"));
      } catch (_) {}
      try {
        await updateDoc(doc(db, "groups", groupId, "players", linkedPlayerId), {
          linkedUserUid: null,
          profilePhotoURL: deleteField(),
        });
      } catch (_) {}
    }

    leavingGroupIdsRef.current.add(groupId);
    await deleteDoc(doc(db, "groups", groupId, "members", user.uid));
    setDoc(doc(db, "users", user.uid, "profile", "main"),
      { groupIds: arrayRemove(groupId), joinCodes: { [groupId]: deleteField() } },
      { merge: true }
    ).catch(() => {});
    const remaining = myGroups.filter((g) => g.id !== groupId);
    setMyGroups(remaining);
    if (activeGroupId === groupId) {
      localStorage.removeItem("activeGroupId");
      if (remaining.length > 0) {
        setActiveGroupId(remaining[0].id);
        setActiveTab("profile");
      } else {
        setActiveGroupId("");
      }
    }
  }

  // Drop a group this account is no longer a member of: from the profile (so
  // the loader stops looking for it), from the group list, and as the open group.
  // Records a group in the user's profile, which is how the next sign-in
  // finds it. Retried: on a fresh sign-up this write can be refused while the
  // auth token reaches Firestore, and a silent miss sent returning members to
  // the create/join screen. The members collection-group lookup is the backup.
  async function rememberGroupInProfile(uid, gid, code = "") {
    const patch = { groupIds: arrayUnion(gid), ...(code ? { joinCodes: { [gid]: code } } : {}) };
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        await setDoc(doc(db, "users", uid, "profile", "main"), patch, { merge: true });
        return;
      } catch (err) {
        if (attempt === 3) console.error("Saving group to profile failed:", err?.code, err?.message);
        else await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
  }

  function forgetGroup(gid, name = "") {
    if (!user || !gid) return;
    if (!user.isAnonymous) {
      setDoc(doc(db, "users", user.uid, "profile", "main"),
        { groupIds: arrayRemove(gid), joinCodes: { [gid]: deleteField() } },
        { merge: true }
      ).catch(() => {});
    }
    setMyGroups((prev) => prev.filter((g) => g.id !== gid));
    try {
      const lastKey = getLastGroupStorageKey(user.uid);
      if (localStorage.getItem(lastKey) === gid) localStorage.removeItem(lastKey);
    } catch (_) { /* storage blocked */ }
    setToastType("default");
    setToastMessage(name ? `You're no longer a member of ${name}` : "You're no longer a member of that group");
  }

  // Our membership in the open group vanished (member doc gone, or reads now
  // denied). Close the group AND take it out of the list straight away —
  // otherwise the "reopen last group" effect reselects it and re-subscribes to
  // targets Firestore just rejected, which trips an SDK internal assertion.
  // Then decide what happened once the listeners have detached.
  function handleLostMembership(gid) {
    if (!user || !gid || leavingGroupIdsRef.current.has(gid) || lostGroupIdsRef.current.has(gid)) return;
    lostGroupIdsRef.current.add(gid);
    const name = myGroupsRef.current.find((g) => g.id === gid)?.name || "";
    setMyGroups((prev) => prev.filter((g) => g.id !== gid));
    if (activeGroupIdRef.current === gid) {
      setActiveGroupId("");
      localStorage.removeItem("activeGroupId");
    }
    setTimeout(() => {
      resolveLostMembership(gid, name).finally(() => lostGroupIdsRef.current.delete(gid));
    }, 400);
  }

  // Confirm with a server read (a permission error during sign-in can look the
  // same), then try the loader's self-heal: rejoin with the code saved at join
  // time. A removal rotates the invite code, so the rules refuse that and we
  // forget the group. Anything else (still a member, restored, or can't tell)
  // re-runs the group loader, which brings the group back if it should be there.
  async function resolveLostMembership(gid, name) {
    const reload = () => setGroupLoadNonce((n) => n + 1);
    const memberRef = doc(db, "groups", gid, "members", user.uid);
    try {
      const snap = await getDocFromServer(memberRef);
      if (snap.exists()) { reload(); return; }
    } catch (_) {
      reload(); // offline or token not ready
      return;
    }
    let code = "";
    if (!user.isAnonymous) {
      try {
        const profileSnap = await getDocFromServer(doc(db, "users", user.uid, "profile", "main"));
        code = String(profileSnap.data()?.joinCodes?.[gid] || "");
      } catch (_) {
        reload();
        return;
      }
    }
    if (code) {
      try {
        await setDoc(memberRef, {
          uid: user.uid,
          role: "viewer",
          inviteCode: code,
          displayName: user.displayName || "",
          createdAt: Date.now(),
        }, { merge: true });
        reload(); // restored
        return;
      } catch (err) {
        if (err?.code !== "permission-denied") { reload(); return; }
      }
    }
    forgetGroup(gid, name);
  }

  // Swap the group's invite code for a fresh one. The old code stops working,
  // which is what keeps a removed member out until someone shares the new one.
  async function rotateInviteCode() {
    const oldCode = String(groupMeta?.inviteCode || "").trim();
    const newCode = await generateUniqueInviteCode();
    await setDoc(doc(db, "inviteCodes", newCode), { groupId: activeGroupId, groupName: groupMeta?.name || "" });
    await setDoc(doc(db, "groups", activeGroupId), { inviteCode: newCode }, { merge: true });
    if (oldCode && oldCode !== newCode) {
      try { await deleteDoc(doc(db, "inviteCodes", oldCode)); } catch (_) {}
    }
    return newCode;
  }

  // Remove a member: they lose access, their player leaves the roster (games
  // and stats keep it), and the invite code changes so they can't rejoin with
  // the old one. Their own device drops the group next time it checks.
  async function removeMemberFromGroup(targetUid) {
    if (!canManageSettings || !activeGroupId || !targetUid) return;
    if (targetUid === user?.uid) return; // cannot remove self via this path
    if (targetUid === groupMeta?.createdBy) {
      showAlert("The group's creator can't be removed.");
      return;
    }
    try {
      const membersSnap = await getDocs(collection(db, "groups", activeGroupId, "members"));
      const targetDoc = membersSnap.docs.find((d) => d.id === targetUid);
      if (!targetDoc) return;
      const targetRole = String(targetDoc.data()?.role || "viewer");
      if (targetRole === "moderator") {
        const moderators = membersSnap.docs.filter((d) => String(d.data()?.role || "viewer") === "moderator");
        if (moderators.length <= 1) {
          showAlert("This is the only moderator in the group. Assign another moderator before removing them.");
          return;
        }
      }

      // 1) New invite code first. If this fails, stop: removing them while the
      //    old code still works would let them straight back in.
      const newCode = await rotateInviteCode();
      // The code they joined with, if it's an older one still pointing here.
      const joinedWith = String(targetDoc.data()?.inviteCode || "").trim();
      if (joinedWith && joinedWith !== newCode) {
        try { await deleteDoc(doc(db, "inviteCodes", joinedWith)); } catch (_) {}
      }

      // 2) Player: unlink, strip account data, and take it off the roster.
      //    `formerMemberUid` lets a moderator link them back if they rejoin.
      const linkedPlayerIds = new Set(
        [String(targetDoc.data()?.playerId || "").trim()]
          .concat(players.filter((p) => String(p.linkedUserUid || "") === targetUid).map((p) => p.id))
          .filter(Boolean)
      );
      for (const pid of linkedPlayerIds) {
        try { await deleteDoc(doc(db, "groups", activeGroupId, "players", pid, "private", "contact")); } catch (_) {}
        try {
          await updateDoc(doc(db, "groups", activeGroupId, "players", pid), {
            linkedUserUid: null,
            profilePhotoURL: deleteField(),
            hasCustomPhoto: deleteField(),
            removedFromGroup: true,
            formerMemberUid: targetUid,
            removedAt: Date.now(),
          });
        } catch (_) {}
      }
      try { await deleteDoc(doc(db, "groups", activeGroupId, "pendingPlayerLinks", targetUid)); } catch (_) {}

      // 3) Membership last.
      await deleteDoc(doc(db, "groups", activeGroupId, "members", targetUid));
      setEditingMemberId(null);
      setToastType("success");
      setToastMessage(`Member removed. New invite code: ${newCode}`);
    } catch (err) {
      console.error("removeMemberFromGroup failed:", err);
      setToastMessage("Could not remove member. Please try again.");
    }
  }

  // Permanent group delete, in two steps: a summary of what goes (from the
  // server's dry run), then typing the group's name. The deleteGroup Cloud
  // Function checks the role, the name and the live game again itself.
  // Never carry a half-done delete into another group or a reopened sheet.
  useEffect(() => {
    setDeleteGroupFlow(null);
  }, [showGroupSettings, activeGroupId]);

  async function openDeleteGroup(groupId) {
    if (!user || !groupId) return;
    setDeleteGroupFlow({ groupId, step: "summary", info: null, typed: "", busy: false, error: "" });
    try {
      const res = await httpsCallable(getFunctions(), "deleteGroup")({ groupId, dryRun: true });
      setDeleteGroupFlow((f) => (f && f.groupId === groupId ? { ...f, info: res.data } : f));
    } catch (err) {
      setDeleteGroupFlow((f) => (f && f.groupId === groupId ? { ...f, error: err?.message || "Could not load the group" } : f));
    }
  }

  // Leave the group in the app first, then delete it on the server. Listeners
  // still open on a group while its data and our membership vanish get
  // "permission denied" mid-switch, which can wedge Firestore's client (it
  // stops loading anything until the app restarts).
  async function confirmDeleteGroup() {
    const flow = deleteGroupFlow;
    if (!user || !flow?.info || flow.busy) return;
    const groupId = flow.groupId;
    const name = flow.info.name || "the group";
    const before = myGroupsRef.current;
    const wasActive = activeGroupIdRef.current === groupId;
    const remaining = before.filter((g) => g.id !== groupId);
    // Our membership is about to vanish; don't treat it as being removed.
    leavingGroupIdsRef.current.add(groupId);
    setDeleteGroupFlow(null);
    setShowGroupSettings(false);
    setDeletingGroupName(name);
    setMyGroups(remaining);
    try {
      const lastKey = getLastGroupStorageKey(user.uid);
      if (localStorage.getItem(lastKey) === groupId) localStorage.removeItem(lastKey);
    } catch (_) { /* storage blocked */ }
    if (wasActive) {
      localStorage.removeItem("activeGroupId");
      // Next group, or none (onboarding shows once the delete is done).
      setActiveGroupId(remaining[0]?.id || "");
      setActiveTab("profile");
    }
    // Let React unmount the old group's listeners before the server deletes it.
    await new Promise((r) => setTimeout(r, 300));
    try {
      await httpsCallable(getFunctions(), "deleteGroup")({ groupId, confirmName: flow.typed });
    } catch (err) {
      // Nothing was deleted: put the group back and reopen it.
      leavingGroupIdsRef.current.delete(groupId);
      setDeletingGroupName("");
      setMyGroups(before);
      if (wasActive) setActiveGroupId(groupId);
      showAlert(`Could not delete ${name}: ${err?.message || "please try again."}`);
      return;
    }
    setDeletingGroupName("");
    setToastType("default");
    setToastMessage(`Deleted ${name}`);
  }

  async function signInWithGoogle() {
    if (googleSignInLoading) return;
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      setGoogleSignInLoading(true);
      const cred = await signInWithPopup(auth, provider);
      claimDeviceSession(cred.user.uid);
    } catch (err) {
      console.error("Google sign-in failed:", err?.code, err?.message);
      const code = String(err?.code || "unknown_error");
      if (code === "auth/popup-blocked") {
        showAlert("Popup blocked by browser. Allow popups for this site and try again.");
      } else {
        showAlert(`Sign-in failed: ${code}`);
      }
    } finally {
      setGoogleSignInLoading(false);
    }
  }

  function getEmailAuthErrorMessage(err) {
    const code = String(err?.code || "");
    if (code === "auth/email-already-in-use") return "An account already uses this email. Sign in instead.";
    if (code === "auth/invalid-email") return "Enter a valid email address.";
    if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") return "Email or password is incorrect.";
    if (code === "auth/weak-password") return "Use at least 6 characters for your password.";
    if (code === "auth/too-many-requests") return "Too many attempts. Wait a moment, then try again.";
    if (code === "auth/network-request-failed") return "Connection failed. Check your internet and try again.";
    return "We couldn't finish that. Try again.";
  }

  async function handleEmailAuth(event) {
    event?.preventDefault?.();
    if (emailAuthLoading) return;
    const name = emailAuthName.trim();
    const email = emailAuthEmail.trim().toLowerCase();
    const password = emailAuthPassword;
    setEmailAuthError("");
    setEmailResetSent(false);

    if (!email) {
      setEmailAuthError("Enter your email address.");
      return;
    }
    if (emailAuthMode === "signup" && !name) {
      setEmailAuthError("Enter the name your poker group will recognize.");
      return;
    }
    if (password.length < 6) {
      setEmailAuthError("Password must be at least 6 characters.");
      return;
    }

    const auth = getAuth();
    try {
      setEmailAuthLoading(true);
      if (emailAuthMode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
        try {
          await sendEmailVerification(cred.user);
        } catch (verificationErr) {
          console.warn("Email verification send failed:", verificationErr?.code, verificationErr?.message);
        }
        claimDeviceSession(cred.user.uid);
        setWizardName(name);
        setWizardEmail(email);
        setToastMessage("Account created. Check your email to verify it.");
        setToastType("success");
      } else {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        claimDeviceSession(cred.user.uid);
      }
      setEmailAuthPassword("");
    } catch (err) {
      console.error("Email auth failed:", err?.code, err?.message);
      setEmailAuthError(getEmailAuthErrorMessage(err));
    } finally {
      setEmailAuthLoading(false);
    }
  }

  async function sendEmailPasswordReset() {
    const email = emailAuthEmail.trim().toLowerCase();
    setEmailAuthError("");
    setEmailResetSent(false);
    if (!email) {
      setEmailAuthError("Enter your email address first.");
      return;
    }
    try {
      await sendPasswordResetEmail(getAuth(), email);
      setEmailResetSent(true);
    } catch (err) {
      console.error("Password reset failed:", err?.code, err?.message);
      setEmailAuthError(getEmailAuthErrorMessage(err));
    }
  }

  function getAccountActionErrorMessage(err) {
    const code = String(err?.code || "");
    if (code === "auth/provider-already-linked") return "That sign-in method is already connected.";
    if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") return "That login is already connected to another account.";
    if (code === "functions/already-exists" || code === "already-exists") return "That login is already connected to another account.";
    if (code === "auth/popup-closed-by-user") return "Google connection was cancelled.";
    if (code === "functions/unauthenticated" || code === "unauthenticated") return "Sign out and sign back in, then try again.";
    if (code === "auth/requires-recent-login") return "For security, sign out and sign back in, then try again.";
    if (code === "auth/wrong-password" || code === "auth/invalid-credential") return "That current password is incorrect.";
    if (code === "auth/too-many-requests") return "Too many attempts. Wait a moment and try again.";
    if (code === "auth/invalid-email") return "Enter a valid email address.";
    if (code === "auth/weak-password") return "Use at least 6 characters for your password.";
    if (code === "auth/network-request-failed") return "Connection failed. Check your internet and try again.";
    return "We couldn't update your account. Try again.";
  }

  function userHasProvider(providerId) {
    return !!user?.providerData?.some((provider) => provider.providerId === providerId);
  }

  function userHasTelegramProvider() {
    return !!String(userProfile?.telegramId || "").trim() || String(user?.uid || "").startsWith("tg_");
  }

  async function sendCurrentUserVerificationEmail() {
    const authUser = getAuth().currentUser;
    if (!authUser?.email || authUser.emailVerified) return;
    setAccountError("");
    setAccountMessage("");
    setAccountLoadingAction("verify-email");
    try {
      await sendEmailVerification(authUser);
      setAccountMessage("Verification email sent. Check your inbox.");
      setToastMessage("Verification email sent");
      setToastType("success");
    } catch (err) {
      console.error("Email verification failed:", err?.code, err?.message);
      const message = getAccountActionErrorMessage(err);
      setAccountError(message);
      setToastMessage(message);
    } finally {
      setAccountLoadingAction("");
    }
  }

  async function connectGoogleProvider() {
    const auth = getAuth();
    const authUser = auth.currentUser;
    if (!authUser || userHasProvider("google.com")) return;
    setAccountError("");
    setAccountMessage("");
    setAccountLoadingAction("google");
    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(authUser, provider);
      setAccountMessage("Google sign-in connected.");
      setToastMessage("Google sign-in connected");
      setToastType("success");
    } catch (err) {
      console.error("Google link failed:", err?.code, err?.message);
      const message = getAccountActionErrorMessage(err);
      setAccountError(message);
      setToastMessage(message);
    } finally {
      setAccountLoadingAction("");
    }
  }

  async function addPasswordProvider() {
    const authUser = getAuth().currentUser;
    if (!authUser || userHasProvider("password")) return;
    const email = String(accountEmailDraft || authUser.email || profileEmailDraft || userProfile?.email || "").trim().toLowerCase();
    const password = accountPasswordDraft;
    setAccountError("");
    setAccountMessage("");
    if (!email) {
      setAccountError("Enter the email address you want to use for sign-in.");
      return;
    }
    if (password.length < 6) {
      setAccountError("Password must be at least 6 characters.");
      return;
    }
    setAccountLoadingAction("password");
    try {
      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(authUser, credential);
      await setDoc(doc(db, "users", result.user.uid, "profile", "main"), { email }, { merge: true });
      setAccountPasswordDraft("");
      setAccountEmailDraft(email);
      if (!result.user.emailVerified) {
        try { await sendEmailVerification(result.user); } catch (_) {}
      }
      setAccountMessage("Password sign-in connected. Check your email to verify it.");
      setToastMessage("Password sign-in connected");
      setToastType("success");
    } catch (err) {
      console.error("Password link failed:", err?.code, err?.message);
      const message = getAccountActionErrorMessage(err);
      setAccountError(message);
      setToastMessage(message);
    } finally {
      setAccountLoadingAction("");
    }
  }

  async function changeUserPassword() {
    const authUser = getAuth().currentUser;
    if (!authUser || !userHasProvider("password")) return;
    const email = String(authUser.email || accountEmailDraft || userProfile?.email || "").trim().toLowerCase();
    const currentPassword = currentPasswordDraft;
    const newPassword = newPasswordDraft;
    setAccountError("");
    setAccountMessage("");
    if (!email) {
      setAccountError("We couldn't find the email for your account. Sign out and sign back in, then try again.");
      return;
    }
    if (!currentPassword) {
      setAccountError("Enter your current password.");
      return;
    }
    if (newPassword.length < 6) {
      setAccountError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPasswordDraft) {
      setAccountError("New passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setAccountError("Your new password must be different from your current one.");
      return;
    }
    setAccountLoadingAction("change-password");
    try {
      const credential = EmailAuthProvider.credential(email, currentPassword);
      await reauthenticateWithCredential(authUser, credential);
      await updatePassword(authUser, newPassword);
      setCurrentPasswordDraft("");
      setNewPasswordDraft("");
      setConfirmPasswordDraft("");
      setShowChangePassword(false);
      setAccountMessage("Password updated.");
      setToastMessage("Password updated");
      setToastType("success");
    } catch (err) {
      console.error("Password change failed:", err?.code, err?.message);
      const message = getAccountActionErrorMessage(err);
      setAccountError(message);
      setToastMessage(message);
    } finally {
      setAccountLoadingAction("");
    }
  }

  const connectTelegramProvider = useCallback(async (telegramUser) => {
    const authUser = getAuth().currentUser;
    const alreadyConnected = !!String(userProfile?.telegramId || "").trim() || String(user?.uid || "").startsWith("tg_");
    if (!authUser || alreadyConnected) return;
    setAccountError("");
    setAccountMessage("");
    setAccountLoadingAction("telegram");
    try {
      const functions = getFunctions();
      const verifyTelegramAuth = httpsCallable(functions, 'verifyTelegramAuth');
      const result = await verifyTelegramAuth({ ...telegramUser, linkCurrentAccount: true });
      const telegramId = String(result.data?.telegramId || telegramUser?.id || "");
      const telegramProfilePatch = {
        telegramId,
        telegramUsername: telegramUser?.username || "",
        telegramFirstName: telegramUser?.first_name || "",
        telegramLastName: telegramUser?.last_name || "",
        telegramPhotoURL: telegramUser?.photo_url || "",
        telegramLinkedAt: Date.now(),
      };
      setUserProfile((prev) => ({ ...(prev || {}), ...telegramProfilePatch }));
      try {
        await setDoc(doc(db, "users", authUser.uid, "profile", "main"), telegramProfilePatch, { merge: true });
      } catch (profileErr) {
        console.warn("Telegram profile cache update failed:", profileErr?.code, profileErr?.message);
      }
      setAccountMessage("Telegram sign-in connected.");
      setToastMessage("Telegram sign-in connected");
      setToastType("success");
    } catch (err) {
      console.error("Telegram link failed:", err?.code, err?.message);
      const code = String(err?.code || "");
      const message = code === "functions/already-exists" || code === "already-exists"
        ? "That Telegram account is already connected to another user."
        : getAccountActionErrorMessage(err);
      setAccountError(message);
      setToastMessage(message);
    } finally {
      setAccountLoadingAction("");
    }
  }, [user, userProfile]);

  async function claimDeviceSession(uid) {
    const sessionId = generateSessionId();
    pendingSessionClaimRef.current = sessionId;
    try {
      await setDoc(doc(db, "users", uid, "profile", "session"), {
        sessionId,
        updatedAt: serverTimestamp(),
        device: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 160),
      });
      setStoredSessionId(uid, sessionId);
    } catch (err) {
      console.error("Session claim failed:", err?.code, err?.message);
    } finally {
      if (pendingSessionClaimRef.current === sessionId) pendingSessionClaimRef.current = "";
    }
  }

  const handleTelegramAuth = useCallback(async (telegramUser) => {
    try {
      setTelegramSignInLoading(true);
      const functions = getFunctions();
      const verifyTelegramAuth = httpsCallable(functions, 'verifyTelegramAuth');

      const result = await verifyTelegramAuth(telegramUser);
      const { customToken } = result.data;

      const auth = getAuth();
      const cred = await signInWithCustomToken(auth, customToken);
      claimDeviceSession(cred.user.uid);

      setToastMessage("Signed in with Telegram");
      setToastType("success");
    } catch (err) {
      console.error("Telegram sign-in failed:", err);
      setToastMessage("Telegram authentication failed");
    } finally {
      setTelegramSignInLoading(false);
    }
  }, []);

  async function signOutUser() {
    if (user?.uid && activeGroupId) {
      localStorage.setItem(getLastGroupStorageKey(user.uid), activeGroupId);
    }
    await signOut(getAuth());
    setActiveGroupId("");
    localStorage.removeItem("activeGroupId");
  }

  async function deleteAccount() {
    if (!user || user.isAnonymous) return;
    try {
      // Check if sole moderator in any group that still has other members
      for (const group of myGroups) {
        const membersSnap = await getDocs(collection(db, "groups", group.id, "members"));
        const myDoc = membersSnap.docs.find((d) => d.id === user.uid);
        if (!myDoc) continue;
        if (String(myDoc.data()?.role || "viewer") !== "moderator") continue;
        const moderators = membersSnap.docs.filter((d) => String(d.data()?.role || "viewer") === "moderator");
        const otherMembers = membersSnap.docs.filter((d) => d.id !== user.uid && !d.data()?.isGuest);
        if (moderators.length <= 1 && otherMembers.length > 0) {
          setToastMessage(`You are the only moderator in "${group.name || "a group"}". Transfer ownership before deleting your account.`);
          return;
        }
      }

      // Unlink from players and remove member docs in every group
      for (const group of myGroups) {
        const memberRef = doc(db, "groups", group.id, "members", user.uid);
        const memberSnap = await getDoc(memberRef);
        if (!memberSnap.exists()) continue;
        const linkedPlayerId = String(memberSnap.data()?.playerId || "").trim();
        if (linkedPlayerId) {
          try { await deleteDoc(doc(db, "groups", group.id, "players", linkedPlayerId, "private", "contact")); } catch (_) {}
          try {
            await updateDoc(doc(db, "groups", group.id, "players", linkedPlayerId), {
              linkedUserUid: null,
              profilePhotoURL: deleteField(),
            });
          } catch (_) {}
        }
        try { await deleteDoc(doc(db, "groups", group.id, "trainer", user.uid)); } catch (_) {}
        await deleteDoc(memberRef);
      }
      // Delete Firestore profile docs
      try { await deleteDoc(doc(db, "users", user.uid, "profile", "main")); } catch (_) {}
      try { await deleteDoc(doc(db, "users", user.uid, "profile", "trainer")); } catch (_) {}
      // Queue deletion email before the Firebase Auth account is removed.
      try {
        const queueAccountDeletionEmail = httpsCallable(getFunctions(), "queueAccountDeletionEmail");
        await queueAccountDeletionEmail();
      } catch (err) {
        console.warn("Account deletion email queue failed:", err?.code, err?.message);
      }
      // Delete the Firebase Auth account
      await deleteUser(getAuth().currentUser);
      setActiveGroupId("");
      localStorage.removeItem("activeGroupId");
    } catch (err) {
      if (err?.code === "auth/requires-recent-login") {
        setToastMessage("Please sign out and sign back in, then try again.");
      } else {
        console.error("deleteAccount failed:", err);
        setToastMessage("Could not delete account. Please try again.");
      }
    }
  }

  function makeInviteCode() {
    const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // 32 unambiguous chars (no 0/O/1/I)
    const bytes = crypto.getRandomValues(new Uint8Array(10));
    return Array.from(bytes, (b) => CHARS[b % CHARS.length]).join("");
  }

  async function generateUniqueInviteCode(maxAttempts = 12) {
    for (let i = 0; i < maxAttempts; i += 1) {
      const code = makeInviteCode();
      // Check the lean inviteCodes lookup, not the full groups collection.
      const snap = await getDoc(doc(db, "inviteCodes", code));
      if (!snap.exists()) return code;
    }
    throw new Error("Could not generate a unique invite code. Please try again.");
  }

  async function createGroup() {
    if (!user) return;
    if (user.isAnonymous) {
      showAlert("Guest users cannot create groups. Sign in with Google to create one.");
      return;
    }
    try {
    const name = groupNameInput.trim() || "My Poker Group";
    const inviteCode = await generateUniqueInviteCode();
    const authNameParts = splitNameParts(user.displayName || "");
    const profileSeed = {
      firstName: currentUserProfileData?.firstName || authNameParts.firstName || "",
      lastName: currentUserProfileData?.lastName || authNameParts.lastName || "",
      nickname: currentUserProfileData?.nickname || "",
      displayName: currentUserProfileData?.displayName || user.displayName || "",
      profileName: currentUserProfileData?.profileName || getProfilePreferredName({
        nickname: currentUserProfileData?.nickname || "",
        firstName: currentUserProfileData?.firstName || authNameParts.firstName || "",
        displayName: user.displayName || "",
      }),
      ...(currentUserProfileData?.profilePhotoURL
        ? { profilePhotoURL: currentUserProfileData.profilePhotoURL, hasCustomPhoto: true }
        : {}),
    };
    const groupRef = await addDoc(collection(db, "groups"), {
      name,
      createdAt: Date.now(),
      createdBy: user.uid,
      inviteCode,
      hiddenStatsPlayerIds: []
    });
    await setDoc(doc(db, "inviteCodes", inviteCode), { groupId: groupRef.id, groupName: name });
    await setDoc(doc(db, "groups", groupRef.id, "members", user.uid), {
      uid: user.uid,
      ...profileSeed,
      role: "moderator",
      createdAt: Date.now()
    });
    // Initialize group-scoped defaults so each group has independent settings.
    await setDoc(doc(db, "groups", groupRef.id, "settings", "jackpot"), { base: 0 }, { merge: true });
    await setDoc(doc(db, "groups", groupRef.id, "settings", "ranking"), DEFAULT_RANKING_CONFIG, { merge: true });
    await setDoc(doc(db, "groups", groupRef.id, "settings", "game"), { thirdPlaceEnabled: false }, { merge: true });
    rememberGroupInProfile(user.uid, groupRef.id);
    setMyGroups((prev) => {
      const next = [{ id: groupRef.id, name, createdAt: Date.now(), createdBy: user.uid, inviteCode }, ...prev.filter((g) => g.id !== groupRef.id)];
      next.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      return next;
    });
    setActiveGroupId(groupRef.id);
    setGroupNameInput("");
    } catch (err) {
      console.error("createGroup failed:", err?.code, err?.message, err);
      showAlert(`Could not create group: ${err?.message || "Unknown error"}`);
    }
  }

  async function joinGroupWithCode(authUser, codeInput, forcedRole = null, silent = false) {
    if (!authUser || !codeInput.trim()) return false;
    const code = codeInput.trim().toUpperCase();
    // Look up the group through the inviteCodes collection. Rules block listing
    // groups, so there is no scan fallback: legacy groups get their inviteCodes
    // doc back-filled when a moderator opens the app. The group doc itself is
    // members-only, so it's read after the membership write below, not here.
    let invite = null;
    try {
      const lookupSnap = await getDoc(doc(db, "inviteCodes", code));
      const groupId = lookupSnap.exists() ? String(lookupSnap.data()?.groupId || "").trim() : "";
      if (groupId) invite = { groupId, groupName: String(lookupSnap.data()?.groupName || "") };
    } catch (_) {
      // Lookup failed — surfaces as "not found" below.
    }

    if (!invite) {
      // On a fresh sign-in the auth token may not have propagated yet, so the
      // lookup can transiently fail and look like "not found". The silent path
      // (auto-join from an invite link) suppresses the toast and retries.
      if (!silent) setToastMessage("Invite code not found. Check the code and try again.");
      return false;
    }
    const g = { id: invite.groupId };
    const authNameParts = splitNameParts(authUser.displayName || "");
    const nextFirstName = currentUserProfileData?.firstName || authNameParts.firstName || (authUser.isAnonymous ? "Guest" : "");
    const nextNickname = currentUserProfileData?.nickname || "";

    // Read the existing member doc before writing.
    // - The member read rule allows a user to always read their own doc by uid
    //   (request.auth.uid == memberId), so this works even before joining.
    // - If the doc already exists we preserve the current role (the user might be
    //   an editor or moderator rejoining after a logout); only new joins get the
    //   default "viewer" role.  Writing a different role would violate the UPDATE
    //   rule that requires role to be unchanged by non-moderators.
    const memberRef = doc(db, "groups", g.id, "members", authUser.uid);
    let existingRole = null;
    try {
      const existingSnap = await getDoc(memberRef);
      if (existingSnap.exists()) existingRole = String(existingSnap.data()?.role || "");
    } catch (_) {}
    const role = existingRole || forcedRole || "viewer";

    await setDoc(memberRef, {
      email: deleteField(), // strip any legacy email from the merged result
      uid: authUser.uid,
      firstName: nextFirstName,
      lastName: currentUserProfileData?.lastName || authNameParts.lastName || "",
      nickname: nextNickname,
      displayName: currentUserProfileData?.displayName || authUser.displayName || (authUser.isAnonymous ? "Guest Viewer" : ""),
      profileName: currentUserProfileData?.profileName || getProfilePreferredName({ nickname: nextNickname, firstName: nextFirstName, displayName: authUser.displayName || (authUser.isAnonymous ? "Guest User" : "") }),
      ...(currentUserProfileData?.profilePhotoURL
        ? { profilePhotoURL: currentUserProfileData.profilePhotoURL, hasCustomPhoto: true }
        : {}),
      role,
      // Rules require a valid invite code for this group to create a membership.
      inviteCode: code,
      isGuest: !!authUser.isAnonymous,
      createdAt: Date.now()
    }, { merge: true });

    // Now a member, so the group doc is readable. A code left pointing at a
    // deleted group gets the stray membership removed and reads as not found.
    let groupData = { name: invite.groupName };
    try {
      const groupSnap = await getDoc(doc(db, "groups", g.id));
      if (!groupSnap.exists()) {
        deleteDoc(memberRef).catch(() => {});
        if (!silent) setToastMessage("Invite code not found. Check the code and try again.");
        return false;
      }
      groupData = groupSnap.data();
    } catch (_) {
      // Transient read failure: keep the invite's group name; the live group
      // listeners fill in the rest once the group opens.
    }
    // Record the group ID in the user's profile so it's found on next login,
    // plus the code, which the orphaned-membership self-heal needs to rejoin.
    if (!authUser.isAnonymous) rememberGroupInProfile(authUser.uid, g.id, code);
    setMyGroups((prev) => {
      const data = { id: g.id, ...groupData };
      const next = [data, ...prev.filter((x) => x.id !== g.id)];
      next.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      return next;
    });
    setActiveGroupId(g.id);

    // Note: creating/linking this member's player profile is handled by the
    // ensure-linked-player effect (see below), which self-heals both new joins
    // and members who joined before that flow existed. Doing it there (rather
    // than inline here) avoids racing a not-yet-propagated auth token on a fresh
    // sign-in, and keeps a single source of truth for the link.

    return true;
  }

  async function joinGroup() {
    if (!user || !joinCodeInput.trim()) return;
    try {
      const ok = await joinGroupWithCode(user, joinCodeInput);
      if (ok) setJoinCodeInput("");
    } catch (err) {
      console.error("joinGroup failed:", err?.code, err?.message, err);
      setToastMessage("Could not join that group. Check the code or ask for a new invite.");
    }
  }

  async function joinAsGuestViewer() {
    const code = guestJoinCodeInput.trim() || pendingInviteCode.trim();
    if (!code) return;
    try {
      const auth = getAuth();
      const cred = await signInAnonymously(auth);
      const ok = await joinGroupWithCode(cred.user, code, "viewer");
      if (ok) setGuestJoinCodeInput("");
    } catch (err) {
      console.error("joinAsGuestViewer failed:", err?.code, err?.message, err);
      setToastMessage("Could not join as guest. Check the invite code and try again.");
    }
  }

  async function updateMemberRole(memberUid, role) {
    if (!canManageSettings || !activeGroupId) return;
    await setDoc(doc(db, "groups", activeGroupId, "members", memberUid), { role }, { merge: true });
  }

  async function saveMembersChanges() {
    if (!canManageSettings || !activeGroupId) return;
    const currentModerators = membersForRoles.filter((m) => String(m.role || "viewer") === "moderator");
    const myCurrentRole = String(membersForRoles.find((m) => m.id === user?.uid)?.role || "viewer");
    const myNextRole = String(memberRoleDraft[user?.uid] || myCurrentRole);
    if (myCurrentRole === "moderator" && myNextRole !== "moderator" && currentModerators.length <= 1) {
      showAlert("This group has only one moderator. You cannot change your own role until another moderator is assigned.");
      return;
    }
    const projectedModerators = membersForRoles.filter((m) => {
      const nextRole = String(memberRoleDraft[m.id] || m.role || "viewer");
      return nextRole === "moderator";
    }).length;
    if (projectedModerators < 1) {
      showAlert("Each group must have at least one moderator.");
      return;
    }

    for (const m of membersForRoles) {
      const nextRole = memberRoleDraft[m.id] || "viewer";
      const nextPlayerId = memberPlayerDraft[m.id] || "";
      const memberChanges = [];
      if ((m.role || "viewer") !== nextRole) {
        await updateMemberRole(m.id, nextRole);
        memberChanges.push(`role: ${m.role || "viewer"} -> ${nextRole}`);
      }
      if ((m.playerId || "") !== nextPlayerId) {
        const prevLinked = players.find((p) => p.linkedUserUid === m.id);
        if (prevLinked && prevLinked.id !== nextPlayerId) {
          await setDoc(doc(db, "groups", activeGroupId, "players", prevLinked.id), {
            linkedUserUid: null,
            profilePhotoURL: deleteField(),
            hasCustomPhoto: deleteField(),
          }, { merge: true });
        }
        if (nextPlayerId) {
          const linkedFirstName = String(m.firstName || "").trim();
          const linkedLastName = String(m.lastName || "").trim();
          const linkedNickname = String(m.nickname || "").trim();
          const linkedEmail = String(m.email || "").trim().toLowerCase();
          const linkedPhoto = String(m.profilePhotoURL || "").trim();
          const linkedName = getProfilePreferredName({
            nickname: linkedNickname,
            firstName: linkedFirstName,
            profileName: m.profileName || "",
            displayName: m.displayName || "",
          });
          const playerPatch = {
            linkedUserUid: m.id,
            type: "linked",
            // Back on the roster if this was a removed member's player.
            removedFromGroup: deleteField(),
            formerMemberUid: deleteField(),
            removedAt: deleteField(),
            ...getSharedProfilePhotoPatch({ profilePhotoURL: linkedPhoto, hasCustomPhoto: m.hasCustomPhoto }),
          };
          const existingPlayer = players.find((p) => p.id === nextPlayerId);
          const playerNameUpdatedAt = Number(existingPlayer?.nameUpdatedAt || 0);
          const memberNameUpdatedAt = Number(m.nameUpdatedAt || m.profileNameUpdatedAt || m.groupNicknameUpdatedAt || 0);
          const shouldSyncNameFields = memberNameUpdatedAt > 0
            ? memberNameUpdatedAt >= playerNameUpdatedAt
            : playerNameUpdatedAt === 0;
          if (shouldSyncNameFields) {
            playerPatch.firstName = linkedFirstName;
            playerPatch.lastName = linkedLastName;
            playerPatch.nickname = linkedNickname;
            if (linkedName) playerPatch.name = linkedName;
            if (memberNameUpdatedAt > 0) {
              playerPatch.nameUpdatedAt = memberNameUpdatedAt;
              playerPatch.nameUpdatedBy = m.id;
              playerPatch.nameUpdatedSource = "member";
            }
          }
          await setDoc(doc(db, "groups", activeGroupId, "players", nextPlayerId), playerPatch, { merge: true });
          if (linkedEmail) {
            await setDoc(
              doc(db, "groups", activeGroupId, "players", nextPlayerId, "private", "contact"),
              { email: linkedEmail },
              { merge: true }
            );
          }
        }
        await setDoc(doc(db, "groups", activeGroupId, "members", m.id), { playerId: nextPlayerId || null }, { merge: true });
        const nextPlayer = players.find((p) => p.id === nextPlayerId);
        memberChanges.push(`linked player: ${prevLinked?.name || "none"} -> ${nextPlayer?.name || "none"}`);
      }
      if (memberChanges.length) {
        logActivity("member_updated", {
          memberName: m.profileName || m.displayName || "Member",
          changes: memberChanges,
        });
      }
    }
  }

  async function copyInviteCode() {
    const code = String(groupMeta?.inviteCode || "").trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setToastType("success");
      setToastMessage("Invite code copied!");
    } catch (err) {
      setToastMessage("Could not copy invite code");
    }
  }

  async function shareInviteLink() {
    const code = String(groupMeta?.inviteCode || "").trim();
    if (!code) return;
    const groupName = String(groupMeta?.name || "").trim();
    const inviteUrl = `${window.location.origin}/?join=${code}${groupName ? `&gname=${encodeURIComponent(groupName)}` : ""}`;
    if (navigator.share) {
      try {
        await navigator.share({ url: inviteUrl });
      } catch (err) {
        if (err?.name !== "AbortError") setToastMessage("Could not share invite link");
      }
    } else {
      try {
        await navigator.clipboard.writeText(inviteUrl);
        setToastType("success");
        setToastMessage("Invite link copied!");
      } catch (_) {
        setToastMessage("Could not copy invite link");
      }
    }
  }

  async function regenerateInviteCode() {
    if (!canManageSettings || !activeGroupId) return;
    if (!(await showConfirm({ title: "Generate a new invite code?", message: "The current code will stop working immediately.", confirmLabel: "New code", destructive: true }))) return;
    try {
      await rotateInviteCode();
      setToastMessage("Invite code regenerated");
      setToastType("success");
    } catch (err) {
      console.error("Regenerate invite code failed:", err?.code, err?.message);
      setToastMessage("Could not regenerate invite code");
    }
  }

  // ── Live Game Functions ──────────────────────────────────────────

  function formatElapsed(ms) {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${m}:${String(sec).padStart(2, "0")}`;
  }

  async function startLiveGame(playerIds) {
    const ids = playerIds || selectedPlayers;
    if (!canEditGames || !activeGroupId || !ids.length) return;
    const buyInAmt = parseFloat(liveSetupBuyIn) || 0;
    if (buyInAmt <= 0) { setToastMessage("Enter a valid buy-in amount"); return; }
    const initialBuyIns = {};
    const initialChips = {};
    const startedAt = Date.now();
    ids.forEach(id => { initialBuyIns[id] = 1; initialChips[id] = null; });
    try {
      await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"), {
        status: "active",
        startedAt,
        pausedAt: null,
        totalPausedMs: 0,
        buyInAmount: buyInAmt,
        location: location.trim() || "",
        currency: String(groupMeta?.currency || "EUR").toUpperCase(),
        playerIds: ids,
        buyIns: initialBuyIns,
        chipCounts: initialChips,
        isOut: {},
        events: ids.map((id) => liveEvent("buyin", id, startedAt)),
        jackpot: null,
        startedBy: user.uid,
        endedAt: null,
        // The id the game will be saved under, so its live log and its saved
        // log are one history.
        gameId: doc(collection(db, "groups", activeGroupId, "games")).id,
        ...editStamp(),
      });
      // liveGame Firestore listener updates state; Game tab renders tracker automatically
    } catch (err) {
      console.error("startLiveGame failed:", err?.code, err?.message);
      setToastMessage("Could not open the live ledger. Try again.");
    }
  }

  async function liveAddPlayerToGame(pid) {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"), {
      playerIds: arrayUnion(pid),
      buyIns: { [pid]: 1 },
      chipCounts: { [pid]: null },
      isOut: { [pid]: false },
      events: arrayUnion(liveEvent("buyin", pid)),
      ...editStamp(),
    }, { merge: true });
  }

  // Create a brand-new group player from the live table and seat them right away.
  async function liveCreateAndSeatPlayer() {
    const name = liveNewPlayerName.trim();
    if (!name) return;
    try {
      const newId = await addPlayer(name, "", "live");
      if (!newId) { setToastMessage("Could not add player. You may not have permission."); return; }
      await liveAddPlayerToGame(newId);
      setLiveNewPlayerOpen(false);
      setLiveNewPlayerName("");
      setShowAddPlayerToLive(false);
      setToastMessage(`${name} added to the game`);
    } catch (err) {
      console.error("liveCreateAndSeatPlayer failed:", err?.code, err?.message);
      setToastMessage("Could not add player.");
    }
  }

  function flashRebuyFeedback(playerId, type) {
    if (lgRebuyFeedbackTimerRef.current) clearTimeout(lgRebuyFeedbackTimerRef.current);
    setLgRebuyFeedback({ pid: playerId, type });
    lgRebuyFeedbackTimerRef.current = setTimeout(() => {
      setLgRebuyFeedback({ pid: null, type: null });
      lgRebuyFeedbackTimerRef.current = null;
    }, 700);
  }

  // Buy-in counts change with server-side increments, not read-then-write from
  // the local snapshot, so taps from two phones at once both count.
  async function liveAddBuyIn(playerId) {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
      { buyIns: { [playerId]: increment(1) }, events: arrayUnion(liveEvent("rebuy", playerId)), ...editStamp() }, { merge: true });
    flashRebuyFeedback(playerId, "add");
  }

  async function liveRemoveBuyIn(playerId) {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    const cur = liveGame.buyIns?.[playerId] || 1;
    if (cur <= 1) return;
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
      { buyIns: { [playerId]: increment(-1) }, events: arrayUnion(liveEvent("undo", playerId)), ...editStamp() }, { merge: true });
    flashRebuyFeedback(playerId, "remove");
  }

  async function liveSetChipCount(playerId, value) {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    const num = value === "" ? null : parseFloat(value);
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
      { chipCounts: { [playerId]: isNaN(num) ? null : num }, ...editStamp() }, { merge: true });
  }

  async function liveToggleOut(playerId) {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    const cur = liveGame.isOut?.[playerId] || false;
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
      { isOut: { [playerId]: !cur }, events: arrayUnion(liveEvent(cur ? "back" : "out", playerId)), ...editStamp() }, { merge: true });
  }

  async function livePauseResume() {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    if (liveGame.status === "active") {
      await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
        { status: "paused", pausedAt: Date.now(), ...editStamp() }, { merge: true });
    } else if (liveGame.status === "paused") {
      const addPaused = Date.now() - (liveGame.pausedAt || Date.now());
      await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"),
        { status: "active", pausedAt: null, totalPausedMs: (liveGame.totalPausedMs || 0) + addPaused, ...editStamp() }, { merge: true });
    }
  }

  async function liveSaveJackpot() {
    if (!canEditGames || !activeGroupId || !liveGame) return;
    const amt = parseFloat(liveJackpotAmount) || 0;
    const cardsStr = liveJackpotCardSlots.map(c => `${c.rank}${c.suit}`).join(" ");
    await setDoc(doc(db, "groups", activeGroupId, "liveGame", "current"), {
      jackpot: {
        amount: amt,
        winnerId: liveJackpotWinnerId || null,
        cards: cardsStr,
      },
      ...editStamp(),
    }, { merge: true });
    setLiveJackpotOpen(false);
    setToastMessage("Jackpot added to this live game"); setToastType("success");
  }

  function openEndGame() {
    if (!liveGame) return;
    // Don't pre-fill winner dropdowns — user must select manually
    setLiveEndFirstId("");
    setLiveEndSecondId("");
    setLiveEndThirdId("");
    setLiveEndNotes("");
    // A jackpot recorded at the live table carries over; it can still be
    // changed or switched off before saving.
    const jp = liveGame.jackpot;
    setLiveEndJackpotOn(!!jp?.winnerId);
    setLiveJackpotWinnerId(jp?.winnerId || "");
    setLiveJackpotCardSlots(parseJackpotCards(jp?.cards));
    setLiveJackpotOpen(false);
    setLiveEndFlow(true);
  }

  // Jackpot winner (players at this table) and the five-card picker, shared by
  // the live table's jackpot form and the Save Results screen.
  function renderJackpotWinnerSelect() {
    return (
      <select className="input" value={liveJackpotWinnerId} onChange={e => setLiveJackpotWinnerId(e.target.value)} style={{ marginBottom: 10 }}>
        <option value="">— select player —</option>
        {(liveGame?.playerIds || []).map(pid => (
          <option key={pid} value={pid}>{getPlayerName(pid) || pid}</option>
        ))}
      </select>
    );
  }
  function renderJackpotCardPicker() {
    return (
      <div className="card-selector-grid" style={{ marginBottom: 12 }}>
        {liveJackpotCardSlots.map((c, idx) => (
          <div key={idx} className="card-selector-item">
            <span className="card-selector-label">#{idx + 1}</span>
            <div className="card-selector-row">
              <select className="select card-rank-select" aria-label={`Card ${idx + 1} rank`} value={c.rank} onChange={e => setLiveJackpotCardSlots(arr => arr.map((x, i2) => i2 === idx ? { ...x, rank: e.target.value } : x))}>
                {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <select className="select card-suit-select" aria-label={`Card ${idx + 1} suit`} value={c.suit} style={getSuitStyle(c.suit)} onChange={e => setLiveJackpotCardSlots(arr => arr.map((x, i2) => i2 === idx ? { ...x, suit: e.target.value } : x))}>
                {SUITS.map(suit => <option key={suit} value={suit} style={getSuitStyle(suit)}>{suit}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // The buy-ins and winners the live game will be saved with. The Save Results
  // screen previews payouts from this same object via enrichGame, so what it
  // shows is exactly what the ledger computes.
  function buildLiveGameResult() {
    const buyInAmt = liveGame.buyInAmount || 0;
    const buyInsRecord = {};
    (liveGame.playerIds || []).forEach(id => {
      buyInsRecord[id] = (liveGame.buyIns?.[id] || 1) * buyInAmt;
    });
    return {
      buyIns: buyInsRecord,
      jackpotCut: jackpotCutForNewGame(buyInAmt, sidePrizeConfig.enabled),
      firstWinnerId: liveEndFirstId || null,
      secondWinnerId: liveEndSecondId || null,
      thirdWinnerId: thirdPlaceEnabled ? (liveEndThirdId || null) : null,
      splitWinnerId: null,
    };
  }
  const liveEndPreview = liveEndFlow && liveGame ? enrichGame(buildLiveGameResult()) : null;

  // Winner picker on Save Results: the same tappable tiles as game setup. A
  // player already placed elsewhere is disabled; an optional place can be
  // cleared by tapping the selected tile again.
  function renderLivePlacePicker(label, value, onChange, takenIds, optional) {
    return (
      <div className="ns-players-grid lg-place-grid" role="radiogroup" aria-label={label}>
        {(liveGame?.playerIds || []).map((pid) => {
          const p = players.find((x) => x.id === pid);
          const name = p ? getPlayerPreferredName(p) : pid;
          const selected = value === pid;
          return (
            <button
              key={pid}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={takenIds.includes(pid)}
              className={`ns-player-chip lg-place-tile${selected ? " selected" : ""}`}
              onClick={() => onChange(selected ? (optional ? "" : pid) : pid)}
            >
              <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={30} />
              <span className="ns-player-name">{name}</span>
              {selected && <span className="ns-player-check">✓</span>}
            </button>
          );
        })}
      </div>
    );
  }
  const liveEndPayouts = liveEndPreview ? computePayouts(liveEndPreview) : {};

  async function saveLiveGameToHistory() {
    if (!activeGroupId || !liveGame) return;
    if (!liveEndFirstId) {
      setToastMessage("Choose the winner before saving the game.");
      return;
    }
    if (liveEndJackpotOn && !liveJackpotWinnerId) {
      setToastMessage("Pick who hit the jackpot, or switch it off.");
      return;
    }
    const endedAt = Date.now();
    const payload = {
      date: new Date().toISOString().slice(0, 10),
      location: liveGame.location || "",
      notes: liveEndNotes.trim(),
      ...buildLiveGameResult(),
      jackpotWinnerId: liveEndJackpotOn ? liveJackpotWinnerId : null,
      jackpotCards: liveEndJackpotOn ? liveJackpotCardSlots.map(c => `${c.rank}${c.suit}`).join(" ") : "",
      buyInAmount: liveGame.buyInAmount || 0,
      durationMs: endedAt - liveGame.startedAt - (liveGame.totalPausedMs || 0),
      // Timeline for Game Info: when the game ran and each buy-in, rebuy and bust.
      startedAt: liveGame.startedAt,
      endedAt,
      events: cleanGameEvents(liveGame.events),
      createdAt: endedAt,
      createdBy: user.uid,
      createdByName: currentUserProfileData?.profileName || user.displayName || "",
      ...editStamp(),
    };
    try {
      // Saved under the id the live game was given, so both logs line up.
      const ref = liveGame.gameId
        ? doc(db, "groups", activeGroupId, "games", liveGame.gameId)
        : doc(collection(db, "groups", activeGroupId, "games"));
      await setDoc(ref, payload);
      await closeLiveGame("saved");
      logActivity("game_created", { gameId: ref.id, gameDate: payload.date, changes: ["live game saved"] });
      setLiveEndFlow(false);
      setSelectedPlayers([]);  // clear so hasUnsavedGame doesn't fire a false warning
      setToastMessage("Game saved to the ledger"); setToastType("success");
    } catch (err) {
      console.error("saveLiveGameToHistory failed:", err?.code, err?.message);
      setToastMessage("Could not save this game. Your live ledger is still open.");
    }
  }

  function openGameTimelineEdit(game) {
    if (!savedGameTimeline(game)) return;
    setTimelineEdit({
      mode: "game",
      gameId: game.id,
      startedAt: game.startedAt,
      endedAt: game.endedAt,
      events: cleanGameEvents(game.events),
      playerIds: Object.keys(game.buyIns || {}),
    });
  }

  function openLiveTimelineEdit() {
    if (!liveGame) return;
    setTimelineEdit({
      mode: "live",
      startedAt: liveGame.startedAt,
      endedAt: null,
      events: cleanGameEvents(liveGame.events),
      playerIds: liveGame.playerIds || [],
    });
  }

  // Writes a timeline edit in a transaction against the doc as it is now, so
  // rebuys tapped on another phone while the editor was open aren't lost.
  // Added or removed rebuys change the player's buy-in total to match.
  async function saveTimelineEdit({ startedAt, endedAt, events, reason }) {
    const edit = timelineEdit;
    if (!edit || !activeGroupId) return;
    if (edit.mode === "live") {
      if (!canEditGames) throw new Error("You can't edit this live game.");
      const ref = doc(db, "groups", activeGroupId, "liveGame", "current");
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("This live game has ended.");
        const cur = snap.data();
        const { events: next, rebuyDelta } = applyTimelineEdit(cur.events, edit.events, events);
        const buyIns = { ...(cur.buyIns || {}) };
        Object.entries(rebuyDelta).forEach(([pid, n]) => { buyIns[pid] = Math.max(1, (buyIns[pid] || 1) + n); });
        const isOut = { ...(cur.isOut || {}), ...outFromEvents(next) };
        tx.update(ref, { events: next, buyIns, isOut, startedAt, ...editStamp(reason) });
      });
    } else {
      const target = games.find((g) => g.id === edit.gameId);
      if (!canManageSingleGame(target)) throw new Error("You can't change finished games in this group.");
      const ref = doc(db, "groups", activeGroupId, "games", edit.gameId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("This game no longer exists.");
        const cur = snap.data();
        const { events: next, rebuyDelta } = applyTimelineEdit(cur.events, edit.events, events);
        const amount = Number(cur.buyInAmount) || 0;
        const buyIns = { ...(cur.buyIns || {}) };
        Object.entries(rebuyDelta).forEach(([pid, n]) => { buyIns[pid] = Math.max(amount, (Number(buyIns[pid]) || 0) + n * amount); });
        // Keep the paused time; the duration follows the new start and end.
        const paused = Math.max(0, (cur.endedAt - cur.startedAt) - (Number(cur.durationMs) || 0));
        tx.update(ref, {
          events: next,
          buyIns,
          startedAt,
          endedAt,
          durationMs: Math.max(0, endedAt - startedAt - paused),
          ...editStamp(reason),
        });
      });
      logActivity("game_updated", { gameId: edit.gameId, gameDate: target?.date || "", changes: ["timeline edited"] });
    }
    setTimelineEdit(null);
    setToastMessage("Timeline updated"); setToastType("success");
  }

  // Deleting the live game needs closedBy set to you first (the rules check
  // it), so the session log can name who saved or discarded it.
  async function closeLiveGame(closing) {
    const ref = doc(db, "groups", activeGroupId, "liveGame", "current");
    await setDoc(ref, { closing, closedBy: user.uid, ...editStamp() }, { merge: true });
    await deleteDoc(ref);
  }

  async function discardLiveGame() {
    if (!canEditGames || !activeGroupId) return;
    if (!(await showConfirm({ title: "Discard this live game?", message: "All buy-in data will be lost. The change log keeps a record that you discarded it.", confirmLabel: "Discard game", destructive: true }))) return;
    try {
      await closeLiveGame("discarded");
      setLiveEndFlow(false);
      setSelectedPlayers([]);  // clear so hasUnsavedGame doesn't fire a false warning
    } catch (err) {
      setToastMessage("Could not discard this live game.");
    }
  }

  function beginLiveBuyInSwipe(event, playerId) {
    // The whole player row is the swipe surface; taps on its interactive
    // children (Set stack, Out, chip input) must keep working normally.
    if (event.target.closest && event.target.closest("button, input, select, a")) return;
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* pointer already gone */ }
    lgSwipeRef.current = {
      pid: playerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setLgSwipeState({ pid: playerId, dx: 0, active: true, armed: false });
  }

  // A swipe commits only when it's long and clearly horizontal: MIN_DX at
  // minimum, dominantly sideways, and either FULL_DX of travel or the finger
  // reaching the screen edge in the drag direction.
  function liveBuyInSwipeArmed(event, swipe) {
    const dx = event.clientX - swipe.startX;
    const dy = Math.abs(event.clientY - swipe.startY);
    if (Math.abs(dx) < LIVE_BUYIN_SWIPE_MIN_DX) return false;
    if (Math.abs(dx) < dy * 2) return false;
    if (Math.abs(dx) >= LIVE_BUYIN_SWIPE_FULL_DX) return true;
    const vw = window.innerWidth || 0;
    return dx > 0
      ? event.clientX >= vw - LIVE_BUYIN_SWIPE_EDGE
      : event.clientX <= LIVE_BUYIN_SWIPE_EDGE;
  }

  function updateLiveBuyInSwipe(event, playerId) {
    const swipe = lgSwipeRef.current;
    if (!swipe || swipe.pid !== playerId) return;
    setLgSwipeState({
      pid: playerId,
      dx: event.clientX - swipe.startX,
      active: true,
      armed: liveBuyInSwipeArmed(event, swipe),
    });
  }

  function openLgPlayerSheet(playerId) {
    const chips = liveGame?.chipCounts?.[playerId];
    setChipInputDraft(chips !== null && chips !== undefined ? String(chips) : "");
    lgPlayerSheetOpenedAtRef.current = Date.now();
    setLgPlayerSheetPid(playerId);
  }

  function finishLiveBuyInSwipe(event, playerId, buyInCount) {
    const swipe = lgSwipeRef.current;
    lgSwipeRef.current = null;
    setLgSwipeState({ pid: null, dx: 0, active: false, armed: false });
    if (!swipe || swipe.pid !== playerId) return;
    if (liveBuyInSwipeArmed(event, swipe)) {
      const dx = event.clientX - swipe.startX;
      if (dx > 0) liveAddBuyIn(playerId);
      else if (buyInCount > 1) liveRemoveBuyIn(playerId);
      return;
    }
    // A barely-moved press is a tap: open the player's control sheet
    const dx = Math.abs(event.clientX - swipe.startX);
    const dy = Math.abs(event.clientY - swipe.startY);
    if (dx < 10 && dy < 10) openLgPlayerSheet(playerId);
  }

  function cancelLiveBuyInSwipe() {
    lgSwipeRef.current = null;
    setLgSwipeState({ pid: null, dx: 0, active: false, armed: false });
  }

  const syncUserProfileAcrossGroups = useCallback(async (profilePayload) => {
    if (!user?.uid || !myGroups.length) return;
    const syncStartedAt = Date.now();
    await Promise.all(myGroups.map(async (group) => {
      const memberRef = doc(db, "groups", group.id, "members", user.uid);
      const memberSnap = await getDoc(memberRef);
      const memberData = memberSnap.data() || {};
      const hadGroupNickname = !!String(memberData.groupNickname || "").trim();
      const profileNameUpdatedAt = Number(profilePayload.nameUpdatedAt || profilePayload.profileNameUpdatedAt || 0);
      const effectiveNameUpdatedAt = hadGroupNickname ? syncStartedAt : profileNameUpdatedAt;
      await setDoc(memberRef, {
        ...stripMemberPrivateFields({ uid: user.uid, ...profilePayload }),
        groupNickname: deleteField(),
        ...(hadGroupNickname ? { nameUpdatedAt: effectiveNameUpdatedAt, groupNicknameUpdatedAt: effectiveNameUpdatedAt } : {}),
      }, { merge: true });
      const playerId = String(memberData.playerId || "").trim();
      if (!playerId) return;
      const playerRef = doc(db, "groups", group.id, "players", playerId);
      const playerSnap = await getDoc(playerRef);
      if (!playerSnap.exists()) return; // never recreate a deleted/missing player via merge
      const playerNameUpdatedAt = Number(playerSnap.data()?.nameUpdatedAt || 0);
      const shouldSyncNameFields = effectiveNameUpdatedAt > 0
        ? effectiveNameUpdatedAt >= playerNameUpdatedAt
        : playerNameUpdatedAt === 0;
      const playerPatch = {
        linkedUserUid: user.uid,
        ...getSharedProfilePhotoPatch(profilePayload),
      };
      if (shouldSyncNameFields) {
        playerPatch.firstName = profilePayload.firstName || "";
        playerPatch.lastName = profilePayload.lastName || "";
        playerPatch.nickname = profilePayload.nickname || "";
        playerPatch.name = profilePayload.profileName || profilePayload.firstName || "";
        if (effectiveNameUpdatedAt > 0) {
          playerPatch.nameUpdatedAt = effectiveNameUpdatedAt;
          playerPatch.nameUpdatedBy = user.uid;
          playerPatch.nameUpdatedSource = "member";
        }
      }
      await setDoc(playerRef, playerPatch, { merge: true });
      const playerEmail = String(profilePayload.email || "").trim().toLowerCase();
      if (playerEmail) {
        await setDoc(
          doc(db, "groups", group.id, "players", playerId, "private", "contact"),
          { email: playerEmail },
          { merge: true }
        );
      }
    }));
  }, [myGroups, user]);

  useEffect(() => {
    if (!user || user.isAnonymous || !currentUserProfileData || !groupsLoaded || !myGroups.length) return;
    syncUserProfileAcrossGroups(currentUserProfileData)
      .catch((err) => console.error("Profile sync across groups failed:", err?.code, err?.message));
  }, [currentUserProfileData, groupsLoaded, myGroups, syncUserProfileAcrossGroups, user]);

  useEffect(() => {
    if (!user || user.isAnonymous || !currentUserProfileData || !userProfileLoaded) return;
    const authNameParts = splitNameParts(user.displayName || "");
    const defaultPreferredName = getProfilePreferredName({
      nickname: currentUserProfileData.nickname,
      firstName: currentUserProfileData.firstName || authNameParts.firstName,
      profileName: currentUserProfileData.profileName,
      displayName: user.displayName || "",
    });
    const nextProfileDoc = {
      firstName: currentUserProfileData.firstName || authNameParts.firstName || "",
      lastName: currentUserProfileData.lastName || authNameParts.lastName || "",
      nickname: currentUserProfileData.nickname || "",
      email: currentUserProfileData.email || String(user.email || "").trim().toLowerCase(),
      profileName: defaultPreferredName,
      displayName: String(user.displayName || "").trim(),
      profilePhotoURL: currentUserProfileData.photoSource === "upload" ? (currentUserProfileData.profilePhotoURL || "") : "",
    };

    const sameDoc =
      String(userProfile?.firstName || "") === nextProfileDoc.firstName &&
      String(userProfile?.lastName || "") === nextProfileDoc.lastName &&
      String(userProfile?.nickname || "") === nextProfileDoc.nickname &&
      String(userProfile?.email || "").trim().toLowerCase() === nextProfileDoc.email &&
      String(userProfile?.profileName || "") === nextProfileDoc.profileName &&
      String(userProfile?.displayName || "") === nextProfileDoc.displayName;

    if (sameDoc) return;

    setDoc(doc(db, "users", user.uid, "profile", "main"), nextProfileDoc, { merge: true })
      .catch((err) => console.error("User profile sync failed:", err?.code, err?.message));
  }, [currentUserProfileData, user, userProfile, userProfileLoaded]);

  // Privacy + invite-code migrations — runs when a moderator enters a group.
  // 1. Strips legacy `email` fields from members/* and players/* docs.
  // 2. Migrates player emails to groups/{gid}/players/{pid}/private/contact.
  // 3. Back-fills inviteCodes/{code} lookup docs for any groups that pre-date
  //    the lookup collection (one-time, idempotent per group).
  useEffect(() => {
    if (!user || user.isAnonymous || !activeGroupId || groupRole !== "moderator") return;
    let cancelled = false;
    (async () => {
      try {
        // 1 & 2: email field cleanup for active group
        const membersSnap = await getDocs(collection(db, "groups", activeGroupId, "members"));
        for (const m of membersSnap.docs) {
          if (cancelled) return;
          const mData = m.data() || {};
          const memberPatch = {};
          if ("email" in mData) memberPatch.email = deleteField();
          // Strip Google auth photo URLs stored before the opt-in photo gate.
          if ("profilePhotoURL" in mData && !mData.hasCustomPhoto) memberPatch.profilePhotoURL = deleteField();
          if (Object.keys(memberPatch).length) {
            try { await updateDoc(m.ref, memberPatch); } catch (_) {}
          }
        }
        const playersSnap = await getDocs(collection(db, "groups", activeGroupId, "players"));
        for (const p of playersSnap.docs) {
          if (cancelled) return;
          const data = p.data() || {};
          const playerPatch = {};
          const legacyEmail = String(data.email || "").trim().toLowerCase();
          if (legacyEmail) {
            try {
              await setDoc(
                doc(db, "groups", activeGroupId, "players", p.id, "private", "contact"),
                { email: legacyEmail },
                { merge: true }
              );
            } catch (_) {}
            playerPatch.email = deleteField();
          }
          // Clear stale Google auth photo URLs from linked player docs.
          // Only custom-uploaded photos (hasCustomPhoto: true) should be stored here.
          if (data.linkedUserUid && "profilePhotoURL" in data && !data.hasCustomPhoto) {
            playerPatch.profilePhotoURL = deleteField();
          }
          if (Object.keys(playerPatch).length) {
            try { await updateDoc(p.ref, playerPatch); } catch (_) {}
          }
        }

        // 3: back-fill inviteCodes lookup for all groups the user is moderator of
        for (const group of myGroups) {
          if (cancelled) return;
          const inviteCode = String(group.inviteCode || "").trim();
          if (!inviteCode) continue;
          try {
            const codeSnap = await getDoc(doc(db, "inviteCodes", inviteCode));
            const memberSnap = await getDoc(doc(db, "groups", group.id, "members", user.uid));
            const isMod = String(memberSnap.data()?.role || "") === "moderator";
            if (!codeSnap.exists()) {
              if (isMod) {
                await setDoc(doc(db, "inviteCodes", inviteCode), { groupId: group.id, groupName: group.name || "" });
              }
            } else if (!codeSnap.data()?.groupName && isMod) {
              // Backfill groupName into existing inviteCodes docs that predate the field
              await updateDoc(doc(db, "inviteCodes", inviteCode), { groupName: group.name || "" });
            }
          } catch (_) {}
        }
      } catch (err) {
        console.error("Privacy/invite cleanup failed:", err?.code, err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user, activeGroupId, groupRole, myGroups]);

  function clearGroupPhotoDraft() {
    if (groupPhotoPreviewURL) URL.revokeObjectURL(groupPhotoPreviewURL);
    setGroupPhotoPreviewURL(null);
    setGroupCropBlobDraft(null);
  }

  function clearPhotoDrafts() {
    if (photoPreviewURL) URL.revokeObjectURL(photoPreviewURL);
    setPhotoPreviewURL(null);
    setCropBlobDraft(null);
    setPhotoSourceDraft(null);
    setAvatarSeedDraft(null);
  }

  function clearWizardPhotoDraft() {
    if (wizardPhotoPreviewURL) URL.revokeObjectURL(wizardPhotoPreviewURL);
    setWizardPhotoPreviewURL(null);
    setWizardCropBlobDraft(null);
  }

  async function saveMyProfile() {
    if (!user || user.isAnonymous) return;
    const firstName = String(profileFirstNameDraft || "").trim();
    if (!firstName) {
      setToastMessage("First name is required");
      return;
    }
    const lastName = String(profileLastNameDraft || "").trim();
    const nickname = String(profileNicknameDraft || "").trim();
    const email = String(profileEmailDraft || user.email || "").trim().toLowerCase();
    const profileName = getProfilePreferredName({ nickname, firstName });
    const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const profileNameChanged =
      String(userProfile?.firstName || "").trim() !== firstName ||
      String(userProfile?.lastName || "").trim() !== lastName ||
      String(userProfile?.nickname || "").trim() !== nickname ||
      String(userProfile?.profileName || "").trim() !== profileName ||
      String(userProfile?.displayName || "").trim() !== (displayName || profileName || user.displayName || "");
    const profileNameUpdatedAt = profileNameChanged ? Date.now() : Number(userProfile?.nameUpdatedAt || 0);

    // Resolve final photo state from drafts
    let finalPhotoURL = currentUserProfileData?.profilePhotoURL || "";
    let finalHasCustomPhoto = !!currentUserProfileData?.hasCustomPhoto;
    const photoChanges = {};

    if (cropBlobDraft) {
      // Upload the deferred blob now
      const existingURL = userProfile?.profilePhotoURL || "";
      if (existingURL) {
        const m = existingURL.match(/\/o\/(.+?)\?/);
        if (m) try { await deleteObject(storageRef(storage, decodeURIComponent(m[1]))); } catch (_) {}
      }
      const path = `avatars/${user.uid}/profile/${Date.now()}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, cropBlobDraft, { contentType: "image/jpeg" });
      finalPhotoURL = await getDownloadURL(ref);
      finalHasCustomPhoto = true;
      photoChanges.profilePhotoURL = finalPhotoURL;
      photoChanges.photoSource = "upload";
      photoChanges.hasCustomPhoto = true;
    } else if (photoSourceDraft === "generated" || photoSourceDraft === "delete") {
      // Delete the uploaded file from Storage
      const existingURL = userProfile?.profilePhotoURL || "";
      if (existingURL) {
        const m = existingURL.match(/\/o\/(.+?)\?/);
        if (m) try { await deleteObject(storageRef(storage, decodeURIComponent(m[1]))); } catch (_) {}
      }
      finalPhotoURL = "";
      finalHasCustomPhoto = false;
      photoChanges.profilePhotoURL = "";
      photoChanges.photoSource = "generated";
      photoChanges.hasCustomPhoto = deleteField();
    } else if (photoSourceDraft === "google") {
      finalPhotoURL = "";
      finalHasCustomPhoto = false;
      photoChanges.photoSource = "google";
      photoChanges.hasCustomPhoto = deleteField();
    }

    // Always persist the resolved photoSource so it survives sign-out/sign-in.
    // photoChanges.photoSource is only set above when the user actively changed
    // their photo during this edit session; if they didn't, we write the current
    // source here. "provider" (e.g. Telegram CDN URL) is promoted to "upload" so
    // the stored profilePhotoURL (= user.photoURL at this point) is used next time.
    if (!photoChanges.photoSource) {
      // Use raw Firestore value — currentUserProfileData infers "google" when
      // userProfile is null (still loading), which would overwrite a real upload.
      const src = userProfile?.photoSource;
      if (src === "provider") {
        photoChanges.photoSource = "upload";
        photoChanges.hasCustomPhoto = true;
      } else if (src === "google") {
        photoChanges.photoSource = "google";
        finalPhotoURL = ""; // Google CDN URL is always read live — don't persist it
      } else if (src) {
        photoChanges.photoSource = src;
      }
      // src undefined → skip; merge:true preserves the existing Firestore value
    }

    if (avatarSeedDraft !== null) photoChanges.avatarSeed = avatarSeedDraft;

    const payload = {
      firstName,
      lastName,
      nickname,
      email,
      profileName,
      displayName: displayName || profileName || user.displayName || "",
      ...(profileNameChanged ? {
        nameUpdatedAt: profileNameUpdatedAt,
        profileNameUpdatedAt,
      } : {}),
    };
    try {
      await setDoc(doc(db, "users", user.uid, "profile", "main"), {
        ...payload,
        profilePhotoURL: finalPhotoURL,
        ...photoChanges,
      }, { merge: true });
    } catch (err) {
      console.error("saveMyProfile — user doc failed:", err?.code, err?.message);
      setToastMessage("Could not save profile. Please try again.");
      return;
    }
    try {
      const currentUser = getAuth().currentUser;
      if (currentUser) {
        await updateProfile(currentUser, {
          displayName: payload.displayName,
          photoURL: finalPhotoURL || null,
        });
      }
    } catch (err) {
      console.warn("saveMyProfile — auth update failed:", err?.code, err?.message);
    }
    try {
      await syncUserProfileAcrossGroups({ ...payload, profilePhotoURL: finalPhotoURL, hasCustomPhoto: finalHasCustomPhoto });
    } catch (err) {
      console.warn("saveMyProfile — group sync failed:", err?.code, err?.message);
    }
    if (activeGroupId) {
      const existingGroupNickname = String(myMember?.groupNickname || "").trim();
      const groupNicknameChanged = !!existingGroupNickname;
      const groupNameUpdatedAt = groupNicknameChanged ? Date.now() : profileNameUpdatedAt;
      const sharedProfilePayload = {
        ...payload,
        profilePhotoURL: finalPhotoURL,
        hasCustomPhoto: finalHasCustomPhoto,
      };
      const memberNameTimestampPatch = (profileNameChanged || groupNicknameChanged)
        ? {
          nameUpdatedAt: groupNameUpdatedAt,
          ...(profileNameChanged ? { profileNameUpdatedAt } : {}),
          ...(groupNicknameChanged ? { groupNicknameUpdatedAt: groupNameUpdatedAt } : {}),
        }
        : {};
      try {
        await setDoc(
          doc(db, "groups", activeGroupId, "members", user.uid),
          {
            ...stripMemberPrivateFields({ uid: user.uid, ...sharedProfilePayload }),
            groupNickname: deleteField(),
            ...memberNameTimestampPatch,
          },
          { merge: true }
        );
        await syncLinkedPlayerProfile(user.uid, { ...sharedProfilePayload, groupNickname: "", ...memberNameTimestampPatch });
      } catch (err) {
        console.warn("saveMyProfile — group nickname save failed:", err?.code, err?.message);
      }
    }
    clearPhotoDrafts();
    setEditingProfile(false);
    setToastMessage("Profile saved");
    setToastType("success");
  }

  function markPhotoPromptSeen() {
    if (!user || userProfile?.photoPromptSeen) return;
    setDoc(doc(db, "users", user.uid, "profile", "main"), { photoPromptSeen: true }, { merge: true }).catch(() => {});
  }

  function openAvatarPicker() {
    markPhotoPromptSeen();
    setShowAvatarPicker(true);
  }

  function handleAvatarFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      setToastMessage("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropTarget("profile");
      setCropImageSrc(e.target.result);
      setCropNaturalSize({ w: 0, h: 0 });
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  }

  function handleWizardAvatarFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      setToastMessage("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropTarget("wizard");
      setCropImageSrc(e.target.result);
      setCropNaturalSize({ w: 0, h: 0 });
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  }

  function handleCropImageLoad() {
    const img = cropImgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setCropNaturalSize({ w, h });
    const CROP_SIZE = 280;
    const minScale = Math.max(CROP_SIZE / w, CROP_SIZE / h);
    setCropScale(minScale);
    setCropOffset({ x: 0, y: 0 });
  }

  function clampCropOffset(ox, oy, scale) {
    const CROP_SIZE = 280;
    const maxOx = Math.max(0, (cropNaturalSize.w * scale - CROP_SIZE) / 2);
    const maxOy = Math.max(0, (cropNaturalSize.h * scale - CROP_SIZE) / 2);
    return {
      x: Math.min(maxOx, Math.max(-maxOx, ox)),
      y: Math.min(maxOy, Math.max(-maxOy, oy)),
    };
  }

  function handleCropMouseDown(e) {
    e.preventDefault();
    setCropDragStart({ cx: e.clientX, cy: e.clientY, ox: cropOffset.x, oy: cropOffset.y });
  }

  function handleCropMouseMove(e) {
    if (!cropDragStart) return;
    const dx = e.clientX - cropDragStart.cx;
    const dy = e.clientY - cropDragStart.cy;
    setCropOffset(clampCropOffset(cropDragStart.ox + dx, cropDragStart.oy + dy, cropScale));
  }

  function handleCropMouseUp() { setCropDragStart(null); }

  function handleCropWheel(e) {
    e.preventDefault();
    const CROP_SIZE = 280;
    const minScale = cropNaturalSize.w ? Math.max(CROP_SIZE / cropNaturalSize.w, CROP_SIZE / cropNaturalSize.h) : 1;
    const newScale = Math.min(minScale * 4, Math.max(minScale, cropScale * (1 - e.deltaY * 0.001)));
    setCropScale(newScale);
    setCropOffset(prev => clampCropOffset(prev.x, prev.y, newScale));
  }

  function handleCropTouchStart(e) {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      setCropDragStart({ cx: t.clientX, cy: t.clientY, ox: cropOffset.x, oy: cropOffset.y });
      cropLastTouchDist.current = null;
    } else if (e.touches.length === 2) {
      cropLastTouchDist.current = {
        dist: Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY),
        scale: cropScale,
      };
      setCropDragStart(null);
    }
  }

  function handleCropTouchMove(e) {
    if (e.touches.length === 1 && cropDragStart) {
      const t = e.touches[0];
      setCropOffset(clampCropOffset(cropDragStart.ox + t.clientX - cropDragStart.cx, cropDragStart.oy + t.clientY - cropDragStart.cy, cropScale));
    } else if (e.touches.length === 2 && cropLastTouchDist.current) {
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      const CROP_SIZE = 280;
      const minScale = cropNaturalSize.w ? Math.max(CROP_SIZE / cropNaturalSize.w, CROP_SIZE / cropNaturalSize.h) : 1;
      const newScale = Math.min(minScale * 4, Math.max(minScale, cropLastTouchDist.current.scale * dist / cropLastTouchDist.current.dist));
      setCropScale(newScale);
      setCropOffset(prev => clampCropOffset(prev.x, prev.y, newScale));
    }
  }

  function handleCropTouchEnd() {
    setCropDragStart(null);
    cropLastTouchDist.current = null;
  }

  async function confirmCrop() {
    const img = cropImgRef.current;
    if (!img || !user || !cropNaturalSize.w) return;
    setCropUploading(true);
    try {
      const CROP_SIZE = 280;
      const OUTPUT_SIZE = 600;
      const dispW = cropNaturalSize.w * cropScale;
      const dispH = cropNaturalSize.h * cropScale;
      const left = CROP_SIZE / 2 - dispW / 2 + cropOffset.x;
      const top = CROP_SIZE / 2 - dispH / 2 + cropOffset.y;
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      const s = OUTPUT_SIZE / CROP_SIZE;
      ctx.drawImage(img, left * s, top * s, dispW * s, dispH * s);

      // Compress to JPEG under 1 MB
      const maxBytes = 1024 * 1024;
      let quality = 0.92;
      let blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", quality));
      while (blob && blob.size > maxBytes && quality > 0.15) {
        quality -= 0.08;
        const q = quality;
        blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", q));
      }

      if (cropTarget === "group") {
        // Defer group photo upload — store blob for group settings save
        if (groupPhotoPreviewURL) URL.revokeObjectURL(groupPhotoPreviewURL);
        setGroupCropBlobDraft(blob);
        setGroupPhotoPreviewURL(URL.createObjectURL(blob));
        setCropImageSrc(null);
        setCropTarget(null);
        setToastMessage("Photo selected — tap Save to apply");
        setToastType("success");
        return;
      }

      if (cropTarget === "wizard") {
        // Defer onboarding upload until the profile step is completed.
        if (wizardPhotoPreviewURL) URL.revokeObjectURL(wizardPhotoPreviewURL);
        setWizardCropBlobDraft(blob);
        setWizardPhotoPreviewURL(URL.createObjectURL(blob));
        setCropImageSrc(null);
        setCropTarget("profile");
        setToastMessage("Photo selected");
        setToastType("success");
        return;
      }

      if (editingProfile) {
        // Defer upload — store blob as draft and show a local preview
        if (photoPreviewURL) URL.revokeObjectURL(photoPreviewURL);
        setCropBlobDraft(blob);
        setPhotoSourceDraft("upload");
        setPhotoPreviewURL(URL.createObjectURL(blob));
        setCropImageSrc(null);
        setToastMessage("Photo selected — tap Save to apply");
        setToastType("success");
        return;
      }

      // Immediate save (called from photo prompt outside edit mode)
      const path = `avatars/${user.uid}/profile/${Date.now()}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: "image/jpeg" });
      const profilePhotoURL = await getDownloadURL(ref);
      await setDoc(doc(db, "users", user.uid, "profile", "main"), {
        profilePhotoURL,
        photoSource: "upload",
        hasCustomPhoto: true,
        photoPromptSeen: true,
      }, { merge: true });
      await updateProfile(getAuth().currentUser, {
        displayName: currentUserProfileData?.displayName || user.displayName || null,
        photoURL: profilePhotoURL,
      });
      await syncUserProfileAcrossGroups({
        ...(currentUserProfileData || {}),
        profilePhotoURL,
        hasCustomPhoto: true,
      });
      if (activeGroupId) {
        await syncLinkedPlayerProfile(user.uid, {
          ...(currentUserProfileData || {}),
          profilePhotoURL,
          hasCustomPhoto: true,
        });
      }
      setCropImageSrc(null);
      setToastMessage("Profile picture updated");
      setToastType("success");
    } catch (err) {
      console.error("Avatar upload failed:", err?.code, err?.message);
      setToastMessage("Could not update profile picture");
    } finally {
      setCropUploading(false);
    }
  }

  async function handleSelectGooglePhoto() {
    const googlePhotoURL = currentUserProfileData?.googleProviderPhotoURL || "";
    if (!user || !googlePhotoURL) return;
    setShowAvatarPicker(false);
    if (editingProfile) {
      // Defer to Save
      if (photoPreviewURL) { URL.revokeObjectURL(photoPreviewURL); setPhotoPreviewURL(null); }
      setCropBlobDraft(null);
      setPhotoSourceDraft("google");
      return;
    }
    // Immediate save (called from photo prompt outside edit mode)
    try {
      await setDoc(doc(db, "users", user.uid, "profile", "main"), {
        photoSource: "google",
        hasCustomPhoto: false,
        photoPromptSeen: true,
      }, { merge: true });
      await syncUserProfileAcrossGroups({
        ...(currentUserProfileData || {}),
        profilePhotoURL: "",
        hasCustomPhoto: false,
      });
      if (activeGroupId) {
        await syncLinkedPlayerProfile(user.uid, {
          ...(currentUserProfileData || {}),
          profilePhotoURL: "",
          hasCustomPhoto: false,
        });
      }
      setToastMessage("Profile picture updated");
      setToastType("success");
    } catch (err) {
      console.error("Select Google photo failed:", err?.code, err?.message);
      setToastMessage("Could not update profile picture");
    }
  }

  function handleDeletePhoto() {
    if (!user) return;
    setShowAvatarPicker(false);
    // Always deferred — only accessible from picker inside edit mode
    if (photoPreviewURL) { URL.revokeObjectURL(photoPreviewURL); setPhotoPreviewURL(null); }
    setCropBlobDraft(null);
    setPhotoSourceDraft("delete"); // "delete" = remove uploaded photo on Save
  }

  function handleSelectGeneratedAvatar() {
    if (!user) return;
    setShowAvatarPicker(false);
    // Always deferred — only accessible from picker inside edit mode
    if (photoPreviewURL) { URL.revokeObjectURL(photoPreviewURL); setPhotoPreviewURL(null); }
    setCropBlobDraft(null);
    setPhotoSourceDraft("generated");
  }


  const idToNameMap = useMemo(() => {
    const m = {};
    players.forEach((p) => { m[p.id] = getPlayerPreferredName(p); });
    return m;
  }, [players]);

  const playerAvatarMap = useMemo(() => {
    const linkedMemberByPlayerId = {};
    const linkedMemberByUid = {};
    members.forEach((member) => {
      if (member?.playerId) linkedMemberByPlayerId[String(member.playerId)] = member;
      if (member?.id) linkedMemberByUid[String(member.id)] = member;
    });

    const map = {};
    players.forEach((player) => {
      const linkedMember =
        linkedMemberByPlayerId[String(player.id)] ||
        linkedMemberByUid[String(player.linkedUserUid || "")];
      const isCurrentUser = String(player.linkedUserUid || "") === String(user?.uid || "");
      if (isCurrentUser) {
        // Use the resolved profilePhotoURL which already respects photoSource
        // (empty string when "generated", Google URL when "google", upload URL when "upload").
        map[player.id] = currentUserProfileData?.profilePhotoURL || userProfile?.profilePhotoURL || myMember?.profilePhotoURL || "";
      } else {
        map[player.id] = player.profilePhotoURL || linkedMember?.profilePhotoURL || "";
      }
    });
    return map;
  }, [members, myMember, players, user, userProfile, currentUserProfileData]);

  const getPlayerName = useCallback((id) => {
    if (id == null || id === "") return "";
    return idToNameMap[String(id)] || idToNameMap[id] || "";
  }, [idToNameMap]);

  const getPlayerAvatar = useCallback((id) => {
    if (id == null || id === "") return "";
    return playerAvatarMap[String(id)] || cachedPlayerAvatarMap[String(id)] || "";
  }, [cachedPlayerAvatarMap, playerAvatarMap]);

  useEffect(() => {
    if (!activeGroupId) return;
    const groupPhotoURL = groupMeta
      ? String(groupMeta.photoURL || "")
      : String(cachedGroupPhotoURL || "");
    const playerAvatars = { ...cachedPlayerAvatarMap };
    Object.entries(playerAvatarMap || {}).forEach(([playerId, url]) => {
      if (url) playerAvatars[playerId] = url;
      else if (players.some((player) => String(player.id) === String(playerId))) delete playerAvatars[playerId];
    });
    try {
      localStorage.setItem(`${IMAGE_URL_CACHE_PREFIX}${activeGroupId}`, JSON.stringify({
        updatedAt: Date.now(),
        groupPhotoURL,
        playerAvatars,
      }));
    } catch (_) {}
  }, [activeGroupId, cachedGroupPhotoURL, cachedPlayerAvatarMap, groupMeta, players, playerAvatarMap]);

  useEffect(() => {
    const urls = [
      groupMeta?.photoURL || cachedGroupPhotoURL,
      cachedGroupPhotoURL,
      ...Object.values(cachedPlayerAvatarMap || {}),
      ...Object.values(playerAvatarMap || {}),
    ].filter(Boolean);
    Array.from(new Set(urls)).forEach((url) => {
      const image = new Image();
      image.decoding = "async";
      image.src = url;
      if (image.decode) image.decode().catch(() => {});
    });
  }, [cachedGroupPhotoURL, cachedPlayerAvatarMap, groupMeta?.photoURL, playerAvatarMap]);

  const formatCurrency = useCallback((n, currencyCode = activeCurrencyCode) => {
    const value = Number(n || 0);
    const normalizedCurrency = String(currencyCode || activeCurrencyCode || "EUR").toUpperCase();
    const hasDecimal = value !== Math.floor(value);
    try {
      return new Intl.NumberFormat(appLocale, {
        style: "currency",
        currency: normalizedCurrency,
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return currencyFormatter.format(value);
    }
  }, [activeCurrencyCode, appLocale, currencyFormatter]);
  const formatCompactCurrency = useCallback((n, currencyCode = activeCurrencyCode) => {
    const value = Math.abs(Number(n || 0));
    const normalizedCurrency = String(currencyCode || activeCurrencyCode || "EUR").toUpperCase();
    try {
      return new Intl.NumberFormat(appLocale, {
        style: "currency",
        currency: normalizedCurrency,
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value);
    } catch {
      return compactCurrencyFormatter.format(value);
    }
  }, [activeCurrencyCode, appLocale, compactCurrencyFormatter]);
  const euro = useCallback((n) => formatCurrency(n), [formatCurrency]);
  
  function simulateJackpot(glist, base) {
    const byTime = [...glist].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    let balance = Math.max(0, Number(base) || 0);
    const awards = {};
    byTime.forEach((g) => {
      // Rule:
      // 1) If jackpot is hit this game -> player gets the pre-game balance only, then reset to this game's cut.
      // 2) The cut taken from 1st place in the current game (jpTaken: its buy-in, or 5 on older games) becomes the new starting balance for the next game.
      // 3) If no hit, nothing is paid out; still add jpTaken to the balance.
      if (g.jackpotWinnerId) {
        // Pay the pre-game balance to the winner, then reset the jackpot to this game's contribution only
        awards[g.id] = balance;
        balance = (g.jpTaken || 0);
      } else {
        // No hit: accumulate this game's contribution
        awards[g.id] = 0;
        balance = balance + (g.jpTaken || 0);
      }
    });
    return { balance, awards };
  }

  const enrichedGames = games.map(enrichGame);

  const availableYears = useMemo(() => {
    const years = new Set(enrichedGames.map(g => new Date((g.date || '') + 'T00:00:00').getFullYear()).filter(y => !isNaN(y)));
    return [...years].sort((a, b) => b - a);
  }, [enrichedGames]);

  const filteredEnrichedGames = useMemo(() => {
    if (dateFilterPreset === "all") return enrichedGames;
    const now = new Date();
    let start, end;
    const calendarRange = calendarPeriodRange(dateFilterPreset, now);
    if (calendarRange) {
      ({ start, end } = calendarRange);
    } else if (/^\d{4}$/.test(dateFilterPreset)) {
      const year = Number(dateFilterPreset);
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31, 23, 59, 59);
    } else if (dateFilterPreset === "custom") {
      start = dateFilterStart ? new Date(dateFilterStart + 'T00:00:00') : new Date(0);
      end = dateFilterEnd ? new Date(dateFilterEnd + 'T23:59:59') : now;
    } else {
      return enrichedGames;
    }
    return enrichedGames.filter(g => {
      const d = new Date((g.date || '') + 'T00:00:00');
      return d >= start && d <= end;
    });
  }, [enrichedGames, dateFilterPreset, dateFilterStart, dateFilterEnd]);

  const enrichedGamesSorted = useMemo(() => {
    return [...filteredEnrichedGames].sort((a, b) => {
      const ta = new Date(a.date || 0).getTime();
      const tb = new Date(b.date || 0).getTime();
      if (tb !== ta) return tb - ta; // primary: game date desc
      const ca = a.createdAt || 0;
      const cb = b.createdAt || 0;
      return cb - ca; // secondary: createdAt desc
    });
  }, [filteredEnrichedGames]);
  const jackpotSim = useMemo(() => simulateJackpot(enrichedGames, Math.max(0, Number(jackpotBase) || 0)), [enrichedGames, jackpotBase]);
  const jackpotBalance = jackpotSim.balance;
  const biggestJackpot = useMemo(() => {
    const awards = Object.values(jackpotSim.awards || {}).filter(v => v > 0);
    return awards.length > 0 ? Math.max(...awards) : 0;
  }, [jackpotSim.awards]);

  // Jackpot "race to the record": games played since the last hit, and what
  // each future night adds (one buy-in from 1st place; the group's default
  // buy-in if set, else the latest game's cut).
  const jackpotRun = useMemo(() => {
    // Same order as simulateJackpot (when games were saved), so the count
    // always agrees with the balance.
    const ordered = [...enrichedGames].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    let lastHitIdx = -1;
    ordered.forEach((g, i) => {
      if (g.jackpotWinnerId && (jackpotSim.awards[g.id] || 0) > 0) lastHitIdx = i;
    });
    const latest = ordered[ordered.length - 1];
    const defaultBuyIn = Number(groupMeta?.defaultBuyIn) || 0;
    const perNight = defaultBuyIn > 0 ? defaultBuyIn : (latest ? getJackpotCut(latest) : 0);
    return {
      gamesSinceHit: lastHitIdx >= 0 ? ordered.length - 1 - lastHitIdx : null,
      perNight,
    };
  }, [enrichedGames, jackpotSim.awards, groupMeta?.defaultBuyIn]);

  const lastJackpotEvent = useMemo(() => {
    // Walk enrichedGames sorted newest-first to find last jackpot award
    const sorted = [...enrichedGames].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (db !== da) return db - da;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
    for (const g of sorted) {
      const award = jackpotSim.awards[g.id] || 0;
      if (g.jackpotWinnerId && award > 0) {
        const winner = players.find(p => p.id === g.jackpotWinnerId);
        return { name: winner?.name || g.jackpotWinnerId, id: g.jackpotWinnerId, amount: award, date: g.date };
      }
    }
    return null;
  }, [enrichedGames, jackpotSim.awards, players]);

  // Full jackpot win history (newest first) for the history modal
  const jackpotHistory = useMemo(() => {
    const sorted = [...enrichedGames].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (db !== da) return db - da;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
    const rows = [];
    for (const g of sorted) {
      const award = jackpotSim.awards[g.id] || 0;
      if (g.jackpotWinnerId && award > 0) {
        const winner = players.find(p => p.id === g.jackpotWinnerId);
        rows.push({
          gameId: g.id,
          winnerId: g.jackpotWinnerId,
          name: winner?.name || g.jackpotWinnerId,
          amount: award,
          date: g.date,
          cards: g.jackpotCards || "",
        });
      }
    }
    return rows;
  }, [enrichedGames, jackpotSim.awards, players]);

  // Per-player jackpot totals (most won first) for the history modal summary
  const jackpotTotals = useMemo(() => {
    const byPlayer = {};
    for (const row of jackpotHistory) {
      if (!byPlayer[row.winnerId]) {
        byPlayer[row.winnerId] = { winnerId: row.winnerId, name: row.name, total: 0, wins: 0 };
      }
      byPlayer[row.winnerId].total += row.amount;
      byPlayer[row.winnerId].wins += 1;
    }
    return Object.values(byPlayer).sort((a, b) => b.total - a.total);
  }, [jackpotHistory]);

  // Sorted players alphabetically for UI components
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [players]);
  // Players who can be picked for a game. A removed member's player stays in
  // `players` (history and stats still need it) but leaves the roster until a
  // moderator links them again.
  const rosterPlayers = useMemo(() => sortedPlayers.filter((p) => !p.removedFromGroup), [sortedPlayers]);

  // Sorted selected players alphabetically for buy-ins section
  const sortedSelectedPlayers = useMemo(() => {
    return selectedPlayers
      .map(playerId => players.find(p => p.id === playerId))
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
      .map(p => p.id);
  }, [selectedPlayers, players]);

  const hiddenStatsPlayerIdSet = useMemo(() => {
    return new Set(
      Object.entries(statsVisibilityDraft)
        .filter(([, visible]) => visible === false)
        .map(([id]) => id)
    );
  }, [statsVisibilityDraft]);

  const totals = useMemo(() => {
    const map = {};
    players.forEach((p) => {
      map[p.id] = { id: p.id, name: getPlayerPreferredName(p), buy: 0, payout: 0, profit: 0 };
    });

    filteredEnrichedGames.forEach((g) => {
      Object.keys(g.buyIns || {}).forEach((playerId) => {
        if (!map[playerId] && players.find(p => p.id === playerId)) {
          const player = players.find(p => p.id === playerId);
          map[playerId] = { id: playerId, name: getPlayerPreferredName(player), buy: 0, payout: 0, profit: 0 };
        }
        if (map[playerId]) {
          map[playerId].buy += Number(g.buyIns[playerId] || 0);
        }
      });

      const gPayouts = computePayouts(g, jackpotSim.awards);
      Object.entries(gPayouts).forEach(([pid, amt]) => {
        if (map[pid]) map[pid].payout += amt;
      });
    });
    Object.values(map).forEach((m) => (m.profit = m.payout - m.buy));
    return map;
  }, [players, filteredEnrichedGames, jackpotSim.awards]);

  const statsTable = useMemo(() => {
    const rows = Object.values(totals)
      .filter((t) => !hiddenStatsPlayerIdSet.has(String(t.id)))
      .filter((t) => t.buy > 0 || t.payout > 0)
      .map((t) => ({ 
        id: t.id, 
        name: t.name, 
        buy: Number(t.buy.toFixed(2)), 
        payout: Number(t.payout.toFixed(2)), 
        profit: Number(t.profit.toFixed(2)) 
      }));
    rows.sort((a, b) => {
      if (a.buy === 0 && b.buy === 0) {
        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      }
      if (a.buy === 0) return 1;
      if (b.buy === 0) return -1;
      return b.profit - a.profit;
    });
    return rows;
  }, [totals, hiddenStatsPlayerIdSet]);

  const rankingTable = useMemo(() => {
    const orderedGames = [...filteredEnrichedGames].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (da !== db) return da - db;
      return (a.createdAt || 0) - (b.createdAt || 0);
    });

    const ranking = {};
    players.forEach((p) => {
      ranking[p.id] = {
        id: p.id,
        name: getPlayerPreferredName(p),
        points: 0,
        games: 0,
        buyInCount: 0,
        firsts: 0,
        seconds: 0,
        thirds: 0,
        jackpots: 0,
        streak: 0,
        bestStreak: 0,
        pointsFromNet: 0,
        netEuroTotal: 0,
        pointsFromPlacement: 0,
        pointsFromJackpot: 0,
        pointsFromBuyInPenalty: 0,
        rebuyCount: 0,
        pointsFromStreak: 0,
        streakBonusUnits: 0,
        pointsFromCapAdjustment: 0,
        events: [],
        playedGameIds: [],
        firstGameIds: [],
        secondGameIds: [],
        thirdGameIds: [],
        jackpotGameIds: []
      };
    });

    orderedGames.forEach((g) => {
      const payouts = computePayouts(g, jackpotSim.awards);
      const participants = Object.keys(g.buyIns || {});
      // Count buy-ins in this game's own unit (a 20 buy-in is 1, not 4).
      const buyInUnit = getGameBuyInUnit(g);

      participants.forEach((playerId) => {
        if (!ranking[playerId]) return;
        const buyIn = Number(g.buyIns?.[playerId] || 0);
        const payout = Number(payouts[playerId] || 0);
        const net = payout - buyIn;
        const isFirst = g.firstWinnerId === playerId || g.splitWinnerId === playerId;
        const isSecond = g.secondWinnerId === playerId;
        const isThird = g.thirdWinnerId === playerId;
        const isJackpot = g.jackpotWinnerId === playerId;

        ranking[playerId].games += 1;
        ranking[playerId].playedGameIds.push(g.id);
        if (buyIn > 0) ranking[playerId].buyInCount += (buyIn / buyInUnit);
        if (isFirst) {
          ranking[playerId].firsts += 1;
          ranking[playerId].firstGameIds.push(g.id);
        }
        if (isSecond) {
          ranking[playerId].seconds += 1;
          ranking[playerId].secondGameIds.push(g.id);
        }
        if (isThird) {
          ranking[playerId].thirds += 1;
          ranking[playerId].thirdGameIds.push(g.id);
        }
        if (isJackpot) {
          ranking[playerId].jackpots += 1;
          ranking[playerId].jackpotGameIds.push(g.id);
        }

        if (net > rankingConfig.streakMinNetEuro) {
          ranking[playerId].streak += 1;
          ranking[playerId].bestStreak = Math.max(ranking[playerId].bestStreak, ranking[playerId].streak);
        } else {
          ranking[playerId].streak = 0;
        }

        const buyInUnits = buyIn / buyInUnit;
        const netPoints = net * rankingConfig.netMultiplier;
        const placementPoints = (isFirst ? rankingConfig.firstPlaceBonus : 0) + (isSecond ? rankingConfig.secondPlaceBonus : 0) + (isThird ? rankingConfig.thirdPlaceBonus : 0);
        const jackpotPoints = 0;
        // No penalty for the first buy-in; penalize rebuys to keep ranking fair.
        const rebuyUnits = Math.max(0, buyInUnits - 1);
        const buyInPenalty = -rankingConfig.rebuyPenalty * rebuyUnits;
        const streakBonus = net > rankingConfig.streakMinNetEuro
          ? Math.min(rankingConfig.streakMax, Math.max(0, ranking[playerId].streak - 1) * rankingConfig.streakStep)
          : 0;
        const rawDelta = netPoints + placementPoints + jackpotPoints + buyInPenalty + streakBonus;
        const cappedDelta = Math.max(rankingConfig.gameCapMin, Math.min(rankingConfig.gameCapMax, rawDelta));
        const capAdjustment = cappedDelta - rawDelta;

        ranking[playerId].pointsFromNet += netPoints;
        ranking[playerId].netEuroTotal += net;
        ranking[playerId].pointsFromPlacement += placementPoints;
        ranking[playerId].pointsFromJackpot += jackpotPoints;
        ranking[playerId].pointsFromBuyInPenalty += buyInPenalty;
        ranking[playerId].rebuyCount += rebuyUnits;
        ranking[playerId].pointsFromStreak += streakBonus;
        ranking[playerId].streakBonusUnits += (rankingConfig.streakStep > 0 ? streakBonus / rankingConfig.streakStep : 0);
        ranking[playerId].pointsFromCapAdjustment += capAdjustment;
        ranking[playerId].points += cappedDelta;
        ranking[playerId].events.push({
          gameId: g.id,
          date: g.date || "",
          location: g.location || "—",
          net: Number(net.toFixed(2)),
          delta: Number(cappedDelta.toFixed(2)),
          netPoints: Number(netPoints.toFixed(2)),
          placementPoints: Number(placementPoints.toFixed(2)),
          jackpotPoints: Number(jackpotPoints.toFixed(2)),
          buyInPenalty: Number(buyInPenalty.toFixed(2)),
          streakBonus: Number(streakBonus.toFixed(2))
        });
      });
    });

    const rows = Object.values(ranking)
      .filter((row) => row.games > 0)
      .map((row) => {
        const points = Math.round(row.points);
        const financial = totals[row.id] || { profit: 0 };
        return {
          ...row,
          buyInCount: Math.round(row.buyInCount),
          points,
          profit: Number((financial.profit || 0).toFixed(2)),
          recentEvents: row.events.slice(-5).reverse()
        };
      });

    rows.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.profit !== a.profit) return b.profit - a.profit;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    return rows.map((row, idx) => ({ ...row, rank: idx + 1 }));
  }, [filteredEnrichedGames, players, jackpotSim.awards, totals, rankingConfig]);

  const rankingById = useMemo(() => {
    const m = {};
    rankingTable.forEach((row) => { m[row.id] = row; });
    return m;
  }, [rankingTable]);

  // Biggest single-game net per player (for the "Big Night" badge).
  const maxSingleNetById = useMemo(() => {
    const m = {};
    filteredEnrichedGames.forEach((g) => {
      const payouts = computePayouts(g, jackpotSim.awards);
      Object.keys(g.buyIns || {}).forEach((pid) => {
        const net = Number(payouts[pid] || 0) - Number(g.buyIns[pid] || 0);
        if (m[pid] === undefined || net > m[pid]) m[pid] = net;
      });
    });
    return m;
  }, [filteredEnrichedGames, jackpotSim.awards]);

  // Earned badge icons per player, shown next to names in the Home profit/loss list.
  const playerBadgeIconsById = useMemo(() => {
    const out = {};
    Object.keys(rankingById).forEach((id) => {
      const p = rankingById[id] || {};
      const stats = {
        firsts: p.firsts || 0,
        bestStreak: p.bestStreak || 0,
        jackpots: p.jackpots || 0,
        netProfit: totals[id]?.profit ?? p.netEuroTotal ?? 0,
        maxSingleNet: maxSingleNetById[id] ?? 0,
        games: p.games || 0,
      };
      out[id] = computeBadges(stats).filter((b) => b.earned).map((b) => ({ id: b.id, glyph: b.glyph, accent: b.accent }));
    });
    return out;
  }, [rankingById, totals, maxSingleNetById]);

  const rankingRowsVisible = rankingTable;

  // Each player's nights in the selected period, oldest first: the net result
  // of every game they played. Feeds the Home sparklines and top movers.
  const playerNightsById = useMemo(() => {
    const ordered = [...filteredEnrichedGames].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const db = new Date(b.date || 0).getTime();
      if (da !== db) return da - db;
      return (a.createdAt || 0) - (b.createdAt || 0);
    });
    const map = {};
    ordered.forEach((g) => {
      const payouts = computePayouts(g, jackpotSim.awards);
      Object.keys(g.buyIns || {}).forEach((pid) => {
        const net = Number(payouts[pid] || 0) - Number(g.buyIns[pid] || 0);
        (map[pid] = map[pid] || []).push({ net, date: g.date, gameId: g.id });
      });
    });
    return map;
  }, [filteredEnrichedGames, jackpotSim.awards]);

  // Top movers: the best run over each player's last three nights, the single
  // biggest night, and the worst run. Hidden players ("My Charts") are skipped.
  const topMovers = useMemo(() => {
    const entries = Object.entries(playerNightsById)
      .filter(([pid]) => !hiddenStatsPlayerIdSet.has(String(pid)) && players.some((p) => p.id === pid));
    if (!entries.length) return [];
    const nameOf = (pid) => getPlayerPreferredName(players.find((p) => p.id === pid) || { name: pid });
    let hot = null;
    let cold = null;
    let best = null;
    entries.forEach(([pid, nights]) => {
      const recent = nights.slice(-3);
      const run = recent.reduce((sum, n) => sum + n.net, 0);
      if (!hot || run > hot.value) hot = { pid, value: run, nights: recent.length };
      if (!cold || run < cold.value) cold = { pid, value: run, nights: recent.length };
      nights.forEach((n) => {
        if (!best || n.net > best.value) best = { pid, value: n.net, date: n.date, gameId: n.gameId };
      });
    });
    const out = [];
    if (hot && hot.value > 0) out.push({ key: "hot", label: "Hot hand", name: nameOf(hot.pid), ...hot });
    if (best && best.value > 0) out.push({ key: "best", label: "Best night", name: nameOf(best.pid), ...best });
    if (cold && cold.value < 0) out.push({ key: "cold", label: "Cold streak", name: nameOf(cold.pid), ...cold });
    return out;
  }, [playerNightsById, hiddenStatsPlayerIdSet, players]);

  // The signed-in user's own player and where they stand in the current period,
  // for the Home "You" card and the highlighted row in the stats table. The
  // member doc's playerId is the canonical link (every linking flow writes it,
  // and the Profile tab reads it first); a player's linkedUserUid is only the
  // fallback, since an old player can keep a stale one after a re-link.
  const myPlayerId = useMemo(() => {
    if (!user) return null;
    const memberLink = String(myMember?.playerId || "").trim();
    if (memberLink && players.some((p) => p.id === memberLink)) return memberLink;
    return players.find((p) => p.linkedUserUid === user.uid)?.id || null;
  }, [players, user, myMember]);
  const openTrainer = useCallback(() => setTrainerOpen(true), []);
  // How you appear on the group's trainer leaderboard.
  const trainerIdentity = useMemo(() => ({
    name: (myPlayerId && getPlayerName(myPlayerId)) || currentUserProfileData?.profileName || user?.displayName || "Player",
    photoURL: (myPlayerId && getPlayerAvatar(myPlayerId)) || currentUserProfileData?.profilePhotoURL || "",
    playerId: myPlayerId || "",
  }), [myPlayerId, getPlayerName, getPlayerAvatar, currentUserProfileData, user]);
  const historyRowsVisible = enrichedGamesSorted;


  const gamesById = useMemo(() => {
    const m = {};
    enrichedGamesSorted.forEach((g) => { m[g.id] = g; });
    return m;
  }, [enrichedGamesSorted]);
  const relatedGames = useMemo(() => {
    if (!relatedGamesView?.gameIds) return [];
    return relatedGamesView.gameIds.map((id) => gamesById[id]).filter(Boolean);
  }, [relatedGamesView, gamesById]);


  // Predictive analytics: Bayesian win likelihood per player
  // Uses Laplace smoothing so players with few games converge toward the base rate
  const winLikelihoods = useMemo(() => {
    const active = rankingTable.filter(r => r.games >= 2);
    if (active.length < 2) return [];
    const alpha = 1; // smoothing pseudo-count
    const N = active.length; // number of competing players (prior denominator)
    const raw = active.map(r => ({
      id: r.id,
      name: r.name,
      games: r.games,
      firsts: r.firsts,
      // (wins + α) / (games + N·α) — shrinks toward 1/N for few games
      score: (r.firsts + alpha) / (r.games + N * alpha),
    }));
    const total = raw.reduce((s, r) => s + r.score, 0);
    return raw
      .map(r => ({ ...r, pct: Math.round((r.score / total) * 100) }))
      .sort((a, b) => b.pct - a.pct);
  }, [rankingTable]);

  // Chart colours (Chart.js can't read CSS variables). Profit
  // and loss use the same up/down greens and reds as the money pills.
  const chartPalette = useMemo(() => ({
      positive: "rgba(48, 209, 88, 0.85)",
      positiveBorder: "#30D158",
      negative: "rgba(255, 69, 58, 0.85)",
      negativeBorder: "#FF453A",
      zero: "rgba(152, 152, 159, 0.4)",
      tooltipBg: "#1C1C1E",
      tooltipText: "#F5F5F7",
      tooltipMuted: "#98989F",
      tooltipBorder: "rgba(255,255,255,0.10)",
      axis: "#8E8E93",
      tick: "#AEAEB2",
      label: "#E5E5EA",
      baseline: "rgba(255,255,255,0.22)",
      grid: "rgba(255,255,255,0.06)",
      zeroline: "rgba(255,255,255,0.18)",
    }), []);



  // Player Management Functions
  async function addPlayer(nameInput = newPlayerName, emailInput = newPlayerEmail, source = "settings", extraFields = {}) {
    if (!canAddPlayers || !activeGroupId) return null; // moderators + editors can create players
    const firstName = String(nameInput || "").trim();
    const email = String(emailInput || "").trim().toLowerCase();
    if (!firstName) return null;
    const lastName = String(extraFields.lastName || "").trim();
    const nickname = String(extraFields.nickname || "").trim();
    const ref = await addDoc(collection(db, "groups", activeGroupId, "players"), {
      firstName,
      lastName,
      nickname,
      name: getPlayerPreferredName({ firstName, nickname }),
      type: "guest",
      linkedUserUid: null,
      createdAt: Date.now()
    });
    // Player contact email lives in the moderator-only /private subcollection, so
    // only moderators can set it. Editors add players without an email.
    if (email && canManagePlayers) {
      await setDoc(
        doc(db, "groups", activeGroupId, "players", ref.id, "private", "contact"),
        { email },
        { merge: true }
      );
    }
    logActivity("player_created", {
      playerName: getPlayerPreferredName({ firstName, nickname }),
      changes: [`player added: ${getPlayerPreferredName({ firstName, nickname })}`],
    });
    if (source === "new-game") {
      setQuickPlayerName("");
      setQuickPlayerEmail("");
    } else {
      setNewPlayerName("");
      setNewPlayerEmail("");
    }
    return ref.id;
  }

  async function addPlayerFromNewGame() {
    try {
      const newId = await addPlayer(quickPlayerName, quickPlayerEmail, "new-game");
      if (!newId) return;
      setSelectedPlayers((prev) => (prev.includes(newId) ? prev : [...prev, newId]));
      setToastMessage("Player added");
    } catch (err) {
      console.error("Failed to add player from New Game:", err?.code, err?.message);
      showAlert("Could not add player. You may not have permission.");
    }
  }

  async function openPlayerSettings(player) {
    setPlayerSettingsId(player.id);
    setPlayerFirstNameDraft(String(player.firstName || player.name || ""));
    setPlayerLastNameDraft(String(player.lastName || ""));
    setPlayerNicknameDraft(String(player.nickname || ""));
    setPlayerEmailDraft("");
        if (!activeGroupId) return;
    try {
      const snap = await getDoc(doc(db, "groups", activeGroupId, "players", player.id, "private", "contact"));
      const existingEmail = String(snap.data()?.email || "").trim();
      setPlayerEmailDraft(existingEmail);
    } catch (err) {
      // Non-moderators may not be able to read; that's fine — leave the draft blank.
    }
  }

  async function savePlayerSettings() {
    if (!playerSettingsId) return;
    const firstName = String(playerFirstNameDraft || "").trim();
    if (!firstName) {
      setToastMessage("First name is required");
      return;
    }
    const lastName = String(playerLastNameDraft || "").trim();
    const nickname = playerNicknameDraft.trim();
    const email = String(playerEmailDraft || "").trim().toLowerCase();
    const currentPlayer = players.find((player) => player.id === playerSettingsId);
    const nextPlayerName = getPlayerPreferredName({ firstName, nickname });
    const nameChanged =
      String(currentPlayer?.firstName || "") !== firstName ||
      String(currentPlayer?.lastName || "") !== lastName ||
      String(currentPlayer?.nickname || "") !== nickname ||
      String(currentPlayer?.name || "") !== nextPlayerName;
    const nameUpdatedAt = nameChanged ? Date.now() : Number(currentPlayer?.nameUpdatedAt || 0);
    const playerChanges = [];
    if (String(currentPlayer?.firstName || "") !== firstName) playerChanges.push(`first name: ${String(currentPlayer?.firstName || "none")} -> ${firstName}`);
    if (String(currentPlayer?.lastName || "") !== lastName) playerChanges.push(`last name: ${String(currentPlayer?.lastName || "none")} -> ${lastName || "none"}`);
    if (String(currentPlayer?.nickname || "") !== nickname) playerChanges.push(`nickname: ${String(currentPlayer?.nickname || "none")} -> ${nickname || "none"}`);
    // Email changes are intentionally omitted from the activity log — the log is
    // readable by all members and emails are private (stored in the private subdoc).
    await updateDoc(doc(db, "groups", activeGroupId, "players", playerSettingsId), {
      firstName,
      lastName,
      nickname,
      name: nextPlayerName,
      ...(nameChanged ? {
        nameUpdatedAt,
        nameUpdatedBy: user?.uid || null,
        nameUpdatedSource: "moderator",
      } : {}),
    });
    await setDoc(
      doc(db, "groups", activeGroupId, "players", playerSettingsId, "private", "contact"),
      { email },
      { merge: true }
    );
    if (playerChanges.length) {
      logActivity("player_updated", {
        playerName: nextPlayerName || currentPlayer?.name || "Player",
        changes: playerChanges,
      });
    }
    setPlayerSettingsId(null);
    setPlayerFirstNameDraft("");
    setPlayerLastNameDraft("");
    setPlayerEmailDraft("");
        setPlayerNicknameDraft("");
  }

  async function deletePlayer(playerId) {
    if (!canManagePlayers || !activeGroupId) return;
    const player = players.find((p) => p.id === playerId);
    const pid = String(playerId);
    const playerName = String(player?.name || "").trim();
    // Strict safety check: block delete if referenced by any game
    // (supports current id-based data and legacy name-based fields).
    const hasGames = games.some((game) => {
      const buyInKeys = Object.keys(game.buyIns || {});
      const inBuyIns = buyInKeys.some((k) => String(k) === pid || (playerName && String(k) === playerName));
      const firstRef = String(game.firstWinnerId || "");
      const secondRef = String(game.secondWinnerId || "");
      const jackpotRef = String(game.jackpotWinnerId || "");
      const inWinners =
        firstRef === pid || secondRef === pid || jackpotRef === pid ||
        (playerName && (firstRef === playerName || secondRef === playerName || jackpotRef === playerName));
      return inBuyIns || inWinners;
    });
    
    if (hasGames) {
      showAlert("Cannot delete player who has played games. Player data must be preserved for historical records.");
      return;
    }

    const displayName = getPlayerName(playerId) || "this player";
    const confirmed = await showConfirm({ title: `Delete ${displayName}?`, message: "This can't be undone.", confirmLabel: "Delete player", destructive: true });
    if (!confirmed) return;
    
    try {
      await deleteDoc(doc(db, "groups", activeGroupId, "players", playerId));
      // Don't leave a member pointing at the deleted player.
      for (const m of members.filter((mm) => String(mm.playerId || "") === pid)) {
        try {
          await setDoc(doc(db, "groups", activeGroupId, "members", m.id), { playerId: deleteField() }, { merge: true });
        } catch (err) {
          console.error("Unlink member from deleted player failed:", err?.code, err?.message);
        }
      }
      logActivity("player_deleted", {
        playerName: displayName,
        changes: [`player deleted: ${displayName}`],
      });
      setSelectedPlayers(prev => prev.filter(id => id !== playerId));
    } catch (err) {
      console.error("Failed to delete player:", err?.code, err?.message);
      showAlert(`Could not delete player: ${String(err?.code || "permission-denied")}`);
    }
  }

  function togglePlayerSelection(playerId) {
    setSelectedPlayers(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  }

  function resetForm() {
    setLocation("");
    setGameDate(new Date().toISOString().slice(0, 10));
    setBuyIns({});
    setFirstWinnerId("");
    setSecondWinnerId("");
    setThirdWinnerId("");
    setJackpotWinnerId("");
    setIsSplitPot(false);
    setSplitWinnerId("");
    setJackpotCards([{ suit: "♠", rank: "A" }, { suit: "♠", rank: "K" }, { suit: "♠", rank: "Q" }, { suit: "♠", rank: "J" }, { suit: "♠", rank: "10" }]);
    setEditingId(null);
    setSelectedPlayers([]);
    setGameNotes("");
    setGameEditReason("");
  }

  // Jackpot cut shown on (and saved with) the manual game form: an edited game
  // keeps its own; a new one takes one buy-in from 1st place.
  const manualEditingGame = editingId ? games.find((g) => g.id === editingId) : null;
  const manualJackpotCut = manualEditingGame
    ? getJackpotCut(manualEditingGame)
    : jackpotCutForNewGame(
      inferBuyInFromTotals(Object.fromEntries(selectedPlayers.map((pid) => [pid, buyIns[pid]]))),
      sidePrizeConfig.enabled
    );

  async function saveGame() {
    if (!activeGroupId) return;
    if (selectedPlayers.length === 0) {
      setToastMessage("Select at least one player before saving.");
      return;
    }
    // Validate buy-ins: all must be at least 5
    for (let i = 0; i < selectedPlayers.length; ++i) {
      const playerId = selectedPlayers[i];
      if (Number(buyIns[playerId] || 0) < 5) {
        setToastMessage("Each selected player needs a buy-in of at least 5.");
        return;
      }
    }

    // NEW: prevent duplicate jackpot cards (same rank + suit)
    if (jackpotWinnerId) {
      const combos = jackpotCards.map(c => `${c.rank}-${c.suit}`);
      const uniq = new Set(combos);
      if (uniq.size !== combos.length) {
        setToastMessage("Jackpot cards must be unique. Change any duplicate card.");
        return;
      }
    }

    const id = editingId || null;
    const existing = id ? games.find((g) => g.id === id) : null;
    if (id && !existing) {
      setToastMessage("This game no longer exists.");
      return;
    }
    if (id && !canManageSingleGame(existing)) {
      setToastMessage("You can't change finished games in this group.");
      return;
    }
    if (!id && !canCreateGames) {
      setToastMessage("You do not have permission to add games.");
      return;
    }
    const payload = {
      date: gameDate,
      location: location || "",
      notes: gameNotes.trim() || "",
      buyIns: Object.fromEntries(selectedPlayers.map((playerId) => [playerId, Number(buyIns[playerId] || 0)])),
      // Only new games get a cut; an edit keeps the one the game was saved with.
      ...(existing ? {} : { jackpotCut: manualJackpotCut }),
      firstWinnerId: firstWinnerId || null,
      secondWinnerId: isSplitPot ? null : (secondWinnerId || null),
      thirdWinnerId: (!isSplitPot && thirdWinnerId) ? thirdWinnerId : null,
      splitWinnerId: (isSplitPot && splitWinnerId) ? splitWinnerId : null,
      jackpotWinnerId: jackpotWinnerId || null,
      jackpotCards: jackpotWinnerId ? jackpotCards.map((c) => `${c.rank}${c.suit}`).join(" ") : "",
      createdAt: existing?.createdAt || Date.now(),
      createdBy: existing?.createdBy || user?.uid || null,
      createdByName: existing?.createdByName || myMember?.profileName || user?.displayName || (user?.isAnonymous ? "Guest User" : "User"),
      ...editStamp(id ? gameEditReason.trim().slice(0, 500) : ""),
    };

    const getN = (pid) => {
      const player = players.find(p => p.id === pid);
      return player ? getPlayerPreferredName(player) : null;
    };
    const describePlayers = (gameLike) => Object.keys(gameLike?.buyIns || {}).map((pid) => getN(pid) || getPlayerName(pid) || pid).sort();
    const describeWinner = (pid) => getN(pid) || "none";
    const activityDetails = {
      gameId: id,
      gameDate,
      location: location || "",
      playerCount: selectedPlayers.length,
      firstWinnerName: getN(firstWinnerId) || null,
      secondWinnerName: isSplitPot ? (getN(splitWinnerId) || null) : (getN(secondWinnerId) || null),
      thirdWinnerName: (!isSplitPot && thirdWinnerId) ? (getN(thirdWinnerId) || null) : null,
    };
    if (id) {
      const previousPlayers = describePlayers(existing);
      const nextPlayers = describePlayers(payload);
      const nextChanges = [];
      if (String(existing?.date || "") !== String(payload.date || "")) nextChanges.push(`date: ${String(existing?.date || "none")} -> ${String(payload.date || "none")}`);
      if (String(existing?.location || "") !== String(payload.location || "")) nextChanges.push(`location: ${String(existing?.location || "none")} -> ${String(payload.location || "none")}`);
      if (String(existing?.notes || "") !== String(payload.notes || "")) nextChanges.push(`notes: ${String(existing?.notes || "none")} -> ${String(payload.notes || "none")}`);
      if (JSON.stringify(previousPlayers) !== JSON.stringify(nextPlayers)) nextChanges.push(`players: ${previousPlayers.join(", ") || "none"} -> ${nextPlayers.join(", ") || "none"}`);
      if (String(existing?.firstWinnerId || "") !== String(payload.firstWinnerId || "")) nextChanges.push(`1st place: ${describeWinner(existing?.firstWinnerId)} -> ${describeWinner(payload.firstWinnerId)}`);
      const prevSecondId = existing?.splitWinnerId || existing?.secondWinnerId || "";
      const nextSecondId = payload.splitWinnerId || payload.secondWinnerId || "";
      if (String(prevSecondId) !== String(nextSecondId)) nextChanges.push(`2nd place: ${describeWinner(prevSecondId)} -> ${describeWinner(nextSecondId)}`);
      if (String(existing?.thirdWinnerId || "") !== String(payload.thirdWinnerId || "")) nextChanges.push(`3rd place: ${describeWinner(existing?.thirdWinnerId)} -> ${describeWinner(payload.thirdWinnerId)}`);
      if (String(existing?.jackpotWinnerId || "") !== String(payload.jackpotWinnerId || "")) nextChanges.push(`jackpot: ${describeWinner(existing?.jackpotWinnerId)} -> ${describeWinner(payload.jackpotWinnerId)}`);
      await updateDoc(doc(db, "groups", activeGroupId, "games", id), payload);
      logActivity("game_updated", { ...activityDetails, changes: nextChanges });
    } else {
      const createdRef = await addDoc(collection(db, "groups", activeGroupId, "games"), payload);
      logActivity("game_created", {
        ...activityDetails,
        gameId: createdRef.id,
        changes: [
          `players: ${describePlayers(payload).join(", ") || "none"}`,
          `1st place: ${describeWinner(payload.firstWinnerId)}`,
          `2nd place: ${describeWinner(payload.splitWinnerId || payload.secondWinnerId)}`,
          ...(payload.thirdWinnerId ? [`3rd place: ${describeWinner(payload.thirdWinnerId)}`] : []),
          ...(payload.jackpotWinnerId ? [`jackpot: ${describeWinner(payload.jackpotWinnerId)}`] : []),
        ],
      });
    }
    if (payload.jackpotWinnerId) haptic([40, 80, 40]);
    if (!id) {
      setConfettiIntense(!!payload.jackpotWinnerId);
      setConfettiTrigger((t) => t + 1);
    }
    resetForm();
    setShowManualGameEntry(false);
    setActiveTab(id ? (editReturnTab || "sessions") : "sessions");
    setGameSavedMessage(id ? "Game updated successfully." : "Game saved successfully.");
    setToastType("success");
    setToastMessage(id ? "Game updated in the ledger" : "Game saved to the ledger");
  }

  function exportGamesCsv(source = filteredEnrichedGames) {
    const list = [...source].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    if (list.length === 0) {
      setToastMessage("No sessions to export in this range.");
      return;
    }
    const rows = [["Date", "Location", "Pot", "Player", "Buy-in", "Payout", "Net", "Place", "Jackpot"]];
    list.forEach((g) => {
      const payouts = computePayouts(g, jackpotSim.awards);
      Object.keys(g.buyIns || {}).forEach((pid) => {
        const buyIn = Number(g.buyIns[pid] || 0);
        const payout = Number(payouts[pid] || 0);
        const place = (g.firstWinnerId === pid || g.splitWinnerId === pid) ? "1st"
          : g.secondWinnerId === pid ? "2nd"
          : g.thirdWinnerId === pid ? "3rd" : "";
        rows.push([
          g.date || "",
          g.location || "",
          Number(g.pot || 0),
          getPlayerName(pid),
          buyIn,
          payout,
          Number((payout - buyIn).toFixed(2)),
          place,
          g.jackpotWinnerId === pid ? "Yes" : "",
        ]);
      });
    });
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const safeName = (groupMeta?.name || "tallypot").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "tallypot";
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}-sessions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setToastType("success");
    setToastMessage(`Exported ${list.length} session${list.length === 1 ? "" : "s"} to CSV`);
  }

  function editGame(g) {
    if (!canManageSingleGame(g)) {
      setToastMessage("You can't change finished games in this group.");
      return;
    }
    setEditReturnTab(activeTab);
    setShowManualGameEntry(true);
    setEditingId(g.id);
    setGameEditReason("");
    setGameDate(g.date || new Date().toISOString().slice(0,10));
    setLocation(g.location || "");
    setGameNotes(g.notes || "");
    setBuyIns({ ...(g.buyIns || {}) });
    setSelectedPlayers(Object.keys(g.buyIns || {}));
    setFirstWinnerId(g.firstWinnerId || "");
    setSecondWinnerId(g.secondWinnerId || "");
    setIsSplitPot(!!g.splitWinnerId);
    setSplitWinnerId(g.splitWinnerId || "");
    setJackpotWinnerId(g.jackpotWinnerId || "");
    if (g.jackpotCards) {
      const parts = String(g.jackpotCards).split(" ").slice(0, 5);
      const parsed = parts.map((s) => ({ suit: s.slice(-1), rank: s.slice(0, -1) }));
      if (parsed.length === 5) setJackpotCards(parsed);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteGame(id) {
    if (!activeGroupId) return;
    const target = games.find((g) => g.id === id);
    if (!canManageSingleGame(target)) {
      setToastMessage("You can't change finished games in this group.");
      return;
    }
    const confirmed = await showConfirm({ title: "Delete this game from the ledger?", message: "This can't be undone.", confirmLabel: "Delete game", destructive: true });
    if (!confirmed) return;
    const gameDate = target?.date || "";
    const gameLocation = target?.location || "";
    const gameRef = doc(db, "groups", activeGroupId, "games", id);
    // The rules allow the delete only after deletingBy names you.
    await updateDoc(gameRef, { deletingBy: user.uid, ...editStamp() });
    await deleteDoc(gameRef);
    logActivity("game_deleted", {
      gameDate,
      location: gameLocation,
      changes: [`session deleted: ${gameLocation || "No location"}${gameDate ? ` on ${formatGameDate(gameDate)}` : ""}`],
    });
    if (editingId === id) resetForm();
  }

  async function logActivity(type, details = {}) {
    if (!user || !activeGroupId) return;
    try {
      await addDoc(collection(db, "groups", activeGroupId, "activity"), {
        type,
        actorId: user.uid,
        actorName: myMember?.groupNickname || myMember?.profileName || user?.displayName || (user?.isAnonymous ? "Guest" : "User"),
        timestamp: Date.now(),
        ...details,
      });
    } catch (err) {
      console.error("Activity log failed:", err?.code, err?.message);
    }
  }

  const RANKS = ["A","K","Q","J","10","9","8","7","6","5","4","3","2"];
  const SUITS = ["♠","♥","♦","♣"];

  const getRankingPointsClass = useCallback((points) => {
    if (points >= 250) return 'profit-high';
    if (points >= 80) return 'profit-medium';
    if (points >= 0) return 'profit-low';
    return 'profit-negative';
  }, []);

  const getSuitStyle = (suit) => {
    if (suit === "♥" || suit === "♦") {
      return { color: '#dc2626' };
    }
    return { color: '#000000' };
  };

  const openRelatedGames = useCallback((title, gameIds) => {
    setRelatedGamesView({ title, gameIds: Array.from(new Set(gameIds || [])) });
  }, []);

  // Short date format for app list views
  const formatGameDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return shortDateFormatter.format(date);
  }, [shortDateFormatter]);

  const historyHighlights = useMemo(() => {
    if (!enrichedGamesSorted.length) return null;
    const totalPot = filteredEnrichedGames.reduce((sum, g) => sum + Number(g.pot || 0), 0);
    const biggestPot = filteredEnrichedGames.reduce((max, g) => Math.max(max, Number(g.pot || 0)), 0);
    const potsWithValue = filteredEnrichedGames.map(g => Number(g.pot || 0)).filter(p => p > 0);
    const lowestPot = potsWithValue.length > 0 ? Math.min(...potsWithValue) : 0;
    const averagePotVal = potsWithValue.length > 0 ? totalPot / potsWithValue.length : 0;
    return {
      totalPot,
      biggestPot,
      lowestPot,
      averagePotVal,
    };
  }, [filteredEnrichedGames, enrichedGamesSorted]);

  const formatActivityTimestamp = useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    // Older entries use the same date format as the rest of the app (region setting).
    const isoDay = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const time = date.toLocaleTimeString(appLocale, { hour: "2-digit", minute: "2-digit" });
    return `${formatGameDate(isoDay)}, ${time}`;
  }, [appLocale, formatGameDate]);

  const getActivityContent = useCallback((entry) => {
    const actorName = entry.actorName || "Someone";
    const changeList = Array.isArray(entry.changes) ? entry.changes.filter(Boolean) : [];
    switch (entry.type) {
      case "group_created":
        return {
          icon: <Plus size={16} />,
          title: `${actorName} created the group.`,
          changes: changeList,
        };
      case "game_created":
        return {
          icon: <Plus size={16} />,
          title: `${actorName} added a session${entry.location ? ` at ${entry.location}` : ""}${entry.gameDate ? ` on ${formatGameDate(entry.gameDate)}` : ""}.`,
          changes: changeList,
        };
      case "game_updated":
        return {
          icon: <Pencil size={16} />,
          title: `${actorName} edited a session${entry.location ? ` at ${entry.location}` : ""}${entry.gameDate ? ` on ${formatGameDate(entry.gameDate)}` : ""}.`,
          changes: changeList,
        };
      case "game_deleted":
        return {
          icon: <Trash2 size={16} />,
          title: `${actorName} deleted a session${entry.location ? ` at ${entry.location}` : ""}${entry.gameDate ? ` on ${formatGameDate(entry.gameDate)}` : ""}.`,
          changes: changeList,
        };
      case "live_discarded":
        return {
          icon: <Trash2 size={16} />,
          title: `${actorName} discarded a live game.`,
          changes: changeList,
        };
      case "player_created":
        return {
          icon: <Plus size={16} />,
          title: `${actorName} added player ${entry.playerName || "Unknown player"}.`,
          changes: changeList,
        };
      case "player_updated":
        return {
          icon: <Pencil size={16} />,
          title: `${actorName} updated ${entry.playerName || "a player"}.`,
          changes: changeList,
        };
      case "player_deleted":
        return {
          icon: <Trash2 size={16} />,
          title: `${actorName} removed player ${entry.playerName || "Unknown player"}.`,
          changes: changeList,
        };
      case "group_photo_updated":
        return {
          icon: <Pencil size={16} />,
          title: `${actorName} changed the group picture.`,
          changes: changeList,
        };
      case "group_updated":
        return {
          icon: <MessageSquare size={16} />,
          title: `${actorName} updated group info.`,
          changes: changeList,
        };
      case "member_updated":
        return {
          icon: <Users size={16} />,
          title: `${actorName} updated ${entry.memberName || "a member"}.`,
          changes: changeList,
        };
      case "settings_changed":
        return {
          icon: <MessageSquare size={16} />,
          title: `${actorName} changed ${entry.settingType || "group"} settings.`,
          changes: changeList,
        };
      default:
        return {
          icon: <History size={16} />,
          title: `${actorName} made a change.`,
          changes: changeList,
        };
    }
  }, [formatGameDate]);

  const bottomNavItems = useMemo(() => {
    const items = [
      { key: "home", label: "Home", icon: HomeIcon },
      { key: "ranking", label: "Ranking", icon: RankingIcon },
      ...(canCreateGames || liveGame ? [{ key: "new", label: "Game", icon: liveGame ? LiveGameIcon : NewGameIcon }] : []),
      { key: "sessions", label: "Sessions", icon: SessionsIcon },
      { key: "profile", label: "Profile", icon: ProfileIcon },
    ];
    return items;
  }, [canCreateGames, liveGame]);

  // Rendered on the onboarding screens too (not just inside a group), so errors
  // like "Invite code not found" and "You're no longer a member" are seen there.
  const toastNode = (
    <>
      {deletingGroupName && (
        <div className="group-deleting-overlay" role="status" aria-live="polite">
          <div className="group-deleting-box">Deleting {deletingGroupName}…</div>
        </div>
      )}
      {toastMessage ? <div className={`app-toast app-toast--${toastType}`} role="status" aria-live="polite">{toastMessage}</div> : null}
    </>
  );

  if (!authReady) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: "var(--primary-light, #34D399)", letterSpacing: "-0.03em" }}>{APP_NAME}</div>
          <div style={{ marginTop: 12, color: "var(--muted-foreground, #64748B)", fontSize: 14 }}>Loading…</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-hero">
          <img src={logo} alt="TallyPot logo" className="auth-hero-logo" />
          <div className="auth-hero-name">{APP_NAME}</div>
          <div className="auth-hero-tagline">A private poker ledger for your regular table.</div>
        </div>
        <div className="auth-body">
          {pendingInviteCode && (
            <div className="auth-invite-banner">
              <span>You've been invited to join a group.</span>
              <span className="auth-invite-banner-code">{pendingInviteGroupName || pendingInviteCode}</span>
              <span>Create an account to keep your history, or join as a guest below.</span>
            </div>
          )}
          <div className="auth-account-card">
            <div className="auth-mode-toggle" role="tablist" aria-label="Account mode">
              <button
                type="button"
                className={emailAuthMode === "signin" ? "active" : ""}
                onClick={() => { setEmailAuthMode("signin"); setEmailAuthError(""); setEmailResetSent(false); }}
              >
                Sign in
              </button>
              <button
                type="button"
                className={emailAuthMode === "signup" ? "active" : ""}
                onClick={() => { setEmailAuthMode("signup"); setEmailAuthError(""); setEmailResetSent(false); }}
              >
                Create account
              </button>
            </div>
            <form className="auth-email-form" onSubmit={handleEmailAuth}>
              {emailAuthMode === "signup" && (
                <label className="auth-field">
                  <span>Your name</span>
                  <input
                    className="input auth-input"
                    value={emailAuthName}
                    onChange={(e) => setEmailAuthName(e.target.value)}
                    placeholder="Name your table knows"
                    autoComplete="name"
                  />
                </label>
              )}
              <label className="auth-field">
                <span>Email address</span>
                <input
                  className="input auth-input"
                  type="email"
                  value={emailAuthEmail}
                  onChange={(e) => setEmailAuthEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  inputMode="email"
                />
              </label>
              <label className="auth-field">
                <span>Password</span>
                <input
                  className="input auth-input"
                  type="password"
                  value={emailAuthPassword}
                  onChange={(e) => setEmailAuthPassword(e.target.value)}
                  placeholder={emailAuthMode === "signup" ? "At least 6 characters" : "Your password"}
                  autoComplete={emailAuthMode === "signup" ? "new-password" : "current-password"}
                />
              </label>
              {emailAuthError && <div className="auth-inline-error">{emailAuthError}</div>}
              {emailResetSent && <div className="auth-inline-success">Password reset email sent.</div>}
              <button className="btn auth-primary-btn" type="submit" disabled={emailAuthLoading}>
                {emailAuthLoading ? "Working…" : emailAuthMode === "signup" ? "Create account" : "Sign in"}
              </button>
              {emailAuthMode === "signin" && (
                <button className="auth-text-btn" type="button" onClick={sendEmailPasswordReset}>
                  Forgot password?
                </button>
              )}
            </form>
          </div>

          <div className="auth-divider"><span>or continue with</span></div>
          <div className="auth-provider-row">
            <button className="btn btn-google" onClick={signInWithGoogle} disabled={googleSignInLoading}>
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {googleSignInLoading ? "Signing in…" : "Google"}
            </button>
            <TelegramLoginButton
              botName={process.env.REACT_APP_TELEGRAM_BOT_NAME || "TallyPotBot"}
              botId={process.env.REACT_APP_TELEGRAM_BOT_ID || "8779378012"}
              onAuth={handleTelegramAuth}
              loading={telegramSignInLoading}
            />
          </div>
          {(showGuestJoin || pendingInviteCode) ? (
            <div className="auth-guest-box">
              <p className="auth-guest-label">Just want to peek? Join as a guest viewer.</p>
              <input
                className="input"
                value={guestJoinCodeInput || pendingInviteCode}
                onChange={(e) => setGuestJoinCodeInput(e.target.value.toUpperCase())}
                placeholder="Enter invite code"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                name="guestInviteCode"
              />
              <button className="btn secondary" style={{ width: "100%", marginTop: 10 }} onClick={joinAsGuestViewer}>Join as guest viewer</button>
            </div>
          ) : (
            <button type="button" className="auth-text-btn auth-guest-link" onClick={() => setShowGuestJoin(true)}>
              Have an invite code? Join as guest
            </button>
          )}
          <div className="auth-legal">
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <span>·</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          </div>
        </div>
      </div>
    );
  }

  if (!activeGroupId) {
    // Groups for a signed-in account are loaded asynchronously. Until that
    // finishes, an empty myGroups does NOT mean "new user" — so show a loading
    // state rather than the new-user onboarding. This is what stops a returning
    // user (new device / fresh login) from being wrongly asked for an invite code.
    if (!user.isAnonymous && !groupsLoaded) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ textAlign: "center", maxWidth: 320 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: "var(--primary-light, #34D399)", letterSpacing: "-0.03em" }}>{APP_NAME}</div>
            <div style={{ marginTop: 12, color: "var(--muted-foreground, #64748B)", fontSize: 14 }}>Loading your groups…</div>
            {groupLoadSlow && (
              <div style={{ marginTop: 24 }}>
                <div style={{ color: "var(--muted-foreground, #64748B)", fontSize: 13 }}>
                  This is taking longer than usual. Check your connection.
                </div>
                {groupLoadError && (
                  <div style={{ marginTop: 10, fontSize: 11, color: "var(--destructive, #EF4444)", wordBreak: "break-word" }}>
                    {groupLoadError}
                  </div>
                )}
                <button className="btn" style={{ marginTop: 16, width: "100%" }} onClick={() => window.location.reload()}>Reload</button>
                <button className="btn secondary" style={{ marginTop: 8, width: "100%" }} onClick={signOutUser}>Sign out</button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Start a group / join with a code — shared by the first-run screen and
    // the group picker. Explains what each path means for someone new.
    const renderGroupChooser = ({ mode, name, setName, code, setCode, onCreate, onJoin, firstRun }) => (
      <div className="ob-chooser">
        <div className="ob-segmented" role="group" aria-label="Start or join a group">
          <button type="button" className={`ob-segment${mode === "create" ? " is-active" : ""}`} aria-pressed={mode === "create"} onClick={() => setGroupChooserMode("create")}>
            Start a group
          </button>
          <button type="button" className={`ob-segment${mode === "join" ? " is-active" : ""}`} aria-pressed={mode === "join"} onClick={() => setGroupChooserMode("join")}>
            Join a group
          </button>
        </div>

        {mode === "create" ? (
          <div className="ob-panel" key="create">
            <input
              className="ob-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Group name, e.g. Saturday Night"
              aria-label="Group name"
              maxLength={60}
            />
            {firstRun && (
              <ol className="ob-steps" aria-label="How a group works">
                <li className="ob-step">
                  <span className="ob-step-num" aria-hidden="true">1</span>
                  <span className="ob-step-text"><strong>Name your group.</strong> You'll be its moderator, so you can change everything.</span>
                </li>
                <li className="ob-step">
                  <span className="ob-step-num" aria-hidden="true">2</span>
                  <span className="ob-step-text"><strong>Add your players.</strong> Everyone at your table, even friends who don't have the app.</span>
                </li>
                <li className="ob-step">
                  <span className="ob-step-num" aria-hidden="true">3</span>
                  <span className="ob-step-text"><strong>Invite your friends.</strong> Send the invite link. When they join, they're linked to their player and see their own stats.</span>
                </li>
              </ol>
            )}
            <button type="button" className="btn ob-primary" onClick={onCreate} disabled={!name.trim()}>Create group</button>
          </div>
        ) : (
          <div className="ob-panel" key="join">
            <input
              className="ob-input ob-input--code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Invite code"
              aria-label="Invite code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <p className="ob-note">
              Ask your host for the invite link. Opening it fills in the code for you. You'll join as a viewer, and the host can let you edit games.
            </p>
            <button type="button" className="btn ob-primary" onClick={onJoin} disabled={!code.trim()}>Join group</button>
          </div>
        )}
      </div>
    );

    // Returning user who already has groups — show group picker directly.
    if (myGroups.length > 0) {
      const pickerMode = groupChooserMode || "join";
      return (
        <div className="ob-page">
          <div className="ob-topbar">
            <span className="ob-brand">
              <img src={logo} alt="" className="ob-brand-logo" />
              {APP_NAME}
            </span>
            <button type="button" className="ob-text-btn" onClick={signOutUser}>Sign out</button>
          </div>
          <h1 className="ob-title">Your groups</h1>
          <div className="ob-list">
            {myGroups.map((g) => (
              <button key={g.id} type="button" className="ob-group-row" onClick={() => setActiveGroupId(g.id)}>
                {g.photoURL ? (
                  <img src={g.photoURL} alt="" className="ob-group-photo" />
                ) : (
                  <span className="ob-group-photo ob-group-photo--icon" aria-hidden="true"><Users size={18} /></span>
                )}
                <span className="ob-group-name">{g.name || "Unnamed group"}</span>
                <ChevronRight size={18} className="ob-chevron" aria-hidden="true" />
              </button>
            ))}
          </div>

          <h2 className="ob-section-title">Add a group</h2>
          {user.isAnonymous ? (
            renderGroupChooser({ mode: "join", code: joinCodeInput, setCode: setJoinCodeInput, onJoin: joinGroup })
          ) : (
            renderGroupChooser({
              mode: pickerMode,
              name: groupNameInput,
              setName: setGroupNameInput,
              code: joinCodeInput,
              setCode: setJoinCodeInput,
              onCreate: createGroup,
              onJoin: joinGroup,
            })
          )}
          {toastNode}
        </div>
      );
    }

    // Guest (anonymous) with no group yet — invite-code-only join screen.
    if (user.isAnonymous) {
      const guestCode = wizardJoinCode || pendingInviteCode || "";
      return (
        <div className="wizard-page">
          <div className="wizard-step">
            <div className="wizard-icon-wrap">
              <div className="wizard-icon-circle"><Users size={44} /></div>
            </div>
            <h1 className="wizard-title">Join a poker ledger</h1>
            <p className="wizard-body">Paste the invite code from your host to view the group.</p>
            <div className="wizard-group-option wizard-group-option--join wizard-group-option--guest">
              <input
                className="input wizard-input"
                value={guestCode}
                onChange={(e) => setWizardJoinCode(e.target.value.toUpperCase())}
                placeholder="Paste invite code"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
              />
            </div>
            <button
              className="btn wizard-btn-primary"
              style={{ marginTop: 20 }}
              onClick={async () => {
                if (!guestCode.trim()) return;
                const ok = await joinGroupWithCode(user, guestCode.trim().toUpperCase(), "viewer");
                if (ok) setWizardJoinCode("");
              }}
              disabled={!guestCode.trim()}
            >
              Join as guest viewer
            </button>
            <button className="btn secondary" style={{ width: "100%", marginTop: 16, fontSize: 13 }} onClick={signOutUser}>
              <LogOut size={14} style={{ marginRight: 6 }} /> Sign out
            </button>
          </div>
          {toastNode}
        </div>
      );
    }

    // ── Brand-new signed-in user: one screen to confirm the name and get to a table ──
    // Name/photo are already known from sign-up (or the auth provider), so we
    // don't re-ask for them as separate steps — just confirm and pick a group.
    const onboardFirstName = splitNameParts(wizardName || user.displayName || "").firstName;
    const firstRunMode = groupChooserMode || (wizardJoinCode.trim() ? "join" : "create");
    const finishOnboarding = async (mode) => {
      if (!wizardName.trim()) return;
      if (mode === "join" && wizardJoinCode.trim()) await completeWizard("join", null, wizardJoinCode.trim());
      if (mode === "create" && wizardGroupName.trim()) await completeWizard("create", wizardGroupName.trim(), null);
    };
    return (
      <div className="ob-page">
        <div className="ob-topbar">
          <span className="ob-brand">
            <img src={logo} alt="" className="ob-brand-logo" />
            {APP_NAME}
          </span>
          <button type="button" className="ob-text-btn" onClick={signOutUser}>Sign out</button>
        </div>

        <div className="ob-hero">
          <input ref={wizardAvatarInputRef} type="file" accept="image/*" onChange={handleWizardAvatarFileChange} style={{ display: "none" }} />
          <button className="wizard-avatar-circle" type="button" onClick={() => wizardAvatarInputRef.current?.click()} aria-label="Add profile photo">
            <AvatarCircle
              src={wizardPhotoPreviewURL || user?.photoURL || ""}
              alt="Your photo"
              fallbackLabel={wizardName || user?.displayName || "Player"}
              size={72}
            />
            <span className="wizard-avatar-plus" aria-hidden="true"><Camera size={14} /></span>
          </button>
          {wizardPhotoPreviewURL && (
            <button className="ob-text-btn ob-text-btn--small" type="button" onClick={clearWizardPhotoDraft}>Remove photo</button>
          )}
          <h1 className="ob-title ob-title--center">Welcome{onboardFirstName ? `, ${onboardFirstName}` : ""}</h1>
          <p className="ob-lede">TallyPot keeps the books for your home game: buy-ins, results, the jackpot and who owes whom.</p>
        </div>

        <h2 className="ob-section-title">Your name</h2>
        <input
          className="ob-input"
          value={wizardName}
          onChange={(e) => setWizardName(e.target.value)}
          placeholder="Your display name"
          aria-label="Your display name"
          autoComplete="name"
        />
        <p className="ob-note">This is how you'll show up in your groups.</p>

        <h2 className="ob-section-title">Get to a table</h2>
        {renderGroupChooser({
          mode: firstRunMode,
          name: wizardGroupName,
          setName: setWizardGroupName,
          code: wizardJoinCode,
          setCode: setWizardJoinCode,
          onCreate: () => finishOnboarding("create"),
          onJoin: () => finishOnboarding("join"),
          firstRun: true,
        })}
        {!wizardName.trim() && <p className="ob-note ob-note--warn">Add your name to continue.</p>}
        {toastNode}
      </div>
    );
  }

  const standardFilterKeys = ["1w", "1m", "2m", "3m", "6m", "1y"];
  const groupRepeatPeriodKey = (() => {
    const key = normalizeGroupDefaultPeriod(groupMeta?.defaultPeriod);
    return key !== "all" && !standardFilterKeys.includes(key) ? key : null;
  })();
  const filterPresets = [
    { key: "all", label: "All Time" },
    // The group's own tournament period, when it isn't one of the standard ones.
    ...(groupRepeatPeriodKey ? [{ key: groupRepeatPeriodKey, label: `This period (${formatPeriodRange(calendarPeriodRange(groupRepeatPeriodKey))})` }] : []),
    ...standardFilterKeys.map(key => ({ key, label: calendarPeriodLabel(key) })),
    // The current year is "This Year" above.
    ...availableYears.filter(y => y !== new Date().getFullYear()).map(y => ({ key: String(y), label: String(y) })),
    { key: "custom", label: "Custom" },
  ];

  const activeFilterPreset = filterPresets.find((p) => p.key === dateFilterPreset);
  const fmtFilterDate = (v) => (v ? new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "…");
  const activeFilterLabel = dateFilterPreset === "custom"
    ? (dateFilterStart || dateFilterEnd ? `${fmtFilterDate(dateFilterStart)} – ${fmtFilterDate(dateFilterEnd)}` : "Custom")
    : activeFilterPreset?.label || "All Time";
  const isDateFiltered = dateFilterPreset !== "all";

  // One "Filter" button; the periods live in a bottom sheet so the screen
  // doesn't carry a row of tabs.
  // compact = icon-only (Home header); a dot marks an active filter.
  const renderDateFilter = (compact = false) => (
    <div className={`date-filter-section${compact ? " date-filter-section--compact" : ""}`}>
      <button
        type="button"
        className={`date-filter-btn${compact ? " date-filter-btn--icon" : ""}${isDateFiltered ? " active" : ""}`}
        aria-haspopup="dialog"
        aria-label={compact ? `Filter: ${activeFilterLabel}` : undefined}
        onClick={() => setDateFilterSheetOpen(true)}
      >
        <SlidersHorizontal size={compact ? 18 : 15} />
        {!compact && <span>Filter</span>}
        {!compact && isDateFiltered && <span className="date-filter-btn-value">{activeFilterLabel}</span>}
      </button>
      {dateFilterSheetOpen && createPortal(
        <div className="tp-sheet-overlay date-filter-overlay" onClick={() => setDateFilterSheetOpen(false)}>
          <div className="date-filter-sheet" role="dialog" aria-label="Filter by time period" onClick={(e) => e.stopPropagation()}>
            <div className="date-filter-sheet-header">
              <span className="date-filter-sheet-title">Time period</span>
              <button type="button" className="date-filter-sheet-done" onClick={() => setDateFilterSheetOpen(false)}>Done</button>
            </div>
            <div className="ios-list date-filter-options">
              {filterPresets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`date-filter-option${dateFilterPreset === p.key ? " active" : ""}`}
                  aria-pressed={dateFilterPreset === p.key}
                  onClick={() => {
                    setDateFilterPreset(p.key);
                    if (p.key !== "custom") setDateFilterSheetOpen(false);
                  }}
                >
                  <span>{p.label}</span>
                  {dateFilterPreset === p.key && <Check size={18} />}
                </button>
              ))}
            </div>
            {dateFilterPreset === "custom" && (
              <div className="date-filter-custom-row">
                <label className="date-filter-custom-field">
                  <span>From</span>
                  <input className="input" type="date" value={dateFilterStart} onChange={e => setDateFilterStart(e.target.value)} />
                </label>
                <label className="date-filter-custom-field">
                  <span>To</span>
                  <input className="input" type="date" value={dateFilterEnd} onChange={e => setDateFilterEnd(e.target.value)} />
                </label>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="container"
      onTouchStart={handlePullTouchStart}
      onTouchMove={handlePullTouchMove}
      onTouchEnd={handlePullTouchEnd}
      onTouchCancel={handlePullTouchEnd}
    >
      {(pullDistance > 0 || pullRefreshing) && (
        <div
          className={`pull-refresh-indicator${pullActiveRef.current ? " is-dragging" : ""}`}
          aria-hidden="true"
          style={{
            transform: `translate3d(-50%, ${pullRefreshing ? 0 : -56 + Math.min(pullDistance / PULL_REFRESH_THRESHOLD, 1) * 56}px, 0) scale(${pullRefreshing ? 1 : 0.86 + Math.min(pullDistance / PULL_REFRESH_THRESHOLD, 1) * 0.14})`,
            opacity: pullRefreshing ? 1 : Math.min(pullDistance / 40, 1),
          }}
        >
          <div className={`pull-refresh-icon-wrap${pullDistance >= PULL_REFRESH_THRESHOLD ? ' ready' : ''}${pullRefreshing ? ' refreshing' : ''}`}>
            <svg
              className="pull-refresh-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: pullRefreshing ? undefined : `rotate(${Math.min(pullDistance / PULL_REFRESH_MAX_DISTANCE, 1) * 360}deg)` }}
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
              <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
            </svg>
          </div>
        </div>
      )}
      <div
        className="app-shell"
        style={{
          transform: (pullDistance > 0 || pullRefreshing)
            ? `translate3d(0, ${pullRefreshing ? 44 : Math.min(pullDistance * 0.38, 44)}px, 0)`
            : undefined,
          transition: pullActiveRef.current ? "none" : undefined,
          willChange: (pullDistance > 0 || pullRefreshing) ? "transform" : undefined,
        }}
      >
      {/* ══════════════════════════════════════════
           HOME TAB
           ══════════════════════════════════════════ */}
      {activeTab === "home" && (
        <>
          <div className="home-group-header">
            <img
              className={`app-logo-small${((groupMeta ? groupMeta.photoURL : cachedGroupPhotoURL)) ? " app-logo-group-photo" : ""}`}
              src={(groupMeta ? groupMeta.photoURL : cachedGroupPhotoURL) || logo}
              alt="Group logo"
            />
            <span className="home-group-name">{groupMeta?.name || "Group"}</span>
            {renderDateFilter(true)}
          </div>
          {canManageSettings && gamesReady && games.length === 0 && !setupHidden.includes(activeGroupId) && (
            <GroupSetupCard
              groupName={groupMeta?.name}
              playerCount={rosterPlayers.length}
              memberCount={members.filter((m) => !m.isGuest).length}
              gameCount={liveGame ? 1 : 0}
              onAddPlayers={() => { setShowPlayerManager(true); setShowAddPlayerForm(rosterPlayers.length < 2); }}
              onInvite={shareInviteLink}
              onStartGame={() => setActiveTab("new")}
              onDismiss={() => setSetupHidden((prev) => {
                const next = [...prev.filter((id) => id !== activeGroupId), activeGroupId].slice(-20);
                try { localStorage.setItem("tp-setup-hidden", JSON.stringify(next)); } catch (_) {}
                return next;
              })}
            />
          )}
          {liveGame && (
            <LiveLedgerCard
              liveGame={liveGame}
              getName={getPlayerName}
              formatMoney={(n) => formatCurrency(n, liveGame.currency)}
              formatClock={formatElapsed}
              onOpen={() => setActiveTab("new")}
            />
          )}
          {sectionVis.home_jackpot !== false && sidePrizeConfig.enabled && (
            <JackpotOrbit
              balance={jackpotBalance}
              record={biggestJackpot}
              history={jackpotHistory}
              gamesSinceHit={jackpotRun.gamesSinceHit}
              perNight={jackpotRun.perNight}
              lastWinnerName={lastJackpotEvent ? getPlayerName(lastJackpotEvent.id) || lastJackpotEvent.name : ""}
              getAvatar={getPlayerAvatar}
              formatMoney={(n) => euro(n)}
              onOpen={jackpotHistory.length > 0 ? () => setJackpotHistoryOpen(true) : null}
              onFlick={openTrainer}
            />
          )}
          {sectionVis.home_chart !== false && topMovers.length > 0 && (
            <div className="home-movers">
              <div className="ios-section-head"><span>Top movers</span></div>
              <div className="movers-row">
                {topMovers.map((m) => (
                  <button
                    type="button"
                    key={m.key}
                    className="mover-card"
                    onClick={() => (m.gameId ? setInfoGameId(m.gameId) : setPlayerProfileId(m.pid))}
                  >
                    <span className="mover-kind">{m.label}</span>
                    <span className="mover-name">{m.name}</span>
                    {(() => {
                      const text = `${m.value > 0 ? "+" : ""}${euro(m.value)}`;
                      // Four-digit amounts step down a size so they fit a third of the screen.
                      return <span className={`mover-value ${m.value > 0 ? "pl-pos" : m.value < 0 ? "pl-neg" : ""}${text.length > 7 ? " mover-value--long" : ""}`}>{text}</span>;
                    })()}
                    <span className="mover-when">{m.gameId ? formatGameDate(m.date) : `last ${m.nights} ${m.nights === 1 ? "night" : "nights"}`}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {sectionVis.home_stats !== false && statsTable.length > 0 && (
            <div className="home-watchlist">
              <div className="ios-section-head">
                <span>Profit / loss</span>
                <button type="button" onClick={() => setActiveTab("ranking")}>Ranking</button>
              </div>
              <div className="ios-list">
                {statsTable.map((r) => {
                  const isMe = r.id === myPlayerId;
                  const nights = playerNightsById[r.id] || [];
                  let running = 0;
                  const series = [0, ...nights.map((n) => (running += n.net))];
                  return (
                    <button type="button" key={r.id} className={`ios-row watch-row${isMe ? " ios-row--me" : ""}`} onClick={() => setPlayerProfileId(r.id)}>
                      <AvatarCircle src={getPlayerAvatar(r.id)} alt={r.name} fallbackLabel={r.name} size={32} />
                      <span className="ios-row-main">
                        <span className="ios-row-title">
                          {r.name}
                          {isMe && <span className="you-tag">You</span>}
                          {(playerBadgeIconsById[r.id] || []).slice(0, 3).map((b) => (
                            <span key={b.id} className="watch-badge" aria-hidden="true">{makeBadgeSVG(b.glyph, 14, false, b.accent)}</span>
                          ))}
                        </span>
                        <span className="ios-row-sub">
                          {nights.length} {nights.length === 1 ? "game" : "games"}
                          {rankingById[r.id] ? ` · ${rankingById[r.id].points} pts` : ""}
                        </span>
                      </span>
                      <Sparkline values={series} />
                      <MoneyPill value={r.profit} euro={euro} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "ranking" && (
        <>
          <div className="tab-title-header">
            <h1 className="tab-title">Ranking</h1>
            {renderDateFilter()}
          </div>
          {rankingRowsVisible.length === 0 ? (
            <div className="leaderboard-note" style={{ marginTop: 40, textAlign: "center" }}>No ranking data yet. Log a session to get started.</div>
          ) : (
            <>
              <p className="ranking-legend">
                <span className="form-dot form-dot-win" /> Up
                <span className="form-dot form-dot-loss" /> Down
                <span className="ranking-legend-note">Last 5 games, newest on the right</span>
              </p>
              <div className="ios-list ranking-list">
                {rankingRowsVisible.map((row) => {
                  const isMe = row.id === myPlayerId;
                  const formDots = row.recentEvents.slice(0, 5).reverse();
                  return (
                    <div key={row.id} className={`ranking-item${isMe ? " ios-row--me" : ""}${row.rank === 1 ? " ranking-item--lead" : ""}`}>
                      <span className="ios-row-rank">{row.rank}</span>
                      <button type="button" className="ranking-item-avatar" onClick={() => setPlayerProfileId(row.id)} aria-label={`Open ${row.name}'s profile`}>
                        <AvatarCircle src={getPlayerAvatar(row.id)} alt={row.name} fallbackLabel={row.name} size={34} />
                      </button>
                      <div className="ranking-item-main">
                        <div className="ranking-item-top">
                          <button type="button" className="ranking-item-name" onClick={() => setPlayerProfileId(row.id)}>
                            {row.name}{isMe && <span className="you-tag">You</span>}
                          </button>
                          <MoneyPill value={row.profit} euro={euro} />
                        </div>
                        <div className="ranking-item-meta">
                          {formDots.length > 0 && (
                            <span className="form-guide" role="img" aria-label={`Last ${formDots.length} games: ${formDots.map((ev) => (ev.net > 0 ? "up" : ev.net < 0 ? "down" : "even")).join(", ")}`}>
                              {formDots.map((ev, i) => (
                                <span key={i} className={`form-dot ${ev.net > 0 ? "form-dot-win" : ev.net < 0 ? "form-dot-loss" : "form-dot-even"}`} />
                              ))}
                            </span>
                          )}
                          <span className="ranking-item-pts">{row.points} pts</span>
                        </div>
                        <div className="ranking-item-badges">
                          <button type="button" className="ranking-item-count ranking-item-count--first" disabled={!row.firsts} onClick={() => openRelatedGames(`${row.name} - 1st Place Games`, row.firstGameIds)}>{row.firsts}× 1st</button>
                          <button type="button" className="ranking-item-count ranking-item-count--second" disabled={!row.seconds} onClick={() => openRelatedGames(`${row.name} - 2nd Place Games`, row.secondGameIds)}>{row.seconds}× 2nd</button>
                          {row.jackpots > 0 && (
                            <button type="button" className="ranking-item-count ranking-item-count--jp" onClick={() => openRelatedGames(`${row.name} - Jackpot Games`, row.jackpotGameIds)}>{row.jackpots}× JP</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="ranking-explainer">
                <p className="ranking-explainer-text">
                  Points go up when you win money and down when you lose it. Finishing 1st or 2nd adds a bonus, and so does a winning streak. Rebuys cost a few points, and no single night can move your score too far.
                </p>
                <button
                  type="button"
                  className="info-toggle-btn"
                  aria-expanded={rankingInfoOpen}
                  aria-label={rankingInfoOpen ? "Hide the scoring formula" : "Show the scoring formula"}
                  onClick={() => setRankingInfoOpen((v) => !v)}
                >
                  <Info size={16} />
                </button>
              </div>
              {rankingInfoOpen && (
                <div className="ranking-explainer-formula">
                  Score = profit × {rankingConfig.netMultiplier} + 1st place {rankingConfig.firstPlaceBonus} / 2nd place {rankingConfig.secondPlaceBonus} + streak bonus − rebuy penalty ({rankingConfig.rebuyPenalty} each). Each night counts between {rankingConfig.gameCapMin} and {rankingConfig.gameCapMax} points.
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "sessions" && (
        <>
          <div className="tab-title-header">
            <h1 className="tab-title">Sessions</h1>
            {sessionsSubtab === "history" && renderDateFilter()}
          </div>
          <div className="chart-mode-toggle sessions-subtabs">
            <button
              className={`chart-mode-btn${sessionsSubtab === "history" ? " active" : ""}`}
              onClick={() => setSessionsSubtab("history")}
            >
              History
            </button>
            <button
              className={`chart-mode-btn${sessionsSubtab === "activity" ? " active" : ""}`}
              onClick={() => setSessionsSubtab("activity")}
            >
              Activity
            </button>
          </div>
          {sessionsSubtab === "history" ? (
            <>
              {sectionVis.sessions_highlights !== false && historyHighlights && (
                <div className="history-highlights">
                  <div className="history-card"><div className="history-label">Games</div><div className="history-value">{filteredEnrichedGames.length}</div></div>
                  <div className="history-card"><div className="history-label">Total pot</div><div className="history-value">{euro(historyHighlights.totalPot)}</div></div>
                  <div className="history-card"><div className="history-label">Biggest pot</div><div className="history-value">{euro(historyHighlights.biggestPot)}</div></div>
                  <div className="history-card"><div className="history-label">Smallest pot</div><div className="history-value">{euro(historyHighlights.lowestPot)}</div></div>
                </div>
              )}
              {historyRowsVisible.length === 0 ? (
                <div className="ledger-empty-state">
                  <div className="ledger-empty-title">No sessions in this ledger yet</div>
                  <div className="ledger-empty-copy">Start a live game or log a completed one to build the money trail.</div>
                  {canCreateGames && (
                    <button className="btn" onClick={() => setActiveTab("new")}>Start live game mode</button>
                  )}
                </div>
              ) : (
                <>
                <div className="sessions-export-row">
                  <button className="sessions-export-btn" onClick={() => exportGamesCsv()}>
                    <FileText size={15} />
                    Export CSV
                  </button>
                </div>
                <div className={`game-feed-list${canCreateGames && !liveGame ? " game-feed-list--fab" : ""}`}>
                  {historyRowsVisible.map((g) => (
                    <GameRow key={g.id} g={g} formatGameDate={formatGameDate} getPlayerName={getPlayerName} getPlayerAvatar={getPlayerAvatar} euro={euro} setInfoGameId={setInfoGameId} jackpotAward={jackpotSim.awards[g.id]} />
                  ))}
                </div>
                </>
              )}
            </>
          ) : (
            <div className="activity-feed">
              {activityEntries.length === 0 ? (
                <div className="ledger-empty-state">
                  <div className="ledger-empty-title">No changes recorded</div>
                  <div className="ledger-empty-copy">Edits, joins, and saved games will appear here for auditability.</div>
                </div>
              ) : (
                <div className="activity-list">
                  {activityEntries.map((entry) => <ActivityItem key={entry.id} entry={entry} games={games} groupRole={groupRole} getActivityContent={getActivityContent} formatActivityTimestamp={formatActivityTimestamp} setInfoGameId={setInfoGameId} />)}
                </div>
              )}
            </div>
          )}

        </>
      )}

      {/* ══════════════════════════════════════════
           GAME TAB — setup / live tracker / end flow
           ══════════════════════════════════════════ */}
      {activeTab === "new" && !canCreateGames && !liveGame && (
        <div className="card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Access restricted</div>
          <div className="muted" style={{ fontSize: 14 }}>Only editors and moderators can add or edit games.</div>
        </div>
      )}
      {activeTab === "new" && (canCreateGames || liveGame) && (
        <div key={liveGame ? (liveEndFlow ? "end-flow" : "tracker") : "setup"} className="lg-tab-view">

          {!liveGame ? (
            /* ── SETUP ──────────────────────────────── */
            <div className="lg-setup">
              <section className="game-night-setup-hero">
                <h1 className="game-night-setup-title">Set the table ledger</h1>
                <p className="game-night-setup-copy">Choose the buy-in and seated players once. Rebuys and stacks are tracked from the live table.</p>
              </section>
              <div className="ns-section-label">Stakes</div>
              <div className="ns-detail-list">
                <div className="ns-detail-row">
                  <span className="ns-detail-label">Buy-in</span>
                  <input
                    className="ns-detail-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="5"
                    value={liveSetupBuyIn}
                    onChange={(e) => setLiveSetupBuyIn(e.target.value)}
                    placeholder={groupMeta?.defaultBuyIn ? String(groupMeta.defaultBuyIn) : "20"}
                  />
                  <span style={{ color: "var(--muted-foreground)", fontSize: 13, flexShrink: 0 }}>{activeCurrencyCode}</span>
                </div>
                <div className="ns-detail-row">
                  <span className="ns-detail-label">Table</span>
                  <input
                    className="ns-detail-input"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Kitchen table, club, or host"
                  />
                </div>
              </div>

              <div className="ns-section-header">
                <div>
                  <div className="ns-section-label ns-section-label--tight">Players</div>
                  <div className="ns-section-helper">{selectedPlayers.length ? `${selectedPlayers.length} selected` : "Tap everyone sitting in"}</div>
                </div>
                {canAddPlayers && !showInlineAddPlayer && (
                  <button className="ns-add-top-btn" onClick={() => setShowInlineAddPlayer(true)}>+ Add</button>
                )}
              </div>
              <div className="ns-players-wrap ns-players-grid">
                {rosterPlayers.map((player) => (
                  <div
                    key={player.id}
                    className={`ns-player-chip ${selectedPlayers.includes(player.id) ? "selected" : ""}`}
                    onClick={() => togglePlayerSelection(player.id)}
                  >
                    <AvatarCircle src={getPlayerAvatar(player.id)} alt={getPlayerPreferredName(player)} fallbackLabel={getPlayerPreferredName(player)} size={30} />
                    <span className="ns-player-name">{getPlayerPreferredName(player)}</span>
                    {selectedPlayers.includes(player.id) && <span className="ns-player-check">✓</span>}
                  </div>
                ))}
              </div>

              {canAddPlayers && showInlineAddPlayer && (
                <div className="ns-add-player-form">
                  <div className="ns-add-player-form-fields">
                    <input
                      className="ns-add-player-input"
                      value={quickPlayerName}
                      onChange={(e) => setQuickPlayerName(e.target.value)}
                      placeholder="Name"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter" && quickPlayerName.trim()) { addPlayerFromNewGame(); setShowInlineAddPlayer(false); setQuickPlayerEmail(""); } if (e.key === "Escape") { setShowInlineAddPlayer(false); setQuickPlayerName(""); setQuickPlayerEmail(""); } }}
                    />
                    {canManagePlayers && (
                      <input
                        className="ns-add-player-input"
                        value={quickPlayerEmail}
                        onChange={(e) => setQuickPlayerEmail(e.target.value)}
                        placeholder="Email (Optional)"
                        type="email"
                        inputMode="email"
                      />
                    )}
                  </div>
                  <div className="ns-add-player-form-actions">
                    <button className="ns-add-player-submit" disabled={!quickPlayerName.trim()} onClick={() => { addPlayerFromNewGame(); setShowInlineAddPlayer(false); setQuickPlayerEmail(""); }}>Add Player</button>
                    <button className="ns-add-player-cancel" onClick={() => { setShowInlineAddPlayer(false); setQuickPlayerName(""); setQuickPlayerEmail(""); }}>Cancel</button>
                  </div>
                </div>
              )}

              <button
                className="ns-live-start-btn"
                style={{ marginTop: 24, opacity: (!selectedPlayers.length || !parseFloat(liveSetupBuyIn)) ? 0.45 : 1 }}
                disabled={!selectedPlayers.length || !parseFloat(liveSetupBuyIn)}
                onClick={() => startLiveGame(selectedPlayers)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>
                {selectedPlayers.length ? "Open live ledger" : "Select players to start"}
              </button>
            </div>

          ) : !liveEndFlow ? (
            /* ── TRACKER ────────────────────────────── */
            <>
              <div className="lg-header lg-header--sticky">
                <div className="lg-header-center">
                  <span className="lg-header-title">Live game</span>
                  <span className={liveGame.status === "paused" ? "lg-paused-badge" : "lg-live-badge"}>
                    {liveGame.status === "paused" ? "PAUSED" : "● LIVE"}
                  </span>
                </div>
                {canEditGames && (
                  <div className="lg-header-actions">
                    <button className="lg-action-btn lg-action-pause" onClick={livePauseResume} aria-label={liveGame.status === "paused" ? "Resume" : "Pause"}>
                      {liveGame.status === "paused"
                        ? <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                        : <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>}
                      <span>{liveGame.status === "paused" ? "Resume" : "Pause"}</span>
                    </button>
                    <button className="lg-action-btn lg-action-end" onClick={openEndGame}>
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/><line x1="21" y1="12" x2="3" y2="12" /></svg>
                      <span>End</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="lg-body">
                {/* Hero: game clock + total pot in one card */}
                {(() => {
                  // Preview with the exact math the saved game will use (enrichGame),
                  // so the split shown here matches Save Results and the ledger.
                  const buyInUnit = liveGame.buyInAmount || 0;
                  const preview = enrichGame({
                    buyIns: Object.fromEntries((liveGame.playerIds || []).map((id) => [id, (liveGame.buyIns?.[id] || 0) * buyInUnit])),
                    jackpotCut: jackpotCutForNewGame(buyInUnit, sidePrizeConfig.enabled),
                    thirdWinnerId: thirdPlaceEnabled ? "preview" : null,
                  });
                  const { pot, jpCut } = preview;
                  const firstNet = preview.firstGross - jpCut;
                  const showThird = thirdPlaceEnabled;
                  return (
                    <section className={`lg-pot-card${liveGame.status === "paused" ? " lg-pot-card--paused" : ""}`}>
                      <div className="lg-pot-top">
                        <span className={`lg-pot-status${liveGame.status === "paused" ? " lg-pot-status--paused" : ""}`}>
                          <span className="lg-pot-status-dot" />
                          {liveGame.status === "paused" ? "Paused" : "Running"} · <LiveElapsed liveGame={liveGame} format={formatElapsed} />
                        </span>
                        <span className="lg-pot-meta">
                          Started {liveGame.startedAt ? new Date(liveGame.startedAt).toLocaleTimeString(appLocale, { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                        </span>
                      </div>
                      <div className="lg-pot-hero">
                        <span className="lg-pot-label">Pot · {(liveGame.playerIds || []).length} players · {formatCurrency(buyInUnit, liveGame.currency)} buy-in</span>
                        <span className="lg-pot-value">{formatCurrency(pot, liveGame.currency)}</span>
                      </div>
                      {pot > 0 && (
                        <div className="lg-pot-splits">
                          <div><span>1st</span><b>{formatCurrency(firstNet, liveGame.currency)}</b></div>
                          <div><span>2nd</span><b>{formatCurrency(preview.secondGross, liveGame.currency)}</b></div>
                          {showThird && <div><span>3rd</span><b>{formatCurrency(preview.thirdGross, liveGame.currency)}</b></div>}
                          {jpCut > 0 && <div className="lg-pot-split--jackpot"><span>Jackpot</span><b>+{formatCurrency(jpCut, liveGame.currency)}</b></div>}
                        </div>
                      )}
                      {pot > 0 && jpCut > 0 && (
                        <div className="lg-pot-note">
                          1st place gets {formatCurrency(preview.firstGross, liveGame.currency)} − {formatCurrency(jpCut, liveGame.currency)} for the jackpot.
                        </div>
                      )}
                    </section>
                  );
                })()}

                {/* Player rows */}
                <div className="lg-players-head">
                  <div className="ios-section-head">
                    <span>Players</span>
                    <span className="lg-players-count">{(liveGame.playerIds || []).length} seated</span>
                  </div>
                  {canEditGames && (
                    <p className="lg-players-hint">Swipe right to add a buy-in, left to remove one. Tap a name to manage.</p>
                  )}
                </div>
                <div className="lg-players">
                  {[...(liveGame.playerIds || [])]
                    .sort((a, b) => {
                      // Most buy-ins first; out players sink to the bottom
                      const outA = liveGame.isOut?.[a] ? 1 : 0;
                      const outB = liveGame.isOut?.[b] ? 1 : 0;
                      if (outA !== outB) return outA - outB;
                      return (liveGame.buyIns?.[b] || 0) - (liveGame.buyIns?.[a] || 0);
                    })
                    .map(pid => {
                    const player = players.find(p => p.id === pid);
                    const name = player?.name || player?.firstName || pid;
                    const buyInCount = liveGame.buyIns?.[pid] || 0;
                    const totalInvested = buyInCount * (liveGame.buyInAmount || 0);
                    const chips = liveGame.chipCounts?.[pid];
                    const pl = chips !== null && chips !== undefined ? chips - totalInvested : null;
                    const isOut = liveGame.isOut?.[pid] || false;
                    const swipeDx = lgSwipeState.pid === pid ? lgSwipeState.dx : 0;
                    const swipeAdd = swipeDx > 0;
                    const swipeRemove = swipeDx < 0 && buyInCount > 1;
                    const swipeArmed = lgSwipeState.pid === pid && lgSwipeState.armed && (swipeAdd || swipeRemove);
                    const isDragging = lgSwipeState.pid === pid && lgSwipeState.active;
                    // Card follows the finger, dampened; a blocked remove (only 1
                    // buy-in left) barely budges so the gesture reads as refused.
                    const dragDx = swipeDx > 0
                      ? Math.min(84, swipeDx * 0.38)
                      : swipeRemove
                        ? Math.max(-84, swipeDx * 0.38)
                        : Math.max(-14, swipeDx * 0.1);
                    const rebuyFeedbackType = lgRebuyFeedback.pid === pid ? lgRebuyFeedback.type : null;
                    return (
                      <div
                        key={pid}
                        className={`lg-player-shell${canEditGames ? " lg-player-shell--tappable" : ""}${swipeAdd ? " lg-player-shell--add" : swipeRemove ? " lg-player-shell--remove" : ""}${swipeArmed ? " lg-player-shell--armed" : ""}`}
                        role={canEditGames ? "button" : undefined}
                        tabIndex={canEditGames ? 0 : undefined}
                        aria-label={canEditGames ? `Manage ${name}` : undefined}
                        onKeyDown={canEditGames ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLgPlayerSheet(pid); } } : undefined}
                        onPointerDown={canEditGames ? (e) => beginLiveBuyInSwipe(e, pid) : undefined}
                        onPointerMove={canEditGames ? (e) => updateLiveBuyInSwipe(e, pid) : undefined}
                        onPointerUp={canEditGames ? (e) => finishLiveBuyInSwipe(e, pid, buyInCount) : undefined}
                        onPointerCancel={canEditGames ? cancelLiveBuyInSwipe : undefined}
                        onLostPointerCapture={canEditGames ? cancelLiveBuyInSwipe : undefined}
                      >
                        {canEditGames && (
                          <>
                            <div className="lg-swipe-under lg-swipe-under--add" aria-hidden="true">
                              <span className="lg-swipe-under-label">+1 buy-in</span>
                            </div>
                            <div className="lg-swipe-under lg-swipe-under--remove" aria-hidden="true">
                              <span className="lg-swipe-under-label">−1 buy-in</span>
                            </div>
                          </>
                        )}
                        <div
                          className={`lg-player-row${isOut ? " out" : ""}${rebuyFeedbackType ? ` lg-player-row--rebuy-${rebuyFeedbackType}` : ""}${swipeAdd ? " lg-player-row--swiping-add" : swipeRemove ? " lg-player-row--swiping-remove" : ""}${isDragging ? " lg-player-row--dragging" : ""}`}
                          style={dragDx ? { transform: `translateX(${dragDx}px)` } : undefined}
                        >
                          <div className="lg-player-main">
                            <div className="lg-player-identity">
                              <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={36} />
                              <div className="lg-player-namebox">
                                <div className="lg-player-name">{name}{isOut && <span className="lg-out-tag">OUT</span>}</div>
                                <div className="lg-player-sub">{buyInCount} buy-in{buyInCount === 1 ? "" : "s"}</div>
                              </div>
                            </div>
                            <div className="lg-player-buyin" aria-label={`${name}: ${buyInCount} buy-in${buyInCount === 1 ? "" : "s"}, ${formatCurrency(totalInvested, liveGame.currency)} total`}>
                              <span className="lg-buyin-total">{formatCurrency(totalInvested, liveGame.currency)}</span>
                              {(chips !== null && chips !== undefined) && (
                                <span className="lg-stack-line">
                                  Stack {formatCurrency(chips, liveGame.currency)}
                                  {pl !== null && (
                                    <span className={`lg-pl${pl >= 0 ? " pos" : " neg"}`}>{pl >= 0 ? "+" : ""}{formatCurrency(pl, liveGame.currency)}</span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Player control sheet: tap a card to adjust buy-ins, stack, out.
                    Portaled to <body> — the animated tab view's transform would
                    otherwise re-anchor the fixed overlay and push it off-screen. */}
                {lgPlayerSheetPid && canEditGames && typeof document !== "undefined" && (() => {
                  const pid = lgPlayerSheetPid;
                  if (!(liveGame.playerIds || []).includes(pid)) return null;
                  const player = players.find(p => p.id === pid);
                  const name = player?.name || player?.firstName || pid;
                  const buyInCount = liveGame.buyIns?.[pid] || 0;
                  const totalInvested = buyInCount * (liveGame.buyInAmount || 0);
                  const chips = liveGame.chipCounts?.[pid];
                  const pl = chips !== null && chips !== undefined ? chips - totalInvested : null;
                  const isOut = liveGame.isOut?.[pid] || false;
                  const closeSheet = () => setLgPlayerSheetPid(null);
                  const onOverlayClick = () => {
                    if (Date.now() - lgPlayerSheetOpenedAtRef.current < 350) return;
                    closeSheet();
                  };
                  return createPortal(
                    <div className="tp-sheet-overlay" onClick={onOverlayClick}>
                      <div className="lg-player-sheet" role="dialog" aria-label={`Manage ${name}`} onClick={e => e.stopPropagation()}>
                        <div className="lg-sheet-header">
                          <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={42} />
                          <div className="lg-sheet-title">
                            <span className="lg-sheet-name">{name}{isOut && <span className="lg-out-tag">OUT</span>}</span>
                            <span className="lg-sheet-sub">
                              {formatCurrency(totalInvested, liveGame.currency)} in play
                              {pl !== null && <span className={`lg-pl${pl >= 0 ? " pos" : " neg"}`}> · {pl >= 0 ? "+" : ""}{formatCurrency(pl, liveGame.currency)}</span>}
                            </span>
                          </div>
                        </div>
                        <div className="lg-sheet-row">
                          <span className="lg-sheet-label">Buy-ins</span>
                          <div className="lg-sheet-stepper">
                            <button
                              className="lg-sheet-step lg-sheet-step--minus"
                              disabled={buyInCount <= 1}
                              aria-label={`Remove one buy-in from ${name}`}
                              onClick={() => liveRemoveBuyIn(pid)}
                            >−</button>
                            <span className="lg-sheet-count">
                              {buyInCount}×
                              <span className="lg-sheet-count-amt">{formatCurrency(totalInvested, liveGame.currency)}</span>
                            </span>
                            <button
                              className="lg-sheet-step lg-sheet-step--plus"
                              aria-label={`Add one buy-in for ${name}`}
                              onClick={() => liveAddBuyIn(pid)}
                            >＋</button>
                          </div>
                        </div>
                        <div className="lg-sheet-row">
                          <span className="lg-sheet-label">Stack</span>
                          <input
                            className="input lg-sheet-stack-input"
                            type="number"
                            inputMode="decimal"
                            placeholder="Chip count"
                            value={chipInputDraft}
                            onChange={e => setChipInputDraft(e.target.value)}
                            onBlur={() => liveSetChipCount(pid, chipInputDraft)}
                            onKeyDown={e => { if (e.key === "Enter") { liveSetChipCount(pid, chipInputDraft); e.currentTarget.blur(); } }}
                          />
                        </div>
                        <button
                          className={`tp-sheet-btn lg-sheet-out-btn${isOut ? " lg-sheet-out-btn--in" : ""}`}
                          onClick={() => liveToggleOut(pid)}
                        >
                          <LogOut size={16} /> {isOut ? "Back in the game" : "Out of the game"}
                        </button>
                        <button className="tp-sheet-btn tp-sheet-cancel" onClick={closeSheet}>Done</button>
                      </div>
                    </div>,
                    document.body
                  );
                })()}

                {/* Add player to ongoing game */}
                {canEditGames && (() => {
                  const activePids = new Set(liveGame.playerIds || []);
                  const available = players.filter(p => !activePids.has(p.id) && !p.removedFromGroup);
                  if (!available.length && !canAddPlayers) return null;
                  return (
                    <div className="lg-add-player-section">
                      <button
                        className="lg-add-player-toggle"
                        onClick={() => { setShowAddPlayerToLive(o => !o); setLiveNewPlayerOpen(false); setLiveNewPlayerName(""); }}
                      >
                        <Plus size={15} />
                        Add player
                        <ChevronDown size={15} style={{ marginLeft: "auto", transform: showAddPlayerToLive ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                      </button>
                      {showAddPlayerToLive && (
                        <div className="lg-add-player-body">
                          <div className="lg-add-player-grid">
                            {available.map(p => (
                              <button
                                key={p.id}
                                className="lg-add-player-chip"
                                onClick={() => { liveAddPlayerToGame(p.id); setShowAddPlayerToLive(false); }}
                              >
                                <AvatarCircle src={getPlayerAvatar(p.id)} alt={getPlayerPreferredName(p)} fallbackLabel={getPlayerPreferredName(p)} size={24} />
                                {getPlayerPreferredName(p)}
                              </button>
                            ))}
                            {canAddPlayers && !liveNewPlayerOpen && (
                              <button
                                className="lg-add-player-chip lg-add-player-chip--new"
                                onClick={() => { setLiveNewPlayerName(""); setLiveNewPlayerOpen(true); }}
                              >
                                <Plus size={14} />
                                New player
                              </button>
                            )}
                          </div>
                          {liveNewPlayerOpen && (
                            <div className="lg-new-player-row">
                              <input
                                className="input"
                                autoFocus
                                value={liveNewPlayerName}
                                onChange={(e) => setLiveNewPlayerName(e.target.value)}
                                placeholder="Player name"
                                onKeyDown={(e) => { if (e.key === "Enter" && liveNewPlayerName.trim()) liveCreateAndSeatPlayer(); }}
                              />
                              <button className="btn" disabled={!liveNewPlayerName.trim()} onClick={liveCreateAndSeatPlayer}>Add</button>
                              <button className="btn secondary" onClick={() => { setLiveNewPlayerOpen(false); setLiveNewPlayerName(""); }}>Cancel</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Jackpot section */}
                {canEditGames && (
                  <div className="lg-jackpot-section">
                    <button className="lg-jackpot-toggle" onClick={() => {
                      if (!liveJackpotOpen) {
                        setLiveJackpotWinnerId(liveGame.jackpot?.winnerId || "");
                        // Prefill with the recorded amount, else the running jackpot
                        // balance; the pen button unlocks it for manual edits.
                        const prefill = liveGame.jackpot?.amount > 0
                          ? liveGame.jackpot.amount
                          : Math.round(((jackpotBalance || 0) + Number.EPSILON) * 100) / 100;
                        setLiveJackpotAmount(prefill > 0 ? String(prefill) : "");
                        setLiveJackpotAmountLocked(prefill > 0);
                        if (liveGame.jackpot?.cards) setLiveJackpotCardSlots(parseJackpotCards(liveGame.jackpot.cards));
                      }
                      setLiveJackpotOpen(o => !o);
                    }}>
                      🏅 {liveGame.jackpot ? "Edit jackpot" : "Record jackpot"}
                      <svg style={{ transform: liveJackpotOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {liveJackpotOpen && (
                      <div className="lg-jackpot-form">
                        <label className="input-label">Winner</label>
                        {renderJackpotWinnerSelect()}
                        <label className="input-label">Amount ({liveGame.currency})</label>
                        {liveJackpotAmountLocked ? (
                          <div className="lg-jackpot-amount-row">
                            <span className="lg-jackpot-amount-value">{formatCurrency(parseFloat(liveJackpotAmount) || 0, liveGame.currency)}</span>
                            {!(liveGame.jackpot?.amount > 0) && <span className="lg-jackpot-amount-note">current jackpot</span>}
                            <button type="button" className="lg-jackpot-amount-edit" aria-label="Edit amount" onClick={() => setLiveJackpotAmountLocked(false)}>
                              <Pencil size={14} />
                            </button>
                          </div>
                        ) : (
                          <input className="input" type="number" inputMode="decimal" autoFocus value={liveJackpotAmount} onChange={e => setLiveJackpotAmount(e.target.value)} placeholder="0" style={{ marginBottom: 12 }} />
                        )}
                        <label className="input-label">Cards</label>
                        {renderJackpotCardPicker()}
                        <button className="btn" style={{ width: "100%" }} onClick={liveSaveJackpot}>Save Jackpot</button>
                      </div>
                    )}
                    {liveGame.jackpot && !liveJackpotOpen && (
                      <div className="lg-jackpot-preview">
                        <span className="lg-jackpot-player">
                          <AvatarCircle
                            src={getPlayerAvatar(liveGame.jackpot.winnerId)}
                            alt={getPlayerName(liveGame.jackpot.winnerId)}
                            fallbackLabel={getPlayerName(liveGame.jackpot.winnerId) || liveGame.jackpot.winnerId || "Player"}
                            size={24}
                          />
                          🏆 {getPlayerName(liveGame.jackpot.winnerId) || liveGame.jackpot.winnerId || "—"}
                        </span>
                        {liveGame.jackpot.amount > 0 && <span>{formatCurrency(liveGame.jackpot.amount, liveGame.currency)}</span>}
                        {liveGame.jackpot.cards && <span className="lg-jackpot-cards">{liveGame.jackpot.cards}</span>}
                      </div>
                    )}
                  </div>
                )}

                {canEditGames && (
                  <button type="button" className="tl-open-btn" onClick={openLiveTimelineEdit}>
                    <Pencil size={16} aria-hidden="true" /> Edit timeline
                  </button>
                )}

                {liveGame.gameId && (
                  <>
                    <div className="ios-section-head" style={{ marginTop: 8, paddingBottom: 0 }}>
                      <span>Change log</span>
                    </div>
                    <SessionLog
                      entries={liveGameLog}
                      getName={getPlayerName}
                      formatMoney={(n) => formatCurrency(n, liveGame.currency)}
                      emptyText="Every buy-in, rebuy and change at this table shows up here, with who made it."
                    />
                  </>
                )}

                {canEditGames && (
                  <button className="btn-signout" style={{ marginTop: 16, color: "var(--danger, #ef4444)", borderColor: "var(--danger, #ef4444)" }} onClick={discardLiveGame}>
                    Discard game
                  </button>
                )}
              </div>
            </>

          ) : (
            /* ── END FLOW ───────────────────────────── */
            <>
              <div className="lg-header lg-header--sticky">
                <button className="gs-back-btn" onClick={() => setLiveEndFlow(false)}>
                  <ChevronLeft size={18} />Back
                </button>
                <div className="lg-header-center">
                  <span style={{ fontSize: 15, fontWeight: 700 }}>Save Results</span>
                </div>
                <div style={{ width: 60 }} />
              </div>
              <div className="lg-body">
                <div className="lg-end-summary">
                  <span>Duration: <LiveElapsed liveGame={liveGame} format={formatDurationLabel} /></span>
                  <span>Pot: {formatCurrency(liveEndPreview.pot, liveGame.currency)}</span>
                </div>
                {(() => {
                  const unpaid = liveEndFirstId ? liveEndPreview.pot - liveEndPreview.jpTaken - Object.values(liveEndPayouts).reduce((a, b) => a + b, 0) : 0;
                  const hasMismatch = Math.abs(unpaid) > 0.01;
                  return (
                    <div className={`lg-save-review${hasMismatch ? " lg-save-review--warning" : ""}`}>
                      <div className="lg-save-review-title">Review before saving</div>
                      <div className="lg-save-review-copy">
                        {hasMismatch
                          ? `${formatCurrency(unpaid, liveGame.currency)} of the pot isn't paid to anyone. Pick a 2nd place.`
                          : `Confirm winners. Payouts use the group's split${liveEndPreview.jpTaken > 0 ? `, with ${formatCurrency(liveEndPreview.jpTaken, liveGame.currency)} from 1st place going to the jackpot` : ""}. The saved result updates the ledger immediately.`}
                      </div>
                    </div>
                  );
                })()}

                <label className="input-label">1st place</label>
                {renderLivePlacePicker("1st place", liveEndFirstId, setLiveEndFirstId, [liveEndSecondId, liveEndThirdId], false)}
                <div className="lg-save-payout">
                  Payout {formatCurrency(liveEndPreview.firstGross - liveEndPreview.jpCut, liveGame.currency)}
                  {liveEndPreview.jpCut > 0 && (
                    <span className="lg-save-payout-cut"> · {formatCurrency(liveEndPreview.firstGross, liveGame.currency)} − {formatCurrency(liveEndPreview.jpCut, liveGame.currency)} to the jackpot</span>
                  )}
                </div>

                <label className="input-label">2nd place <span className="input-label-hint">optional</span></label>
                {renderLivePlacePicker("2nd place", liveEndSecondId, setLiveEndSecondId, [liveEndFirstId, liveEndThirdId], true)}
                <div className="lg-save-payout">Payout {formatCurrency(liveEndPreview.secondGross, liveGame.currency)}</div>

                {thirdPlaceEnabled && (
                  <>
                    <label className="input-label">3rd place <span className="input-label-hint">optional</span></label>
                    {renderLivePlacePicker("3rd place", liveEndThirdId, setLiveEndThirdId, [liveEndFirstId, liveEndSecondId], true)}
                    <div className="lg-save-payout">{liveEndThirdId ? `Payout ${formatCurrency(liveEndPreview.thirdGross, liveGame.currency)}` : "Pick a 3rd place to split 50 / 30 / 20"}</div>
                  </>
                )}

                {(sidePrizeConfig.enabled || liveGame.jackpot) && (
                  <>
                    <label className="input-label">Jackpot <span className="input-label-hint">optional</span></label>
                    <div className="lg-save-jackpot">
                      <div className="gs-row">
                        <span className="gs-row-label">
                          Jackpot hit this game
                          <span className="lg-save-jackpot-sub">Current jackpot {formatCurrency(Math.round(jackpotBalance || 0), liveGame.currency)}</span>
                        </span>
                        <button
                          type="button"
                          className={`profile-toggle${liveEndJackpotOn ? " on" : ""}`}
                          onClick={() => setLiveEndJackpotOn(on => !on)}
                          role="switch"
                          aria-checked={liveEndJackpotOn}
                          aria-label="Jackpot hit this game"
                        >
                          <div className="profile-toggle-thumb" />
                        </button>
                      </div>
                      {liveEndJackpotOn && (
                        <div className="lg-save-jackpot-form">
                          <label className="input-label">Winner</label>
                          {renderJackpotWinnerSelect()}
                          <label className="input-label">Cards</label>
                          {renderJackpotCardPicker()}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <label className="input-label">Notes <span className="input-label-hint">optional</span></label>
                <input className="input" value={liveEndNotes} onChange={e => setLiveEndNotes(e.target.value)} placeholder="Any notes about the game" style={{ marginBottom: 20 }} />

                {/* No width: 100% — .lg-body children stretch and get side margins. */}
                <button className="btn" disabled={!liveEndFirstId || (liveEndJackpotOn && !liveJackpotWinnerId)} onClick={saveLiveGameToHistory}>
                  {!liveEndFirstId ? "Pick 1st place to save" : liveEndJackpotOn && !liveJackpotWinnerId ? "Pick the jackpot winner to save" : "Save Game"}
                </button>
              </div>
            </>
          )}

        </div>
      )}

      {/* ══════════════════════════════════════════
           PROFILE TAB
           ══════════════════════════════════════════ */}
      {activeTab === "profile" && (
        <div className="profile-screen">
          <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarFileChange} style={{ display: "none" }} />
          <input ref={groupPhotoInputRef} type="file" accept="image/*" onChange={handleGroupPhotoFileChange} style={{ display: "none" }} />

          {/* Header */}
          <div className="profile-screen-header">
            <h1 className="profile-screen-title">Profile</h1>
            {!user?.isAnonymous && !editingProfile && (
              <button className="profile-edit-btn" onClick={() => setEditingProfile(true)}>Edit</button>
            )}
          </div>

          {/* Avatar + name */}
          <div className="profile-hero">
            <div className="profile-hero-avatar" onClick={!user?.isAnonymous && editingProfile ? openAvatarPicker : undefined} style={{ cursor: !user?.isAnonymous && editingProfile ? "pointer" : "default" }}>
              <AvatarCircle
                src={
                  editingProfile
                    ? (photoPreviewURL || (photoSourceDraft === "google" ? (currentUserProfileData?.googleProviderPhotoURL || "") : photoSourceDraft === "generated" || photoSourceDraft === "delete" ? "" : (currentUserProfileData?.profilePhotoURL || "")))
                    : (currentUserProfileData?.profilePhotoURL || "")
                }
                alt="Profile"
                fallbackLabel={user?.isAnonymous ? "G" : (currentUserProfileData?.profileName || user?.displayName || "User")}
                size={80}
              />
              {!user?.isAnonymous && editingProfile && (
                <div className="profile-hero-camera-badge">{avatarUploadLoading ? "…" : <Pencil size={12} />}</div>
              )}
            </div>
            <div className="profile-hero-name">{user?.isAnonymous ? "Guest User" : (currentUserProfileData?.profileName || user?.displayName || "Player")}</div>
            <div className="profile-hero-sub">Member since {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}</div>
          </div>

          {/* Photo prompt — shown once until the user opens the avatar picker */}
          {!user?.isAnonymous && !editingProfile && currentUserProfileData?.photoSource === "generated" && !userProfile?.photoPromptSeen && (
            <div className="photo-prompt-card">
              <div className="photo-prompt-body">
                <Camera size={20} className="photo-prompt-icon" />
                <p className="photo-prompt-text">Add a profile photo so group members can recognise you</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                <button className="btn" style={{ width: "100%" }} onClick={() => { markPhotoPromptSeen(); avatarInputRef.current?.click(); }}>
                  Upload photo
                </button>
                {currentUserProfileData?.googleProviderPhotoURL && (
                  <button className="btn secondary" style={{ width: "100%" }} onClick={() => { markPhotoPromptSeen(); handleSelectGooglePhoto(); }}>
                    Use Google photo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Edit profile inline */}
          {editingProfile && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 16 }}>Edit account profile</h3>
              <label className="input-label">First name</label>
              <input className="input" value={profileFirstNameDraft} onChange={(e) => setProfileFirstNameDraft(e.target.value)} placeholder="First name" style={{ marginBottom: 12 }} />
              <label className="input-label">Last name</label>
              <input className="input" value={profileLastNameDraft} onChange={(e) => setProfileLastNameDraft(e.target.value)} placeholder="Last name" style={{ marginBottom: 12 }} />
              <label className="input-label">Nickname</label>
              <input className="input" value={profileNicknameDraft} onChange={(e) => setProfileNicknameDraft(e.target.value)} placeholder="Nickname" style={{ marginBottom: 12 }} />
              <label className="input-label">Email address</label>
              <input className="input" type="email" value={profileEmailDraft} onChange={(e) => setProfileEmailDraft(e.target.value)} placeholder="Email address" style={{ marginBottom: 12 }} />
              <div className="row" style={{ gap: 10 }}>
                <button className="btn" style={{ flex: 1 }} onClick={saveMyProfile}>Save</button>
                <button className="btn secondary" style={{ flex: 1 }} onClick={() => { clearPhotoDrafts(); setEditingProfile(false); }}>Cancel</button>
              </div>
              <button
                className="btn secondary"
                style={{ width: "100%", marginTop: 12, color: "var(--danger, #ef4444)", borderColor: "var(--danger, #ef4444)" }}
                onClick={() => setConfirmDeleteAccount(true)}
              >
                Delete account
              </button>
            </div>
          )}

          {/* 3-stat cards */}
          {(() => {
            const cgSessions = crossGroupStats?.sessions ?? 0;
            const cgPL = crossGroupStats?.pl ?? 0;
            const cgWins = crossGroupStats?.wins ?? 0;
            const winRate = cgSessions > 0 ? Math.round((cgWins / cgSessions) * 100) : null;
            return (
              <div className="profile-stat-cards">
                <div className="profile-stat-card">
                  <div className="profile-stat-card-label">Sessions</div>
                  <div className="profile-stat-card-value">{cgSessions || "—"}</div>
                </div>
                <div className="profile-stat-card">
                  <div className="profile-stat-card-label">All-time P&amp;L</div>
                  <div className={`profile-stat-card-value ${cgPL > 0 ? "pl-pos" : cgPL < 0 ? "pl-neg" : ""}`}>
                    {crossGroupStats ? `${cgPL > 0 ? "+" : cgPL < 0 ? "-" : ""}${formatCompactCurrency(Math.abs(cgPL))}` : "—"}
                  </div>
                </div>
                <div className="profile-stat-card">
                  <div className="profile-stat-card-label">Win rate</div>
                  <div className="profile-stat-card-value">{winRate !== null ? `${winRate}%` : "—"}</div>
                </div>
              </div>
            );
          })()}

          {/* My Groups */}
          <div className="profile-section-label">MY GROUPS</div>
          <div className="profile-groups-card">
            {(() => {
              const displayGroups = myGroups.some(g => g.id === activeGroupId) || !activeGroupId || !groupMeta
                ? myGroups
                : [...myGroups, { id: activeGroupId, name: groupMeta.name, photoURL: groupMeta.photoURL }];
              return displayGroups.length === 0 ? (
              <div className="profile-groups-empty">No groups yet. Create or join one below.</div>
            ) : (
              <div className="profile-groups-list">
                {[...displayGroups].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })).map((g) => {
                  const isActive = g.id === activeGroupId;
                  const hasProfit = Object.prototype.hasOwnProperty.call(groupProfitById, g.id);
                  const pl = hasProfit ? groupProfitById[g.id] : null;
                  const groupCurrency = String(g.currency || "EUR").toUpperCase();
                  return (
                    <div key={g.id} className={`profile-group-row${isActive ? " active" : ""}`}>
                      <button
                        className="profile-group-row-main"
                        onClick={() => {
                          if (isActive) return;
                          if (!activeGroupId) { setActiveGroupId(g.id); return; }
                          showConfirm({
                            title: `Switch to ${g.name || "this group"}?`,
                            message: "Home, rankings and sessions will show this group instead.",
                            confirmLabel: "Switch group",
                          }).then((ok) => { if (ok) setActiveGroupId(g.id); });
                        }}
                      >
                        <div className="profile-group-icon">
                          <img src={g.photoURL || logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.9 }} />
                        </div>
                        <div className="profile-group-info">
                          <div className="profile-group-name">{g.name || g.id}</div>
                          {isActive && <div className="profile-group-sub">Active group</div>}
                        </div>
                        <div className={`profile-group-pl ${pl !== null && pl > 0 ? "pl-pos" : pl !== null && pl < 0 ? "pl-neg" : "pl-neutral"}`}>
                          {pl !== null ? `${pl > 0 ? "+" : ""}${formatCurrency(pl, groupCurrency)}` : "—"}
                        </div>
                      </button>
                      <button
                        className="profile-group-row-arrow"
                        onClick={() => {
                          if (!isActive) {
                            preSettingsActiveGroupIdRef.current = activeGroupId;
                            setPendingGroupSettingsOpen(true);
                            setActiveGroupId(g.id);
                            return;
                          }
                          setShowGroupSettings(true);
                        }}
                        aria-label="Group settings"
                      >
                        <ChevronRight size={20} strokeWidth={2.4} />
                      </button>
                    </div>
                  );
                })}
              </div>
            );})()}
          </div>

          <div className="profile-groups-action-row">
            {!user?.isAnonymous && (
              <button className="profile-groups-action-card profile-groups-action-card--new" onClick={() => setSettingsPanel(settingsPanel === "groups" ? null : "groups")}>
                <div className="profile-groups-action-icon profile-groups-action-icon--new"><Plus size={18} /></div>
                <span>New group</span>
              </button>
            )}
            <button className="profile-groups-action-card profile-groups-action-card--join" onClick={() => { setJoinCodeInput(""); setSettingsPanel(settingsPanel === "joinGroup" ? null : "joinGroup"); }}>
              <div className="profile-groups-action-icon profile-groups-action-icon--join"><LogIn size={17} /></div>
              <span>Join group</span>
            </button>
          </div>
          {settingsPanel === "groups" && (
            <div className="profile-inline-panel">
              <input className="input" value={groupNameInput} onChange={(e) => setGroupNameInput(e.target.value)} placeholder="Group name" autoFocus onKeyDown={(e) => { if (e.key === "Enter" && groupNameInput.trim()) { createGroup(); setSettingsPanel(null); } }} />
              <button className="btn" disabled={!groupNameInput.trim()} onClick={() => { createGroup(); setSettingsPanel(null); }}>Create</button>
              <button className="btn secondary" onClick={() => setSettingsPanel(null)}>Cancel</button>
            </div>
          )}
          {settingsPanel === "joinGroup" && (
            <div className="profile-inline-panel">
              <input className="input" value={joinCodeInput} onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())} placeholder="Enter invite code" autoCapitalize="characters" spellCheck={false} onKeyDown={(e) => { if (e.key === "Enter" && joinCodeInput.trim()) { joinGroup(); setSettingsPanel(null); } }} />
              <button className="btn" disabled={!joinCodeInput.trim()} onClick={() => { joinGroup(); setSettingsPanel(null); }}>Join</button>
              <button className="btn secondary" onClick={() => setSettingsPanel(null)}>Cancel</button>
            </div>
          )}

          <>
              <div className="profile-section-label">ACCOUNT</div>
              <div className="profile-prefs-list">
                <button
                  className="profile-pref-row"
                  type="button"
                  onClick={() => {
                    const opening = settingsPanel !== "account";
                    setSettingsPanel(opening ? "account" : null);
                    if (opening) {
                      setAccountEmailDraft(String(user?.email || userProfile?.email || profileEmailDraft || "").trim().toLowerCase());
                      setAccountPasswordDraft("");
                      setAccountMessage("");
                      setAccountError("");
                      setShowChangePassword(false);
                      setCurrentPasswordDraft("");
                      setNewPasswordDraft("");
                      setConfirmPasswordDraft("");
                    }
                  }}
                >
                  <div className="profile-pref-icon profile-pref-icon--account"><ShieldCheck size={16} /></div>
                  <div className="profile-pref-label">
                    {user?.isAnonymous ? "Create an account" : "Sign-in methods"}
                    <div className="profile-pref-subtext">
                      {user?.isAnonymous
                        ? "Keep your guest session permanently"
                        : [
                            userHasProvider("password") && "Email password",
                            userHasProvider("google.com") && "Google",
                            userHasTelegramProvider() && "Telegram",
                          ].filter(Boolean).join(" · ") || "Not set up"}
                    </div>
                  </div>
                  {!user?.isAnonymous && userHasProvider("password") && user?.email && !user.emailVerified && (
                    <span className="profile-pref-badge profile-pref-badge--warn">Verify email</span>
                  )}
                  <ChevronRight size={16} style={{ color: "var(--muted-foreground)" }} />
                </button>
              </div>
              {settingsPanel === "account" && (
                <div className="profile-page-overlay">
                  <div className="profile-page">
                    <div className="profile-page-topbar">
                      <button className="profile-page-back" aria-label="Back" onClick={() => setSettingsPanel(null)}>
                        <ArrowLeft size={20} />
                      </button>
                      <span className="profile-page-topbar-name">{user?.isAnonymous ? "Create an account" : "Sign-in methods"}</span>
                    </div>
                    <div className="profile-page-content profile-settings-content">
                      <div className="profile-inline-panel account-settings-panel">
                  {!user?.isAnonymous && (
                    <div className="account-status-list">
                      {[
                        { id: "password", label: "Email & password", connected: userHasProvider("password") },
                        { id: "google.com", label: "Google", connected: userHasProvider("google.com") },
                        { id: "telegram", label: "Telegram", connected: userHasTelegramProvider() },
                      ].map((method) => (
                        <div key={method.id} className="account-status-row">
                          <span>{method.label}</span>
                          <span className={`account-status-badge${method.connected ? " account-status-badge--on" : ""}`}>
                            {method.connected ? "Connected" : "Not connected"}
                          </span>
                        </div>
                      ))}
                      {user?.email && (
                        <div className="account-status-row">
                          <span>Email verification</span>
                          <span className={`account-status-badge${user.emailVerified ? " account-status-badge--on" : ""}`}>
                            {user.emailVerified ? "Verified" : "Not verified"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {accountError && <div className="auth-inline-error">{accountError}</div>}
                  {accountMessage && <div className="auth-inline-success">{accountMessage}</div>}

                  {user?.email && !user.emailVerified && (
                    <button className="btn secondary" onClick={sendCurrentUserVerificationEmail} disabled={accountLoadingAction === "verify-email"}>
                      {accountLoadingAction === "verify-email" ? "Sending…" : "Send verification email"}
                    </button>
                  )}

                  {!userHasProvider("google.com") && (
                    <button className="btn account-connect-btn btn-google" onClick={connectGoogleProvider} disabled={accountLoadingAction === "google"}>
                      <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {accountLoadingAction === "google" ? "Connecting…" : "Connect Google"}
                    </button>
                  )}

                  {!userHasTelegramProvider() && (
                    <TelegramLoginButton
                      botName={process.env.REACT_APP_TELEGRAM_BOT_NAME || "TallyPotBot"}
                      botId={process.env.REACT_APP_TELEGRAM_BOT_ID || "8779378012"}
                      onAuth={connectTelegramProvider}
                      loading={accountLoadingAction === "telegram"}
                      label="Connect Telegram"
                      loadingLabel="Connecting…"
                    />
                  )}

                  {!userHasProvider("password") ? (
                    <div className="account-password-box">
                      <label className="input-label">Email address</label>
                      <input
                        className="input"
                        type="email"
                        value={accountEmailDraft}
                        onChange={(e) => setAccountEmailDraft(e.target.value)}
                        placeholder="Email address"
                        autoComplete="email"
                      />
                      <label className="input-label">Password</label>
                      <input
                        className="input"
                        type="password"
                        value={accountPasswordDraft}
                        onChange={(e) => setAccountPasswordDraft(e.target.value)}
                        placeholder="Create password"
                        autoComplete="new-password"
                      />
                      <button className="btn" onClick={addPasswordProvider} disabled={accountLoadingAction === "password"}>
                        {accountLoadingAction === "password" ? "Connecting…" : "Add password"}
                      </button>
                    </div>
                  ) : !showChangePassword ? (
                    <button
                      className="btn secondary"
                      onClick={() => {
                        setAccountError("");
                        setAccountMessage("");
                        setCurrentPasswordDraft("");
                        setNewPasswordDraft("");
                        setConfirmPasswordDraft("");
                        setShowChangePassword(true);
                      }}
                    >
                      Change password
                    </button>
                  ) : (
                    <div className="account-password-box">
                      <label className="input-label">Current password</label>
                      <input
                        className="input"
                        type="password"
                        value={currentPasswordDraft}
                        onChange={(e) => setCurrentPasswordDraft(e.target.value)}
                        placeholder="Current password"
                        autoComplete="current-password"
                      />
                      <label className="input-label">New password</label>
                      <input
                        className="input"
                        type="password"
                        value={newPasswordDraft}
                        onChange={(e) => setNewPasswordDraft(e.target.value)}
                        placeholder="At least 6 characters"
                        autoComplete="new-password"
                      />
                      <label className="input-label">Confirm new password</label>
                      <input
                        className="input"
                        type="password"
                        value={confirmPasswordDraft}
                        onChange={(e) => setConfirmPasswordDraft(e.target.value)}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                      />
                      <button className="btn" onClick={changeUserPassword} disabled={accountLoadingAction === "change-password"}>
                        {accountLoadingAction === "change-password" ? "Updating…" : "Update password"}
                      </button>
                      <button
                        className="btn secondary"
                        onClick={() => {
                          setShowChangePassword(false);
                          setCurrentPasswordDraft("");
                          setNewPasswordDraft("");
                          setConfirmPasswordDraft("");
                          setAccountError("");
                        }}
                        disabled={accountLoadingAction === "change-password"}
                      >
                        Cancel
                      </button>
                      <div className="profile-pref-footnote" style={{ marginTop: 4 }}>
                        Forgot your current password? Sign out and use “Forgot password?” on the sign-in screen.
                      </div>
                    </div>
                  )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>

          {/* About & Legal */}
          <div className="profile-section-label">ABOUT &amp; LEGAL</div>
          <div className="profile-prefs-list">
            <button className="profile-pref-row" onClick={() => setWhatsNewOpen(true)}>
              <div className="profile-pref-icon profile-pref-icon--whatsnew"><Sparkles size={16} /></div>
              <div className="profile-pref-label">What's new</div>
              <span className="profile-pref-value">v{APP_VERSION}</span>
              <ChevronRight size={16} style={{ color: "var(--muted-foreground)" }} />
            </button>
            <a className="profile-pref-row" href="/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="profile-pref-icon profile-pref-icon--privacy"><Shield size={16} /></div>
              <div className="profile-pref-label">Privacy Policy</div>
              <ChevronRight size={16} style={{ color: "var(--muted-foreground)" }} />
            </a>
            <a className="profile-pref-row" href="/terms" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="profile-pref-icon profile-pref-icon--terms"><FileText size={16} /></div>
              <div className="profile-pref-label">Terms of Service</div>
              <ChevronRight size={16} style={{ color: "var(--muted-foreground)" }} />
            </a>
          </div>

          {/* Sign out */}
          <div className="profile-prefs-list">
            <button className="profile-pref-row profile-pref-row--signout" onClick={signOutUser}>
              Sign out
            </button>
          </div>
          <div className="profile-version">TallyPot v{APP_VERSION}</div>

          {/* Delete account confirmation dialog */}
          {confirmDeleteAccount && (
            <div className="group-confirm-overlay">
              <div className="group-confirm-box">
                <div className="group-confirm-title">Delete Account</div>
                <div className="group-confirm-msg">
                  Your account will be permanently deleted. Your player profile and game history will remain in any groups you belong to, but your account login will be gone and cannot be recovered.
                </div>
                <div className="group-confirm-msg" style={{ marginTop: 12 }}>
                  Type <strong>DELETE</strong> to confirm.
                </div>
                <input
                  className="input"
                  style={{ marginTop: 8, marginBottom: 20 }}
                  value={deleteAccountInput}
                  onChange={(e) => setDeleteAccountInput(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  spellCheck={false}
                />
                <div className="group-confirm-actions">
                  <button className="btn secondary" onClick={() => { setConfirmDeleteAccount(false); setDeleteAccountInput(""); }}>Cancel</button>
                  <button className="btn danger" disabled={deleteAccountInput !== "DELETE"} onClick={() => { setConfirmDeleteAccount(false); setDeleteAccountInput(""); deleteAccount(); }}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* live game setup modal and full-screen overlay removed — tracker now lives in the Game tab */}

      {/* ══════════════════════════════════════════
           MANUAL GAME ENTRY OVERLAY
           (opened from Sessions FAB or edit action)
           ══════════════════════════════════════════ */}
      {showManualGameEntry && typeof document !== "undefined" && createPortal(
        <div className="gs-overlay gs-overlay--manual-log" style={{ zIndex: 1300 }}>
          <div className="gs-screen gs-screen--manual-log">
            <div className="gs-header">
              <button className="gs-back-btn" onClick={() => { resetForm(); setShowManualGameEntry(false); }}>
                <ChevronLeft size={20} />Sessions
              </button>
              <h2 className="gs-title">{editingId ? "Edit Session" : "Log Session"}</h2>
              <button className="ns-save-btn" onClick={saveGame} disabled={!canCreateGames}>Save</button>
            </div>
            <div className="gs-body">

              {/* DETAILS */}
              <div className="ns-section-label">Details</div>
              <div className="ns-detail-list">
                <div className="ns-detail-row">
                  <span className="ns-detail-label">Group</span>
                  {myGroups.length > 1 ? (
                    <select className="ns-detail-select" value={activeGroupId} onChange={(e) => setActiveGroupId(e.target.value)}>
                      {myGroups.map((g) => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
                    </select>
                  ) : (
                    <span className="ns-detail-value">{groupMeta?.name || "—"}</span>
                  )}
                </div>
                <div className="ns-detail-row">
                  <span className="ns-detail-label">Date</span>
                  <div className="ns-detail-date-wrap">
                    <span className="ns-detail-date-display">{formatGameDate(gameDate)}</span>
                    <input className="ns-detail-input ns-detail-input--date-hidden" type="date" value={gameDate} onChange={(e) => setGameDate(e.target.value)} />
                  </div>
                </div>
                <div className="ns-detail-row">
                  <span className="ns-detail-label">Location</span>
                  <input className="ns-detail-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              {/* PLAYERS */}
              <div className="ns-section-header" style={{ marginTop: 24 }}>
                <div className="ns-section-label" style={{ paddingBottom: 8 }}>Players</div>
                {canAddPlayers && !showInlineAddPlayer && (
                  <button className="ns-add-top-btn" onClick={() => setShowInlineAddPlayer(true)}>+ Add</button>
                )}
              </div>
              <div className="ns-players-wrap ns-players-grid">
                {rosterPlayers.map((player) => (
                  <div
                    key={player.id}
                    className={`ns-player-chip ${selectedPlayers.includes(player.id) ? "selected" : ""}`}
                    onClick={() => togglePlayerSelection(player.id)}
                  >
                    <AvatarCircle src={getPlayerAvatar(player.id)} alt={getPlayerPreferredName(player)} fallbackLabel={getPlayerPreferredName(player)} size={30} />
                    <span className="ns-player-name">{getPlayerPreferredName(player)}</span>
                    {selectedPlayers.includes(player.id) && <span className="ns-player-check">✓</span>}
                  </div>
                ))}
              </div>
              {canAddPlayers && showInlineAddPlayer && (
                <div className="ns-add-player-form">
                  <div className="ns-add-player-form-fields">
                    <input className="ns-add-player-input" value={quickPlayerName} onChange={(e) => setQuickPlayerName(e.target.value)} placeholder="Name" autoFocus onKeyDown={(e) => { if (e.key === "Enter" && quickPlayerName.trim()) { addPlayerFromNewGame(); setShowInlineAddPlayer(false); setQuickPlayerEmail(""); } if (e.key === "Escape") { setShowInlineAddPlayer(false); setQuickPlayerName(""); setQuickPlayerEmail(""); } }} />
                    <input className="ns-add-player-input" value={quickPlayerEmail} onChange={(e) => setQuickPlayerEmail(e.target.value)} placeholder="Email (Optional)" type="email" inputMode="email" />
                  </div>
                  <div className="ns-add-player-form-actions">
                    <button className="ns-add-player-submit" disabled={!quickPlayerName.trim()} onClick={() => { addPlayerFromNewGame(); setShowInlineAddPlayer(false); setQuickPlayerEmail(""); }}>Add Player</button>
                    <button className="ns-add-player-cancel" onClick={() => { setShowInlineAddPlayer(false); setQuickPlayerName(""); setQuickPlayerEmail(""); }}>Cancel</button>
                  </div>
                </div>
              )}

              {selectedPlayers.length > 0 && (
                <>
                  <h3 style={{ marginTop: 24 }}>Buy-ins</h3>
                  <div className="grid manual-buyins-grid">
                    {sortedSelectedPlayers.map((playerId) => {
                      const player = players.find(p => p.id === playerId);
                      return (
                        <div key={playerId} className="player-row">
                          <div style={{ fontWeight: '500' }}>{getPlayerPreferredName(player || {})}</div>
                          <input ref={(el) => (buyRefs.current[playerId] = el)} className="input" type="number" min={0} step={5} inputMode="numeric" pattern="[0-9]*" value={buyIns[playerId] ?? ""} placeholder="Buy-in" onChange={(e) => setBuyIns((m) => ({ ...m, [playerId]: e.target.value }))} />
                        </div>
                      );
                    })}
                  </div>

                  <h3 style={{ marginTop: 24 }}>Winners</h3>
                  <div className="row" style={{ alignItems: "center", gap: 16, marginBottom: 8 }}>
                    <label className="toggle-label">
                      <input type="checkbox" checked={isSplitPot} onChange={(e) => { setIsSplitPot(e.target.checked); if (!e.target.checked) setSplitWinnerId(""); }} />
                      <span>Split pot (tie for 1st)</span>
                    </label>
                  </div>
                  <div className="row">
                    <div style={{ minWidth: 220 }}>
                      <label>1st place</label>
                      <select className="select" value={firstWinnerId} onChange={(e) => setFirstWinnerId(e.target.value)}>
                        <option value="">— none —</option>
                        {sortedPlayers.filter(p => selectedPlayers.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{getPlayerPreferredName(p)}</option>)}
                      </select>
                    </div>
                    {isSplitPot ? (
                      <div style={{ minWidth: 220 }}>
                        <label>1st place (tie)</label>
                        <select className="select" value={splitWinnerId} onChange={(e) => setSplitWinnerId(e.target.value)}>
                          <option value="">— none —</option>
                          {sortedPlayers.filter(p => selectedPlayers.includes(p.id) && p.id !== firstWinnerId).map((p) => <option key={p.id} value={p.id}>{getPlayerPreferredName(p)}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div style={{ minWidth: 220 }}>
                        <label>2nd place</label>
                        <select className="select" value={secondWinnerId} onChange={(e) => setSecondWinnerId(e.target.value)}>
                          <option value="">— none —</option>
                          {sortedPlayers.filter(p => selectedPlayers.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{getPlayerPreferredName(p)}</option>)}
                        </select>
                      </div>
                    )}
                    {!isSplitPot && firstWinnerId && secondWinnerId && thirdPlaceEnabled && (
                      <div style={{ minWidth: 220 }}>
                        <label>3rd place <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>(optional)</span></label>
                        <select className="select" value={thirdWinnerId} onChange={(e) => setThirdWinnerId(e.target.value)}>
                          <option value="">— none —</option>
                          {sortedPlayers.filter(p => selectedPlayers.includes(p.id) && p.id !== firstWinnerId && p.id !== secondWinnerId).map((p) => <option key={p.id} value={p.id}>{getPlayerPreferredName(p)}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  {selectedPlayers.length > 0 && (
                    <p className="jackpot-cut-note">
                      {manualJackpotCut > 0
                        ? `${formatCurrency(manualJackpotCut)} of 1st place's winnings goes into the jackpot${isSplitPot ? ", before the pot is split" : ""}.`
                        : "Jackpot is off, so 1st place keeps the full share."}
                    </p>
                  )}

                  <h3 style={{ marginTop: 24 }}>Jackpot</h3>
                  <div className="row">
                    <div style={{ minWidth: 220 }}>
                      <label>Jackpot winner</label>
                      <select className="select" value={jackpotWinnerId} onChange={(e) => setJackpotWinnerId(e.target.value)}>
                        <option value="">— none —</option>
                        {sortedPlayers.filter(p => selectedPlayers.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{getPlayerPreferredName(p)}</option>)}
                      </select>
                    </div>
                  </div>
                  {jackpotWinnerId !== "" && (
                    <div className="card-selector-grid" style={{ marginTop: 16 }}>
                      {jackpotCards.map((c, idx) => (
                        <div key={idx} className="card-selector-item">
                          <span className="card-selector-label">#{idx + 1}</span>
                          <div className="card-selector-row">
                            <select className="select card-rank-select" value={c.rank} onChange={(e) => setJackpotCards((arr) => arr.map((x, i2) => i2 === idx ? { ...x, rank: e.target.value } : x))}>
                              {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <select className="select card-suit-select" value={c.suit} style={getSuitStyle(c.suit)} onChange={(e) => setJackpotCards((arr) => arr.map((x, i2) => i2 === idx ? { ...x, suit: e.target.value } : x))}>
                              {SUITS.map((suit) => <option key={suit} value={suit} style={getSuitStyle(suit)}>{suit}</option>)}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div style={{ marginTop: 24 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <MessageSquare size={14} style={{ color: "var(--muted-foreground)" }} />
                  Notes
                </label>
                <textarea className="input" value={gameNotes} onChange={(e) => setGameNotes(e.target.value)} placeholder="Add notes or comments about this game…" rows={3} style={{ resize: "vertical", minHeight: 72, fontFamily: "inherit" }} />
              </div>

              {editingId && (
                <div style={{ marginTop: 16 }}>
                  <label htmlFor="game-edit-reason" style={{ display: "block", marginBottom: 6 }}>Reason for the change (optional)</label>
                  <input id="game-edit-reason" className="input" value={gameEditReason} maxLength={500} onChange={(e) => setGameEditReason(e.target.value)} placeholder="For example: forgot Sara's rebuy" />
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted-foreground)" }}>Your changes are saved to the game's change log with your name.</div>
                </div>
              )}

              <div style={{ marginTop: 24, paddingBottom: 8 }}>
                <button className="btn ns-save-big" style={{ width: "100%" }} onClick={saveGame} disabled={!canCreateGames}>
                  {editingId ? "Save changes" : "Save session"}
                </button>
                {!editingId && (
                  <button className="btn secondary ns-reset-btn" style={{ width: "100%", marginTop: 8 }} onClick={resetForm}>Reset</button>
                )}
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ══════════════════════════════════════════
           GROUP SETTINGS FULL-SCREEN OVERLAY
           (opened via 3-dots in Group Dashboard)
           ══════════════════════════════════════════ */}
      {showGroupSettings && (
        <div className="gs-overlay gs-overlay--group-settings">
          {/* Main group settings screen */}
          {groupSettingsSection === null && (
            <div className="gs-screen gs-screen--group-settings">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => {
                  resetGroupSettingsDrafts();
                  setEditingGroupIdentity(false);
                  setShowGroupSettings(false);
                }}><ChevronLeft size={20} />Profile</button>
                <h2 className="gs-title">Group Settings</h2>
                <span className="gs-header-spacer" />
              </div>
              <div className="gs-body gs-body--settings-home" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                {/* Group photo + name */}
                <div className="gs-group-identity">
                  <div className="gs-group-icon-wrap" onClick={editingGroupIdentity ? openGroupPhotoPicker : undefined} style={{ cursor: editingGroupIdentity ? "pointer" : "default", position: "relative" }}>
                    <img className="gs-group-photo" src={groupPhotoPreviewURL || (groupMeta ? groupMeta.photoURL : cachedGroupPhotoURL) || logo} alt="" />
                    {editingGroupIdentity && (
                      <div className="profile-hero-camera-badge" style={{ width: 22, height: 22, bottom: -4, right: -4 }}><Pencil size={10} /></div>
                    )}
                  </div>
                  {editingGroupIdentity && groupMeta?.photoURL && (
                    <button
                      className="btn secondary"
                      style={{ marginTop: 8, fontSize: 13, padding: "6px 14px" }}
                      onClick={deleteGroupPhoto}
                    >
                      Remove photo
                    </button>
                  )}
                  {editingGroupIdentity ? (
                    <input
                      className="input"
                      value={groupNameDraft}
                      onChange={(e) => setGroupNameDraft(e.target.value)}
                      placeholder="Group name"
                      style={{ marginTop: 10, textAlign: "center" }}
                      autoFocus
                    />
                  ) : (
                    <div className="gs-group-name">{groupMeta?.name || "Group"}</div>
                  )}
                  {canManageSettings && (
                    <button
                      className={`gs-identity-edit-btn${editingGroupIdentity ? " active" : ""}`}
                      onClick={() => {
                        if (editingGroupIdentity) {
                          if (groupSettingsDirty) saveMainGroupSettings();
                        } else {
                          setGroupNameDraft(groupMeta?.name || "");
                        }
                        setEditingGroupIdentity((prev) => !prev);
                      }}
                    >
                      <Pencil size={14} />
                      <span>{editingGroupIdentity ? "Done editing" : "Edit"}</span>
                    </button>
                  )}
                </div>

                <div className="gs-menu-sections">
                  <section className="gs-menu-section gs-menu-section--primary" aria-label="Game basics">
                    <div className="gs-menu-section-heading">
                      <span>Game basics</span>
                      <span>{canManageSettings ? "Most nights start here" : "Group info"}</span>
                    </div>
                    <div className="gs-menu-list">
                      <button className="gs-menu-card" onClick={() => setGroupSettingsSection("general")}>
                        <div className="gs-menu-icon gs-icon-general"><SlidersHorizontal size={18} /></div>
                        <div className="gs-menu-text">
                          <div className="gs-menu-title">Table Defaults</div>
                          <div className="gs-menu-subtitle">
                            {canManageSettings ? "Currency, buy-in, period, invite link" : "Invite code and group info"}
                          </div>
                        </div>
                        <ChevronRight size={16} className="gs-menu-chevron" />
                      </button>

                      {!user?.isAnonymous && (
                        <button className="gs-menu-card" onClick={() => openPeopleSettings("members")}>
                          <div className="gs-menu-icon gs-icon-members"><UserCog size={18} /></div>
                          <div className="gs-menu-text">
                            <div className="gs-menu-title">People</div>
                            <div className="gs-menu-subtitle">
                              {canManageSettings ? "Members, roles, and table roster" : "Members and linked player profiles"}
                            </div>
                          </div>
                          <ChevronRight size={16} className="gs-menu-chevron" />
                        </button>
                      )}

                      <button className="gs-menu-card" onClick={() => setGroupSettingsSection("statsVisibility")}>
                        <div className="gs-menu-icon gs-icon-preferences"><Eye size={18} /></div>
                        <div className="gs-menu-text">
                          <div className="gs-menu-title">My Charts</div>
                          <div className="gs-menu-subtitle">Choose who appears in your stats</div>
                        </div>
                        <ChevronRight size={16} className="gs-menu-chevron" />
                      </button>

                    </div>
                  </section>

                  {canManageSettings && (
                    <section className="gs-menu-section" aria-label="Advanced rules">
                      <div className="gs-menu-section-heading">
                        <span>Advanced rules</span>
                        <span>Defaults work for most groups</span>
                      </div>
                      <button
                        type="button"
                        className="gs-advanced-toggle"
                        onClick={() => setAdvancedRulesOpen((open) => !open)}
                        aria-expanded={advancedRulesOpen}
                      >
                        <div className="gs-advanced-toggle-text">
                          <span>Money rules and ranking</span>
                          <span>Payouts, jackpot, formula, and access controls</span>
                        </div>
                        <ChevronDown size={18} className={advancedRulesOpen ? "gs-advanced-toggle-icon open" : "gs-advanced-toggle-icon"} />
                      </button>
                      {advancedRulesOpen && (
                        <div className="gs-menu-list gs-menu-list--advanced">
                        <>
                          <button className="gs-menu-card" onClick={() => setGroupSettingsSection("payouts")}>
                            <div className="gs-menu-icon gs-icon-payouts"><CircleDollarSign size={18} /></div>
                            <div className="gs-menu-text">
                              <div className="gs-menu-title">Payout Rules</div>
                              <div className="gs-menu-subtitle">Winner splits and third-place payouts</div>
                            </div>
                            <ChevronRight size={16} className="gs-menu-chevron" />
                          </button>

                          <button className="gs-menu-card" onClick={() => setGroupSettingsSection("jackpot")}>
                            <div className="gs-menu-icon gs-icon-sideprize"><Coins size={18} /></div>
                            <div className="gs-menu-text">
                              <div className="gs-menu-title">Jackpot</div>
                              <div className="gs-menu-subtitle">Side pot rules and starting balance</div>
                            </div>
                            <ChevronRight size={16} className="gs-menu-chevron" />
                          </button>

                          <button className="gs-menu-card" onClick={() => setGroupSettingsSection("ranking")}>
                            <div className="gs-menu-icon gs-icon-ranking"><ListChecks size={18} /></div>
                            <div className="gs-menu-text">
                              <div className="gs-menu-title">Ranking</div>
                              <div className="gs-menu-subtitle">How points stay fair over a season</div>
                            </div>
                            <ChevronRight size={16} className="gs-menu-chevron" />
                          </button>

                          <button className="gs-menu-card" onClick={() => setGroupSettingsSection("permissions")}>
                            <div className="gs-menu-icon gs-icon-permissions"><ShieldCheck size={18} /></div>
                            <div className="gs-menu-text">
                              <div className="gs-menu-title">Permissions</div>
                              <div className="gs-menu-subtitle">Role rules for logging and balances</div>
                            </div>
                            <ChevronRight size={16} className="gs-menu-chevron" />
                          </button>
                        </>
                        </div>
                      )}
                    </section>
                  )}
                </div>

                {/* LEAVE / DELETE GROUP */}
                <div style={{ padding: "24px 0 calc(env(safe-area-inset-bottom, 0px) + 48px)" }}>
                  {/* The creator can't leave (they'd leave the group without its creator). */}
                  {groupMeta?.createdBy !== user?.uid && (
                    <button className="gs-leave-btn" onClick={() => setConfirmGroupAction({ id: activeGroupId, name: groupMeta?.name || activeGroupId, action: "leave" })}>
                      Leave group
                    </button>
                  )}
                  {groupRole === "moderator" && !user?.isAnonymous && (
                    <div className="gs-danger-zone">
                      <div className="gs-danger-zone-text">Deletes the group, its sessions, players and change log for every member. This can't be undone.</div>
                      <button className="gs-leave-btn" onClick={() => openDeleteGroup(activeGroupId)}>
                        Delete group permanently
                      </button>
                    </div>
                  )}
                </div>

                {/* Leave confirm dialog */}
                {confirmGroupAction && (
                  <div className="group-confirm-overlay">
                    <div className="group-confirm-box">
                      <div className="group-confirm-title">Leave Group</div>
                      <div className="group-confirm-msg">
                        {`Leave "${confirmGroupAction.name}"? You can rejoin with the invite code.`}
                      </div>
                      <div className="group-confirm-actions">
                        <button className="btn secondary" onClick={() => setConfirmGroupAction(null)}>Cancel</button>
                        <button className="btn danger" onClick={() => { setConfirmGroupAction(null); setShowGroupSettings(false); leaveGroup(confirmGroupAction.id); }}>
                          Leave
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete group: step 1 shows what goes, step 2 asks for the name. */}
                {deleteGroupFlow && (() => {
                  const { info, step, typed, busy, error } = deleteGroupFlow;
                  const name = String(info?.name || "").trim();
                  const nameMatches = !!name && typed.trim() === name;
                  const close = () => { if (!busy) setDeleteGroupFlow(null); };
                  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
                  return (
                    <div className="group-confirm-overlay">
                      <div className="group-confirm-box">
                        <div className="group-confirm-title">Delete group permanently?</div>
                        {!info && !error && <div className="group-confirm-msg">Checking the group…</div>}
                        {info && step === "summary" && (
                          <>
                            <div className="group-confirm-msg">
                              {`"${name}" and everything in it will be deleted for all ${plural(info.members, "member")}:`}
                              <ul className="gs-delete-list">
                                <li>{plural(info.games, "session")}</li>
                                <li>{plural(info.players, "player")} and their stats</li>
                                <li>Settings, invite codes and the change log</li>
                              </ul>
                              Nobody can get it back, you included.
                            </div>
                            {info.liveGame && (
                              <div className="gs-delete-warning">A live game is running. End or discard it before you delete the group.</div>
                            )}
                            {info.games > 0 && activeGroupId === deleteGroupFlow.groupId && (
                              <button className="gs-delete-export" onClick={() => exportGamesCsv(enrichedGames)}>
                                Export all sessions (CSV) first
                              </button>
                            )}
                          </>
                        )}
                        {info && step === "confirm" && (
                          <div className="group-confirm-msg">
                            Type <strong>{name}</strong> to confirm.
                            <input
                              className="gs-delete-input"
                              value={typed}
                              disabled={busy}
                              autoFocus
                              autoComplete="off"
                              autoCorrect="off"
                              autoCapitalize="off"
                              spellCheck={false}
                              placeholder={name}
                              onChange={(e) => setDeleteGroupFlow((f) => (f ? { ...f, typed: e.target.value, error: "" } : f))}
                            />
                          </div>
                        )}
                        {error && <div className="gs-delete-warning">{error}</div>}
                        <div className="group-confirm-actions">
                          <button className="btn secondary" disabled={busy} onClick={close}>Cancel</button>
                          {info && step === "summary" && (
                            <button className="btn danger" disabled={info.liveGame} onClick={() => setDeleteGroupFlow((f) => ({ ...f, step: "confirm", error: "" }))}>
                              Continue
                            </button>
                          )}
                          {info && step === "confirm" && (
                            <button className="btn danger" disabled={!nameMatches || busy} onClick={confirmDeleteGroup}>
                              {busy ? "Deleting…" : "Delete forever"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── General sub-screen ── */}
          {groupSettingsSection === "general" && (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">General</h2>
                {canManageSettings && groupSettingsDirty ? (
                  <button className="gs-done-btn" onClick={() => { saveMainGroupSettings(); setGroupSettingsSection(null); }}>Save</button>
                ) : <span />}
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                {canManageSettings && (
                  <>
                    <div className="gs-section-label">GROUP DEFAULTS</div>
                    <div className="gs-list">
                      <div className="gs-row">
                        <span className="gs-row-label">Currency</span>
                        <select
                          className="gs-row-select"
                          value={groupCurrencyDraft}
                          onChange={(e) => setGroupCurrencyDraft(String(e.target.value || "EUR").toUpperCase())}
                        >
                          <option value="EUR">EUR (€)</option>
                          <option value="USD">USD ($)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="SEK">SEK (kr)</option>
                          <option value="NOK">NOK (kr)</option>
                        </select>
                      </div>
                      <div className="gs-row">
                        <span className="gs-row-label">Default stakes</span>
                        <input
                          className="gs-row-input"
                          value={groupStakesDraft}
                          onChange={(e) => setGroupStakesDraft(e.target.value)}
                          placeholder="e.g. NL50"
                        />
                      </div>
                      <div className="gs-row">
                        <span className="gs-row-label">Default buy-in</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            className="gs-row-input"
                            type="number"
                            min="0"
                            step="5"
                            inputMode="numeric"
                            value={groupDefaultBuyInDraft}
                            onChange={(e) => setGroupDefaultBuyInDraft(e.target.value)}
                            placeholder="e.g. 50"
                          />
                          <span style={{ color: "var(--muted-foreground)", fontSize: 13, flexShrink: 0 }}>{activeCurrencyCode}</span>
                        </div>
                      </div>
                      {(() => {
                        const range = calendarPeriodRange(groupDefaultPeriodDraft);
                        const isPreset = GROUP_DEFAULT_PERIOD_OPTIONS.some((o) => o.key === groupDefaultPeriodDraft);
                        return (
                          <div className="gs-row">
                            <span className="gs-row-copy-block">
                              <span className="gs-row-label">Default period</span>
                              <span className="gs-row-help">
                                {range
                                  ? `Charts and rankings open on the current period for everyone: now ${formatPeriodRange(range)}.`
                                  : "Charts and rankings open on the current period for everyone, so tournaments reset on their own."}
                              </span>
                            </span>
                            <select
                              className="gs-row-select"
                              aria-label="Default period"
                              value={groupDefaultPeriodDraft}
                              onChange={(e) => setGroupDefaultPeriodDraft(normalizeGroupDefaultPeriod(e.target.value))}
                            >
                              {!isPreset && (
                                <option value={groupDefaultPeriodDraft}>{groupDefaultPeriodLabel(groupDefaultPeriodDraft)}</option>
                              )}
                              {GROUP_DEFAULT_PERIOD_OPTIONS.map((o) => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
                <div className="gs-section-label">INVITE</div>
                <div className="gs-list">
                  <div className="gs-row">
                    <span className="gs-row-label">Invite code</span>
                    <button type="button" className="invite-code-copy-btn gs-row-value gs-row-copy" onClick={copyInviteCode}>
                      {groupMeta?.inviteCode || "—"}&nbsp;
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    </button>
                  </div>
                  {canManageSettings && (
                    <div className="gs-row" style={{ cursor: "pointer" }} onClick={regenerateInviteCode}>
                      <span className="gs-row-label">Regenerate code</span>
                      <span className="gs-row-value" style={{ color: "var(--danger, #ef4444)", display: "flex", alignItems: "center", gap: 6 }}>
                        Regenerate&nbsp;
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                      </span>
                    </div>
                  )}
                  <div className="gs-row" style={{ cursor: "pointer" }} onClick={shareInviteLink}>
                    <span className="gs-row-label">Invite to group</span>
                    <span className="gs-row-value" style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--primary-light)" }}>
                      Share link&nbsp;
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                    </span>
                  </div>
                  {groupMeta?.createdAt && (
                    <div className="gs-row">
                      <span className="gs-row-label">Created</span>
                      <span className="gs-row-value">{new Date(groupMeta.createdAt).toLocaleDateString(appLocale, { month: "short", day: "numeric", year: "numeric" })}</span>
                    </div>
                  )}
                </div>
              </div>
              {canManageSettings && groupSettingsDirty && (
                <div className="settings-page-footer">
                  <button className="btn" style={{ width: "100%" }} onClick={() => { saveMainGroupSettings(); setGroupSettingsSection(null); }}>
                    Save changes
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── People sub-screen (Members & Players, directly editable) ── */}
          {groupSettingsSection === "people" && (
            <div className="gs-screen">
              {peopleTab === "members" && editingMemberId !== null ? (
                /* ===== Member detail editor ===== */
                (() => {
                  const m = membersForRoles.find(x => x.id === editingMemberId);
                  if (!m) { setEditingMemberId(null); return null; }
                  const memberDetailDirty =
                    String(memberRoleDraft[m.id] || m.role || "viewer") !== String(m.role || "viewer") ||
                    String(memberPlayerDraft[m.id] || "") !== String(m.playerId || "");
                  return (
                    <>
                      <div className="gs-header">
                        <button className="gs-back-btn" onClick={() => setEditingMemberId(null)}><ArrowLeft size={18} /> Back</button>
                        <h2 className="gs-title">{m.groupNickname || m.profileName || m.displayName || "Member"}</h2>
                        <span />
                      </div>
                      <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                        <div className="settings-page-section-label">Role</div>
                        <select className="select" value={memberRoleDraft[m.id] || m.role || "viewer"} onChange={(e) => setMemberRoleDraft(prev => ({ ...prev, [m.id]: e.target.value }))}>
                          <option value="viewer">Viewer — can view all data</option>
                          <option value="editor">Editor — can add &amp; edit games</option>
                          <option value="moderator">Moderator — full control</option>
                        </select>
                        <div className="settings-page-section-label" style={{ marginTop: 24 }}>Linked Player</div>
                        {(() => {
                          const claimedPlayerIds = new Set(
                            membersForRoles
                              .filter(other => other.id !== m.id)
                              .map(other => memberPlayerDraft[other.id] || "")
                              .filter(Boolean)
                          );
                          return (
                            <select className="select" value={memberPlayerDraft[m.id] ?? m.linkedPlayerId ?? ""} onChange={(e) => setMemberPlayerDraft(prev => ({ ...prev, [m.id]: e.target.value }))}>
                              <option value="">— none —</option>
                              {sortedPlayers.map((p) => (
                                <option key={p.id} value={p.id} disabled={claimedPlayerIds.has(p.id)}>
                                  {p.name}{claimedPlayerIds.has(p.id) ? " (assigned)" : p.removedFromGroup ? " (removed)" : ""}
                                </option>
                              ))}
                            </select>
                          );
                        })()}
                      </div>
                      <div className="settings-page-footer" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <button
                          className={memberDetailDirty ? "btn" : "btn secondary"}
                          style={{ width: "100%" }}
                          onClick={() => {
                            if (memberDetailDirty) {
                              saveMembersChanges();
                            }
                            setEditingMemberId(null);
                          }}
                        >
                          {memberDetailDirty ? "Save" : "Back"}
                        </button>
                        {m.id !== user?.uid && (
                          <button
                            className="btn"
                            style={{ width: "100%", background: "transparent", color: "var(--danger, #ef4444)", border: "1px solid var(--danger, #ef4444)" }}
                            onClick={() => setConfirmRemoveMember({ id: m.id, name: m.groupNickname || m.profileName || m.displayName || "Member" })}
                          >
                            Remove from group
                          </button>
                        )}
                      </div>
                      {confirmRemoveMember && confirmRemoveMember.id === m.id && (
                        <div className="group-confirm-overlay">
                          <div className="group-confirm-box">
                            <div className="group-confirm-title">Remove Member</div>
                            <div className="group-confirm-msg">
                              Remove <strong>{confirmRemoveMember.name}</strong> from the group? They lose access and their player leaves the roster; past games stay as they are. The invite code will change, so they can only come back if someone shares the new one.
                            </div>
                            <div className="group-confirm-actions">
                              <button className="btn secondary" onClick={() => setConfirmRemoveMember(null)}>Cancel</button>
                              <button className="btn danger" onClick={() => { const id = confirmRemoveMember.id; setConfirmRemoveMember(null); removeMemberFromGroup(id); }}>Remove</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : (
                <>
                  <div className="gs-header">
                    <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                    <h2 className="gs-title">People</h2>
                    {peopleTab === "players" && canAddPlayers && !showAddPlayerForm ? (
                      <button className="pm-add-btn" onClick={() => { setShowAddPlayerForm(true); setPlayerSettingsId(null); }}>+ Add</button>
                    ) : (
                      <span />
                    )}
                  </div>
                  <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                    <div className="people-tabs" role="tablist" aria-label="People sections">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={peopleTab === "members"}
                        className={`people-tab${peopleTab === "members" ? " active" : ""}`}
                        onClick={() => { setPeopleTab("members"); setShowAddPlayerForm(false); setPlayerSettingsId(null); }}
                      >
                        Members
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={peopleTab === "players"}
                        className={`people-tab${peopleTab === "players" ? " active" : ""}`}
                        onClick={() => { setPeopleTab("players"); setEditingMemberId(null); }}
                      >
                        Players
                      </button>
                    </div>

                    {peopleTab === "members" ? (
                      <>
                        <p className="people-tab-hint">
                          {canManageSettings
                            ? "Tap a member to set their role or link them to a player profile."
                            : "Signed-in members and how they're linked to the roster."}
                        </p>
                        {pendingPlayerLinks.length > 0 && canManageSettings && (
                          <div className="pending-links-banner">
                            <div className="pending-links-title">⚠ Player link requests</div>
                            {pendingPlayerLinks.map(pl => {
                              const member = membersForRoles.find(m => m.id === pl.memberUid);
                              return (
                                <div key={pl.id} className="pending-link-row">
                                  <div className="pending-link-info">
                                    <span className="pending-link-name">{pl.memberName}</span>
                                    {pl.isDuplicate
                                      ? <span className="pending-link-sub">Multiple players share this name — review to link or create new</span>
                                      : pl.returning
                                      ? <span className="pending-link-sub">Rejoined — link them back to their player</span>
                                      : <span className="pending-link-sub">Possible match — review to link or create new</span>
                                    }
                                  </div>
                                  <button
                                    className="pending-link-btn"
                                    onClick={async () => {
                                      if (member) setEditingMemberId(member.id);
                                      await deleteDoc(doc(db, "groups", activeGroupId, "pendingPlayerLinks", pl.id));
                                    }}
                                  >Review</button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="settings-members-list">
                          {[...membersForRoles].sort((a, b) => {
                            const roleOrder = { moderator: 0, editor: 1, viewer: 2 };
                            const rA = roleOrder[memberRoleDraft[a.id] || a.role || "viewer"] ?? 2;
                            const rB = roleOrder[memberRoleDraft[b.id] || b.role || "viewer"] ?? 2;
                            if (rA !== rB) return rA - rB;
                            return (a.profileName || a.displayName || a.id).localeCompare(b.profileName || b.displayName || b.id, undefined, { sensitivity: "base" });
                          }).map((m) => {
                            const role = memberRoleDraft[m.id] || m.role || "viewer";
                            const linkedPlayer = sortedPlayers.find(p => p.id === (memberPlayerDraft[m.id] ?? m.linkedPlayerId ?? ""));
                            const info = (
                              <>
                                <AvatarCircle src={m.profilePhotoURL || ""} alt={m.profileName || m.displayName} fallbackLabel={m.profileName || m.displayName || m.id} size={36} />
                                <div className="settings-member-item-info">
                                  <div className="settings-member-item-name">{m.groupNickname || m.profileName || m.displayName || "Member"}</div>
                                  <div className="settings-member-item-meta">
                                    <span className={`settings-member-role-badge settings-member-role-${role}`}>{role}</span>
                                    {linkedPlayer && <span className="settings-member-linked">· {linkedPlayer.name}</span>}
                                  </div>
                                </div>
                              </>
                            );
                            return canManageSettings ? (
                              <button key={m.id} className="settings-member-item" onClick={() => setEditingMemberId(m.id)}>
                                {info}
                                <ChevronRight size={16} className="settings-list-chevron" />
                              </button>
                            ) : (
                              <div key={m.id} className="settings-member-item">{info}</div>
                            );
                          })}
                          {membersForRoles.length === 0 && <div className="leaderboard-note">No signed-in members yet.</div>}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Add Player form */}
                        {showAddPlayerForm && canAddPlayers ? (
                          <>
                            <div className="gs-section-label">New Player</div>
                            <div className="gs-list">
                              <div className="pm-field-row">
                                <span className="pm-field-label">First name</span>
                                <input className="pm-field-input" type="text" placeholder="Required" value={newPlayerFirstName} onChange={(e) => setNewPlayerFirstName(e.target.value)} autoFocus />
                              </div>
                              <div className="pm-field-row">
                                <span className="pm-field-label">Last name</span>
                                <input className="pm-field-input" type="text" placeholder="Optional" value={newPlayerLastName} onChange={(e) => setNewPlayerLastName(e.target.value)} />
                              </div>
                              <div className="pm-field-row">
                                <span className="pm-field-label">Nickname</span>
                                <input className="pm-field-input" type="text" placeholder="Optional" value={newPlayerNickname} onChange={(e) => setNewPlayerNickname(e.target.value)} maxLength={20} />
                              </div>
                              {canManagePlayers && (
                                <div className="pm-field-row">
                                  <span className="pm-field-label">Email</span>
                                  <input className="pm-field-input" type="email" placeholder="Optional" value={newPlayerEmail} onChange={(e) => setNewPlayerEmail(e.target.value)} />
                                </div>
                              )}
                            </div>
                            <div className="pm-form-actions">
                              <button className="pm-cancel-btn" onClick={() => {
                                setShowAddPlayerForm(false);
                                setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
                              }}>Cancel</button>
                              <button
                                className="pm-save-btn"
                                disabled={!newPlayerFirstName.trim()}
                                onClick={async () => {
                                  await addPlayer(newPlayerFirstName, newPlayerEmail, "settings", { lastName: newPlayerLastName, nickname: newPlayerNickname });
                                  setShowAddPlayerForm(false);
                                  setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
                                }}
                              >Add Player</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="people-tab-hint">
                              {canManagePlayers
                                ? "Tap a player to edit their name, nickname, or email. Players power games and stats."
                                : canAddPlayers
                                ? "Tap “+ Add” to create a player. Only moderators can edit or remove players."
                                : "Player profiles used for games and stats."}
                            </p>
                            {sortedPlayers.length === 0 ? (
                              <div className="pm-empty">{canAddPlayers ? "No players yet. Tap “+ Add” to create the first one." : "No players yet."}</div>
                            ) : (
                              <div className="gs-list">
                                {sortedPlayers.map((player) => {
                                  const pid = String(player.id);
                                  const pname = String(player.name || "").trim();
                                  const gamesPlayed = games.filter((game) => {
                                    const buyInKeys = Object.keys(game.buyIns || {});
                                    const inBuyIns = buyInKeys.some((k) => String(k) === pid || (pname && String(k) === pname));
                                    const firstRef = String(game.firstWinnerId || "");
                                    const secondRef = String(game.secondWinnerId || "");
                                    const jackpotRef = String(game.jackpotWinnerId || "");
                                    const inWinners = firstRef === pid || secondRef === pid || jackpotRef === pid ||
                                      (pname && (firstRef === pname || secondRef === pname || jackpotRef === pname));
                                    return inBuyIns || inWinners;
                                  }).length;
                                  const linkedMember = membersForRoles.find((mm) => String(memberPlayerDraft[mm.id] || mm.linkedPlayerId || "") === pid);
                                  const isEditing = playerSettingsId === player.id;
                                  return (
                                    <React.Fragment key={player.id}>
                                      <button
                                        className={`gs-row gs-row-btn pm-player-row${isEditing ? " pm-player-row--active" : ""}`}
                                        onClick={() => { if (isEditing) { setPlayerSettingsId(null); } else { openPlayerSettings(player); } }}
                                        disabled={!canManagePlayers}
                                      >
                                        <AvatarCircle src={getPlayerAvatar(player.id)} alt={getPlayerPreferredName(player)} fallbackLabel={getPlayerPreferredName(player)} size={38} />
                                        <div className="pm-player-info">
                                          <span className="pm-player-name">{getPlayerPreferredName(player)}</span>
                                          <span className="pm-player-sub">
                                            {linkedMember
                                              ? `Linked to ${linkedMember.groupNickname || linkedMember.profileName || linkedMember.displayName || "member"}`
                                              : player.removedFromGroup
                                              ? "Removed from group · kept in history"
                                              : (player.nickname || player.lastName ? `${player.firstName}${player.lastName ? ` ${player.lastName}` : ""}` : "Roster profile")}
                                          </span>
                                        </div>
                                        <span className="pm-games-badge">{gamesPlayed}g</span>
                                        {canManagePlayers && <ChevronRight size={16} className={`pm-chevron${isEditing ? " pm-chevron--open" : ""}`} />}
                                      </button>
                                      {isEditing && canManagePlayers && (
                                        <div className="pm-edit-panel">
                                          <div className="pm-edit-fields">
                                            <div className="pm-field-row">
                                              <label className="pm-field-label">First name</label>
                                              <input className="pm-field-input" type="text" placeholder="First name" value={playerFirstNameDraft} onChange={(e) => setPlayerFirstNameDraft(e.target.value)} />
                                            </div>
                                            <div className="pm-field-row">
                                              <label className="pm-field-label">Last name</label>
                                              <input className="pm-field-input" type="text" placeholder="Optional" value={playerLastNameDraft} onChange={(e) => setPlayerLastNameDraft(e.target.value)} />
                                            </div>
                                            <div className="pm-field-row">
                                              <label className="pm-field-label">Nickname</label>
                                              <input className="pm-field-input" type="text" placeholder="Optional" value={playerNicknameDraft} onChange={(e) => setPlayerNicknameDraft(e.target.value)} maxLength={20} />
                                            </div>
                                            <div className="pm-field-row">
                                              <label className="pm-field-label">Email</label>
                                              <input className="pm-field-input" type="email" placeholder="Optional" value={playerEmailDraft} onChange={(e) => setPlayerEmailDraft(e.target.value)} />
                                            </div>
                                          </div>
                                          <div className="pm-edit-actions">
                                            <button
                                              className="pm-delete-btn"
                                              onClick={() => deletePlayer(player.id)}
                                              disabled={gamesPlayed > 0}
                                              title={gamesPlayed > 0 ? `In ${gamesPlayed} game(s) — cannot delete` : "Delete player"}
                                            >
                                              <Trash2 size={15} />
                                              Delete
                                            </button>
                                            <div style={{ display: "flex", gap: 8 }}>
                                              <button className="pm-cancel-btn" onClick={() => { setPlayerSettingsId(null); setPlayerFirstNameDraft(""); setPlayerLastNameDraft(""); setPlayerEmailDraft(""); setPlayerNicknameDraft(""); }}>Cancel</button>
                                              <button className="pm-save-btn" onClick={savePlayerSettings}>Save</button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {peopleTab === "members" && canManageSettings && (
                    <div className="settings-page-footer">
                      <button
                        className={memberSettingsDirty ? "btn" : "btn secondary"}
                        style={{ width: "100%" }}
                        onClick={() => {
                          if (memberSettingsDirty) {
                            saveMembersChanges();
                          }
                          setGroupSettingsSection(null);
                        }}
                      >
                        {memberSettingsDirty ? "Save" : "Back"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Payouts sub-screen ── */}
          {groupSettingsSection === "payouts" && canManageSettings && (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">Payout Rules</h2>
                <span />
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                <p className="settings-page-desc">Configure how the pot is split between winners. Percentages are used as suggestions when ending a live game.</p>
                <div className="gs-section-label">PLACES</div>
                <div className="gs-list">
                  <div className="gs-row">
                    <span className="gs-row-label">3rd place winner</span>
                    <button
                      className={`profile-toggle${thirdPlaceDraft ? " on" : ""}`}
                      onClick={() => setThirdPlaceDraft((prev) => !prev)}
                      role="switch"
                      aria-checked={thirdPlaceDraft}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                  <div className="gs-row">
                    <span className="gs-row-label">1st place payout</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        className="gs-row-input"
                        type="number"
                        min="1"
                        max="99"
                        value={groupPayoutFirstDraft}
                        onChange={(e) => setGroupPayoutFirstDraft(e.target.value)}
                        placeholder="65"
                        style={{ width: 60 }}
                      />
                      <span style={{ color: "var(--muted-foreground)", fontSize: 13 }}>%</span>
                    </div>
                  </div>
                  <div className="gs-row">
                    <span className="gs-row-label">2nd place payout</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        className="gs-row-input"
                        type="number"
                        min="1"
                        max="99"
                        value={groupPayoutSecondDraft}
                        onChange={(e) => setGroupPayoutSecondDraft(e.target.value)}
                        placeholder="35"
                        style={{ width: 60 }}
                      />
                      <span style={{ color: "var(--muted-foreground)", fontSize: 13 }}>%</span>
                    </div>
                  </div>
                  {thirdPlaceDraft && (
                    <div className="gs-row">
                      <span className="gs-row-label">3rd place payout</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input
                          className="gs-row-input"
                          type="number"
                          min="0"
                          max="99"
                          value={groupPayoutThirdDraft}
                          onChange={(e) => setGroupPayoutThirdDraft(e.target.value)}
                          placeholder="0"
                          style={{ width: 60 }}
                        />
                        <span style={{ color: "var(--muted-foreground)", fontSize: 13 }}>%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="settings-page-footer">
                <button
                  className={groupSettingsDirty ? "btn" : "btn secondary"}
                  style={{ width: "100%" }}
                  onClick={() => { if (groupSettingsDirty) saveMainGroupSettings(); setGroupSettingsSection(null); }}
                >
                  {groupSettingsDirty ? "Save" : "Back"}
                </button>
              </div>
            </div>
          )}

          {/* ── Jackpot sub-screen ── */}
          {groupSettingsSection === "jackpot" && (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">Jackpot</h2>
                <span />
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                <div className="gs-list">
                  <div className="gs-row">
                    <span className="gs-row-label">Enable jackpot</span>
                    <button
                      className={`profile-toggle${sidePrizeConfigDraft.enabled ? " on" : ""}`}
                      onClick={() => setSidePrizeConfigDraft(prev => ({ ...prev, enabled: !prev.enabled }))}
                      role="switch"
                      aria-checked={sidePrizeConfigDraft.enabled}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                  {!sidePrizeConfigDraft.enabled && (
                    <div style={{ margin: "8px 16px", padding: "10px 14px", background: "var(--warning-bg, rgba(245,158,11,0.12))", borderRadius: 10, fontSize: 13, color: "var(--warning-text, #b45309)", lineHeight: 1.5 }}>
                      Disabling jackpot hides the card from the home screen but does not affect previously recorded sessions.
                    </div>
                  )}
                  <div className="gs-row">
                    <span className="gs-row-label">Collection method</span>
                    <select
                      className="gs-row-select"
                      value={sidePrizeConfigDraft.collectionMethod}
                      onChange={(e) => setSidePrizeConfigDraft(prev => ({ ...prev, collectionMethod: e.target.value }))}
                      disabled={!sidePrizeConfigDraft.enabled}
                    >
                      <option value="per_player">Per player</option>
                      <option value="from_winner">From 1st place</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>
                  {sidePrizeConfigDraft.collectionMethod === "per_player" && (
                    <div className="gs-row">
                      <span className="gs-row-label">Amount per player ({activeCurrencyCode})</span>
                      <input
                        className="gs-row-input"
                        type="number"
                        min="0"
                        step="1"
                        value={sidePrizeConfigDraft.collectionAmount}
                        onChange={(e) => setSidePrizeConfigDraft(prev => ({ ...prev, collectionAmount: Math.max(0, Number(e.target.value) || 0) }))}
                        placeholder="0"
                        disabled={!sidePrizeConfigDraft.enabled}
                        style={{ width: 80 }}
                      />
                    </div>
                  )}
                </div>
                <p className="settings-page-desc" style={{ marginTop: 16 }}>Set the initial jackpot balance carried over from before the app was used.</p>
                <div className="settings-page-section-label">Initial Balance</div>
                <input className="input" type="number" min={0} step={1} value={jackpotBase} onChange={(e) => setJackpotBase(Math.max(0, Number(e.target.value) || 0))} style={{ maxWidth: 200 }} />
              </div>
              <div className="settings-page-footer">
                <button
                  className={jackpotSettingsDirty ? "btn" : "btn secondary"}
                  style={{ width: "100%" }}
                  onClick={() => {
                    if (jackpotSettingsDirty) {
                      saveJackpotBase();
                    }
                    setGroupSettingsSection(null);
                  }}
                >
                  {jackpotSettingsDirty ? "Save" : "Back"}
                </button>
              </div>
            </div>
          )}

          {/* ── Permissions sub-screen ── */}
          {groupSettingsSection === "permissions" && canManageSettings && (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">Permissions</h2>
                {groupSettingsDirty ? (
                  <button className="gs-done-btn" onClick={() => { saveMainGroupSettings(); setGroupSettingsSection(null); }}>Save</button>
                ) : <span />}
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                <p className="settings-page-desc">Control what different member roles can do in this group.</p>
                <div className="gs-section-label">MEMBER ACCESS</div>
                <div className="gs-list">
                  <div className="gs-row">
                    <span className="gs-row-copy-block">
                      <span className="gs-row-label">Editors can log</span>
                      <span className="gs-row-help">Editors can add live games and manual sessions, but cannot change settings.</span>
                    </span>
                    <button
                      className={`profile-toggle${groupPermissions.editorsCanLog ? " on" : ""}`}
                      onClick={() => setGroupPermissions(prev => ({ ...prev, editorsCanLog: !prev.editorsCanLog }))}
                      role="switch"
                      aria-checked={groupPermissions.editorsCanLog}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                  <div className="gs-row">
                    <span className="gs-row-copy-block">
                      <span className="gs-row-label">Editors can change finished games</span>
                      <span className="gs-row-help">Editors can edit, re-time and delete saved games. Every change goes to the game's change log either way.</span>
                    </span>
                    <button
                      className={`profile-toggle${groupPermissions.editorsCanEditFinished ? " on" : ""}`}
                      onClick={() => setGroupPermissions(prev => ({ ...prev, editorsCanEditFinished: !prev.editorsCanEditFinished }))}
                      role="switch"
                      aria-checked={groupPermissions.editorsCanEditFinished}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                  <div className="gs-row">
                    <span className="gs-row-copy-block">
                      <span className="gs-row-label">Viewers see balances</span>
                      <span className="gs-row-help">Viewers can see buy-ins, payouts, profit, and totals. Turn off for read-only guests.</span>
                    </span>
                    <button
                      className={`profile-toggle${groupPermissions.viewersSeeBalances ? " on" : ""}`}
                      onClick={() => setGroupPermissions(prev => ({ ...prev, viewersSeeBalances: !prev.viewersSeeBalances }))}
                      role="switch"
                      aria-checked={groupPermissions.viewersSeeBalances}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                  <div className="gs-row">
                    <span className="gs-row-copy-block">
                      <span className="gs-row-label">Members self-log</span>
                      <span className="gs-row-help">Members can record games for themselves without being an editor.</span>
                    </span>
                    <button
                      className={`profile-toggle${groupPermissions.membersSelfLog ? " on" : ""}`}
                      onClick={() => setGroupPermissions(prev => ({ ...prev, membersSelfLog: !prev.membersSelfLog }))}
                      role="switch"
                      aria-checked={groupPermissions.membersSelfLog}
                    >
                      <div className="profile-toggle-thumb" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Ranking sub-screen ── */}
          {groupSettingsSection === "ranking" && (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={() => setGroupSettingsSection(null)}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">Ranking</h2>
                <span />
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                <p className="settings-page-desc">The default ranking keeps the table competitive without asking your group to understand formulas.</p>
                <div className="ranking-rule-preview">
                  <div className="ranking-rule-preview-row">
                    <span>Profit still matters</span>
                    <strong>{euro(10)} profit adds {(10 * Number(normalizedRankingDraft.netMultiplier || 0)).toFixed(1)} pts</strong>
                  </div>
                  <div className="ranking-rule-preview-row">
                    <span>Places get a bonus</span>
                    <strong>1st +{normalizedRankingDraft.firstPlaceBonus}, 2nd +{normalizedRankingDraft.secondPlaceBonus}{thirdPlaceEnabled ? `, 3rd +${normalizedRankingDraft.thirdPlaceBonus}` : ""}</strong>
                  </div>
                  <div className="ranking-rule-preview-row">
                    <span>Rebuys are softened</span>
                    <strong>-{normalizedRankingDraft.rebuyPenalty} pts after the first buy-in</strong>
                  </div>
                  <div className="ranking-rule-preview-row">
                    <span>One wild night cannot decide the season</span>
                    <strong>{normalizedRankingDraft.gameCapMin} to +{normalizedRankingDraft.gameCapMax} pts per game</strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="ranking-advanced-toggle"
                  onClick={() => setRankingFormulaOpen((open) => !open)}
                  aria-expanded={rankingFormulaOpen}
                >
                  <span>Advanced formula controls</span>
                  <ChevronDown size={18} className={rankingFormulaOpen ? "gs-advanced-toggle-icon open" : "gs-advanced-toggle-icon"} />
                </button>

                {rankingFormulaOpen && (
                  <>
                    <div className="settings-page-fields-grid settings-page-fields-grid--advanced">
                      {[
                        ["Profit multiplier", "netMultiplier", "0.01"],
                        ["1st place bonus", "firstPlaceBonus", "1"],
                        ["2nd place bonus", "secondPlaceBonus", "1"],
                        ["3rd place bonus", "thirdPlaceBonus", "1"],
                        ["Rebuy penalty", "rebuyPenalty", "0.1"],
                        ["Streak step", "streakStep", "1"],
                        ["Streak max", "streakMax", "1"],
                        ["Streak min profit", "streakMinNetEuro", "1"],
                        ["Game min cap", "gameCapMin", "1"],
                        ["Game max cap", "gameCapMax", "1"],
                      ].map(([label, key, step]) => (
                        <div className="settings-page-field" key={key}>
                          <label className="settings-page-field-label">{label}</label>
                          <input className="input" type="number" step={step} value={rankingConfigDraft[key]} onChange={(e) => setRankingConfigDraft((cfg) => ({ ...cfg, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                    <button className="btn secondary" style={{ marginTop: 16, width: "100%" }} onClick={() => setRankingConfigDraft(DEFAULT_RANKING_CONFIG)}>Restore defaults</button>
                  </>
                )}
              </div>
              <div className="settings-page-footer">
                <button
                  className={rankingSettingsDirty ? "btn" : "btn secondary"}
                  style={{ width: "100%" }}
                  onClick={() => {
                    if (rankingSettingsDirty) {
                      saveRankingConfig();
                    }
                    setGroupSettingsSection(null);
                  }}
                >
                  {rankingSettingsDirty ? "Save" : "Back"}
                </button>
              </div>
            </div>
          )}

          {/* ── Stats Visibility sub-screen ── */}
          {groupSettingsSection === "statsVisibility" && (() => {
            // Capture snapshot the first time this screen mounts
            if (!statsVisibilitySnapshotRef.current) {
              statsVisibilitySnapshotRef.current = { ...statsVisibilityDraft };
            }
            const discardAndBack = () => {
              setStatsVisibilityDraft(statsVisibilitySnapshotRef.current);
              statsVisibilitySnapshotRef.current = null;
              setGroupSettingsSection(null);
            };
            const saveAndBack = () => {
              saveStatsVisibility();
              statsVisibilitySnapshotRef.current = null;
              setGroupSettingsSection(null);
            };
            return (
            <div className="gs-screen">
              <div className="gs-header">
                <button className="gs-back-btn" onClick={discardAndBack}><ArrowLeft size={18} /> Back</button>
                <h2 className="gs-title">Chart Visibility</h2>
                <button
                  className="gs-done-btn"
                  onClick={() => setStatsVisibilityDraft(
                    sortedPlayers.reduce((acc, p) => ({ ...acc, [p.id]: true }), {})
                  )}
                >
                  Show all
                </button>
              </div>
              <div className="gs-body" ref={groupSettingsBodyRef} onScroll={handleGroupSettingsBodyScroll}>
                <p className="settings-page-desc">Choose which players appear in charts and the stats table. Synced with your signed-in account.</p>
                {canManageSettings && <button className="btn secondary" style={{ marginBottom: 16 }} onClick={repairLinkedPlayerProfiles}>Repair Linked Avatars</button>}
                <div className="sv2-grid">
                  {sortedPlayers.map((player) => {
                    const isVisible = statsVisibilityDraft[player.id] !== false;
                    return (
                      <button
                        key={player.id}
                        type="button"
                        className={`sv2-card${isVisible ? " sv2-card--on" : ""}`}
                        onClick={() => setStatsVisibilityDraft((prev) => ({ ...prev, [player.id]: !isVisible }))}
                      >
                        <div className="sv2-card-avatar">
                          <AvatarCircle
                            src={getPlayerAvatar(player.id)}
                            alt={getPlayerPreferredName(player)}
                            fallbackLabel={getPlayerPreferredName(player)}
                            size={44}
                          />
                          <span className="sv2-card-badge" aria-hidden="true">
                            {isVisible ? <Check size={10} strokeWidth={3} /> : <EyeOff size={10} />}
                          </span>
                        </div>
                        <span className="sv2-card-name">{getPlayerPreferredName(player)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="settings-page-footer">
                <button
                  className="btn"
                  style={{ width: "100%", opacity: statsVisibilityDirty ? 1 : 0.4 }}
                  onClick={saveAndBack}
                >
                  Save
                </button>
              </div>
            </div>
            );
          })()}

        </div>
      )}
      </div>

      {typeof document !== "undefined" && activeTab === "sessions" && canCreateGames && !liveGame && !infoGameId && createPortal(
        <button
          className="sessions-add-fab"
          aria-label="Log a completed session"
          onClick={() => { resetForm(); setShowManualGameEntry(true); }}
        >
          <Plus size={24} />
        </button>,
        document.body
      )}

      {toastNode}
      {typeof document !== "undefined" && createPortal(<ConfettiBurst trigger={confettiTrigger} intense={confettiIntense} />, document.body)}

      <MenuBar
        items={bottomNavItems}
        activeKey={activeTab}
        onSelect={handleTabSelect}
      />
      <InstallPrompt userId={user?.uid} />

      {/* Players Full-Screen Page */}
      {showPlayerManager && (
        <div className="gs-overlay">
          <div className="gs-screen">
            <div className="gs-header">
              <button className="gs-back-btn" onClick={() => {
                setShowPlayerManager(false);
                setShowGroupSettings(true);
                setShowAddPlayerForm(false);
                setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
                setPlayerSettingsId(null);
              }}>
                <ChevronLeft size={20} />Settings
              </button>
              <h2 className="gs-title">Players</h2>
              {canAddPlayers && !showAddPlayerForm ? (
                <button className="pm-add-btn" onClick={() => { setShowAddPlayerForm(true); setPlayerSettingsId(null); }}>
                  + Add
                </button>
              ) : (
                <div className="gs-header-spacer" />
              )}
            </div>
            <div className="gs-body">

              {/* Add Player Form */}
              {showAddPlayerForm && canAddPlayers && (
                <>
                  <div className="gs-section-label">New Player</div>
                  <div className="gs-list">
                    <div className="pm-field-row">
                      <span className="pm-field-label">First name</span>
                      <input
                        className="pm-field-input"
                        type="text"
                        placeholder="Required"
                        value={newPlayerFirstName}
                        onChange={(e) => setNewPlayerFirstName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="pm-field-row">
                      <span className="pm-field-label">Last name</span>
                      <input
                        className="pm-field-input"
                        type="text"
                        placeholder="Optional"
                        value={newPlayerLastName}
                        onChange={(e) => setNewPlayerLastName(e.target.value)}
                      />
                    </div>
                    <div className="pm-field-row">
                      <span className="pm-field-label">Nickname</span>
                      <input
                        className="pm-field-input"
                        type="text"
                        placeholder="Optional"
                        value={newPlayerNickname}
                        onChange={(e) => setNewPlayerNickname(e.target.value)}
                        maxLength={20}
                      />
                    </div>
                    {canManagePlayers && (
                      <div className="pm-field-row">
                        <span className="pm-field-label">Email</span>
                        <input
                          className="pm-field-input"
                          type="email"
                          placeholder="Optional"
                          value={newPlayerEmail}
                          onChange={(e) => setNewPlayerEmail(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="pm-form-actions">
                    <button className="pm-cancel-btn" onClick={() => {
                      setShowAddPlayerForm(false);
                      setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
                    }}>Cancel</button>
                    <button
                      className="pm-save-btn"
                      disabled={!newPlayerFirstName.trim()}
                      onClick={async () => {
                        await addPlayer(newPlayerFirstName, newPlayerEmail, "settings", { lastName: newPlayerLastName, nickname: newPlayerNickname });
                        setShowAddPlayerForm(false);
                        setNewPlayerFirstName(""); setNewPlayerLastName(""); setNewPlayerNickname(""); setNewPlayerEmail("");
                      }}
                    >Add Player</button>
                  </div>
                </>
              )}

              {/* Player List */}
              {!showAddPlayerForm && (
                <>
                  {sortedPlayers.length === 0 ? (
                    <div className="pm-empty">No players yet. Tap "+ Add" to create the first one.</div>
                  ) : (
                    <>
                      <div className="gs-section-label">{sortedPlayers.length} {sortedPlayers.length === 1 ? "Player" : "Players"}</div>
                      <div className="gs-list">
                        {sortedPlayers.map((player) => {
                          const pid = String(player.id);
                          const pname = String(player.name || "").trim();
                          const gamesPlayed = games.filter((game) => {
                            const buyInKeys = Object.keys(game.buyIns || {});
                            const inBuyIns = buyInKeys.some((k) => String(k) === pid || (pname && String(k) === pname));
                            const firstRef = String(game.firstWinnerId || "");
                            const secondRef = String(game.secondWinnerId || "");
                            const jackpotRef = String(game.jackpotWinnerId || "");
                            const inWinners = firstRef === pid || secondRef === pid || jackpotRef === pid ||
                              (pname && (firstRef === pname || secondRef === pname || jackpotRef === pname));
                            return inBuyIns || inWinners;
                          }).length;
                          const avatarSrc = getPlayerAvatar(player.id);
                          const isEditing = playerSettingsId === player.id;
                          return (
                            <React.Fragment key={player.id}>
                              <button
                                className={`gs-row gs-row-btn pm-player-row${isEditing ? " pm-player-row--active" : ""}`}
                                onClick={() => {
                                  if (isEditing) {
                                    setPlayerSettingsId(null);
                                  } else {
                                    openPlayerSettings(player);
                                  }
                                }}
                                disabled={!canManagePlayers}
                              >
                                <AvatarCircle src={avatarSrc} alt={getPlayerPreferredName(player)} fallbackLabel={getPlayerPreferredName(player)} size={38} />
                                <div className="pm-player-info">
                                  <span className="pm-player-name">{getPlayerPreferredName(player)}</span>
                                  {player.removedFromGroup ? (
                                    <span className="pm-player-sub">Removed from group · kept in history</span>
                                  ) : (player.nickname || player.lastName) && (
                                    <span className="pm-player-sub">
                                      {player.firstName}{player.lastName ? ` ${player.lastName}` : ""}
                                    </span>
                                  )}
                                </div>
                                <span className="pm-games-badge">{gamesPlayed}g</span>
                                <ChevronRight size={16} className={`pm-chevron${isEditing ? " pm-chevron--open" : ""}`} />
                              </button>
                              {isEditing && (
                                <div className="pm-edit-panel">
                                  <div className="pm-edit-fields">
                                    <div className="pm-field-row">
                                      <label className="pm-field-label">First name</label>
                                      <input className="pm-field-input" type="text" placeholder="First name" value={playerFirstNameDraft} onChange={(e) => setPlayerFirstNameDraft(e.target.value)} />
                                    </div>
                                    <div className="pm-field-row">
                                      <label className="pm-field-label">Last name</label>
                                      <input className="pm-field-input" type="text" placeholder="Optional" value={playerLastNameDraft} onChange={(e) => setPlayerLastNameDraft(e.target.value)} />
                                    </div>
                                    <div className="pm-field-row">
                                      <label className="pm-field-label">Nickname</label>
                                      <input className="pm-field-input" type="text" placeholder="Optional" value={playerNicknameDraft} onChange={(e) => setPlayerNicknameDraft(e.target.value)} maxLength={20} />
                                    </div>
                                    <div className="pm-field-row">
                                      <label className="pm-field-label">Email</label>
                                      <input className="pm-field-input" type="email" placeholder="Optional" value={playerEmailDraft} onChange={(e) => setPlayerEmailDraft(e.target.value)} />
                                    </div>
                                  </div>
                                  <div className="pm-edit-actions">
                                    <button
                                      className="pm-delete-btn"
                                      onClick={() => deletePlayer(player.id)}
                                      disabled={gamesPlayed > 0}
                                      title={gamesPlayed > 0 ? `In ${gamesPlayed} game(s) — cannot delete` : "Delete player"}
                                    >
                                      <Trash2 size={15} />
                                      Delete
                                    </button>
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button className="pm-cancel-btn" onClick={() => { setPlayerSettingsId(null); setPlayerFirstNameDraft(""); setPlayerLastNameDraft(""); setPlayerEmailDraft(""); setPlayerNicknameDraft(""); }}>Cancel</button>
                                      <button className="pm-save-btn" onClick={savePlayerSettings}>Save</button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Player Profile Panel */}
      {playerProfileId && (() => {
        const profile = rankingById[playerProfileId] || null;
        const fin = totals[playerProfileId] || null;
        const player = players.find(p => p.id === playerProfileId);
        if (!player && !profile && !fin) return null;
        const name = player?.name || player?.firstName || playerProfileId;
        const displayName = player ? getPlayerPreferredName(player) : name;
        const games = profile?.games ?? 0;
        const firsts = profile?.firsts ?? 0;
        const seconds = profile?.seconds ?? 0;
        const thirds = profile?.thirds ?? 0;
        const jackpots = profile?.jackpots ?? 0;
        const buyIns = profile?.buyInCount ?? 0;
        const winRate = games > 0 ? ((firsts / games) * 100).toFixed(0) : "—";
        const netProfit = fin?.profit ?? profile?.netEuroTotal ?? 0;
        const absNetProfit = Math.abs(netProfit);
        const netProfitDisplay =
          absNetProfit >= 1000
            ? formatCompactCurrency(absNetProfit)
            : formatCurrency(absNetProfit).replace(/([.,]00)(?=\D|$)/, "");
        const signedNetProfit = `${netProfit > 0 ? "+" : netProfit < 0 ? "-" : ""}${netProfitDisplay}`;
        const points = profile?.points ?? "—";
        const bestStreak = profile?.bestStreak ?? 0;
        const formDots = (profile?.events || []).slice(-5).reverse();

        // Single-player mini trend + per-game history (newest first)
        const orderedGames = [...filteredEnrichedGames].sort((a, b) => {
          const da = new Date(a.date || 0).getTime();
          const db = new Date(b.date || 0).getTime();
          if (da !== db) return da - db;
          return (a.createdAt || 0) - (b.createdAt || 0);
        });
        const played = orderedGames.filter(g => g.buyIns?.[playerProfileId]);
        let cum = 0;
        const trendLabels = [];
        const trendData = [];
        const gameHistory = [];
        const h2hMap = {};
        played.forEach(g => {
          const payouts = computePayouts(g, jackpotSim.awards);
          const buyIn = Number(g.buyIns?.[playerProfileId] || 0);
          const payout = Number(payouts[playerProfileId] || 0);
          const net = payout - buyIn;
          cum += net;
          // Head-to-head: compare this player's net vs every co-player in the same game
          Object.keys(g.buyIns || {}).forEach((oppId) => {
            if (oppId === playerProfileId || !g.buyIns?.[oppId]) return;
            const oppNet = Number(payouts[oppId] || 0) - Number(g.buyIns[oppId] || 0);
            const rec = h2hMap[oppId] || { oppId, wins: 0, losses: 0, ties: 0, shared: 0 };
            rec.shared++;
            if (net > oppNet) rec.wins++;
            else if (net < oppNet) rec.losses++;
            else rec.ties++;
            h2hMap[oppId] = rec;
          });
          trendLabels.push(formatGameDate(g.date));
          trendData.push(Number(cum.toFixed(2)));
          gameHistory.push({
            id: g.id, date: g.date, location: g.location,
            buyIn, payout, net: Number(net.toFixed(2)),
            isFirst: g.firstWinnerId === playerProfileId || g.splitWinnerId === playerProfileId,
            isSecond: g.secondWinnerId === playerProfileId,
            isThird: g.thirdWinnerId === playerProfileId,
            isJackpot: g.jackpotWinnerId === playerProfileId,
          });
        });
        const recentHistory = [...gameHistory].reverse().slice(0, 8);

        const H2H_MIN_GAMES = 3;
        const h2hRivals = Object.values(h2hMap)
          .filter((r) => r.shared >= H2H_MIN_GAMES)
          .map((r) => ({ ...r, name: getPlayerName(r.oppId), winPct: r.shared ? Math.round((r.wins / r.shared) * 100) : 0 }))
          .sort((a, b) => b.shared - a.shared || b.wins - a.wins)
          .slice(0, 6);

        const maxSingleNet = gameHistory.reduce((m, g) => Math.max(m, g.net), 0);
        const badges = computeBadges({ firsts, bestStreak, jackpots, netProfit, maxSingleNet, games });
        const earnedBadgeCount = badges.filter((b) => b.earned).length;

        const trendColor = netProfit >= 0 ? '#10B981' : '#EF4444';
        const pointsLabels = (profile?.events || []).map((ev) => formatGameDate(ev.date));
        let cumulativePoints = 0;
        const pointsData = (profile?.events || []).map((ev) => {
          cumulativePoints += Number(ev.delta || 0);
          return Number(cumulativePoints.toFixed(2));
        });
        const miniTrendData = {
          labels: trendLabels,
          datasets: [{
            label: "Profit",
            data: trendData,
            borderColor: trendColor,
            backgroundColor: trendColor + '22',
            borderWidth: 2,
            pointRadius: trendLabels.length <= 10 ? 3 : 1,
            tension: 0.3,
            fill: true,
          }],
        };
        const pointsTrendData = {
          labels: pointsLabels,
          datasets: [{
            label: "Points",
            data: pointsData,
            borderColor: '#F59E0B',
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            borderWidth: 2,
            pointRadius: pointsLabels.length <= 10 ? 4 : 2,
            pointHoverRadius: 6,
            pointHitRadius: 18,
            tension: 0.3,
            fill: true,
          }],
        };
        const miniTrendOptions = {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', intersect: false },
          onClick: (event, activeElements, chart) => {
            const nextActive = activeElements || [];
            const nextKey = nextActive.map((item) => `${item.datasetIndex}:${item.index}`).join("|");
            const currentKey = playerTrendTooltipState?.key || "";

            if (nextKey && suppressedPlayerTrendTooltipKeyRef.current === nextKey) {
              chart.setActiveElements([]);
              chart.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
              chart.update();
              return;
            }

            if (!nextActive.length || nextKey === currentKey) {
              suppressedPlayerTrendTooltipKeyRef.current = nextKey || currentKey;
              chart.setActiveElements([]);
              chart.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
              setPlayerTrendTooltipState(null);
              chart.update();
              return;
            }

            suppressedPlayerTrendTooltipKeyRef.current = "";
            chart.setActiveElements(nextActive);
            chart.tooltip?.setActiveElements?.(nextActive, { x: event.x, y: event.y });
            chart.update();
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: false,
              external: ({ chart, tooltip }) => {
                if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
                  setPlayerTrendTooltipState((prev) => (prev ? null : prev));
                  return;
                }

                const key = tooltip.dataPoints.map((point) => `${point.datasetIndex}:${point.dataIndex}`).join("|");
                if (suppressedPlayerTrendTooltipKeyRef.current && suppressedPlayerTrendTooltipKeyRef.current === key) {
                  setPlayerTrendTooltipState((prev) => (prev?.key === key ? null : prev));
                  return;
                }

                const chartWidth = chart.width || 0;
                const tooltipWidth = Math.min(220, Math.max(160, tooltip.width || 0));
                const left = Math.min(
                  Math.max(8, (tooltip.caretX || 0) - tooltipWidth / 2),
                  Math.max(8, chartWidth - tooltipWidth - 8)
                );
                const top = Math.max(8, (tooltip.caretY || 0) - (tooltip.height || 0) - 18);
                const pointIndex = tooltip.dataPoints[0]?.dataIndex ?? -1;
                const pointsBreakdown = playerProfileChartMode === "points" && pointIndex >= 0
                  ? (() => {
                      const ev = profile?.events?.[pointIndex];
                      if (!ev) return [];
                      const fmtPoints = (value) => `${value >= 0 ? "+" : ""}${Number(value || 0).toFixed(1)} pts`;
                      return [
                        { label: "Net", value: fmtPoints(ev.netPoints) },
                        { label: "Placement", value: fmtPoints(ev.placementPoints) },
                        ...(ev.jackpotPoints ? [{ label: "Jackpot", value: fmtPoints(ev.jackpotPoints) }] : []),
                        ...(ev.streakBonus ? [{ label: "Streak", value: fmtPoints(ev.streakBonus) }] : []),
                        ...(ev.buyInPenalty ? [{ label: "Rebuy", value: fmtPoints(ev.buyInPenalty) }] : []),
                        ...(ev.capAdjustment ? [{ label: "Cap adj.", value: fmtPoints(ev.capAdjustment) }] : []),
                      ];
                    })()
                  : [];

                setPlayerTrendTooltipState({
                  key,
                  left,
                  top,
                  title: tooltip.title?.[0] || "",
                  items: tooltip.dataPoints.map((point) => ({
                    datasetIndex: point.datasetIndex,
                    label: point.dataset.label || (playerProfileChartMode === "points" ? "Points" : "Profit"),
                    color: point.dataset.borderColor || point.dataset.backgroundColor || "#94A3B8",
                    value: playerProfileChartMode === "points"
                      ? `${Number(point.parsed.y).toFixed(1)} pts`
                      : euro(point.parsed.y),
                  })),
                  details: pointsBreakdown,
                });
              },
            },
          },
          scales: {
            x: { ticks: { color: chartPalette.axis, maxRotation: 0, maxTicksLimit: 6, font: { size: 10 } }, grid: { color: chartPalette.grid } },
            y: {
              ticks: {
                color: chartPalette.axis,
                font: { size: 10 },
                callback: (v) => playerProfileChartMode === "points"
                  ? `${Number(v).toFixed(0)}`
                  : euro(v)
              },
              grid: { color: chartPalette.grid }
            },
          },
        };

        const profitColorClass = netProfit > 0 ? 'profile-profit-positive' : netProfit < 0 ? 'profile-profit-negative' : '';
        const playerLikelihood = winLikelihoods.find(w => w.id === playerProfileId);

        return (
          <div className="profile-page-overlay">
            <div className="profile-page">
              <div className="profile-page-topbar">
                <button className="profile-page-back" aria-label="Back" onClick={() => { setPlayerProfileId(null); setH2hInfoOpen(false); }}>
                  <ArrowLeft size={20} />
                </button>
                <span className="profile-page-topbar-name">{displayName}</span>
              </div>
              <div className="profile-page-filter-wrap">
                {renderDateFilter()}
              </div>
              <div className="profile-page-content" key={playerProfileId}>
              <div className="profile-header" style={{ paddingTop: 24 }}>
                <AvatarCircle src={getPlayerAvatar(playerProfileId)} alt={name} fallbackLabel={displayName} size={76} />
                <div className="profile-header-info">
                  <div className="profile-name">{name}</div>
                  {player?.nickname && <div className="profile-nickname">"{player.nickname}"</div>}
                  {profile?.rank && (
                    <div className="profile-rank">Rank #{profile.rank} · {points} pts</div>
                  )}
                </div>
              </div>

              {thirdPlaceEnabled ? (
                <>
                  <div className={`profile-stat-hero ${profitColorClass}`}>
                    <div className="profile-stat-val">{signedNetProfit}</div>
                    <div className="profile-stat-lbl">Profit</div>
                  </div>
                  <div className="profile-stats-grid">
                    <div className="profile-stat"><div className="profile-stat-val">{games}</div><div className="profile-stat-lbl">Games</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{firsts}</div><div className="profile-stat-lbl">1st</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{seconds}</div><div className="profile-stat-lbl">2nd</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{thirds}</div><div className="profile-stat-lbl">3rd</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{jackpots}</div><div className="profile-stat-lbl">JP</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{Math.round(buyIns)}</div><div className="profile-stat-lbl">Buy-ins</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{winRate}{winRate !== "—" ? "%" : ""}</div><div className="profile-stat-lbl">Win %</div></div>
                    <div className="profile-stat"><div className="profile-stat-val">{bestStreak}</div><div className="profile-stat-lbl">Streak</div></div>
                  </div>
                </>
              ) : (
                <div className="profile-stats-grid">
                  <div className="profile-stat"><div className="profile-stat-val">{games}</div><div className="profile-stat-lbl">Games</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{firsts}</div><div className="profile-stat-lbl">1st</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{seconds}</div><div className="profile-stat-lbl">2nd</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{jackpots}</div><div className="profile-stat-lbl">JP</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{Math.round(buyIns)}</div><div className="profile-stat-lbl">Buy-ins</div></div>
                  <div className={`profile-stat ${profitColorClass}`}><div className="profile-stat-val">{signedNetProfit}</div><div className="profile-stat-lbl">Profit</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{winRate}{winRate !== "—" ? "%" : ""}</div><div className="profile-stat-lbl">Win %</div></div>
                  <div className="profile-stat"><div className="profile-stat-val">{bestStreak}</div><div className="profile-stat-lbl">Streak</div></div>
                </div>
              )}

              {playerLikelihood && (
                <>
                  <div className="profile-likelihood">
                    <div className="profile-likelihood-label">
                      Chance to win next
                      <button
                        type="button"
                        className="info-toggle-btn info-toggle-btn--inline"
                        aria-expanded={likelihoodInfoOpen}
                        aria-label={likelihoodInfoOpen ? "Hide how this is calculated" : "How is this calculated?"}
                        onClick={() => setLikelihoodInfoOpen((v) => !v)}
                      >
                        <Info size={13} />
                      </button>
                    </div>
                    <div className="profile-likelihood-bar-wrap">
                      <div className="profile-likelihood-bar" style={{ width: `${playerLikelihood.pct}%` }} />
                    </div>
                    <div className="profile-likelihood-pct">{playerLikelihood.pct}%</div>
                  </div>
                  {likelihoodInfoOpen && (
                    <p className="profile-likelihood-info">
                      An estimate of finishing 1st next game, based on how often each player has won so far. Players with only a few games are pulled toward an even share, so one lucky night doesn't swing it. All players' chances add up to 100%.
                    </p>
                  )}
                </>
              )}

              {formDots.length > 0 && (
                <div className="profile-form-row">
                  <span className="profile-form-label">Form</span>
                  <div className="form-guide">
                    {formDots.map((ev, i) => (
                      <span key={i} className={`form-dot ${ev.net > 0 ? 'form-dot-win' : ev.net < 0 ? 'form-dot-loss' : 'form-dot-even'}`} title={`${ev.net >= 0 ? '+' : ''}${ev.net}`} />
                    ))}
                  </div>
                </div>
              )}

              {trendData.length > 1 && (
                <>
                  <div className="chart-mode-toggle profile-chart-mode-toggle">
                    <button
                      className={`chart-mode-btn${playerProfileChartMode === "profit" ? " active" : ""}`}
                      onClick={() => setPlayerProfileChartMode("profit")}
                    >
                      Profit
                    </button>
                    <button
                      className={`chart-mode-btn${playerProfileChartMode === "points" ? " active" : ""}`}
                      onClick={() => setPlayerProfileChartMode("points")}
                    >
                      Points
                    </button>
                  </div>
                  <div className="profile-trend-wrap">
                    <Line
                      ref={playerTrendChartRef}
                      data={playerProfileChartMode === "points" ? pointsTrendData : miniTrendData}
                      options={miniTrendOptions}
                    />
                  {playerTrendTooltipState && (() => {
                    const dismissPlayerChart = () => {
                      const chart = playerTrendChartRef.current;
                      suppressedPlayerTrendTooltipKeyRef.current = playerTrendTooltipState.key || "";
                      chart?.setActiveElements?.([]);
                      chart?.tooltip?.setActiveElements?.([], { x: 0, y: 0 });
                      chart?.update?.();
                      setPlayerTrendTooltipState(null);
                    };
                    return (
                      <>
                        <div
                          className="chart-tooltip-dismiss-overlay"
                          onPointerDown={(e) => { e.preventDefault(); dismissPlayerChart(); }}
                        />
                        <button
                          type="button"
                          className="chart-tooltip-popup"
                          style={{ left: playerTrendTooltipState.left, top: playerTrendTooltipState.top }}
                          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); dismissPlayerChart(); }}
                        >
                          <div className="chart-tooltip-title">{playerTrendTooltipState.title}</div>
                          {playerTrendTooltipState.items.map((item) => (
                            <div key={`${item.datasetIndex}-${item.label}`} className="chart-tooltip-row">
                              <span className="chart-tooltip-dot" style={{ background: item.color }} />
                              <span className="chart-tooltip-label">{item.label}</span>
                              <span className="chart-tooltip-value">{item.value}</span>
                            </div>
                          ))}
                          {playerTrendTooltipState.details?.length > 0 && (
                            <div className="chart-tooltip-details">
                              {playerTrendTooltipState.details.map((detail) => (
                                <div key={`${detail.label}-${detail.value}`} className="chart-tooltip-detail-row">
                                  <span className="chart-tooltip-detail-label">{detail.label}</span>
                                  <span className="chart-tooltip-detail-value">{detail.value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </button>
                      </>
                    );
                  })()}
                </div>
                </>
              )}

              {(
                <>
                  <div className="profile-section-label">Badges · {earnedBadgeCount}/{badges.length}</div>
                  <div className="hex-badge-grid">
                    {badges.map((b) => {
                      const prog = getBadgeProgress(b.id, { firsts, bestStreak, jackpots, netProfit, maxSingleNet, games }, euro);
                      return (
                        <div key={b.id} className={`hex-badge-card${b.earned ? " earned" : ""}`}>
                          <div className="hex-badge-card-top">
                            {makeBadgeSVG(b.glyph, 46, !b.earned, b.accent)}
                            {b.earned && (
                              <svg className="hex-badge-check" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill={`rgba(${b.accent.glow},.16)`} />
                                <path d="M8 12.4 L11 15.2 L16.2 9" fill="none" stroke={b.accent.light} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="hex-badge-name">{b.label}</div>
                            <div className="hex-badge-desc">{b.desc}</div>
                          </div>
                          <div className="hex-badge-bar-track">
                            <div className="hex-badge-bar-fill" style={{ width: `${prog.pct}%`, background: b.accent.base }} />
                          </div>
                          <div className="hex-badge-label" style={b.earned ? { color: b.accent.light } : undefined}>{prog.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {recentHistory.length > 0 && (
                <>
                  <div className="profile-section-label">Recent Games</div>
                  <div className="profile-game-list">
                    {recentHistory.map(g => (
                      <div key={g.id} className="profile-game-row">
                        <div className="profile-game-left">
                          <div className="profile-game-date">{formatGameDate(g.date)}</div>
                          {g.location && <div className="profile-game-loc">{g.location}</div>}
                          <div className="profile-game-badges">
                            {g.isFirst && <span className="profile-badge profile-badge-first">1st</span>}
                            {g.isSecond && <span className="profile-badge profile-badge-second">2nd</span>}
                            {g.isThird && <span className="profile-badge profile-badge-third">3rd</span>}
                            {g.isJackpot && <span className="profile-badge profile-badge-jp">JP</span>}
                          </div>
                        </div>
                        <div className={`profile-game-net ${g.net > 0 ? 'profit-low' : g.net < 0 ? 'profit-negative' : ''}`}>
                          {g.net > 0 ? '+' : ''}{euro(g.net)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {h2hRivals.length > 0 && (
                <>
                  <div className="profile-section-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    Head to head
                    <button
                      className="h2h-info-btn"
                      onClick={() => setH2hInfoOpen(o => !o)}
                      aria-label="How head to head works"
                    >
                      <Info size={13} strokeWidth={2} />
                    </button>
                  </div>
                  {h2hInfoOpen && (
                    <div className="h2h-info-popover">
                      <strong>W – L – T</strong> counts the nights you both played. A <strong>win</strong> means you made more profit than them that night. A <strong>loss</strong> means they made more. A <strong>tie</strong> means you both netted the same amount.
                    </div>
                  )}
                  <div className="h2h-list">
                    {h2hRivals.map((r) => (
                      <div key={r.oppId} className="h2h-row">
                        <AvatarCircle src={getPlayerAvatar(r.oppId)} alt={r.name} fallbackLabel={r.name} size={32} />
                        <div className="h2h-info">
                          <div className="h2h-name">{r.name}</div>
                          <div className="h2h-bar" aria-hidden="true">
                            <div className="h2h-bar-win" style={{ width: `${r.winPct}%` }} />
                          </div>
                        </div>
                        <div className="h2h-record">
                          <span className={`h2h-rec ${r.wins > r.losses ? 'h2h-rec-win' : r.wins < r.losses ? 'h2h-rec-loss' : ''}`}>
                            {r.wins}–{r.losses}–{r.ties || 0}
                          </span>
                          <span className="h2h-rec-sub">won {r.wins} of {r.shared}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Ranking breakdown (merged from info panel) */}
              {profile && (() => {
                const fmt = v => `${v >= 0 ? '+' : ''}${Number(Number(v).toFixed(1))}`; // 126, 4.5 (no trailing .0)
                const bRows = [
                  { label: "Profit / loss", calc: `${euro(profile.netEuroTotal)} × ${rankingConfig.netMultiplier}`, value: fmt(profile.pointsFromNet), positive: profile.pointsFromNet >= 0 },
                  { label: "Placement bonuses", calc: `${profile.firsts}×${rankingConfig.firstPlaceBonus} + ${profile.seconds}×${rankingConfig.secondPlaceBonus} + ${profile.thirds ?? 0}×${rankingConfig.thirdPlaceBonus}`, value: fmt(profile.pointsFromPlacement), positive: profile.pointsFromPlacement >= 0 },
                  { label: "Streak bonus", calc: `${profile.streakBonusUnits?.toFixed(0) ?? 0} × ${rankingConfig.streakStep}`, value: fmt(profile.pointsFromStreak), positive: profile.pointsFromStreak >= 0 },
                  { label: "Rebuy penalty", calc: `${profile.rebuyCount?.toFixed(0) ?? 0} × −${rankingConfig.rebuyPenalty}`, value: fmt(profile.pointsFromBuyInPenalty), positive: profile.pointsFromBuyInPenalty >= 0 },
                  ...(profile.pointsFromCapAdjustment !== 0 ? [{ label: "Game cap adjust", calc: `${rankingConfig.gameCapMin}..${rankingConfig.gameCapMax}`, value: fmt(profile.pointsFromCapAdjustment), positive: profile.pointsFromCapAdjustment >= 0 }] : []),
                ];
                return (
                  <>
                    <div className="breakdown-section-label" style={{ marginTop: 20 }}>Points Breakdown</div>
                    <div className="breakdown-list">
                      {bRows.map((row, i) => (
                        <div key={i} className="breakdown-row">
                          <div className="breakdown-row-left">
                            <div className="breakdown-row-label">{row.label}</div>
                            <div className="breakdown-row-calc">{row.calc}</div>
                          </div>
                          <div className={`breakdown-row-value ${row.positive ? 'profit-low' : 'profit-negative'}`}>{row.value}</div>
                        </div>
                      ))}
                      <div className="breakdown-row breakdown-total-row">
                        <div className="breakdown-row-label">Total</div>
                        <div className={`breakdown-row-value ${getRankingPointsClass(profile.points)}`}>{profile.points}</div>
                      </div>
                    </div>
                  </>
                );
              })()}
              </div>{/* profile-page-content */}
            </div>{/* profile-page */}
          </div>
        );
      })()}

      {/* Game Saved Modal */}
      {gameSavedMessage && (
        <div className="modal-backdrop" onClick={() => setGameSavedMessage("")}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2>Saved</h2>
              <button className="iconbtn close-btn" aria-label="Close saved message" onClick={() => setGameSavedMessage("")}>✕</button>
            </div>
            <div className="leaderboard-note" style={{ marginTop: 8 }}>{gameSavedMessage}</div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => setGameSavedMessage("")}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Related Games Full-Page Overlay */}
      {relatedGamesView && (
        <div className="profile-page-overlay">
          <div className="profile-page">
            <div className="profile-page-topbar">
              <button className="profile-page-back" aria-label="Back" onClick={() => setRelatedGamesView(null)}>
                <ArrowLeft size={20} />
              </button>
              <span className="profile-page-topbar-name">{relatedGamesView.title}</span>
            </div>
            <div className="profile-page-content" key={relatedGamesView?.title} style={{ paddingTop: '8px' }}>
              {relatedGames.length === 0 ? (
                <div className="leaderboard-note" style={{ marginTop: 40, textAlign: 'center' }}>No matching games.</div>
              ) : (
                <div className="game-feed-list" style={{ marginBottom: 0 }}>
                  {relatedGames.map((g) => (
                    <GameRow key={g.id} g={g} formatGameDate={formatGameDate} getPlayerName={getPlayerName} getPlayerAvatar={getPlayerAvatar} euro={euro} setInfoGameId={setInfoGameId} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Game Info Full-Page Overlay */}
      {(() => {
        const info = enrichedGames.find((x) => x.id === infoGameId) || null;
        if (!info) return null;
        const splitShare = info.splitGross || 0;
        // What 1st place actually takes home: their share after the jackpot cut.
        const firstGrossDisplay = info.splitWinnerId ? splitShare : (info.firstWinnerId ? (info.firstGross || 0) - (info.jpTaken || 0) : 0);
        const secondWin = info.secondWinnerId ? (info.secondGross || 0) : 0;
        const thirdWin = info.thirdWinnerId ? (info.thirdGross || 0) : 0;
        const buyIn = (pid) => Number(info.buyIns?.[pid] || 0);
        const jpAward = (pid) => (pid && info.jackpotWinnerId === pid) ? (jackpotSim.awards[info.id] || 0) : 0;
        const fmtNet = (n) => (n >= 0 ? "+" : "") + euro(n);
        // Net = gross payout received - buy-in + jackpot win
        // jpTaken (jackpot contribution) is deducted from 1st place gross; already baked into splitGross.
        const firstNet = info.firstWinnerId
          ? (info.splitWinnerId ? splitShare : (info.firstGross - (info.jpTaken || 0)))
            - buyIn(info.firstWinnerId) + jpAward(info.firstWinnerId)
          : null;
        const splitNet = info.splitWinnerId
          ? splitShare - buyIn(info.splitWinnerId) + jpAward(info.splitWinnerId)
          : null;
        const secondNet = info.secondWinnerId
          ? secondWin - buyIn(info.secondWinnerId) + jpAward(info.secondWinnerId)
          : null;
        const thirdNet = info.thirdWinnerId
          ? thirdWin - buyIn(info.thirdWinnerId) + jpAward(info.thirdWinnerId)
          : null;
        const buyInEntries = Object.keys(info.buyIns || {})
          .map(pid => ({ pid, name: getPlayerName(pid), val: Number(info.buyIns[pid] || 0) }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        const buyInAmountPerEntry = info.buyInAmount || 0;
        const buyInPerPlayerEntries = buyInAmountPerEntry > 0
          ? buyInEntries.map(({ pid, name, val }) => ({ pid, name, val, isRebuy: val > buyInAmountPerEntry }))
          : buyInEntries.map(({ pid, name, val }) => ({ pid, name, val, isRebuy: false }));
        const durationLabel = formatGameDuration(info.durationMs);
        const settlePayouts = computePayouts(info, jackpotSim.awards);
        const settleNets = Object.keys(info.buyIns || {}).map((pid) => ({
          pid,
          name: getPlayerName(pid),
          net: Number(settlePayouts[pid] || 0) - Number(info.buyIns[pid] || 0),
        }));
        const settlements = computeSettlements(settleNets);
        return (
          <div className="profile-page-overlay" style={infoGameSource === "jackpot-history" ? { zIndex: 901 } : undefined}>
            <div className="profile-page">
              <div className="profile-page-topbar">
                <button className="profile-page-back" aria-label="Back" onClick={() => { setInfoGameId(null); setInfoGameSource(null); }}>
                  <ArrowLeft size={20} />
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <span className="profile-page-topbar-name">Game details</span>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <button className="iconbtn" aria-label="Edit game" disabled={!canManageSingleGame(info)} onClick={() => { editGame(info); setInfoGameId(null); setInfoGameSource(null); }}><Pencil size={18} /></button>
                  <button className="iconbtn" aria-label="Delete game" disabled={!canManageSingleGame(info)} onClick={() => { deleteGame(info.id); setInfoGameId(null); setInfoGameSource(null); }}><Trash2 size={18} /></button>
                </div>
              </div>
              <div className="profile-page-content" key={infoGameId}>

              {/* Pot hero, like a transaction: the amount, then where and when */}
              <div className="game-info-hero">
                <span className="game-info-hero-icon" aria-hidden="true"><Coins size={24} /></span>
                <span className="game-info-hero-value">{euro(info.pot)}</span>
                <span className="game-info-hero-sub">
                  {[info.location || "Game night", formatGameDate(info.date), durationLabel].filter(Boolean).join(" · ")}
                </span>
              </div>

              {/* Winners */}
              <div className="breakdown-section-label">Winners</div>
              <div className="game-info-winners">
                {info.firstWinnerId ? (
                  <div className="game-info-winner-row">
                    <div className={`game-info-winner-badge ${info.splitWinnerId ? "game-info-badge-split" : "game-info-badge-1"}`}>{info.splitWinnerId ? "Tie" : "1st"}</div>
                    <AvatarCircle src={getPlayerAvatar(info.firstWinnerId)} alt={getPlayerName(info.firstWinnerId)} fallbackLabel={getPlayerName(info.firstWinnerId)} size={32} />
                    <div className="game-info-winner-name">{getPlayerName(info.firstWinnerId)}</div>
                    <div className="game-info-winner-prize">
                      <div>{euro(firstGrossDisplay)}</div>
                      {firstNet !== null && <div className="game-info-winner-net">{fmtNet(firstNet)}</div>}
                    </div>
                  </div>
                ) : null}
                {info.splitWinnerId ? (
                  <div className="game-info-winner-row">
                    <div className="game-info-winner-badge game-info-badge-split">Tie</div>
                    <AvatarCircle src={getPlayerAvatar(info.splitWinnerId)} alt={getPlayerName(info.splitWinnerId)} fallbackLabel={getPlayerName(info.splitWinnerId)} size={32} />
                    <div className="game-info-winner-name">{getPlayerName(info.splitWinnerId)}</div>
                    <div className="game-info-winner-prize">
                      <div>{euro(splitShare)}</div>
                      {splitNet !== null && <div className="game-info-winner-net">{fmtNet(splitNet)}</div>}
                    </div>
                  </div>
                ) : null}
                {!info.splitWinnerId && info.secondWinnerId ? (
                  <div className="game-info-winner-row">
                    <div className="game-info-winner-badge game-info-badge-2">2nd</div>
                    <AvatarCircle src={getPlayerAvatar(info.secondWinnerId)} alt={getPlayerName(info.secondWinnerId)} fallbackLabel={getPlayerName(info.secondWinnerId)} size={32} />
                    <div className="game-info-winner-name">{getPlayerName(info.secondWinnerId)}</div>
                    <div className="game-info-winner-prize">
                      <div>{euro(secondWin)}</div>
                      {secondNet !== null && <div className="game-info-winner-net">{fmtNet(secondNet)}</div>}
                    </div>
                  </div>
                ) : null}
                {!info.splitWinnerId && info.thirdWinnerId ? (
                  <div className="game-info-winner-row">
                    <div className="game-info-winner-badge game-info-badge-3">3rd</div>
                    <AvatarCircle src={getPlayerAvatar(info.thirdWinnerId)} alt={getPlayerName(info.thirdWinnerId)} fallbackLabel={getPlayerName(info.thirdWinnerId)} size={32} />
                    <div className="game-info-winner-name">{getPlayerName(info.thirdWinnerId)}</div>
                    <div className="game-info-winner-prize">
                      <div>{euro(thirdWin)}</div>
                      {thirdNet !== null && <div className="game-info-winner-net">{fmtNet(thirdNet)}</div>}
                    </div>
                  </div>
                ) : null}
                {info.jpTaken > 0 && (
                  <div className="game-info-winner-row game-info-cut-row">
                    <div className="game-info-cut-label">
                      <span>Jackpot cut</span>
                      <small>
                        {info.splitWinnerId
                          ? "Taken before the pot was split"
                          : `From 1st place: ${euro(info.firstGross)} − ${euro(info.jpTaken)}`}
                      </small>
                    </div>
                    <span className="game-info-cut-value">{euro(info.jpTaken)}</span>
                  </div>
                )}
                {!info.firstWinnerId && !info.secondWinnerId && <div className="leaderboard-note" style={{ marginTop: 0 }}>No winners recorded.</div>}
              </div>

              {/* Buy-ins */}
              {buyInPerPlayerEntries.length > 0 && (
                <>
                  <div className="breakdown-section-label" style={{ marginTop: 16 }}>Buy-ins</div>
                  <div className="game-info-buyins">
                    {buyInPerPlayerEntries.map(({ pid, name, val, isRebuy }) => (
                      <div key={pid} className="game-info-buyin-row">
                        <span className="game-info-buyin-name">
                          <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={28} />
                          <span className="game-info-buyin-name-text">
                            {name}
                            {isRebuy && <span style={{ marginLeft: 6, fontSize: 11, background: "var(--accent)", color: "var(--accent-foreground)", borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>rebuy</span>}
                          </span>
                        </span>
                        <span className="game-info-buyin-val">{euro(val)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Timeline: how the night played out (games saved with timestamps) */}
              {(() => {
                const timeline = savedGameTimeline(info);
                if (!timeline) return null;
                const built = buildLiveLanes(timeline, timeline.endedAt);
                return (
                  <>
                    <div className="section-label-row" style={{ marginTop: 16 }}>
                      <div className="breakdown-section-label">Timeline</div>
                      {canManageSingleGame(info) && (
                        <button type="button" className="section-label-action" onClick={() => openGameTimelineEdit(info)}>Edit</button>
                      )}
                    </div>
                    <button
                      type="button"
                      className="game-info-timeline game-info-timeline--open"
                      onClick={() => setTimelineDetailOpen(true)}
                      aria-label={`Timeline from ${clock(built.start)} to ${clock(built.end)}. Open the full timeline.`}
                    >
                      <LaneTimeline {...built} getName={getPlayerName} formatMoney={euro} />
                      <span className="game-info-timeline-hint" aria-hidden="true">Full timeline <ChevronRight size={14} /></span>
                    </button>
                    {timelineDetailOpen && (
                      <TimelineDetail
                        title={[info.location || "Game night", formatGameDate(info.date)].filter(Boolean).join(" · ")}
                        startedAt={timeline.startedAt}
                        endedAt={timeline.endedAt}
                        events={timeline.events}
                        playerIds={timeline.playerIds}
                        buyIns={timeline.buyIns}
                        buyInAmount={timeline.buyInAmount}
                        nets={Object.fromEntries(settleNets.map((n) => [n.pid, Number(n.net.toFixed(2))]))}
                        getName={getPlayerName}
                        renderAvatar={(pid, name, size = 32) => <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={size} />}
                        formatMoney={euro}
                        onEdit={canManageSingleGame(info) ? () => openGameTimelineEdit(info) : null}
                        onClose={() => setTimelineDetailOpen(false)}
                      />
                    )}
                  </>
                );
              })()}

              {/* Settle up — who pays whom in the fewest transfers */}
              {settleNets.length > 1 && (
                <>
                  <div className="breakdown-section-label" style={{ marginTop: 16 }}>Settle up</div>
                  {settlements.length === 0 ? (
                    <div className="settle-empty">All square — no payments needed.</div>
                  ) : (
                    <div className="settle-list">
                      {settlements.map((t, i) => (
                        <div key={i} className="settle-row">
                          <span className="settle-flow">
                            <span className="settle-party">
                              <AvatarCircle src={getPlayerAvatar(t.fromPid)} alt={t.fromName} fallbackLabel={t.fromName} size={26} />
                              <span className="settle-name">{t.fromName}</span>
                            </span>
                            <ArrowRight size={15} className="settle-arrow" />
                            <span className="settle-party">
                              <AvatarCircle src={getPlayerAvatar(t.toPid)} alt={t.toName} fallbackLabel={t.toName} size={26} />
                              <span className="settle-name">{t.toName}</span>
                            </span>
                          </span>
                          <span className="settle-amount">{euro(t.amount)}</span>
                        </div>
                      ))}
                      <div className="settle-hint">{settlements.length > 1 ? `${settlements.length} payments settle the night` : "1 payment settles the night"}</div>
                    </div>
                  )}
                </>
              )}

              {/* Jackpot */}
              {info.jackpotWinnerId && (
                <>
                  <div className="breakdown-section-label" style={{ marginTop: 16 }}>Jackpot</div>
                  <div className="game-info-jackpot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AvatarCircle src={getPlayerAvatar(info.jackpotWinnerId)} alt={getPlayerName(info.jackpotWinnerId)} fallbackLabel={getPlayerName(info.jackpotWinnerId)} size={28} />
                      <span className="game-info-winner-name" style={{ fontSize: 14 }}>{getPlayerName(info.jackpotWinnerId)}</span>
                    </div>
                    <span className="game-info-winner-prize">{euro(jackpotSim.awards[info.id] || 0)}</span>
                  </div>
                  {info.jackpotCards && (
                    <div className="game-info-jackpot-cards">
                      {info.jackpotCards.split(' ').filter(Boolean).map((card, i) => (
                        <PlayingCard key={i} card={card} />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Notes */}
              {info.notes && (
                <>
                  <div className="breakdown-section-label" style={{ marginTop: 16 }}>Notes</div>
                  <div className="game-info-notes">{info.notes}</div>
                </>
              )}

              {/* Change log: who saved, edited or re-timed this game, from the server */}
              <div className="breakdown-section-label" style={{ marginTop: 16 }}>Change log</div>
              <SessionLog
                entries={infoGameLog}
                getName={getPlayerName}
                formatMoney={euro}
                emptyText="No changes logged. Games saved before the change log started have no history."
              />
              </div>{/* profile-page-content */}
            </div>{/* profile-page */}
          </div>
        );
      })()}

      {timelineEdit && (
        <TimelineEditor
          live={timelineEdit.mode === "live"}
          startedAt={timelineEdit.startedAt}
          endedAt={timelineEdit.endedAt}
          events={timelineEdit.events}
          playerIds={timelineEdit.playerIds}
          getName={getPlayerName}
          renderAvatar={(pid, name) => <AvatarCircle src={getPlayerAvatar(pid)} alt={name} fallbackLabel={name} size={32} />}
          onSave={saveTimelineEdit}
          onClose={() => setTimelineEdit(null)}
        />
      )}

      {/* What's new page */}
      {whatsNewOpen && (
        <div className="profile-page-overlay">
          <div className="profile-page">
            <div className="profile-page-topbar">
              <button className="profile-page-back" aria-label="Back" onClick={() => setWhatsNewOpen(false)}>
                <ArrowLeft size={20} />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                <span className="profile-page-topbar-name">What's new</span>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 1 }}>Currently on v{APP_VERSION}</span>
              </div>
            </div>
            <div className="profile-page-content" key="whats-new">
              <div className="whatsnew-list">
                {CHANGELOG.map((release, idx) => (
                  <div className="whatsnew-release" key={release.version}>
                    <div className="whatsnew-release-head">
                      <span className="whatsnew-version">v{release.version}</span>
                      {idx === 0 && <span className="whatsnew-latest">Latest</span>}
                      <span className="whatsnew-date">{release.date}</span>
                    </div>
                    <ul className="whatsnew-changes">
                      {release.changes.map((change, i) => (
                        <li className="whatsnew-change" key={i}>{change}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Poker trainer: portalled to <body> so the pull-to-refresh transform on
          the app shell can't become its containing block. */}
      {trainerOpen && createPortal(
        <React.Suspense fallback={<div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "#07090b" }} />}>
          <PokerTrainer
            onClose={() => setTrainerOpen(false)}
            user={user}
            groupId={activeGroupId || ""}
            groupName={groupMeta?.name || ""}
            identity={trainerIdentity}
          />
        </React.Suspense>,
        document.body
      )}

      {/* Jackpot history page */}
      {jackpotHistoryOpen && (
        <div className="profile-page-overlay">
          <div className="profile-page">
            <div className="profile-page-topbar">
              <button className="profile-page-back" aria-label="Back" onClick={() => { setJackpotHistoryOpen(false); setJackpotFilterId(null); }}>
                <ArrowLeft size={20} />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                <span className="profile-page-topbar-name">Jackpot History</span>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 1 }}>Current jackpot: {euro(Math.round(jackpotBalance))}</span>
              </div>
            </div>
            <div className="profile-page-content" key="jackpot-history">
              {jackpotHistory.length === 0 ? (
                <div className="jp-history-empty">No jackpots have been won yet.</div>
              ) : (
                <>
                  <div className="jp-history-section-label">Total won per player</div>
                  <div className="jp-history-totals">
                    {jackpotTotals.map((t) => (
                      <button
                        key={t.winnerId}
                        className={`jp-history-total-chip${jackpotFilterId === t.winnerId ? " jp-history-total-chip-active" : ""}`}
                        aria-pressed={jackpotFilterId === t.winnerId}
                        onClick={() => setJackpotFilterId((cur) => cur === t.winnerId ? null : t.winnerId)}
                      >
                        <span className="jp-history-total-name">{t.name}</span>
                        <span className="jp-history-total-amount">{euro(t.total)}</span>
                      </button>
                    ))}
                  </div>
                  <div className="jp-history-section-header">
                    <span className="jp-history-section-label">
                      {jackpotFilterId ? `${getPlayerName(jackpotFilterId)}'s jackpots` : "All jackpots"}
                    </span>
                    {jackpotFilterId && (
                      <button className="jp-history-clear-filter" onClick={() => setJackpotFilterId(null)}>Show all</button>
                    )}
                  </div>
                  <div className="jp-history-list">
                  {jackpotHistory.filter((row) => !jackpotFilterId || row.winnerId === jackpotFilterId).map((row) => (
                    <button
                      key={row.gameId}
                      className="jp-history-row"
                      onClick={() => { setInfoGameId(row.gameId); setInfoGameSource("jackpot-history"); }}
                    >
                      <AvatarCircle src={getPlayerAvatar(row.winnerId)} alt={row.name} fallbackLabel={row.name} size={36} />
                      <div className="jp-history-row-main">
                        <span className="jp-history-row-name">{row.name}</span>
                        <span className="jp-history-row-date">{formatGameDate(row.date)}</span>
                      </div>
                      <span className="jp-history-row-amount">{euro(row.amount)}</span>
                    </button>
                  ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo crop modal */}
      {cropImageSrc && (
        <div className="modal-backdrop crop-modal-backdrop">
          <div className="modal crop-modal">
            <div className="modal-sheet-header" style={{ marginBottom: 16 }}>
              <span className="modal-sheet-title">Adjust photo</span>
              <button className="iconbtn" aria-label="Cancel" onClick={() => setCropImageSrc(null)}>✕</button>
            </div>

            <div
              className="crop-container"
              onMouseDown={handleCropMouseDown}
              onMouseMove={handleCropMouseMove}
              onMouseUp={handleCropMouseUp}
              onMouseLeave={handleCropMouseUp}
              onWheel={handleCropWheel}
              onTouchStart={handleCropTouchStart}
              onTouchMove={handleCropTouchMove}
              onTouchEnd={handleCropTouchEnd}
            >
              <img
                ref={cropImgRef}
                src={cropImageSrc}
                alt=""
                onLoad={handleCropImageLoad}
                className="crop-image"
                style={{
                  width: cropNaturalSize.w > 0 ? cropNaturalSize.w * cropScale : "auto",
                  height: cropNaturalSize.w > 0 ? cropNaturalSize.h * cropScale : "auto",
                  left: cropNaturalSize.w > 0 ? 140 - (cropNaturalSize.w * cropScale) / 2 + cropOffset.x : 0,
                  top: cropNaturalSize.w > 0 ? 140 - (cropNaturalSize.h * cropScale) / 2 + cropOffset.y : 0,
                  opacity: cropNaturalSize.w > 0 ? 1 : 0,
                }}
                draggable={false}
              />
              <div className="crop-overlay" />
            </div>

            <div className="crop-zoom-row">
              <span className="crop-zoom-label">Zoom</span>
              <input
                type="range"
                className="crop-zoom-slider"
                min={0}
                max={100}
                value={cropNaturalSize.w ? Math.round(((cropScale - Math.max(280 / cropNaturalSize.w, 280 / cropNaturalSize.h)) / (Math.max(280 / cropNaturalSize.w, 280 / cropNaturalSize.h) * 3)) * 100) : 0}
                onChange={(e) => {
                  const minScale = cropNaturalSize.w ? Math.max(280 / cropNaturalSize.w, 280 / cropNaturalSize.h) : 1;
                  const newScale = minScale + (parseFloat(e.target.value) / 100) * minScale * 3;
                  setCropScale(newScale);
                  setCropOffset(prev => clampCropOffset(prev.x, prev.y, newScale));
                }}
              />
            </div>

            <div className="row" style={{ gap: 10, marginTop: 16 }}>
              <button className="btn secondary" style={{ flex: 1 }} onClick={() => setCropImageSrc(null)}>Cancel</button>
              <button className="btn" style={{ flex: 1 }} onClick={confirmCrop} disabled={cropUploading || !cropNaturalSize.w}>
                {cropUploading ? "Saving…" : "Use photo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar picker sheet */}
      {showAvatarPicker && !user?.isAnonymous && (
        <div className="modal-backdrop" onClick={() => setShowAvatarPicker(false)}>
          <div className="modal bottom-sheet avatar-picker-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-drag-handle" />
            <div className="modal-sheet-header">
              <span className="modal-sheet-title">Profile photo</span>
              <button className="iconbtn" aria-label="Close" onClick={() => setShowAvatarPicker(false)}>✕</button>
            </div>

            {(() => {
              // Inside the picker, reflect draft state for checkmarks and preview
              const effectiveSource = photoSourceDraft ?? currentUserProfileData?.photoSource;
              const monogramName = currentUserProfileData?.profileName || user?.displayName || "User";
              const pickerPreviewSrc = photoPreviewURL || (effectiveSource === "google" ? (currentUserProfileData?.googleProviderPhotoURL || "") : effectiveSource === "upload" ? (currentUserProfileData?.profilePhotoURL || "") : "");
              const hasUploadedPhoto = effectiveSource === "upload" || cropBlobDraft;
              return (<>
                <div className="avatar-picker-preview">
                  <AvatarCircle
                    src={pickerPreviewSrc}
                    alt="Profile"
                    fallbackLabel={monogramName}
                    size={80}
                  />
                </div>

                <div className="avatar-picker-options">
                  <button className="avatar-picker-option" onClick={() => { setShowAvatarPicker(false); avatarInputRef.current?.click(); }}>
                    <Camera size={22} className="avatar-picker-option-icon" />
                    <span className="avatar-picker-option-label">Upload a photo</span>
                    {effectiveSource === "upload" && <Check size={16} className="avatar-picker-check" />}
                  </button>

                  {currentUserProfileData?.googleProviderPhotoURL && (
                    <button className="avatar-picker-option" onClick={handleSelectGooglePhoto}>
                      <span className="avatar-picker-thumb avatar-picker-generated-thumb">
                        <img src={currentUserProfileData.googleProviderPhotoURL} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", display: "block" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      </span>
                      <span className="avatar-picker-option-label">Use Google photo</span>
                      {effectiveSource === "google" && <Check size={16} className="avatar-picker-check" />}
                    </button>
                  )}

                  <button className="avatar-picker-option" onClick={handleSelectGeneratedAvatar}>
                    <span className="avatar-picker-thumb avatar-picker-generated-thumb">
                      <AvatarCircle alt="" fallbackLabel={monogramName} size={28} />
                    </span>
                    <span className="avatar-picker-option-label">Monogram avatar</span>
                    {(effectiveSource === "generated" || effectiveSource === "delete") && <Check size={16} className="avatar-picker-check" />}
                  </button>

                  {hasUploadedPhoto && (
                    <button className="avatar-picker-option avatar-picker-option-danger" onClick={handleDeletePhoto}>
                      <Trash2 size={22} className="avatar-picker-option-icon" />
                      <span className="avatar-picker-option-label">Delete uploaded photo</span>
                    </button>
                  )}
                </div>
              </>);
            })()}
          </div>
        </div>
      )}

    </div>
  );
}
